import Phaser from 'phaser';
import { TILE_W, TILE_H } from '../config';
import { PALETTE, PANEL_BG } from '../theme';
import type {
  TurnWorld, Unit, Building, PlayerId, GameConfig, UnitType, BuildingType, Scenario, LocalizedString, BuildingGroup, TerrainType, BattleScript, BattleSide, Domain,
} from '../turn/types';
import { UNIT_DEFS, resolveBaseType } from '../turn/units';
import { BUILDING_DEFS } from '../turn/buildings';
import { TERRAIN_DEFS, getMovementCost, isCoast } from '../turn/terrain';
import { loadScenario } from '../turn/scenarioLoader';
import { ScenarioRunner } from '../turn/scenarioRunner';
import type { ObjectiveStatus } from '../turn/scenarioRunner';
import type { EventAction } from '../turn/types';
import { getMainCatalog, getTutorialCatalog } from '../turn/scenarioCatalog';
import {
  startGame, executeEndTurn, currentPlayerId, currentPlayer, startTraining, cancelTraining, cancelDisruptedTraining, isTileOccupied, checkElimination, checkVictory,
} from '../turn/turnEngine';
import { getMovementRange, findPath, parseCoordKey } from '../turn/pathfinding';
import type { Coord } from '../turn/pathfinding';
import { canAttack, buildBattleScript, calculateDamage, chebyshevDistance, applyAmbushDamage } from '../turn/combat';
import { getEffectiveness } from '../turn/combat-matrix';
import { levelForXp, tierForLevel, starsForLevel } from '../turn/xp';
import { canCapture, canUnitCapture, executeCapture, validateCaptures, getBuildingAt } from '../turn/capture';
import { canBuildOrContinue, executeBuild, validateBuilds, getBuildInProgress } from '../turn/build';
import { aiActNextUnit, aiRecruitAll } from '../turn/ai';
import { canLoadInto, executeLoad, getUnloadTiles, executeUnload } from '../turn/cargo';
import { computePlayerVisible, computeUnitVision, computeBuildingVision, initExplored, updateExplored } from '../turn/vision';
import { computeZocTiles } from '../turn/zoc';
import { computeInfluenceMap, extractFrontLines } from '../turn/frontLine';
import type { FrontLineSegment, InfluenceGrid } from '../turn/frontLine';
import { computeHealTargets, executeHeal, executeRepairs } from '../turn/healing';
import type { HealResult, BuildingRepairResult } from '../turn/healing';
import { consumeMoveSupply, computeResupplyTargets, executeResupply, consumeIdleSupply, applyAttrition, executeBuildingResupply, resolveResupplyDomain } from '../turn/supply';
import type { ResupplyResult, BuildingResupplyResult } from '../turn/supply';
import { applyAuraHealing } from '../turn/auras';
import { canUseAbility, executeAirStrike, executeRecon, RECON_RADIUS, type AirStrikeResult } from '../turn/abilities';
import { SettingsDialog } from '../ui/SettingsDialog';
import { audio } from '../audio/instances';
import { t, tp, localize } from '../i18n';
import { getRoadTextureKey, ALL_ROAD_TEXTURE_KEYS, ROAD_TEXTURES } from '../render/roadTiles';
import { createFlagElement, getRecruitList, getFaction, FACTIONS, FLAG_SHEET, FLAG_RECTS } from '../turn/factions';
import { UNIT_ICON_PATHS, heroPortrait } from '../turn/unitIcons';
import { gameSettings, AI_ANIM_BASE_PAN_MS, AI_ANIM_BASE_DELAY_MS } from '../ui/gameSettings';
import { TERRAIN_COLORS } from '../render/terrainColors';

const HW = TILE_W / 2;
const HH = TILE_H / 2;

/** Base terrain tiles baked from Gemini art (see docs/sprite-prompts/terrain/).
 *  grass = land base, water = sea/port/bridge base, `s-*` = straight beach edge
 *  (sand on that screen edge), `o-*` = beach outer corner (sand wraps that vertex). */
const TERRAIN_TILE_FILES = [
  'grass', 'snow', 'water',
  's-nw', 's-ne', 's-sw', 's-se',          // 1 land edge
  'o-n', 'o-e', 'o-s', 'o-w',              // 2 adjacent (outer corner)
  'cf-ne-sw', 'cf-nw-se',                  // 2 opposite (channel)
  'cf-ne-se-sw', 'cf-nw-se-sw', 'cf-nw-ne-sw', 'cf-nw-ne-se', // 3 edges (inlet)
  'cf-nw-ne-se-sw',                        // 4 edges (pond)
  'oc-n', 'oc-e', 'oc-s', 'oc-w',          // convex (outer) corner — diagonal land tip
] as const;
const terrainTileKey = (f: string): string => `terrain-tile-${f}`;
/** Coast-config subset (every shore tile, excluding the grass/water bases) that
 *  also ships a masonry-quay variant under /terrain/tiles/quay/. A water tile
 *  flagged `quay` draws its shoreline from this set (stone wharf) instead of the
 *  default sandy beach. */
const TERRAIN_BASE_TILES = ['grass', 'snow', 'water'];
const QUAY_TILE_FILES = TERRAIN_TILE_FILES.filter((f) => !TERRAIN_BASE_TILES.includes(f));
const quayTileKey = (f: string): string => `terrain-tile-quay-${f}`;

/** Piered-span bridge sprites (a road deck on a central pier, lifted over the
 *  water bed). A straight bridge gets a span on every tile whose FRONT axis-
 *  neighbour is a bridge (front = screen-down: SW for an NESW deck, SE for an
 *  NWSE deck) — i.e. interior tiles AND the back end (land behind, on the NE/NW
 *  edge), so the pier reads as continuing over the water. The front-most tile
 *  (land ahead, on the SE/SW edge) keeps a plain road deck so no pier juts onto
 *  the shore. Non-straight bridge tiles also fall back to the flat road texture.
 *  The 256-px-wide deck is rendered scaled to TILE_W; the per-key origin is the
 *  normalized Y of the deck centre so the deck lands on the tile diamond and the
 *  pier hangs below. */
const BRIDGE_DECK_W = 256;
const BRIDGE_SPAN_KEY: Record<string, string> = {
  [ROAD_TEXTURES.STRAIGHT_NESW]: 'bridge-span-nesw',
  [ROAD_TEXTURES.STRAIGHT_NWSE]: 'bridge-span-nwse',
};
/** Normalized Y of the deck centre per span key (deck lands on the diamond). */
const BRIDGE_ORIGIN: Record<string, number> = {
  'bridge-span-nesw': 0.3594,
  'bridge-span-nwse': 0.3814,
};
/** Every bridge sprite key, for preloading. */
const ALL_BRIDGE_SPRITE_KEYS = Object.keys(BRIDGE_ORIGIN);
/**
/**
 * Depth offset that puts unit tokens above buildings on the *same tile*.
 * Buildings use `(col+row) * 1000 + 200`, units `(col+row) * 1000 + 300` — so a
 * unit standing on top of a building's tile renders above it, while a unit
 * standing on a tile *behind* (lower col+row) renders behind a building on a
 * tile *in front* (higher col+row). This matches the visual expectation that
 * objects further from the camera in isometric space are occluded by those
 * closer to it.
 *
 * The old `UNIT_DEPTH_BASE = 500_000` constant forced every unit above every
 * building unconditionally, which broke the depth illusion.
 */
const HUD_TOP_PX = 38;
const HUD_BOTTOM_PX = 200;

/** Conceptual radius of a unit's footprint. The painted token background was
 *  removed (owner identity is now tinted into the icon itself), but several
 *  callers still use this to position HUD overlays — HP bar, cargo dots,
 *  ammo alert — at predictable offsets. */
const UNIT_TOKEN_R = 18;

/** Authoring standard for unit-icon source PNGs: 605×364 px (AR ≈ 1.66).
 *  Icons that don't match get scaled by height anyway — silhouette stays
 *  correct, but width drifts off-standard. */
const UNIT_ICON_HEIGHT = 28;

/** Aspect ratio of the standardised authoring size (605 / 364). Used to
 *  place HP bar, cargo dots, ammo alerts relative to where the icon's edges
 *  land on screen — no need to inspect each texture's actual dimensions. */
const UNIT_ICON_STANDARD_AR = 605 / 364;

/** Per-type icon metrics for the BATTLE-WINDOW token sizing (createBattleToken).
 *  MEASURED content fill (`fillW`/`fillH`, fraction of frame) and `size` =
 *  desired on-screen FOOTPRINT (√(width·height)) relative to infantry. Sizing by
 *  footprint balances both dimensions so low-and-wide silhouettes (an AT gun, a
 *  ship hull) don't blow up in width. (World-map icons use the directional
 *  sprite system below.) */
interface IconMetric { fillW: number; fillH: number; size: number }
/** Infantry's √(fillW·fillH) — the footprint baseline the `size` is relative to. */
const ICON_FOOTPRINT_BASE = Math.sqrt(0.47 * 1.0);
const UNIT_ICON_METRICS: Record<string, IconMetric> = {
  // Land vehicles / tanks (base)
  'light-tank': { fillW: 0.48, fillH: 0.57, size: 1.41 },
  'medium-tank': { fillW: 0.67, fillH: 0.76, size: 1.53 },
  'heavy-tank': { fillW: 1.0, fillH: 1.0, size: 1.68 },
  // size bumped (1.86 → 2.35) so the battle-window token reads as a medium tank:
  // the TD `se.png` canvas is very wide (long barrel) with big empty margins, so
  // footprint-normalising by the full canvas otherwise shrank the visible hull
  // below a light tank's. Calibrated to match medium-tank's on-screen footprint.
  'tank-destroyer': { fillW: 0.88, fillH: 0.96, size: 1.8 },
  apc: { fillW: 0.79, fillH: 0.62, size: 1.35 },
  'aa-flakpanzer': { fillW: 0.66, fillH: 1.0, size: 1.5 },
  'anti-tank-artillery': { fillW: 1.0, fillH: 0.63, size: 1.45 },
  'long-range-artillery': { fillW: 1.0, fillH: 0.88, size: 1.68 },
  'supply-truck': { fillW: 0.75, fillH: 0.66, size: 1.46 },
  medic: { fillW: 0.74, fillH: 0.66, size: 1.45 },
  jeep: { fillW: 1.0, fillH: 0.88, size: 1.21 },
  // Foot / mounted troops — the SMALL end of the scale (a soldier must read as
  // clearly smaller than any vehicle; infantry is the size baseline = 1.0).
  // Slightly enlarged from real-life ratio for on-screen readability.
  infantry: { fillW: 0.47, fillH: 1.0, size: 1.0 },
  'infantry-bazooka': { fillW: 0.50, fillH: 1.0, size: 1.05 },
  officer: { fillW: 0.53, fillH: 0.98, size: 1.1 },
  recon: { fillW: 0.70, fillH: 0.62, size: 1.2 },
  cavalry: { fillW: 0.75, fillH: 0.9, size: 1.05 },
  // Mounted uhlan — like cavalry, a touch bigger (elite); the raised lance/flag
  // inflates the e.png canvas height, so fillH is kept modest so the battle-window
  // footprint reads off the horse+rider mass, not the thin pennant.
  'polska-ulani': { fillW: 0.78, fillH: 0.9, size: 1.1 },
  // Faction land variants
  'wlochy-l3-33': { fillW: 0.48, fillH: 0.57, size: 1.4 },
  'francja-renault-r35': { fillW: 0.48, fillH: 0.57, size: 1.41 },
  'japonia-chi-ha': { fillW: 0.67, fillH: 0.76, size: 1.53 },
  'wielka-brytania-churchill': { fillW: 1.0, fillH: 0.70, size: 1.82 },
  'polska-7tp': { fillW: 0.87, fillH: 0.98, size: 1.5 },
  'zsrr-t34': { fillW: 0.92, fillH: 0.98, size: 1.53 },
  'usa-sherman': { fillW: 0.90, fillH: 0.98, size: 1.53 },
  'iii-rzesza-tiger': { fillW: 0.94, fillH: 0.98, size: 1.8 },
  'zsrr-katiusza': { fillW: 0.64, fillH: 1.0, size: 1.55 },
  // Aircraft — bigger than tanks but flat (width carries the size)
  'plane-fighter': { fillW: 1.0, fillH: 0.58, size: 1.45 },
  'plane-bomber': { fillW: 1.0, fillH: 0.49, size: 1.4 },
  'plane-transport': { fillW: 1.0, fillH: 0.55, size: 1.4 },
  'iii-rzesza-stuka': { fillW: 1.0, fillH: 0.49, size: 1.4 },
  'wlochy-sm79-sparviero': { fillW: 1.0, fillH: 0.58, size: 1.45 },
  'japonia-a6m-zero': { fillW: 1.0, fillH: 0.58, size: 1.45 },
  'francja-ms406': { fillW: 1.0, fillH: 0.58, size: 1.45 },
  // Ships — the biggest footprints (long hulls)
  'ship-battleship': { fillW: 1.0, fillH: 0.61, size: 1.72 },
  'ship-aircraft-carrier': { fillW: 1.0, fillH: 0.53, size: 1.72 },
  'ship-submarine': { fillW: 0.93, fillH: 0.52, size: 1.4 },
  'ship-transport': { fillW: 0.97, fillH: 0.42, size: 1.28 },
  'usa-lcvp-higgins': { fillW: 0.97, fillH: 0.42, size: 1.28 },
};

/** Battle-window token render scale (canvas height multiplier) so its on-screen
 *  footprint matches `size` × infantry. */
function unitIconScale(type: string): number {
  const m = UNIT_ICON_METRICS[type];
  return m ? (m.size * ICON_FOOTPRINT_BASE) / Math.sqrt(m.fillW * m.fillH) : 1;
}

/** Screen-facing directions for directional unit sprites (see public/units/<type>/). */
type UnitFacing = 'nw' | 'ne' | 'sw' | 'se' | 'w' | 'e';

/** A unit type's directional sprite set at /units/<type>/<facing>.png.
 *  `height` is the on-screen display height (px) — tuned so unit classes keep
 *  relative proportions (infantry < recon < jeep < light < medium < heavy).
 *  `ar` is the sprite canvas aspect ratio (for overlay placement). `dirs` lists
 *  which facings exist (all current directional units ship all 6 facings). */
interface DirSpec { folder: string; height: number; ar: number; dirs: readonly UnitFacing[]; yNudge?: number; }
const ALL_DIRS: readonly UnitFacing[] = ['nw', 'ne', 'sw', 'se', 'w', 'e'];
const DIRECTIONAL_UNITS: Record<string, DirSpec> = {
  // Foot squads: `ar` is patched at runtime by bakeSquadTextures() to the baked
  // 3-figure footprint; `height` is the GROUP height, so each soldier renders
  // small (clearly under a tank). yNudge softened from the single-figure value.
  'infantry':         { folder: '/units/infantry',            height: 30, ar: 1.55, dirs: ALL_DIRS, yNudge: -3 },
  'infantry-bazooka': { folder: '/units/infantry-bazooka',    height: 30, ar: 1.47, dirs: ALL_DIRS, yNudge: -3 },
  // Commander on foot — single figure (no squad), sized close to a rifleman.
  // Sprites normalised (centred + bottom-aligned in a uniform canvas) so the
  // figure no longer jumps sideways when turning to nw/ne.
  'officer':          { folder: '/units/officer',             height: 26, ar: 333 / 904, dirs: ALL_DIRS, yNudge: -3 },
  'anti-tank-artillery': { folder: '/units/anti-tank-artillery', height: 33, ar: 577 / 385, dirs: ALL_DIRS, yNudge: 4 },
  'recon':            { folder: '/units/recon',               height: 27, ar: 436 / 366, dirs: ALL_DIRS },
  'cavalry':     { folder: '/units/cavalry',     height: 38, ar: 898 / 965, dirs: ALL_DIRS, yNudge: -3 },
  'jeep':        { folder: '/units/jeep',        height: 27, ar: 992 / 849, dirs: ALL_DIRS },
  'light-tank':  { folder: '/units/light-tank',  height: 30, ar: 433 / 348, dirs: ALL_DIRS },
  'medium-tank': { folder: '/units/medium-tank', height: 35, ar: 542 / 389, dirs: ALL_DIRS },
  'heavy-tank':  { folder: '/units/heavy-tank',  height: 38, ar: 558 / 416, dirs: ALL_DIRS },
  'tank-destroyer': { folder: '/units/tank-destroyer', height: 31, ar: 987 / 632, dirs: ALL_DIRS },
  'apc':         { folder: '/units/apc',         height: 31, ar: 905 / 715, dirs: ALL_DIRS },
  'aa-flakpanzer':       { folder: '/units/aa-flakpanzer',       height: 42, ar: 437 / 430, dirs: ALL_DIRS },
  'long-range-artillery': { folder: '/units/long-range-artillery', height: 33, ar: 417 / 382, dirs: ALL_DIRS, yNudge: 4 },
  'supply-truck': { folder: '/units/supply-truck', height: 30, ar: 547 / 417, dirs: ALL_DIRS },
  'medic':       { folder: '/units/medic',       height: 30, ar: 516 / 432, dirs: ALL_DIRS },
  // Faction-unique units with their own directional sets (silhouette per base class).
  'polska-ulani':     { folder: '/units/polska-ulani',     height: 44, ar: 510 / 614, dirs: ALL_DIRS, yNudge: -4 },
  'polska-7tp':       { folder: '/units/polska-7tp',       height: 30, ar: 483 / 368, dirs: ALL_DIRS },
  'iii-rzesza-tiger': { folder: '/units/iii-rzesza-tiger', height: 38, ar: 561 / 413, dirs: ALL_DIRS },
  'iii-rzesza-stuka': { folder: '/units/iii-rzesza-stuka', height: 30, ar: 583 / 344, dirs: ALL_DIRS },
  'zsrr-t34':         { folder: '/units/zsrr-t34',         height: 35, ar: 1092 / 844, dirs: ALL_DIRS },
  'zsrr-katiusza':    { folder: '/units/zsrr-katiusza',    height: 35, ar: 442 / 379, dirs: ALL_DIRS },
  // Heroes (scientists) — single standing pixel-art figure, civilian palette.
  // `ar` = sliced common-canvas width/height; height/yNudge tuned vs the map (see POPRAWA-rzutu doc).
  'hero-polska-a':         { folder: '/units/scientists/maria-sklodowska-curie', height: 36, ar: 555 / 829, dirs: ALL_DIRS, yNudge: -4 },
  'hero-polska-b':         { folder: '/units/scientists/jan-czochralski',        height: 36, ar: 195 / 490, dirs: ALL_DIRS, yNudge: -4 },
  'hero-iii-rzesza-a':     { folder: '/units/scientists/fritz-haber',            height: 36, ar: 220 / 507, dirs: ALL_DIRS, yNudge: -4 },
  'hero-iii-rzesza-b':     { folder: '/units/scientists/karl-landsteiner',       height: 36, ar: 212 / 499, dirs: ALL_DIRS, yNudge: -4 },
  'hero-francja-a':        { folder: '/units/scientists/jean-perrin',            height: 36, ar: 251 / 504, dirs: ALL_DIRS, yNudge: -4 },
  'hero-francja-b':        { folder: '/units/scientists/alexis-carrel',          height: 36, ar: 224 / 507, dirs: ALL_DIRS, yNudge: -4 },
  'hero-zsrr-a':           { folder: '/units/scientists/konstantin-tsiolkovsky', height: 36, ar: 232 / 504, dirs: ALL_DIRS, yNudge: -4 },
  'hero-zsrr-b':           { folder: '/units/scientists/ivan-pavlov',            height: 36, ar: 242 / 515, dirs: ALL_DIRS, yNudge: -4 },
  'hero-usa-a':            { folder: '/units/scientists/thomas-edison',          height: 36, ar: 253 / 515, dirs: ALL_DIRS, yNudge: -4 },
  'hero-usa-b':            { folder: '/units/scientists/robert-goddard',         height: 36, ar: 228 / 512, dirs: ALL_DIRS, yNudge: -4 },
  'hero-wlochy-a':         { folder: '/units/scientists/guglielmo-marconi',      height: 36, ar: 260 / 526, dirs: ALL_DIRS, yNudge: -4 },
  'hero-wlochy-b':         { folder: '/units/scientists/vito-volterra',          height: 36, ar: 308 / 532, dirs: ALL_DIRS, yNudge: -4 },
  'hero-japonia-a':        { folder: '/units/scientists/hideyo-noguchi',         height: 36, ar: 281 / 553, dirs: ALL_DIRS, yNudge: -4 },
  'hero-japonia-b':        { folder: '/units/scientists/shibasaburo-kitasato',   height: 36, ar: 271 / 529, dirs: ALL_DIRS, yNudge: -4 },
  'hero-jugoslawia-a':     { folder: '/units/scientists/nikola-tesla',           height: 36, ar: 318 / 549, dirs: ALL_DIRS, yNudge: -4 },
  'hero-jugoslawia-b':     { folder: '/units/scientists/mihajlo-pupin',          height: 36, ar: 329 / 533, dirs: ALL_DIRS, yNudge: -4 },
  'hero-wielka-brytania-a':{ folder: '/units/scientists/ernest-rutherford',      height: 36, ar: 336 / 535, dirs: ALL_DIRS, yNudge: -4 },
  'hero-wielka-brytania-b':{ folder: '/units/scientists/joseph-john-thomson',    height: 36, ar: 347 / 561, dirs: ALL_DIRS, yNudge: -4 },
  // Air — full 6 directions (side + down-3/4 + up-3/4; se/ne/nw via mirror).
  'plane-fighter':    { folder: '/units/plane-fighter',    height: 24, ar: 1204 / 730, dirs: ALL_DIRS, yNudge: 8 },
  'plane-bomber':     { folder: '/units/plane-bomber',     height: 30, ar: 1211 / 732, dirs: ALL_DIRS, yNudge: 7 },
  'plane-transport':  { folder: '/units/plane-transport',  height: 30, ar: 1224 / 805, dirs: ALL_DIRS },
  // Water — naval grey (no khaki harmonisation); BL=sw, BR=nw, se/ne via mirror.
  'ship-battleship':       { folder: '/units/ship-battleship',       height: 32, ar: 972 / 937, dirs: ALL_DIRS },
  'ship-submarine':        { folder: '/units/ship-submarine',        height: 24, ar: 944 / 770, dirs: ALL_DIRS, yNudge: 4 },
  'ship-aircraft-carrier': { folder: '/units/ship-aircraft-carrier', height: 26, ar: 1021 / 771, dirs: ALL_DIRS },
  'ship-transport':        { folder: '/units/ship-transport',        height: 30, ar: 969 / 849, dirs: ALL_DIRS },
};

function dirIconKey(type: string, facing: UnitFacing): string {
  return `dir:${type}:${facing}`;
}

/** Texture key for an aircraft's ground-shadow silhouette. */
function shadowKey(type: string, facing: 'sw' | 'nw'): string {
  return `shadow:${type}:${facing}`;
}

/** Per-facing source frame for a ground shadow. Only two silhouettes exist
 *  (sw, nw); se/ne are the same art mirrored. */
const SHADOW_FACINGS: Partial<Record<UnitFacing, { facing: 'sw' | 'nw'; flip: boolean }>> = {
  sw: { facing: 'sw', flip: false },
  se: { facing: 'sw', flip: true },
  nw: { facing: 'nw', flip: false },
  ne: { facing: 'nw', flip: true },
};
/** Base aircraft types with their own shadow art. Faction-unique planes reuse
 *  these generically via resolveBaseType (e.g. a6m-zero → plane-fighter). Any
 *  unit whose base type is listed here casts a shadow and lifts by HOVER_LIFT. */
const PLANE_SHADOW_TYPES = new Set<string>(['plane-fighter', 'plane-bomber', 'plane-transport']);
/** Resolve which shadow art a unit uses (by base type), or undefined if none. */
function shadowBaseType(type: string): string | undefined {
  const base = resolveBaseType(type as UnitType);
  return PLANE_SHADOW_TYPES.has(base) ? base : undefined;
}
/** How far (px) a shadow-casting aircraft floats above its ground shadow. */
const HOVER_LIFT = 11;
/** Shadow size relative to the sprite's wingspan. <1 shrinks it so the plane
 *  reads as flying higher (a smaller ground shadow = greater apparent altitude). */
const SHADOW_SCALE = 0.78;

/** Texture key for a faction character's bust portrait (loaded in preload). */
function portraitKey(factionId: string, charId: string): string {
  return `portrait:${factionId}:${charId}`;
}

/** Facing for one 4-connected grid step in the iso projection:
 *  col+1 → se, col-1 → nw, row+1 → sw, row-1 → ne. */
function facingFromStep(dcol: number, drow: number): UnitFacing {
  if (dcol > 0) return 'se';
  if (dcol < 0) return 'nw';
  if (drow > 0) return 'sw';
  return 'ne';
}

function iconHeightFor(type: string): number {
  return DIRECTIONAL_UNITS[type]?.height ?? UNIT_ICON_HEIGHT;
}
function iconArFor(type: string): number {
  return DIRECTIONAL_UNITS[type]?.ar ?? UNIT_ICON_STANDARD_AR;
}
/** Per-type vertical nudge (px, positive = forward/down) for the on-tile icon.
 *  Long-gunned units (artillery, AT gun) carry a tall barrel in their canvas, so
 *  centring puts the hull high and they read as "recessed"; nudging seats them
 *  forward on the tile. */
function iconYNudge(type: string): number {
  return DIRECTIONAL_UNITS[type]?.yNudge ?? 0;
}
/** Size policy variant that respects per-type icon height (directional units larger). */
function fitUnitIconFor(img: Phaser.GameObjects.Image, type: string): void {
  img.displayHeight = iconHeightFor(type);
  img.scaleX = img.scaleY;
}

/** Battle-scene projectile spritesheets (frame sizes per /projectiles/README.md).
 *  Right-aligned horizontal strips: nose fixed at the right, trail grows left. */
const BATTLE_PROJECTILES: Record<string, { frameW: number; frameH: number; frames: number }> = {
  'rifle-bullet': { frameW: 212, frameH: 43, frames: 7 },
  'artillery-shell': { frameW: 297, frameH: 78, frames: 6 },
  'tank-shell': { frameW: 306, frameH: 73, frames: 6 },
  'torpedo': { frameW: 271, frameH: 51, frames: 6 },
  'bazooka-rocket': { frameW: 270, frameH: 56, frames: 6 },
};

/** Pick a unit's battle projectile sprite by base type (faction variants
 *  inherit via resolveBaseType). Bombers use the separate 'bomb' image. */
function battleProjectileKey(type: UnitType): string {
  switch (resolveBaseType(type)) {
    case 'infantry': case 'recon': case 'cavalry': case 'jeep':
    case 'aa-flakpanzer': case 'plane-fighter': return 'rifle-bullet';
    case 'long-range-artillery': case 'ship-battleship': return 'artillery-shell';
    case 'ship-submarine': return 'torpedo';
    case 'infantry-bazooka': return 'bazooka-rocket';
    default: return 'tank-shell'; // tanks, tank-destroyer, anti-tank-artillery
  }
}

/** On-screen thickness of a unit's battle-window round, as a fraction of the
 *  token `size`. A calibre gradient: small-arms units fire tiny bullets, and
 *  guns get visibly fatter by class (light < medium < TD < heavy < artillery <
 *  battleship) so a light tank no longer fires the same round as a heavy one. */
function battleProjectileThicknessFrac(type: UnitType): number {
  switch (resolveBaseType(type)) {
    // Small arms — tiny bullets sprayed in long bursts.
    case 'infantry': case 'recon': case 'cavalry': case 'jeep': return 0.05;
    case 'aa-flakpanzer': case 'plane-fighter': return 0.07; // autocannon / MG
    case 'apc': return 0.10;
    // Tank & AT guns — graduated by calibre.
    case 'light-tank': return 0.13;
    case 'anti-tank-artillery': return 0.16;
    case 'infantry-bazooka': return 0.17;
    case 'medium-tank': return 0.18;
    case 'tank-destroyer': return 0.21;
    case 'heavy-tank': return 0.24;
    // Big indirect / naval — the fattest rounds.
    case 'long-range-artillery': return 0.26;
    case 'ship-submarine': return 0.18; // torpedo
    case 'ship-battleship': return 0.30;
    default: return 0.18;
  }
}

/** Extra downward nudge (fraction of token size) for battle-window tokens whose
 *  art sits high on the tile and reads as "floating". Keyed by base type, so
 *  faction variants inherit it. Kept small so the lowest rank still clears the
 *  window's bottom edge. */
const BATTLE_TOKEN_DROP: Record<string, number> = {
  'heavy-tank': 0.12,
};

/** Per-type death animation in the battle window. Each sheet is a 1×9 horizontal
 *  strip (frame 420×220) at `/explosions/deaths/<type>.png` — a side-view
 *  sequence: intact → struck → explosion → burning → smoking wreck. Sliced from
 *  the Gemini "<unit> explosion" sheets by scripts/slice_death_sheet.py.
 *
 *  `facing` is the direction the source art looks ('e' = barrel/front to the
 *  right, 'w' = to the left); the sprite is flipped when the dying token faces
 *  the other way so it matches the live unit it replaces. `frames` curates which
 *  of the 9 cells play (default 0..8); tank-destroyer drops a regression cell
 *  (5) and an empty trailing cell (8). Keyed by BASE type (faction variants
 *  inherit via resolveBaseType). `hfrac` is the fraction of the frame height the
 *  intact unit (frame 0) fills — used to scale the sprite so the dying unit
 *  matches the live token it replaces, while the explosion grows past the top. */
const DEATH_SHEETS: Record<string, { facing: UnitFacing; hfrac: number; frames: number[] }> = {
  'aa-flakpanzer':        { facing: 'w', hfrac: 0.95, frames: [0, 1, 2, 3, 4, 5, 6, 7, 8] },
  'heavy-tank':           { facing: 'e', hfrac: 0.82, frames: [0, 1, 2, 3, 4, 5, 6, 7, 8] },
  'jeep':                 { facing: 'w', hfrac: 1.00, frames: [0, 1, 2, 3, 4, 5, 6, 7, 8] },
  'light-tank':           { facing: 'e', hfrac: 0.83, frames: [0, 1, 2, 3, 4, 5, 6, 7, 8] },
  'long-range-artillery': { facing: 'e', hfrac: 0.93, frames: [0, 1, 2, 3, 4, 5, 6, 7, 8] },
  'medium-tank':          { facing: 'e', hfrac: 0.86, frames: [0, 1, 2, 3, 4, 5, 6, 7, 8] },
  'recon':                { facing: 'e', hfrac: 0.85, frames: [0, 1, 2, 3, 4, 5, 6, 7, 8] },
  'tank-destroyer':       { facing: 'e', hfrac: 0.75, frames: [0, 1, 2, 3, 4, 6, 7] },
};
const DEATH_SHEET_FRAME_W = 420, DEATH_SHEET_FRAME_H = 220;
/** Death sheet for a unit type (resolving faction variants to their base). */
function deathSheetFor(type: UnitType): { key: string; facing: UnitFacing; hfrac: number; frames: number[] } | undefined {
  const base = resolveBaseType(type);
  const spec = DEATH_SHEETS[type] ?? DEATH_SHEETS[base];
  if (!spec) return undefined;
  const key = DEATH_SHEETS[type] ? type : base;
  return { key, facing: spec.facing, hfrac: spec.hfrac, frames: spec.frames };
}

/** Battle-window weapon SFX clip (in fileSfx CLIP_LIB) for a unit's volley.
 *  One clip is deliberately shared across several types. undefined → silent
 *  (e.g. bombers use bomb visuals, transports don't fire). */
function battleFireClip(type: UnitType): string | undefined {
  switch (resolveBaseType(type)) {
    case 'infantry': case 'recon': case 'cavalry': case 'jeep':
    case 'aa-flakpanzer': case 'plane-fighter': return 'battle-fire-mg';
    case 'infantry-bazooka': return 'battle-fire-bazooka';
    case 'light-tank': case 'medium-tank': case 'heavy-tank':
    case 'tank-destroyer': case 'anti-tank-artillery': case 'apc': return 'battle-fire-cannon';
    case 'long-range-artillery': case 'ship-battleship': return 'battle-fire-heavy-arty';
    default: return undefined;
  }
}

/** Battle-window deployment movement SFX as squads march/drive in. undefined →
 *  silent (aircraft and ships have no ground-movement sound). */
function battleDeployClip(type: UnitType): string | undefined {
  switch (resolveBaseType(type)) {
    case 'infantry': case 'infantry-bazooka': return 'battle-deploy-march';
    case 'cavalry': return 'battle-deploy-cavalry';
    // Tracked vehicles — tank/engine clatter.
    case 'light-tank': case 'medium-tank': case 'heavy-tank': case 'tank-destroyer':
    case 'anti-tank-artillery': case 'aa-flakpanzer': case 'long-range-artillery':
      return 'battle-deploy-tank';
    // Wheeled vehicles — truck engine.
    case 'recon': case 'jeep': case 'apc': case 'supply-truck': case 'medic':
      return 'battle-deploy-truck';
    default: return undefined;
  }
}

/** HIT/death SFX for a unit class, played once per killing volley in the battle
 *  window. Mechanical units (tanks, vehicles, planes, ships, guns) explode;
 *  cavalry and foot troops are reserved for dedicated clips (return undefined →
 *  silent for now). */
function battleHitClip(type: UnitType): string | undefined {
  switch (resolveBaseType(type)) {
    case 'infantry': case 'infantry-bazooka': return undefined; // TODO: 'battle-hit-infantry'
    case 'cavalry': return undefined;                           // TODO: 'battle-hit-cavalry'
    default: return 'battle-hit-mechanical';
  }
}


/** Glow tint on enemies a selected unit can attack — orange-red. */
const ATTACK_GLOW_COLOR = 0xff5a2a;

const PLAYER_COLORS = ['#4a90e2', '#d2483a', '#3a9a4f', '#d4a017', '#9b59b6', '#e67e22'];

/** Shared style for every bottom info-panel section title (terrain/building
 *  name, unit name, recruitment, cargo) so the sections look uniform. The title
 *  now carries the actual name, so it's prominent and truncates rather than
 *  wrapping. */
const INFO_SECTION_HEADER = 'font-size:18px;font-weight:bold;color:#e6e8ec;border-bottom:1px solid #2a3140;padding-bottom:5px;margin-bottom:8px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis';


/** Info-panel icon: fills its (landscape) slot via `object-fit:contain`. The
 *  slot is wider than any icon's aspect ratio, so HEIGHT is always the binding
 *  dimension — every icon scales to the exact same height — and
 *  `object-position:center bottom` seats them on a common baseline. This keeps
 *  the terrain, building and unit icons the same size and aligned despite their
 *  differing source aspect ratios. */
function iconBox(src: string): string {
  return `<img src="${src}" style="width:100%;height:100%;object-fit:contain;object-position:center bottom">`;
}

/** Unit icon for the info panel — slot-filling variant aligned with terrain/building icons. */
function unitInfoIcon(type: string): string {
  // Heroes are presented by their scientist bust portrait (not the map sprite).
  if (type.startsWith('hero-')) {
    const portrait = heroPortrait(type);
    if (portrait) return iconBox(portrait);
  }
  const path = UNIT_ICON_PATHS[type];
  if (path) return iconBox(path);
  // Commander placeholder (no PNG icon yet) — ★ hero / ▲ officer symbol.
  if (type === 'officer' || type.startsWith('hero-')) {
    const symbol = type === 'officer' ? '▲' : '★';
    return `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-size:34px;font-weight:bold;color:#cdd6e4;text-shadow:0 1px 3px #000">${symbol}</div>`;
  }
  return '';
}

/** Dedicated bottom-panel (info) icons per terrain / building type — pixel art
 *  living in /terrain/info-panel-icons/. */
const TERRAIN_INFO_ICON: Record<string, string> = {
  plains: 'plains', water: 'water', coast: 'coast', forest: 'forest',
  mountain: 'mountains', road: 'road', bridge: 'bridge',
};
const BUILDING_INFO_ICON: Record<string, string> = {
  hq: 'hq', barracks: 'barracks', 'vehicle-factory': 'tank-factory',
  'motor-pool': 'motor-pool', airport: 'airport', port: 'shipyard', bunker: 'bunker',
};
function terrainInfoIcon(type: string): string {
  const name = TERRAIN_INFO_ICON[type];
  return name ? iconBox(`/terrain/info-panel-icons/${name}-info-icon.png`) : '';
}
/** Building info icon, falling back to the map icon (e.g. for `city`). */
function buildingInfoIcon(type: string, mapIcon: string): string {
  const name = BUILDING_INFO_ICON[type];
  return iconBox(name ? `/terrain/info-panel-icons/${name}-info-icon.png` : mapIcon);
}
/** Wrap a panel icon in a recessed "carved slot" — inset shadow + frame for
 *  depth. Defaults to a square; pass `h` for a landscape slot (used by the
 *  info-panel icons, whose art is wider than tall). */
function iconSlot(inner: string, w = 46, h = w): string {
  return `<span style="display:inline-flex;align-items:flex-end;justify-content:center;`
    + `flex:0 0 auto;width:${w}px;height:${h}px;border-radius:8px;`
    + `background:linear-gradient(160deg,#10141b,#1b212c);`
    + `box-shadow:inset 0 2px 5px rgba(0,0,0,0.7),inset 0 -1px 2px rgba(255,255,255,0.07);`
    + `border:1px solid #2c3442;padding:4px;box-sizing:border-box">${inner}</span>`;
}

/** Icon files for context-menu order entries (in `/graphics/context-menu-orders/`).
 *  Each asset already carries its own recessed dark frame, so they render raw. */
const ORDER_MENU_ICONS: Record<string, string> = {
  attack: '/graphics/context-menu-orders/attack-context-menu-icon.png',
  capture: '/graphics/context-menu-orders/capture-context-menu-icon.png',
  heal: '/graphics/context-menu-orders/heal-context-menu-icon.png',
  resupply: '/graphics/context-menu-orders/resupply-context-menu-icon.png',
  build: '/graphics/context-menu-orders/build-bunker-context-menu-icon.png',
  unload: '/graphics/context-menu-orders/unload-context-menu-icon.png',
  load: '/graphics/context-menu-orders/load-context-menu-icon.png',
  cancel: '/graphics/context-menu-orders/cancel-orders-context-menu-icon.png',
  end: '/graphics/context-menu-orders/end-orders-context-menu-icon.png',
};

/** Inline order icon for the context menu. The assets already bake in a dark
 *  rounded frame + border, so we show them raw (wrapping in another slot would
 *  double the frame). */
function menuIconSlot(src: string): string {
  return `<img src="${src}" style="flex:0 0 auto;width:32px;height:32px;object-fit:contain">`;
}

/** One entry in the in-world context menu. Most entries are a framed order icon
 *  + label, but `html` allows richer content (e.g. a cargo unit's icon + name +
 *  HP%), and `divider:true` renders a thin separator instead of a button. */
type CtxMenuItem = {
  label?: string;
  color?: string;
  icon?: string;
  html?: string;
  divider?: boolean;
  enabled?: boolean;
  onClick?: () => void;
};

function unitDescription(unitType: string): string {
  return t(`unit.${unitType}.desc`);
}

type OrderKind = 'move' | 'attack' | 'capture' | 'heal' | 'resupply' | 'build';
type OrderEntry = { kind: OrderKind; enabled: boolean };

/** UI state for the turn-based input — order-first, two-step confirmation. */
type InputState =
  | { kind: 'idle' }
  | { kind: 'tileSelected'; col: number; row: number }
  | { kind: 'unitSelected'; unitId: number; range: Map<string, number>; zoc: Set<string>; availableOrders: OrderEntry[]; _cachedNormalKeys?: string[]; _cachedZocKeys?: string[]; _cachedAttackableEnemies?: Set<number>; _cachedAttackKeys?: string[] }
  | { kind: 'orderMove'; unitId: number; range: Map<string, number>; zoc: Set<string>; _cachedNormalKeys?: string[]; _cachedZocKeys?: string[] }
  | { kind: 'confirmMove'; unitId: number; range: Map<string, number>; zoc: Set<string>; targetCol: number; targetRow: number; path: Coord[]; _cachedNormalKeys?: string[]; _cachedZocKeys?: string[] }
  | { kind: 'orderAttack'; unitId: number; attackableEnemies: Set<number>; attackRangeKeys: string[] }
  | { kind: 'confirmAttack'; unitId: number; targetEnemyId: number }
  | { kind: 'moved'; unitId: number; availableOrders: OrderEntry[] }
  | { kind: 'pendingMove'; unitId: number; originCol: number; originRow: number; cost: number; steps: number }
  | { kind: 'pendingMoveAttack'; unitId: number; originCol: number; originRow: number; cost: number; steps: number; attackableEnemies: Set<number> }
  | { kind: 'movedOrderAttack'; unitId: number; attackableEnemies: Set<number> }
  | { kind: 'confirmPostMoveAttack'; unitId: number; targetEnemyId: number }
  | { kind: 'orderUnload'; unitId: number; cargoIndex: number; unloadTiles: { col: number; row: number }[]; pending?: { originCol: number; originRow: number; cost: number; steps: number } }
  | { kind: 'orderOfficerAbility'; unitId: number; abilityId: string; pending?: { originCol: number; originRow: number; cost: number; steps: number } }
  | { kind: 'buildingSelected'; buildingId: number }
  | { kind: 'aiTurn' };

interface TurnReport {
  unitsLost: { type: UnitType; col: number; row: number }[];
  unitsRecruited: { type: UnitType; buildingType: BuildingType }[];
  income: { buildingType: BuildingType; count: number; perBuilding: number; total: number }[];
  totalIncome: number;
}

function emptyTurnReport(): TurnReport {
  return { unitsLost: [], unitsRecruited: [], income: [], totalIncome: 0 };
}

export class TurnGameScene extends Phaser.Scene {
  private gameConfig: GameConfig | null = null;
  private localPlayer: PlayerId = 0;
  private world!: TurnWorld;
  private inputState: InputState = { kind: 'idle' };
  private mapWidth = 0;
  private mapHeight = 0;

  // Graphics / display layers
  private terrainGfx!: Phaser.GameObjects.Graphics;
  /**
   * After `drawTerrain` finishes, we bake the terrain Graphics into a single
   * texture and replace the live Graphics with a static Image — the terrain
   * never animates, so re-rasterising thousands of fillPath polygons every
   * frame is pure waste. Tracked here only for teardown cleanup.
   */
  private terrainBakedKey = '';
  private overlayLayer!: Phaser.GameObjects.Layer;
  /**
   * When the scenario carries a pre-baked tileset image, we render it as
   * the base visual instead of colored terrain diamonds. The gameplay
   * `terrain[][]` grid is still authoritative for movement, defense, etc.
   */
  private scenarioTilesetActive = false;
  private scenarioTilesetImage: Phaser.GameObjects.Image | null = null;
  private frontLineGfx!: Phaser.GameObjects.Graphics;
  private gridGfx!: Phaser.GameObjects.Graphics;
  private highlightGfx!: Phaser.GameObjects.Graphics;
  private battlePreviewGfx!: Phaser.GameObjects.Graphics;
  /** Blinking red SILHOUETTE outline behind each attackable enemy: a red-filled,
   *  slightly-enlarged copy of the unit's icon whose protruding rim reads as a
   *  contour around the sprite. Cheap (textured quads, no postFX). Keyed by unit
   *  id; `frameTargetIds` accumulates the current frame's targets before sync. */
  private targetOutlines = new Map<number, Phaser.GameObjects.Image>();
  private frameTargetIds = new Set<number>();
  private entityContainer!: Phaser.GameObjects.Container;
  private minZoom = 0.3;
  /** Closest the camera can zoom in (raised so the player can get two steps
   *  closer than the old 2.5 cap). */
  private maxZoom = 4.0;
  private worldPxW = 0;
  private worldPxH = 0;
  // Front line cache (recomputed in refresh(), not per-frame)
  private showFrontLine = false;
  private cachedInfluenceMap: InfluenceGrid = { width: 0, height: 0, owner: new Int16Array(0), cost: new Float32Array(0) };
  private cachedFrontLines: FrontLineSegment[] = [];
  // Fog of war — current player's visible tiles (recomputed each turn/move)
  private playerVisible = new Set<string>();
  /** Set when fog changed in a way NOT already painted incrementally (e.g. a
   *  manual vision reveal) — refresh()/fullRefresh() then do one full repaint.
   *  Normal moves paint fog via drawFogDelta/recomputeVisible, so refreshes can
   *  skip the costly whole-map drawFog() that used to stall after every action. */
  private fogDirty = false;

  // Per-tile terrain overlays tracked for glow/tint effects
  private terrainOverlayImages = new Map<string, Phaser.GameObjects.Image>();
  /**
   * Fog of war is rendered as ONE canvas-backed texture covering the entire
   * map, repainted only when visibility changes. The earlier approach used a
   * GameObject.Image per tile (~2904 sprites on Westerplatte) which forced
   * Phaser's display list to iterate/sort every fog tile each frame even when
   * nothing changed — the dominant cost on dense scenarios.
   */
  private fogCanvas: HTMLCanvasElement | null = null;
  private fogCanvasCtx: CanvasRenderingContext2D | null = null;
  private fogTextureKey = '';
  /**
   * Resolution divisor for the fog canvas. Fog is a soft black mask — pixel
   * precision is wasted; rendering at half/quarter resolution and letting the
   * GPU bilinear-upscale looks identical and keeps the texture below WebGL
   * limits on large maps (xlarge at scale 1 would need a 16384×8192 texture).
   * Computed once at scene create based on world bounds.
   */
  private fogScale = 1;

  // Entity sprites
  private unitSprites = new Map<number, Phaser.GameObjects.Graphics>();
  private unitIconImages = new Map<number, Phaser.GameObjects.Image>();
  /** Ground-shadow image for hovering aircraft (unitId → shadow Image). */
  private unitShadowImages = new Map<number, Phaser.GameObjects.Image>();
  /** Placeholder text tokens (★/▲) for commander units that have no PNG icon. */
  private commanderTokens = new Map<number, Phaser.GameObjects.Text>();
  /** Hovered target tile while aiming an officer ability (3×3 preview). */
  private abilityHoverTile: { col: number; row: number } | null = null;
  /** Current screen-facing per unit id, for units with directional sprite sets. */
  private unitFacing = new Map<number, UnitFacing>();
  /** Facing a unit had before a provisional (RMB) move began — restored if the
   *  player cancels, so the sprite doesn't keep the mid-move orientation. */
  private movePreFacing = new Map<number, UnitFacing>();
  private buildingSprites = new Map<number, Phaser.GameObjects.Graphics>();
  private buildingIcons = new Map<number, Phaser.GameObjects.Image>();
  private buildingMasts = new Map<number, Phaser.GameObjects.Image>(); // flag overlay (style.flag==='overlay')
  private trainingGhosts = new Map<number, Phaser.GameObjects.Graphics>(); // buildingId → ghost gfx
  private trainingGhostIcons = new Map<number, Phaser.GameObjects.Image>(); // buildingId → ghost icon
  private ammoAlertIcons = new Map<number, Phaser.GameObjects.Image>(); // unitId → pulsing low-ammo icon
  private fuelAlertIcons = new Map<number, Phaser.GameObjects.Image>(); // unitId → pulsing low-fuel icon
  private hpPercentTexts = new Map<number, Phaser.GameObjects.Text>(); // unitId → "82%" overlay
  private hpBars = new Map<number, Phaser.GameObjects.Graphics>(); // unitId → owner-coloured HP bar
  private cargoBars = new Map<number, Phaser.GameObjects.Graphics>(); // unitId → cargo-slot bar (carriers only)
  private buildingBars = new Map<number, Phaser.GameObjects.Graphics>(); // buildingId → owner/capture bar
  private xpStarTexts = new Map<number, Phaser.GameObjects.Text>(); // unitId → "★★★" rank stars
  /** Building whose glow is currently overridden by selection (white). */
  private glowOverrideBuildingId: number | null = null;
  private tutorialHighlightBuildingId: number | null = null;
  /** Track which unit/terrain objects currently have selection glows (for efficient clear). */
  private glowedUnitIds = new Set<number>();
  private glowedTerrainKeys = new Set<string>();
  private unitGlowColors = new Map<number, number>();
  private terrainGlowColors = new Map<string, number>();
  private prevGlowedUnitIds = new Set<number>();
  private prevGlowedTerrainKeys = new Set<string>();
  /** In-world context menu (orders next to a unit) + its dismiss handler. */
  private contextMenu: HTMLDivElement | null = null;
  private contextMenuDismiss: (() => void) | null = null;
  /** Hovered enemy unit id for attack-range preview (null = nothing hovered). */
  private hoveredEnemyId: number | null = null;
  /** RMB on one of my own units shows its attack range as a transient preview. */
  private attackRangePreviewId: number | null = null;
  /** Interactive green ✓ marker shown on the move target in confirmMove. */
  private confirmMarker: Phaser.GameObjects.Image | null = null;

  // HUD DOM elements
  private topBar!: HTMLDivElement;
  private bottomBar!: HTMLDivElement;
  private infoPanel!: HTMLDivElement;
  private actionsPanel!: HTMLDivElement;
  private minimapPanel!: HTMLDivElement;
  private minimapCanvas!: HTMLCanvasElement;
  private playerListPanel!: HTMLDivElement;
  private messagePanel!: HTMLDivElement;
  private menuOverlay: HTMLDivElement | null = null;
  private victoryOverlay: HTMLDivElement | null = null;
  private unitInfoOverlay: HTMLDivElement | null = null;
  private turnStartOverlay: HTMLDivElement | null = null;
  // Centered recruitment window — opens when an own, idle (not-training)
  // production building is selected; closing it deselects the building.
  private recruitWindow: HTMLDivElement | null = null;
  private recruitWindowBuildingId: number | null = null;
  private recruitSelectedType: UnitType | null = null;
  private settingsDialog = new SettingsDialog();

  // Messages log
  private messages: string[] = [];

  // Turn report data (collected during AI turns, shown at start of human turn)
  private turnReport: TurnReport = emptyTurnReport();
  /** Snapshot of the local player's units taken when they end their turn. Any
   *  entry missing at the next turn-report = a loss, counted once regardless of
   *  cause (enemy attack, attrition, supply) or whether AI was animated. */
  private reportLossSnapshot = new Map<number, { type: UnitType; col: number; row: number }>();

  // RMB pan state (desktop)
  private rmbPan = { active: false, startX: 0, startY: 0, camStartX: 0, camStartY: 0, dragged: false };
  /** True while a battle window is on screen — freezes camera/pointer input so
   *  RMB pan, wheel zoom and keyboard scroll can't move the captured view. */
  private battleActive = false;
  // Touch state (mobile: 1-finger pan, 2-finger pinch zoom)
  private touch = {
    active: false, dragged: false,
    startX: 0, startY: 0, camStartX: 0, camStartY: 0,
    // Pinch zoom
    pinching: false, startDist: 0, startZoom: 1,
    pinchMidX: 0, pinchMidY: 0,
  };

  // Minimap throttle
  private minimapFrame = 0;
  /**
   * Cached static layers of the minimap. For big maps (e.g. 66×44 = 2904 tiles)
   * re-rasterising every cell + the bevel shadow blur 6× per second tanked
   * the framerate, so we bake terrain (with current fog) + bevel into an
   * offscreen canvas and just blit it per frame.
   */
  private minimapStaticCache: HTMLCanvasElement | null = null;
  private minimapStaticDirty = true;
  private onResizeMinimap: (() => void) | null = null;

  // Movement animation lock
  private animating = false;

  /**
   * On-demand render loop. Turn-based gameplay is idle ~95% of the time, so
   * we put the global Phaser TimeStep to sleep when nothing is changing and
   * wake it on any input / state change / animation tick. Pulse animations
   * (highlightTime) only need a 60Hz loop while a pulse-active input state
   * is on screen.
   */
  private redrawPending = true;
  private camTrack = { x: NaN, y: NaN, zoom: NaN };
  private onIdleInput: ((e: Event) => void) | null = null;
  /**
   * Timestamp of the most recent user interaction (input, animation,
   * camera change). The loop only goes to sleep after `SLEEP_DELAY_MS`
   * milliseconds of true inactivity — without this delay every click
   * needed one full RAF cycle to wake the loop, which read as input lag.
   */
  private lastActivityTime = 0;
  private static readonly SLEEP_DELAY_MS = 300;
  /**
   * Cached map-coords screen position for each road overlay tile — used by
   * the viewport culler. Filled when overlays are created.
   */
  private roadOverlayImages = new Map<string, Phaser.GameObjects.Image>();

  /** Single fog darkening level (canvas black-overlay opacity) applied to every
   *  tile a unit isn't currently looking at — the whole map layout stays
   *  readable at one uniform dim. */
  private static readonly FOG_DIM = 0.45;

  // AI turn step-by-step playback
  private skipAiTurn = false;

  // Smart camera: track last notable positions for turn-start panning
  private lastCommandPos: { col: number; row: number } | null = null;
  private lastEnemyAttackPos: { col: number; row: number } | null = null;
  private lastSpottedEnemyPos: { col: number; row: number } | null = null;

  // Scenario runner (victory/defeat/events)
  private runner: ScenarioRunner | null = null;
  /** Reference to the scenario the current match was loaded from. Needed to
   *  look up building names + groups when (re)rendering map labels. */
  private scenario: Scenario | null = null;
  /** Layer holding all on-map text labels (named buildings + groups). Drawn
   *  above battle preview but below the front-line HUD. */
  private buildingLabelLayer: Phaser.GameObjects.Layer | null = null;
  /** buildingId → text label for a named building. */
  private buildingNameLabels = new Map<number, Phaser.GameObjects.Text>();
  /** groupId → text label for a named group. */
  private buildingGroupLabels = new Map<string, Phaser.GameObjects.Text>();
  /** buildingId → scenario-defined `name` (for the info panel). */
  private buildingIdToScenarioName = new Map<number, LocalizedString>();
  /** buildingId → the group it belongs to (for the info panel + pulse). */
  private buildingIdToGroup = new Map<number, BuildingGroup>();
  private scenarioMessageOverlay: HTMLDivElement | null = null;
  /** True once the asset loader's progress is wired to the intro progress bar. */
  private introProgressHooked = false;
  /** Event messages that fired at turn-start; shown on a dispatch screen before
   *  the turn report (instead of the easy-to-miss top banner). */
  private pendingDispatchMessages: string[] = [];
  /** True while draining turn-start events, so message actions queue into the
   *  dispatch screen rather than flashing the in-game banner. */
  private collectingTurnStartDispatch = false;
  /** One-time scenario briefing (objectives + defeat conditions) shown once at start. */
  private briefingShown = false;
  private tutorialBanner: HTMLDivElement | null = null;

  // Highlight pulse animation (0..1 oscillating)
  private highlightTime = 0;

  constructor() {
    super({ key: 'TurnGame' });
  }

  /** Unit icon texture keys — use original colored PNGs directly. */
  private static readonly ICON_KEYS: Partial<Record<string, string>> = {
    infantry: 'icon-infantry-src',
    'infantry-bazooka': 'icon-infantry-bazooka-src',
    recon: 'icon-recon-src',
    cavalry: 'icon-cavalry-src',
    jeep: 'icon-jeep-src',
    'light-tank': 'icon-light-tank-src',
    'medium-tank': 'icon-medium-tank-src',
    'heavy-tank': 'icon-heavy-tank-src',
    apc: 'icon-apc-src',
    'aa-flakpanzer': 'icon-aa-flakpanzer-src',
    'tank-destroyer': 'icon-tank-destroyer-src',
    'long-range-artillery': 'icon-long-range-artillery-src',
    'anti-tank-artillery': 'icon-anti-tank-artillery-src',
    'supply-truck': 'icon-supply-truck-src',
    medic: 'icon-medic-src',
    'plane-fighter': 'icon-plane-fighter-src',
    'plane-bomber': 'icon-plane-bomber-src',
    'plane-transport': 'icon-plane-transport-src',
    'ship-battleship': 'icon-ship-battleship-src',
    'ship-submarine': 'icon-ship-submarine-src',
    'ship-transport': 'icon-ship-transport-src',
    'ship-aircraft-carrier': 'icon-ship-aircraft-carrier-src',
    // Faction-unique units (dedicated texture keys per unit)
    'polska-ulani': 'icon-polska-ulani-src',
    'polska-7tp': 'icon-polska-7tp-src',
    'zsrr-t34': 'icon-zsrr-t34-src',
    'zsrr-katiusza': 'icon-zsrr-katiusza-src',
    'iii-rzesza-tiger': 'icon-iii-rzesza-tiger-src',
    'iii-rzesza-stuka': 'icon-iii-rzesza-stuka-src',
    'usa-sherman': 'icon-usa-sherman-src',
    'usa-lcvp-higgins': 'icon-usa-lcvp-higgins-src',
    'wlochy-l3-33': 'icon-wlochy-l3-33-src',
    'wlochy-sm79-sparviero': 'icon-wlochy-sm79-sparviero-src',
    'japonia-chi-ha': 'icon-japonia-chi-ha-src',
    'japonia-a6m-zero': 'icon-japonia-a6m-zero-src',
    'francja-renault-r35': 'icon-francja-renault-r35-src',
    'francja-ms406': 'icon-francja-ms406-src',
    'jugoslawia-partyzanci': 'icon-jugoslawia-partyzanci-src',
    'jugoslawia-ba64': 'icon-jugoslawia-ba64-src',
    'wielka-brytania-komandosi': 'icon-wielka-brytania-komandosi-src',
    'wielka-brytania-churchill': 'icon-wielka-brytania-churchill-src',
    // Heroes — scientist map icons (loaded from UNIT_ICON_PATHS in preload).
    'hero-polska-a': 'icon-hero-polska-a-src',
    'hero-polska-b': 'icon-hero-polska-b-src',
    'hero-iii-rzesza-a': 'icon-hero-iii-rzesza-a-src',
    'hero-iii-rzesza-b': 'icon-hero-iii-rzesza-b-src',
    'hero-francja-a': 'icon-hero-francja-a-src',
    'hero-francja-b': 'icon-hero-francja-b-src',
    'hero-zsrr-a': 'icon-hero-zsrr-a-src',
    'hero-zsrr-b': 'icon-hero-zsrr-b-src',
    'hero-usa-a': 'icon-hero-usa-a-src',
    'hero-usa-b': 'icon-hero-usa-b-src',
    'hero-wlochy-a': 'icon-hero-wlochy-a-src',
    'hero-wlochy-b': 'icon-hero-wlochy-b-src',
    'hero-japonia-a': 'icon-hero-japonia-a-src',
    'hero-japonia-b': 'icon-hero-japonia-b-src',
    'hero-jugoslawia-a': 'icon-hero-jugoslawia-a-src',
    'hero-jugoslawia-b': 'icon-hero-jugoslawia-b-src',
    'hero-wielka-brytania-a': 'icon-hero-wielka-brytania-a-src',
    'hero-wielka-brytania-b': 'icon-hero-wielka-brytania-b-src',
  };

  /**
   * Maps building type → file stem. Texture key `bldg-<type>` is loaded in preload
   * from /units/<stem>-map-icon.png; ownership is shown by the flag mast overlay
   * (clean buildings, no per-owner baked variants).
   */
  private static readonly BLDG_FILE_STEM: Record<string, string> = {
    hq: 'hq',
    city: 'city',
    barracks: 'barracks',
    'vehicle-factory': 'factory',
    'motor-pool': 'motor-pool',
    airport: 'airport',
    port: 'port',
    bunker: 'bunker',
  };

  /**
   * Per-building display-width multiplier (× TILE_W), default 1.0. The building
   * art shares a uniform baked pad (~1 tile), so no per-type tuning is needed;
   * kept for future overrides.
   */
  private static readonly BLDG_ICON_SCALE: Record<string, number> = {};

  /** Battle-scene background image names under /battle-backgrounds/. */
  private static readonly BATTLE_BG_NAMES = [
    'air', 'water', 'plains', 'forest', 'mountain', 'coast', 'road', 'bridge', 'city', 'building',
  ] as const;

  /**
   * Per-building placement for the 'overlay' (clean) set, hand-tuned against a
   * tile grid because the sprites are irregular (forward aprons, side towers,
   * cannons) — no single auto-formula nails all. Values: [originX, originY,
   * displayWidthMul]. originX/Y anchor the MAIN BODY on the tile (ignoring
   * protrusions); displayWidthMul × TILE_W is the on-screen width.
   */
  /** Global vertical placement offset for buildings (px, subtracted from the tile's
   *  front vertex Y). 0 = the building's platform front vertex (BLDG_FIT originY) sits
   *  exactly on the tile's front vertex, so the base diamond aligns with the tile. */
  private static readonly BLDG_RAISE = 0;

  private static readonly BLDG_FIT: Record<string, [number, number, number]> = {
    // [originX, originY, widthMul] — anchor = each building's PLATFORM FRONT VERTEX,
    // computed from the iso base diamond (left/right vertices → centre → front vertex
    // via 2:1 projection), so placing it at the tile's front vertex (RAISE 0) makes the
    // base diamond coincide with the tile. widthMul sizes the base ≈ 1 tile. Re-derive
    // from the /units art if the building art changes.
    hq: [0.524, 0.857, 1.0],
    city: [0.499, 0.96, 1.0],
    barracks: [0.499, 0.997, 1.0],
    'vehicle-factory': [0.5, 0.89, 1.0],
    'motor-pool': [0.462, 0.819, 1.0],
    airport: [0.499, 0.998, 1.0],
    port: [0.499, 0.954, 1.0],
    bunker: [0.499, 0.978, 1.0],
  };

  /**
   * Per-building vertical nudge (px, positive = lower on screen), default 0.
   * Added on top of BLDG_GLOBAL_Y for fine-tuning individual buildings.
   */
  private static readonly BLDG_ICON_Y_OFFSET: Record<string, number> = {};

  init(data: { config?: GameConfig }): void {
    this.gameConfig = data.config ?? null;
    this.localPlayer = this.gameConfig?.humanPlayer ?? 0;
    // Reset per-scenario state. Phaser's SceneManager reuses this instance across
    // scenarios (scene.start re-runs init() on the same object), so fields with
    // class-initializers do NOT auto-reset. Without this a fresh scenario inherits
    // the previous one's turn report (losses/recruits), message log, queued
    // dispatches and the "briefing already shown" flag.
    this.turnReport = emptyTurnReport();
    this.reportLossSnapshot.clear();
    this.messages = [];
    this.pendingDispatchMessages = [];
    this.briefingShown = false;
  }

  preload(): void {
    // Forward the asset loader's real progress to the intro screen's progress
    // bar (shown by LoadingScene). Hooked once — the LoaderPlugin persists for
    // the scene's lifetime and fires for every load.
    if (!this.introProgressHooked) {
      this.introProgressHooked = true;
      this.load.on('progress', (p: number) => this.game.events.emit('intro-progress', p));
    }

    // Load icon PNGs (only if not already cached from a previous game)
    if (!this.textures.exists('icon-infantry-src')) {
      // Unit icons — load all from UNIT_ICON_PATHS
      for (const [unitType, path] of Object.entries(UNIT_ICON_PATHS)) {
        const key = TurnGameScene.ICON_KEYS[unitType];
        if (key) this.load.image(key, path);
      }
      // Directional unit sprite sets (per facing) under /units/<type>/<facing>.png
      for (const [type, spec] of Object.entries(DIRECTIONAL_UNITS)) {
        for (const f of spec.dirs) this.load.image(dirIconKey(type, f), `${spec.folder}/${f}.png`);
      }
      // Ground-shadow silhouettes for hovering aircraft (sw/nw; se/ne mirrored).
      for (const type of PLANE_SHADOW_TYPES) {
        const folder = DIRECTIONAL_UNITS[type]?.folder ?? `/units/${type}`;
        this.load.image(shadowKey(type, 'sw'), `${folder}/shadow-sw.png`);
        this.load.image(shadowKey(type, 'nw'), `${folder}/shadow-nw.png`);
      }
      // Terrain overlay icons
      this.load.image('terrain-forest-src', '/terrain/forest-map-icon.png');
      this.load.image('terrain-mountain-src', '/terrain/mountain-map-icon.png');
      // Road sprites (auto-tiling: straights, bends, T-junctions, X-cross).
      // Bridges reuse these — a bridge is a road tile drawn over water.
      for (const key of ALL_ROAD_TEXTURE_KEYS) {
        this.load.image(key, `/terrain/${key}.png`);
      }
      // Raised-bridge sprites (span/approach/deck) for straight bridges.
      for (const key of ALL_BRIDGE_SPRITE_KEYS) {
        this.load.image(key, `/terrain/${key}.png`);
      }
      // Base terrain tiles (grass/water + beach shore autotile) — used for
      // scenarios without their own baked tileset (see bakeTerrainTiles).
      for (const f of TERRAIN_TILE_FILES) {
        this.load.image(terrainTileKey(f), `/terrain/tiles/${f}.png`);
      }
      // Masonry-quay shore variant (optional per-tile, see `quay` flag).
      for (const f of QUAY_TILE_FILES) {
        this.load.image(quayTileKey(f), `/terrain/tiles/quay/${f}.png`);
      }
      // Building icons — the detailed isometric art from /units/<stem>-map-icon.png
      // (shared with buildings.ts/editor). Buildings render clean; ownership is shown
      // by the flag mast overlay (see syncBuildingMast).
      for (const [type, stem] of Object.entries(TurnGameScene.BLDG_FILE_STEM)) {
        this.load.image(`bldg-${type}`, `/units/${stem}-map-icon.png`);
      }
      // Flag overlay frames (mast) for 'overlay' styles: mast0 = empty (neutral),
      // mast1-p* = half-raised (capturing), mast2-p* = raised (owner). Recoloured
      // per player by recolor in prep_mast.py; mast stays wooden.
      this.load.image('mast-empty', '/buildings/_mast/mast0.png');
      for (let p = 0; p < 6; p++) {
        this.load.image(`mast-half-p${p}`, `/buildings/_mast/mast1-p${p}.png`);
        this.load.image(`mast-full-p${p}`, `/buildings/_mast/mast2-p${p}.png`);
      }
      // Supply alert icons (low ammo / low fuel) overlaid on map tokens.
      this.load.image('ammo-icon-src', '/graphics/ammo-map-icon.png');
      this.load.image('fuel-icon-src', '/graphics/stats/fuel-icon.png');
      // Explosion spritesheets (4x4 grids, 16 frames each)
      for (let i = 1; i <= 10; i++) {
        const size = (i === 7 || i === 10) ? 64 : 32;
        this.load.spritesheet(`explosion-${i}`, `/explosions/explosion-${i}.png`,
          { frameWidth: size, frameHeight: size });
      }
      // Per-unit death animations for the battle window (1×9 strips, side view).
      for (const key of Object.keys(DEATH_SHEETS)) {
        this.load.spritesheet(`death-${key}`, `/explosions/deaths/${key}.png`,
          { frameWidth: DEATH_SHEET_FRAME_W, frameHeight: DEATH_SHEET_FRAME_H });
      }
      // Battle projectile spritesheets + static bomb (see /projectiles/README.md)
      for (const [key, c] of Object.entries(BATTLE_PROJECTILES)) {
        this.load.spritesheet(key, `/projectiles/${key}.png`, { frameWidth: c.frameW, frameHeight: c.frameH });
      }
      this.load.image('bomb', '/projectiles/bomb.png');
      // Fighter propeller-spin animation (6-frame strip, side view facing left).
      this.load.spritesheet('plane-fighter-prop', '/units/plane-fighter/prop-fly.png',
        { frameWidth: 818, frameHeight: 357 });
      // Battle-scene backgrounds (per domain + terrain / structure).
      for (const name of TurnGameScene.BATTLE_BG_NAMES) {
        this.load.image(`battle-bg-${name}`, `/battle-backgrounds/${name}.png`);
      }
      // Commander HUD in the battle window: nation flag sheet + hero portraits.
      this.load.image('flags-sheet', FLAG_SHEET.url);
      for (const f of FACTIONS) {
        for (const c of f.characters) {
          if (c.portrait) this.load.image(portraitKey(f.id, c.id), c.portrait);
        }
      }
    }
  }


  create(): void {
    this.cameras.main.setBackgroundColor(PALETTE.bg);

    // Bake foot-squad textures (3 figures per directional frame) once the
    // single-figure sprites have loaded, before any unit is drawn.
    this.bakeSquadTextures();

    // Remove any lingering overlays from previous scenes (TutorialScene, etc.)
    document.querySelectorAll('[data-tutorial-overlay]').forEach(el => el.remove());

    // Reset state from previous game (scene instances are reused by Phaser)
    this.inputState = { kind: 'idle' };
    this.animating = false;
    for (const g of this.unitSprites.values()) g.destroy();
    for (const img of this.unitIconImages.values()) img.destroy();
    for (const tok of this.commanderTokens.values()) tok.destroy();
    for (const txt of this.hpPercentTexts.values()) txt.destroy();
    for (const txt of this.xpStarTexts.values()) txt.destroy();
    for (const g of this.buildingSprites.values()) g.destroy();
    for (const t of this.buildingIcons.values()) t.destroy();
    for (const t of this.buildingMasts.values()) t.destroy();
    for (const g of this.trainingGhosts.values()) g.destroy();
    for (const img of this.trainingGhostIcons.values()) img.destroy();
    for (const g of this.hpBars.values()) g.destroy();
    for (const g of this.cargoBars.values()) g.destroy();
    for (const g of this.buildingBars.values()) g.destroy();
    this.unitSprites.clear();
    this.unitIconImages.clear();
    this.commanderTokens.clear();
    this.unitFacing.clear();
    this.hpPercentTexts.clear();
    this.xpStarTexts.clear();
    this.hpBars.clear();
    this.cargoBars.clear();
    this.buildingBars.clear();
    this.buildingSprites.clear();
    this.buildingIcons.clear();
    this.buildingMasts.clear();
    this.trainingGhosts.clear();
    this.trainingGhostIcons.clear();
    this.glowOverrideBuildingId = null;
    this.tutorialHighlightBuildingId = null;
    this.unitGlowColors.clear();
    this.terrainGlowColors.clear();
    this.glowedUnitIds.clear();
    this.glowedTerrainKeys.clear();
    this.prevGlowedUnitIds.clear();
    this.prevGlowedTerrainKeys.clear();
    // Drop any fog canvas/texture left over from a previous game (scene reuse).
    if (this.fogTextureKey && this.textures.exists(this.fogTextureKey)) {
      this.textures.remove(this.fogTextureKey);
    }
    this.fogCanvas = null;
    this.fogCanvasCtx = null;
    this.fogTextureKey = '';
    if (this.terrainBakedKey && this.textures.exists(this.terrainBakedKey)) {
      this.textures.remove(this.terrainBakedKey);
    }
    this.terrainBakedKey = '';
    this.terrainOverlayImages.clear();
    this.roadOverlayImages.clear();
    for (const t of this.buildingNameLabels.values()) t.destroy();
    for (const t of this.buildingGroupLabels.values()) t.destroy();
    this.buildingNameLabels.clear();
    this.buildingGroupLabels.clear();
    this.buildingIdToScenarioName.clear();
    this.buildingIdToGroup.clear();
    this.confirmMarker?.destroy();
    this.confirmMarker = null;
    // Clear stale DOM references (scene instances are reused by Phaser)
    this.tutorialBanner?.remove();
    this.tutorialBanner = null;
    // Reset per-game camera tracking — scene instances are reused across games,
    // so a stale last-command/last-enemy position would pan the new game's
    // first turn to the PREVIOUS game's location (e.g. the old player's HQ).
    this.lastCommandPos = null;
    this.lastEnemyAttackPos = null;
    this.lastSpottedEnemyPos = null;
    this.abilityHoverTile = null;
    this.attackRangePreviewId = null;

    const fallback = getMainCatalog().list()[0];
    const scenario = this.gameConfig?.scenario ?? fallback;
    if (!scenario) {
      console.error('[TurnGameScene] no scenario available');
      return;
    }
    const { world, tagMap } = loadScenario(scenario);
    this.world = world;
    this.scenario = scenario;
    this.runner = new ScenarioRunner(world, scenario, tagMap);
    this.world.humanPlayer = this.localPlayer;
    // Propagate faction assignments to world for faction-aware recruitment.
    // Scenario-forced factions (already set on world.factions by the loader)
    // take precedence; menu picks only fill slots the scenario left unforced.
    if (this.gameConfig?.factions) {
      const forced = this.world.factions; // scenario-forced, may be undefined
      this.world.factions = new Map<number, string>(forced ?? []);
      for (const [pid, pick] of this.gameConfig.factions) {
        if (!this.world.factions.has(pid)) this.world.factions.set(pid, pick.factionId);
      }
    }
    this.mapWidth = this.world.map.width;
    this.mapHeight = this.world.map.height;

    // Compute minimum zoom so visible area (between HUD panels) never exceeds map bounds
    this.worldPxW = (this.mapWidth + this.mapHeight) * HW;
    this.worldPxH = (this.mapWidth + this.mapHeight) * HH;
    const screenW = this.scale.width;
    const screenH = this.scale.height;
    const visibleScreenH = screenH - HUD_TOP_PX - HUD_BOTTOM_PX;
    // Cap minZoom at 2.5 so tiny maps (tutorials) don't over-zoom
    this.minZoom = Math.min(Math.max(screenW / this.worldPxW, visibleScreenH / this.worldPxH, 0.15), 2.5);
    // Start zoomed in to a comfortable tactical view (clamped to valid range),
    // not fully zoomed out to fit the whole map.
    this.cameras.main.zoom = Phaser.Math.Clamp(1.8, this.minZoom, this.maxZoom);

    // Reorder turnOrder so human player always goes first
    const localIdx = this.world.turnOrder.indexOf(this.localPlayer);
    if (localIdx > 0) {
      this.world.turnOrder = [
        ...this.world.turnOrder.slice(localIdx),
        ...this.world.turnOrder.slice(0, localIdx),
      ];
    }

    startGame(this.world);

    // Initialize fog of war explored state (HQ positions + starting vision)
    initExplored(this.world);
    this.recomputeVisible();

    // Create graphics layers — explicit depth ensures correct draw order
    // overlayLayer holds terrain overlays (depth +55/tile) AND fog images (depth +50/tile)
    // so fog renders BELOW overlays per-tile, dimming terrain while overlays stay on top.
    this.terrainGfx = this.add.graphics().setDepth(0);
    this.overlayLayer = this.add.layer().setDepth(1);
    this.gridGfx = this.add.graphics().setDepth(3);
    this.highlightGfx = this.add.graphics().setDepth(4);
    this.entityContainer = this.add.container(0, 0).setDepth(5);
    this.battlePreviewGfx = this.add.graphics().setDepth(5.5);
    this.frontLineGfx = this.add.graphics().setDepth(6);
    // Scenario building / group labels (named tags + groups). One layer for
    // every Phaser.Text we add; depth sits above entity tokens so labels
    // never get covered by units or buildings.
    this.buildingLabelLayer = this.add.layer().setDepth(6.5);

    // Confirm-action marker textures — green disc with ✓ for move, red disc
    // with × for attack. Generated at 4× the display size (supersampling) so
    // the GPU downsampler produces clean edges and the hover-scale tween
    // never has to invent pixels.
    const makeMarker = (key: string, fill: number, stroke: (g: Phaser.GameObjects.Graphics, size: number) => void): void => {
      if (this.textures.exists(key)) return;
      const SS = 4;
      const size = 22 * SS;
      const r = size / 2;
      const g = this.add.graphics();
      g.fillStyle(fill, 1);
      g.fillCircle(r, r, r - 1.5 * SS);
      g.lineStyle(1.5 * SS, 0x000000, 1);
      g.strokeCircle(r, r, r - 1.5 * SS);
      g.lineStyle(2 * SS, 0xffffff, 1);
      stroke(g, size);
      g.generateTexture(key, size, size);
      g.destroy();
    };
    makeMarker('confirm-marker', 0x2e7d32, (g, size) => {
      g.beginPath();
      g.moveTo(size * 0.30, size * 0.52);
      g.lineTo(size * 0.45, size * 0.68);
      g.lineTo(size * 0.72, size * 0.36);
      g.strokePath();
    });
    makeMarker('confirm-attack-marker', 0xb71c1c, (g, size) => {
      g.beginPath();
      g.moveTo(size * 0.32, size * 0.32);
      g.lineTo(size * 0.68, size * 0.68);
      g.moveTo(size * 0.68, size * 0.32);
      g.lineTo(size * 0.32, size * 0.68);
      g.strokePath();
    });

    // Create explosion animations (10 variants, 16 frames each at 24fps)
    for (let i = 1; i <= 10; i++) {
      const key = `explosion-${i}`;
      if (!this.anims.exists(key)) {
        this.anims.create({
          key,
          frames: this.anims.generateFrameNumbers(key, { start: 0, end: 15 }),
          frameRate: 24,
          repeat: 0,
        });
      }
    }

    // Per-unit death animations (battle window): play through the curated frame
    // list once, holding on the final wreck frame.
    for (const [type, spec] of Object.entries(DEATH_SHEETS)) {
      const sheetKey = `death-${type}`, animKey = `death-anim-${type}`;
      if (this.textures.exists(sheetKey) && !this.anims.exists(animKey)) {
        this.anims.create({
          key: animKey,
          frames: this.anims.generateFrameNumbers(sheetKey, { frames: spec.frames }),
          frameRate: 12,
          repeat: 0,
        });
      }
    }

    // Fighter propeller spin — looping 6-frame animation for the battle window.
    if (this.textures.exists('plane-fighter-prop') && !this.anims.exists('fighter-prop')) {
      this.anims.create({
        key: 'fighter-prop',
        frames: this.anims.generateFrameNumbers('plane-fighter-prop', { start: 0, end: 5 }),
        frameRate: 20,
        repeat: -1,
      });
    }

    // Battle projectile fly animations (looping trail).
    for (const [key, c] of Object.entries(BATTLE_PROJECTILES)) {
      const animKey = `${key}-fly`;
      if (this.textures.exists(key) && !this.anims.exists(animKey)) {
        this.anims.create({
          key: animKey,
          frames: this.anims.generateFrameNumbers(key, { start: 0, end: c.frames - 1 }),
          frameRate: 18,
          repeat: -1,
        });
      }
    }

    // Pre-baked tileset image from editor: when present, it owns the entire
    // terrain visual (colored fills, coastline, forest/mountain/road overlays
    // are all skipped). Movement/defense still come from `scenario.terrain`.
    this.scenarioTilesetActive = !!scenario.tilesetImage;
    if (this.scenarioTilesetImage) {
      this.scenarioTilesetImage.destroy();
      this.scenarioTilesetImage = null;
    }
    if (scenario.tilesetImage) {
      const key = `scenario-tileset-${this.scene.key}-${Date.now()}`;
      const htmlImg = new Image();
      htmlImg.onload = () => {
        if (!this.textures.exists(key)) this.textures.addImage(key, htmlImg);
        this.scenarioTilesetImage = this.add.image(0, 0, key);
        this.scenarioTilesetImage.setOrigin(0, 0);
        this.scenarioTilesetImage.setDepth(-1);
      };
      htmlImg.src = scenario.tilesetImage;
    }

    // Prefer baked isometric tiles (grass/water/beach) for scenarios without
    // their own tileset; fall back to flat-colour fills for huge maps or if the
    // tile assets failed to load.
    const tiledTerrain = !this.scenarioTilesetActive && this.bakeTerrainTiles();
    if (!tiledTerrain) {
      this.drawTerrain();
      this.bakeTerrain();
    }
    this.drawTerrainOverlays();
    this.createFogTexture();
    this.drawGrid();
    this.buildBuildingLabels();
    this.syncEntities();
    this.drawFog();
    if (this.showFrontLine) {
      this.recomputeFrontLine();
      this.drawFrontLine();
    }

    // Center camera on cameraFocus or map center (panToTurnStart may shift on turn start)
    if (scenario.cameraFocus) {
      const cf = this.gridToScreen(scenario.cameraFocus.col, scenario.cameraFocus.row);
      this.centerCameraOn(cf.x, cf.y);
    } else {
      this.centerCameraOn(this.worldPxW / 2, this.worldPxH / 2);
    }
    this.clampCamera();

    this.createHud();
    this.bindInput();
    this.installIdleWakeHooks();
    this.updateHud();

    // Size minimap canvas to fill its panel, then draw
    requestAnimationFrame(() => { this.resizeMinimap(); this.drawMinimap(); });
    this.onResizeMinimap = () => this.resizeMinimap();
    window.addEventListener('resize', this.onResizeMinimap);

    // Music playlist continues playing from MainMenuScene — no need to restart

    this.addMessage(t('msg.gameStart'));

    // Scenario runner: initial turn start
    if (this.runner) {
      this.runner.onTurnStart(this.world.turnNumber, currentPlayerId(this.world));
      this.processRunnerActions();
    }

    this.presentScenarioIntro(scenario);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
  }

  /** Center camera on a world point, accounting for HUD panel offset. */
  private centerCameraOn(x: number, y: number): void {
    const cam = this.cameras.main;
    cam.scrollX = x - cam.width / 2;
    // Shift Y so map is centered between top and bottom HUD bars
    cam.scrollY = y - cam.height / 2 - (HUD_TOP_PX - HUD_BOTTOM_PX) / (2 * cam.zoom);
  }

  // --- Isometric projection ---

  private get isoOriginX(): number { return this.mapHeight * HW; }
  private get isoOriginY(): number { return HH; }

  private gridToScreen(col: number, row: number): { x: number; y: number } {
    return {
      x: this.isoOriginX + (col - row) * HW,
      y: this.isoOriginY + (col + row) * HH,
    };
  }

  private screenToGrid(x: number, y: number): { col: number; row: number } {
    const dx = x - this.isoOriginX;
    const dy = y - this.isoOriginY;
    return {
      col: Math.round((dx / HW + dy / HH) / 2),
      row: Math.round((dy / HH - dx / HW) / 2),
    };
  }

  private inBounds(col: number, row: number): boolean {
    return col >= 0 && row >= 0 && col < this.mapWidth && row < this.mapHeight;
  }

  // --- Terrain rendering ---

  /**
   * Bake the freshly-drawn `terrainGfx` Graphics into a single texture and
   * replace it with a static Image. The Graphics object had to rasterise
   * thousands of fillPath polygons every frame in software rendering (and
   * batch them every frame on GPU) — bakery makes terrain a one-quad blit.
   *
   * Skipped when the scenario carries its own pre-baked `tilesetImage` —
   * `drawTerrain` already cleared the Graphics in that case.
   */
  /**
   * For a straight bridge tile, return the piered-span sprite (key + deck-centre
   * origin) when its FRONT axis-neighbour is a bridge — covering interior tiles
   * and the back end (land behind, NE/NW edge). The front end (land ahead, SE/SW
   * edge), single-tile bridges and non-straight bridge tiles return null, so the
   * caller draws a plain road deck (no pier juts onto the shore).
   */
  private bridgeSprite(
    col: number,
    row: number,
    roadKey: string,
  ): { key: string; origin: number } | null {
    if (this.world.map.tiles[row][col].type !== 'bridge') return null;
    const isBridge = (c: number, r: number): boolean =>
      this.inBounds(c, r) && this.world.map.tiles[r][c].type === 'bridge';
    let front: boolean;
    if (roadKey === ROAD_TEXTURES.STRAIGHT_NESW) {
      front = isBridge(col, row + 1); // grid S (screen SW)
    } else if (roadKey === ROAD_TEXTURES.STRAIGHT_NWSE) {
      front = isBridge(col + 1, row); // grid E (screen SE)
    } else {
      return null; // bends / junctions → flat road
    }
    if (!front) return null; // front end (land ahead, SE/SW) → plain road
    const key = BRIDGE_SPAN_KEY[roadKey];
    return { key, origin: BRIDGE_ORIGIN[key] };
  }

  /**
   * Build the base terrain from baked isometric tiles (grass/water + beach
   * shore autotile) into one canvas texture — for scenarios that do NOT carry
   * their own pre-baked `tilesetImage`. Returns false (caller falls back to the
   * flat-colour {@link drawTerrain}/{@link bakeTerrain}) when the tiles aren't
   * loaded or the map is too large for a single canvas texture.
   *
   * Land → grass; water/port/bridge → water; an open-water/port tile bordering
   * land draws a beach shore tile oriented so the sand faces the land (1 land
   * edge → straight, 2 adjacent edges → outer corner). Forest/mountain/road
   * overlays are still painted on top by {@link drawTerrainOverlays}.
   */
  private bakeTerrainTiles(): boolean {
    if (TERRAIN_TILE_FILES.some((f) => !this.textures.exists(terrainTileKey(f)))) return false;
    const w = Math.ceil(this.worldPxW);
    const h = Math.ceil(this.worldPxH);
    if (w <= 0 || h <= 0 || Math.max(w, h) > 4096) return false;
    // Supersample factor: bake at SS× world resolution so detailed pixel-art
    // tiles stay sharp at the camera's max zoom (4×). Largest SS (≤4) that keeps
    // the canvas under the 4096 texture limit; image is displayed scaled to 1×.
    let ss = 4;
    while (ss > 1 && Math.max(w, h) * ss > 4096) ss--;

    const portTiles = new Set<string>();
    for (const b of this.world.buildings.values()) {
      if (b.type === 'port') portTiles.add(`${b.col},${b.row}`);
    }
    const isWater = (c: number, r: number): boolean => {
      // Out of bounds = open sea continues beyond the map → treat as water so
      // edge water tiles don't sprout a spurious beach facing the map border.
      if (!this.inBounds(c, r)) return true;
      if (portTiles.has(`${c},${r}`)) return true;
      const ty = this.world.map.tiles[r][c].type;
      return ty === 'water' || ty === 'bridge';
    };

    const key = `terrain-tiles-${this.scene.key}-${Date.now()}`;
    const canvasTex = this.textures.createCanvas(key, w * ss, h * ss);
    const ctx = canvasTex?.getContext();
    if (!canvasTex || !ctx) return false;
    const src = (f: string): CanvasImageSource =>
      this.textures.get(terrainTileKey(f)).getSourceImage() as CanvasImageSource;
    // Masonry-quay variant; falls back to the sandy beach tile if its texture
    // didn't load, so a missing quay asset degrades gracefully.
    const hasQuay = QUAY_TILE_FILES.every((f) => this.textures.exists(quayTileKey(f)));
    const srcShore = (f: string, quay: boolean): CanvasImageSource =>
      quay && hasQuay
        ? (this.textures.get(quayTileKey(f)).getSourceImage() as CanvasImageSource)
        : src(f);
    // Base land tile (default grass; scenarios may pick e.g. 'snow').
    const landFile = this.scenario?.landTile;
    const grass = src(landFile && this.textures.exists(terrainTileKey(landFile)) ? landFile : 'grass');
    const water = src('water');
    // Land-edge configuration (which of the 4 screen edges face land) → shore tile.
    // Covers every case: 1 edge (straight), 2 adjacent (outer corner), 2 opposite
    // (channel), 3 edges (inlet), 4 edges (pond). Key is land edges in NW-NE-SE-SW
    // order joined by '-'. Empty (no land edge) → plain water.
    const SHORE: Record<string, string> = {
      'NW': 's-nw', 'NE': 's-ne', 'SE': 's-se', 'SW': 's-sw',
      'NW-NE': 'o-n', 'NE-SE': 'o-e', 'SE-SW': 'o-s', 'NW-SW': 'o-w',
      'NE-SW': 'cf-ne-sw', 'NW-SE': 'cf-nw-se',
      'NE-SE-SW': 'cf-ne-se-sw', 'NW-SE-SW': 'cf-nw-se-sw',
      'NW-NE-SW': 'cf-nw-ne-sw', 'NW-NE-SE': 'cf-nw-ne-se',
      'NW-NE-SE-SW': 'cf-nw-ne-se-sw',
    };
    const EDGE_ORDER = ['NW', 'NE', 'SE', 'SW'] as const;

    const cells: Array<[number, number]> = [];
    for (let row = 0; row < this.mapHeight; row++) {
      for (let col = 0; col < this.mapWidth; col++) cells.push([col, row]);
    }
    // back-to-front so the 1px tile overlap composites without gaps
    cells.sort((a, b) => (a[0] + a[1]) - (b[0] + b[1]));

    for (const [col, row] of cells) {
      const { x, y } = this.gridToScreen(col, row);
      const dx = Math.round((x - HW) * ss);
      const dy = Math.round((y - HH) * ss);
      const port = portTiles.has(`${col},${row}`);
      const ty = this.world.map.tiles[row][col].type;
      let img: CanvasImageSource;
      if (!(ty === 'water' || ty === 'bridge' || port)) {
        img = grass;
      } else if (ty === 'bridge') {
        img = water; // bridge crossing keeps a plain water bed; sprite drawn on top
      } else {
        // Masonry quay vs sandy beach for this water tile's shoreline.
        const quay = !!this.world.map.tiles[row][col].quay;
        const land = new Set<string>();
        if (!isWater(col, row - 1)) land.add('NE'); // grid N → NE edge
        if (!isWater(col + 1, row)) land.add('SE'); // grid E → SE edge
        if (!isWater(col, row + 1)) land.add('SW'); // grid S → SW edge
        if (!isWater(col - 1, row)) land.add('NW'); // grid W → NW edge
        if (land.size === 0) {
          // No land on edges, but a convex land tip may touch a diagonal corner →
          // draw a small shore cap (outer corner) toward that diagonal's vertex.
          let cap: CanvasImageSource | undefined;
          if (!isWater(col - 1, row - 1)) cap = srcShore('oc-n', quay);      // NW diag → N vertex
          else if (!isWater(col + 1, row - 1)) cap = srcShore('oc-e', quay); // NE diag → E vertex
          else if (!isWater(col + 1, row + 1)) cap = srcShore('oc-s', quay); // SE diag → S vertex
          else if (!isWater(col - 1, row + 1)) cap = srcShore('oc-w', quay); // SW diag → W vertex
          img = cap ?? water;
        } else {
          const key = EDGE_ORDER.filter((e) => land.has(e)).join('-');
          const file = SHORE[key];
          img = file ? srcShore(file, quay) : water;
        }
      }
      ctx.drawImage(img, dx, dy, TILE_W * ss, TILE_H * ss);
    }

    // Clip the baked terrain to the map's silhouette rhombus with an
    // anti-aliased fill, so the outer boundary is one clean diamond edge
    // instead of a per-tile hard staircase (matches the old flat render's look).
    ctx.globalCompositeOperation = 'destination-in';
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.moveTo(this.mapHeight * HW * ss, 0);                                          // top
    ctx.lineTo((this.mapWidth + this.mapHeight) * HW * ss, this.mapWidth * HH * ss);   // right
    ctx.lineTo(this.mapWidth * HW * ss, (this.mapWidth + this.mapHeight) * HH * ss);   // bottom
    ctx.lineTo(0, this.mapHeight * HH * ss);                                          // left
    ctx.closePath();
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';

    canvasTex.refresh();
    const image = this.add.image(0, 0, key);
    image.setOrigin(0, 0);
    image.setScale(1 / ss); // baked at SS× world resolution → display at 1×
    image.setDepth(0);
    this.terrainBakedKey = key;
    if (this.terrainGfx) this.terrainGfx.clear();
    return true;
  }

  private bakeTerrain(): void {
    if (this.scenarioTilesetActive) return;
    const w = Math.ceil(this.worldPxW);
    const h = Math.ceil(this.worldPxH);
    if (w <= 0 || h <= 0) return;
    // 4096 is the conservative widely-supported WebGL MAX_TEXTURE_SIZE.
    // For larger maps (large 8192×4096, xlarge 16384×8192) generate the
    // texture at half/quarter resolution and let the GPU bilinear-upscale —
    // terrain has soft color blocks and the loss is invisible at normal zoom.
    const MAX_DIM = 4096;
    let scale = 1;
    const longest = Math.max(w, h);
    while (longest / scale > MAX_DIM) scale *= 2;

    // Unique key per scene instance (scenes are reused across games).
    const key = `terrain-baked-${this.scene.key}-${Date.now()}`;
    // Render the Graphics into a temporary texture, then scale-up via Image.
    // generateTexture rasterises the Graphics at 1:1 — for scale-down we
    // first scale the Graphics object, generateTexture, then unscale Graphics
    // before destroy (defensive in case some teardown still touches it).
    if (scale !== 1) {
      this.terrainGfx.setScale(1 / scale);
    }
    this.terrainGfx.generateTexture(key, Math.ceil(w / scale), Math.ceil(h / scale));
    this.terrainGfx.destroy();
    const img = this.add.image(0, 0, key);
    img.setOrigin(0, 0);
    img.setDepth(0);
    img.setDisplaySize(w, h);
    // Replace the reference so any stale code that touches terrainGfx blows
    // up loudly rather than silently scribbling on a destroyed object.
    this.terrainGfx = null as unknown as Phaser.GameObjects.Graphics;
    this.terrainBakedKey = key;
  }

  private drawTerrain(): void {
    if (this.scenarioTilesetActive) {
      this.terrainGfx.clear();
      return;
    }
    const g = this.terrainGfx;
    g.clear();
    // Build a set of port tile positions for water coloring
    const portTiles = new Set<string>();
    for (const b of this.world.buildings.values()) {
      if (b.type === 'port') portTiles.add(`${b.col},${b.row}`);
    }

    const waterColor = TERRAIN_COLORS.water;
    // Ratio of the water strip on coastline tiles (1/4 water, 3/4 terrain)
    const R = 0.25;

    const isWaterAt = (c: number, r: number): boolean => {
      if (!this.inBounds(c, r)) return false;
      // Ports render as water, so neighboring land tiles get a coastline strip
      // facing them — the dock visually meets the sea.
      if (portTiles.has(`${c},${r}`)) return true;
      return this.world.map.tiles[r][c].type === 'water';
    };

    for (let row = 0; row < this.mapHeight; row++) {
      for (let col = 0; col < this.mapWidth; col++) {
        const tile = this.world.map.tiles[row][col];
        const isPort = portTiles.has(`${col},${row}`);
        const color = isPort ? TERRAIN_COLORS.water : (TERRAIN_COLORS[tile.type] ?? TERRAIN_DEFS[tile.type].color);
        const { x, y } = this.gridToScreen(col, row);

        // Draw full tile in terrain color
        g.fillStyle(color, 1);
        g.fillPoints([
          new Phaser.Geom.Point(x, y - HH),
          new Phaser.Geom.Point(x + HW, y),
          new Phaser.Geom.Point(x, y + HH),
          new Phaser.Geom.Point(x - HW, y),
        ], true);

        // Skip coastline rendering for water/bridge/port tiles
        if (tile.type === 'water' || tile.type === 'bridge' || isPort) continue;

        // Detect water neighbors (orthogonal)
        const wN = isWaterAt(col, row - 1);   // shares top-right edge (T–R)
        const wE = isWaterAt(col + 1, row);   // shares bottom-right edge (R–B)
        const wS = isWaterAt(col, row + 1);   // shares bottom-left edge (B–L)
        const wW = isWaterAt(col - 1, row);   // shares top-left edge (L–T)

        if (!wN && !wE && !wS && !wW) {
          // No orthogonal water — check diagonals for corner-only coastlines
          const wNW = isWaterAt(col - 1, row - 1);
          const wNE = isWaterAt(col + 1, row - 1);
          const wSE = isWaterAt(col + 1, row + 1);
          const wSW = isWaterAt(col - 1, row + 1);

          g.fillStyle(waterColor, 1);
          // Top corner (NW diagonal)
          if (wNW) {
            g.fillPoints([
              new Phaser.Geom.Point(x, y - HH),
              new Phaser.Geom.Point(x + R * HW, y - (1 - R) * HH),
              new Phaser.Geom.Point(x - R * HW, y - (1 - R) * HH),
            ], true);
          }
          // Right corner (NE diagonal)
          if (wNE) {
            g.fillPoints([
              new Phaser.Geom.Point(x + HW, y),
              new Phaser.Geom.Point(x + (1 - R) * HW, y + R * HH),
              new Phaser.Geom.Point(x + (1 - R) * HW, y - R * HH),
            ], true);
          }
          // Bottom corner (SE diagonal)
          if (wSE) {
            g.fillPoints([
              new Phaser.Geom.Point(x, y + HH),
              new Phaser.Geom.Point(x - R * HW, y + (1 - R) * HH),
              new Phaser.Geom.Point(x + R * HW, y + (1 - R) * HH),
            ], true);
          }
          // Left corner (SW diagonal)
          if (wSW) {
            g.fillPoints([
              new Phaser.Geom.Point(x - HW, y),
              new Phaser.Geom.Point(x - (1 - R) * HW, y - R * HH),
              new Phaser.Geom.Point(x - (1 - R) * HW, y + R * HH),
            ], true);
          }
          continue;
        }

        // Draw water strips along edges adjacent to water
        g.fillStyle(waterColor, 1);

        // North strip (top-right edge: T → R)
        if (wN) {
          g.fillPoints([
            new Phaser.Geom.Point(x, y - HH),                          // T
            new Phaser.Geom.Point(x + HW, y),                          // R
            new Phaser.Geom.Point(x + (1 - R) * HW, y + R * HH),      // lerp(R, B, R)
            new Phaser.Geom.Point(x - R * HW, y - (1 - R) * HH),      // lerp(T, L, R)
          ], true);
        }

        // East strip (bottom-right edge: R → B)
        if (wE) {
          g.fillPoints([
            new Phaser.Geom.Point(x + HW, y),                          // R
            new Phaser.Geom.Point(x, y + HH),                          // B
            new Phaser.Geom.Point(x - R * HW, y + (1 - R) * HH),      // lerp(B, L, R)
            new Phaser.Geom.Point(x + (1 - R) * HW, y - R * HH),      // lerp(R, T, R)
          ], true);
        }

        // South strip (bottom-left edge: B → L)
        if (wS) {
          g.fillPoints([
            new Phaser.Geom.Point(x, y + HH),                          // B
            new Phaser.Geom.Point(x - HW, y),                          // L
            new Phaser.Geom.Point(x - (1 - R) * HW, y - R * HH),      // lerp(L, T, R)
            new Phaser.Geom.Point(x + R * HW, y + (1 - R) * HH),      // lerp(B, R, R)
          ], true);
        }

        // West strip (top-left edge: L → T)
        if (wW) {
          g.fillPoints([
            new Phaser.Geom.Point(x - HW, y),                          // L
            new Phaser.Geom.Point(x, y - HH),                          // T
            new Phaser.Geom.Point(x + R * HW, y - (1 - R) * HH),      // lerp(T, R, R)
            new Phaser.Geom.Point(x - (1 - R) * HW, y + R * HH),      // lerp(L, B, R)
          ], true);
        }

        // Diagonal corner triangles — only when the diagonal is water
        // but both adjacent orthogonal neighbors are NOT water
        // (if an orthogonal neighbor IS water, its strip already covers the corner)

        // Top corner (NW diagonal: col-1, row-1)
        if (!wN && !wW && isWaterAt(col - 1, row - 1)) {
          g.fillPoints([
            new Phaser.Geom.Point(x, y - HH),
            new Phaser.Geom.Point(x + R * HW, y - (1 - R) * HH),
            new Phaser.Geom.Point(x - R * HW, y - (1 - R) * HH),
          ], true);
        }

        // Right corner (NE diagonal: col+1, row-1)
        if (!wN && !wE && isWaterAt(col + 1, row - 1)) {
          g.fillPoints([
            new Phaser.Geom.Point(x + HW, y),
            new Phaser.Geom.Point(x + (1 - R) * HW, y + R * HH),
            new Phaser.Geom.Point(x + (1 - R) * HW, y - R * HH),
          ], true);
        }

        // Bottom corner (SE diagonal: col+1, row+1)
        if (!wE && !wS && isWaterAt(col + 1, row + 1)) {
          g.fillPoints([
            new Phaser.Geom.Point(x, y + HH),
            new Phaser.Geom.Point(x - R * HW, y + (1 - R) * HH),
            new Phaser.Geom.Point(x + R * HW, y + (1 - R) * HH),
          ], true);
        }

        // Left corner (SW diagonal: col-1, row+1)
        if (!wS && !wW && isWaterAt(col - 1, row + 1)) {
          g.fillPoints([
            new Phaser.Geom.Point(x - HW, y),
            new Phaser.Geom.Point(x - (1 - R) * HW, y - R * HH),
            new Phaser.Geom.Point(x - (1 - R) * HW, y + R * HH),
          ], true);
        }
      }
    }
  }

  /** Place terrain overlay icons (forest, mountain, road) into overlayLayer,
   *  and pre-create fog images in fogLayer. */
  private drawTerrainOverlays(): void {
    // These are gameplay-layer indicators (forest = defense bonus, road =
    // movement bonus, ...) and need to render even on top of a baked
    // tileset so the player can tell what each tile means. PNG transparency
    // on forest/mountain composites cleanly, and roads do too.
    const TERRAIN_ICON_MAP: Partial<Record<string, string>> = {
      forest: 'terrain-forest-src',
      mountain: 'terrain-mountain-src',
    };
    // Per-type width multiplier (× TILE_W): taller features (forest) rise above
    // the tile instead of being squeezed into its footprint.
    const TERRAIN_ICON_SCALE: Partial<Record<string, number>> = {
      forest: 0.7,
      mountain: 0.6,
    };
    // Per-type baseline Y (px below tile center) for the overlay's bottom edge.
    // Default = HH (tile's front vertex); forest sits higher so its trunks rest
    // in the lower-middle of the diamond instead of spilling past the front edge.
    const TERRAIN_ICON_BASE_Y: Partial<Record<string, number>> = {
      forest: 9,
      mountain: 11,
    };
    for (let row = 0; row < this.mapHeight; row++) {
      for (let col = 0; col < this.mapWidth; col++) {
        const tile = this.world.map.tiles[row][col];
        const tileDepth = (col + row) * 1000;

        // Editor-marked "no overlay": the baked map already shows this feature,
        // so skip the sprite while keeping the tile's gameplay terrain type.
        if (tile.noOverlay) continue;

        // Road & bridge sprite overlays — flat road decks filling the tile
        // footprint. Both auto-tile by orthogonal neighbors; a bridge is just a
        // road tile drawn over a water bed.
        if (tile.type === 'road' || tile.type === 'bridge') {
          const key = getRoadTextureKey(this.world.map, col, row);
          const { x, y } = this.gridToScreen(col, row);
          const bridge = this.bridgeSprite(col, row, key);
          if (bridge && this.textures.exists(bridge.key)) {
            // Raised bridge: deck aligns with the tile diamond, piers/abutment hang below.
            const img = this.add.image(x, y, bridge.key);
            img.setOrigin(0.5, bridge.origin);
            img.setScale(TILE_W / BRIDGE_DECK_W);
            img.setDepth(tileDepth + 5);
            this.overlayLayer.add(img);
            this.roadOverlayImages.set(`${col},${row}`, img);
          } else if (this.textures.exists(key)) {
            const img = this.add.image(x, y, key);
            img.setOrigin(0.5, 0.5);
            img.setDisplaySize(TILE_W, TILE_H);
            img.setDepth(tileDepth + 5);
            this.overlayLayer.add(img);
            this.roadOverlayImages.set(`${col},${row}`, img);
          }
          continue;
        }

        // Forest / mountain icon overlays — fit to tile width, preserve aspect ratio
        // Placed in entityContainer so depth sorts correctly against buildings & units
        const texKey = TERRAIN_ICON_MAP[tile.type];
        if (!texKey || !this.textures.exists(texKey)) continue;
        const { x, y } = this.gridToScreen(col, row);
        const img = this.add.image(x, y, texKey);
        img.setOrigin(0.5, 1);
        img.displayWidth = TILE_W * (TERRAIN_ICON_SCALE[tile.type] ?? 1);
        img.scaleY = img.scaleX;
        img.setPosition(x, y + (TERRAIN_ICON_BASE_Y[tile.type] ?? HH));
        img.setDepth(tileDepth + 55);
        this.entityContainer.add(img);
        this.terrainOverlayImages.set(`${col},${row}`, img);
      }
    }
  }

  /**
   * Create Phaser.Text labels for every scenario building with a `name` and
   * every entry in `scenario.buildingGroups`. Labels are placed once here;
   * `updateBuildingLabels()` repaints their colour + visibility from the
   * current world state (called from syncEntities).
   */
  private buildBuildingLabels(): void {
    if (!this.scenario || !this.buildingLabelLayer) return;

    // Helper — pick a localised string preferring the active locale.
    const pickLabel = (ls: string | { pl?: string; en?: string } | undefined): string => {
      if (ls == null) return '';
      if (typeof ls === 'string') return ls;
      return localize(ls);
    };

    // Tag → buildingId so we can resolve group members back to live buildings.
    const tagToBuildingId = new Map<string, number>();
    for (const [id, b] of this.world.buildings) {
      // We need access to the *scenario* tag, not the runtime building. The
      // scenario entries are in scenario.buildings; their index aligns with
      // the building.id sequence because loadScenario assigns ids in order
      // (1, 2, 3…) matching scenario.buildings[i].
      void id; void b;
    }
    // Build tag→id by walking scenario.buildings in declared order.
    // The loader assigns ids starting at 1 in the order it sees entities; the
    // safer approach is to use the existing `runner.tagMap` which is built
    // from the same source. Fall back to scanning if runner is absent.
    const tagMap = (this.runner as unknown as { tagMap?: Map<string, number> })?.tagMap;
    if (tagMap) {
      for (const [tag, id] of tagMap) tagToBuildingId.set(tag, id);
    }

    // Per-building name labels + populate the info-panel name lookup.
    for (let i = 0; i < this.scenario.buildings.length; i++) {
      const sb = this.scenario.buildings[i];
      if (sb.name === undefined) continue;
      const buildingId = sb.tag ? tagToBuildingId.get(sb.tag) : undefined;
      if (buildingId === undefined) continue; // no live building? skip silently
      this.buildingIdToScenarioName.set(buildingId, sb.name);
      const text = pickLabel(sb.name);
      if (text.length === 0) continue;
      const pos = this.gridToScreen(sb.col, sb.row);
      const t = this.add.text(pos.x, pos.y - HH - 10, text, {
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
        fontSize: '9px',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 2,
        // Dense glyph texture so labels stay crisp at high zoom (matches HP%).
        resolution: Math.min(6, Math.ceil((window.devicePixelRatio || 1) * this.maxZoom)),
      });
      t.setOrigin(0.5, 1);
      this.buildingLabelLayer.add(t);
      this.buildingNameLabels.set(buildingId, t);
    }

    // Group labels — placed at the geometric centroid of the group's
    // buildings (averaged grid position). Also populate per-member group
    // lookup for the info panel.
    for (const group of this.scenario.buildingGroups ?? []) {
      const text = pickLabel(group.name);
      let sumCol = 0;
      let sumRow = 0;
      let n = 0;
      for (const tag of group.buildingTags) {
        const id = tagToBuildingId.get(tag);
        if (id === undefined) continue;
        const b = this.world.buildings.get(id);
        if (!b) continue;
        this.buildingIdToGroup.set(id, group);
        sumCol += b.col;
        sumRow += b.row;
        n++;
      }
      if (n === 0 || text.length === 0) continue;
      const centroidCol = sumCol / n;
      const centroidRow = sumRow / n;
      const pos = this.gridToScreen(centroidCol, centroidRow);
      const t = this.add.text(pos.x, pos.y - HH - 34, text, {
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
        fontSize: '13px',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 4,
        fontStyle: 'bold',
        // Dense glyph texture so labels stay crisp at high zoom (matches HP%).
        resolution: Math.min(6, Math.ceil((window.devicePixelRatio || 1) * this.maxZoom)),
      });
      t.setOrigin(0.5, 1);
      this.buildingLabelLayer.add(t);
      this.buildingGroupLabels.set(group.id, t);

      // Make group labels clickable — pulse member buildings on click.
      t.setInteractive({ useHandCursor: true });
      const groupId = group.id;
      t.on('pointerup', (ptr: Phaser.Input.Pointer) => {
        if (ptr.rightButtonReleased()) return; // ignore RMB
        this.pulseGroupHighlight(groupId);
      });
    }
  }

  /**
   * Briefly outline every explored building in `groupId` with a pulsing glow
   * (~1.5s). Buildings still in fog are skipped so the pulse never reveals
   * unseen tiles. Outline colour: unanimous owner palette, or white when
   * mixed / neutral.
   */
  private pulseGroupHighlight(groupId: string): void {
    if (!this.scenario) return;
    const group = (this.scenario.buildingGroups ?? []).find((g) => g.id === groupId);
    if (!group) return;
    const tagMap = (this.runner as unknown as { tagMap?: Map<string, number> })?.tagMap;
    if (!tagMap) return;
    const explored = this.world.explored[this.localPlayer];

    // Determine unanimous owner.
    let unanimous: number | null | undefined = undefined;
    const members: Building[] = [];
    for (const tag of group.buildingTags) {
      const bid = tagMap.get(tag);
      if (bid === undefined) continue;
      const b = this.world.buildings.get(bid);
      if (!b) continue;
      if (!explored.has(`${b.col},${b.row}`)) continue;
      members.push(b);
      if (unanimous === undefined) unanimous = b.owner;
      else if (unanimous !== b.owner) unanimous = null;
    }
    if (members.length === 0) return;

    const colour =
      unanimous != null
        ? PALETTE.player[unanimous % PALETTE.player.length]
        : 0xffffff;

    // Spawn one ellipse outline per building, tween its alpha + scale, destroy.
    for (const b of members) {
      const pos = this.gridToScreen(b.col, b.row);
      const ring = this.add.ellipse(pos.x, pos.y, TILE_W * 0.95, TILE_H * 0.95);
      ring.setStrokeStyle(4, colour, 1);
      ring.setFillStyle();
      ring.setDepth(6.4); // just below labels
      this.buildingLabelLayer?.add(ring);
      this.tweens.add({
        targets: ring,
        scale: 1.25,
        alpha: 0,
        duration: 1500,
        ease: 'Sine.easeOut',
        onComplete: () => ring.destroy(),
      });
    }
  }

  /**
   * Recolour + show/hide building / group labels based on current ownership
   * + fog-of-war state. Called from syncEntities, so it follows every state
   * change. Cheap: a few dozen labels at most.
   */
  private updateBuildingLabels(): void {
    if (!this.scenario) return;
    const playerColorHex = (owner: number | null): string => {
      if (owner == null) return '#cccccc';
      const c = PALETTE.player[owner % PALETTE.player.length];
      return '#' + c.toString(16).padStart(6, '0');
    };

    // Per-building. Labels stay visible even in fog (they mark scenario goals
    // the player must know the location of). Owner colour is only revealed
    // while a unit currently sees the tile — otherwise the label is neutral
    // gray, so ownership stays hidden until spotted (matching the icon/flag).
    for (const [id, label] of this.buildingNameLabels) {
      const b = this.world.buildings.get(id);
      if (!b) { label.setVisible(false); continue; }
      label.setVisible(true);
      const isVisible = this.playerVisible.has(`${b.col},${b.row}`);
      label.setColor(isVisible ? playerColorHex(b.owner) : '#cccccc');
    }

    // Per-group. Same rule — always visible. Colour reveals unanimous owner
    // only when every member is currently in sight; otherwise neutral.
    const tagMap = (this.runner as unknown as { tagMap?: Map<string, number> })?.tagMap;
    for (const group of this.scenario.buildingGroups ?? []) {
      const label = this.buildingGroupLabels.get(group.id);
      if (!label) continue;
      let allVisible = true;
      let unanimousOwner: number | null | undefined = undefined;
      for (const tag of group.buildingTags) {
        const bid = tagMap?.get(tag);
        if (bid === undefined) continue;
        const b = this.world.buildings.get(bid);
        if (!b) continue;
        if (!this.playerVisible.has(`${b.col},${b.row}`)) allVisible = false;
        if (unanimousOwner === undefined) unanimousOwner = b.owner;
        else if (unanimousOwner !== b.owner) unanimousOwner = null;
      }
      label.setVisible(true);
      if (allVisible && unanimousOwner != null) {
        label.setColor(playerColorHex(unanimousOwner));
      } else {
        label.setColor('#cccccc');
      }
    }
  }

  /**
   * Create the fog-of-war canvas (one per scene). The canvas covers the entire
   * map's isometric bounding box; we draw black diamonds into it for hidden /
   * explored tiles, then push it to a Phaser CanvasTexture rendered as a single
   * sprite. Repainting happens in `drawFog` / `drawFogDelta`, NOT per frame.
   *
   * Depth is chosen to sit above terrain + roads (the previous per-tile fog
   * lived at `(col+row)*1000 + 50` inside overlayLayer at depth 1, i.e. below
   * entityContainer at depth 5). A single global depth of 2 places this fog
   * sprite above the road sprites in overlayLayer but below entityContainer —
   * matching the prior visual stacking for the player.
   */
  private createFogTexture(): void {
    // Pick a resolution divisor so the canvas stays under a safe WebGL texture
    // size. 4096 is the conservative widely-supported max; anything beyond
    // halves until it fits. Fog gets bilinear-upscaled by the GPU and looks
    // identical for a black mask.
    const MAX_DIM = 4096;
    const longestSide = Math.max(this.worldPxW, this.worldPxH);
    let scale = 1;
    while (longestSide / scale > MAX_DIM) scale *= 2;
    this.fogScale = scale;

    const cw = Math.max(1, Math.ceil(this.worldPxW / scale));
    const ch = Math.max(1, Math.ceil(this.worldPxH / scale));

    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    this.fogCanvas = canvas;
    this.fogCanvasCtx = ctx;

    // Unique per scene instance — Phaser may reuse the scene; avoid texture
    // collisions across runs.
    this.fogTextureKey = `fog-canvas-${this.scene.key}-${Date.now()}`;
    this.textures.addCanvas(this.fogTextureKey, canvas);

    const img = this.add.image(0, 0, this.fogTextureKey);
    img.setOrigin(0, 0);
    img.setDepth(2);
    // Stretch canvas back up to world space — GPU bilinear interpolation
    // handles the smoothing.
    img.setDisplaySize(this.worldPxW, this.worldPxH);
  }

  /** Stroke a fog diamond at the given tile center onto the offscreen canvas. */
  private fogPaintTile(ctx: CanvasRenderingContext2D, col: number, row: number, alpha: number): void {
    const s = this.fogScale;
    const { x, y } = this.gridToScreen(col, row);
    const cx = x / s, cy = y / s, hw = HW / s, hh = HH / s;
    ctx.globalAlpha = alpha;
    ctx.beginPath();
    ctx.moveTo(cx, cy - hh);
    ctx.lineTo(cx + hw, cy);
    ctx.lineTo(cx, cy + hh);
    ctx.lineTo(cx - hw, cy);
    ctx.closePath();
    ctx.fill();
  }

  /** Clear a single tile's diamond from the offscreen canvas (used by delta). */
  private fogClearTile(ctx: CanvasRenderingContext2D, col: number, row: number): void {
    const s = this.fogScale;
    const { x, y } = this.gridToScreen(col, row);
    const cx = x / s, cy = y / s, hw = HW / s, hh = HH / s;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(cx, cy - hh);
    ctx.lineTo(cx + hw, cy);
    ctx.lineTo(cx, cy + hh);
    ctx.lineTo(cx - hw, cy);
    ctx.closePath();
    ctx.clip();
    // Slight bleed (1px) to make sure adjacent diamonds repainted later don't
    // leave a stroke ridge from previous frame.
    ctx.clearRect(cx - hw - 1, cy - hh - 1, (TILE_W / s) + 2, (TILE_H / s) + 2);
    ctx.restore();
  }

  private drawGrid(): void {
    const g = this.gridGfx;
    g.clear();
    g.lineStyle(1, 0x000000, 0.1);
    for (let row = 0; row < this.mapHeight; row++) {
      for (let col = 0; col < this.mapWidth; col++) {
        const { x, y } = this.gridToScreen(col, row);
        g.strokePoints([
          new Phaser.Geom.Point(x, y - HH),
          new Phaser.Geom.Point(x + HW, y),
          new Phaser.Geom.Point(x, y + HH),
          new Phaser.Geom.Point(x - HW, y),
          new Phaser.Geom.Point(x, y - HH),
        ], false);
      }
    }
  }

  // --- Highlight rendering per state ---
  //
  // The `unitSelected` branch is the busiest by design — when a player picks
  // a unit they should see at a glance:
  //   1. Where it can move (orange movement range contour)
  //   2. Which enemies it can hit (red outline on each attackable enemy)
  //   3. Where its weapons reach (red contour for ranged units only)
  //   4. Predicted damage when hovering an attackable enemy (HP-bar preview)
  // Cursor and "out-of-range" flash are driven by the input handlers, not here.

  private drawHighlights(): void {
    const g = this.highlightGfx;
    g.clear();
    this.battlePreviewGfx.clear();
    this.frameTargetIds.clear();
    this.prepareGlowCycle();
    const st = this.inputState;

    // Officer ability targeting — paint the affected area under the cursor.
    // Air-strike = red 3×3 impact; recon = blue (2R+1)² reveal footprint.
    if (st.kind === 'orderOfficerAbility' && this.abilityHoverTile) {
      const aimingUnit = this.world.units.get(st.unitId);
      const effect = aimingUnit && UNIT_DEFS[aimingUnit.type].abilities?.find((a) => a.id === st.abilityId)?.effect;
      const isRecon = effect === 'recon';
      const radius = isRecon ? RECON_RADIUS : 1;
      const color = isRecon ? 0x7dcfff : 0xf7768e;
      const { col: hc, row: hr } = this.abilityHoverTile;
      for (let dc = -radius; dc <= radius; dc++) {
        for (let dr = -radius; dr <= radius; dr++) {
          const c = hc + dc, r = hr + dr;
          if (!this.inBounds(c, r)) continue;
          const { x, y } = this.gridToScreen(c, r);
          g.fillStyle(color, 0.32);
          g.lineStyle(2, color, 0.9);
          g.beginPath();
          g.moveTo(x, y - HH);
          g.lineTo(x + HW, y);
          g.lineTo(x, y + HH);
          g.lineTo(x - HW, y);
          g.closePath();
          g.fillPath();
          g.strokePath();
        }
      }
      this.syncTargetOutlines(); // drop any lingering outlines while aiming
      return; // nothing else to draw while aiming
    }

    // Commander aura range — faint player-colour fill under a selected commander.
    if ('unitId' in st) {
      const cu = this.world.units.get(st.unitId);
      const auras = cu ? UNIT_DEFS[cu.type].auras : undefined;
      if (cu && auras && auras.length) {
        const maxRange = Math.max(...auras.map((a) => a.range));
        const color = PALETTE.player[cu.owner % PALETTE.player.length];
        g.fillStyle(color, 0.10);
        for (let dc = -maxRange; dc <= maxRange; dc++) {
          for (let dr = -maxRange; dr <= maxRange; dr++) {
            const c = cu.col + dc, r = cu.row + dr;
            if (!this.inBounds(c, r)) continue;
            const { x, y } = this.gridToScreen(c, r);
            g.beginPath();
            g.moveTo(x, y - HH); g.lineTo(x + HW, y); g.lineTo(x, y + HH); g.lineTo(x - HW, y);
            g.closePath(); g.fillPath();
          }
        }
      }
    }

    // Enemy white-blink lives in `unitSelected` and post-move target-picking.

    // Determine which building needs selection glow override
    let selBuildingId: number | null = null;
    if (st.kind === 'buildingSelected') selBuildingId = st.buildingId;
    else if (st.kind === 'moved') {
      const u = this.world.units.get(st.unitId);
      if (u && u.type === 'infantry') {
        const bld = getBuildingAt(this.world, u.col, u.row);
        if (bld && bld.owner !== u.owner) selBuildingId = bld.id;
      }
    }
    // Clear the previous selection postFX so the white/orange aura doesn't
    // linger after the player picks a different target. Ownership tint is
    // re-applied every frame in syncEntities, so no extra restore is needed.
    if (this.glowOverrideBuildingId != null && this.glowOverrideBuildingId !== selBuildingId) {
      const oldIcon = this.buildingIcons.get(this.glowOverrideBuildingId);
      if (oldIcon?.postFX) oldIcon.postFX.clear();
      this.glowOverrideBuildingId = null;
    }
    // Apply selection glow on the target building (only on transition — avoid per-frame clear+add)
    if (selBuildingId != null && selBuildingId !== this.glowOverrideBuildingId) {
      const icon = this.buildingIcons.get(selBuildingId);
      if (icon?.postFX) {
        icon.postFX.clear();
        const color = st.kind === 'moved' ? 0xe0af68 : 0xffffff;
        icon.postFX.addGlow(color, 4, 0, false, 0.1, 6);
      }
      this.glowOverrideBuildingId = selBuildingId;
    }

    // Helper: apply glow on unit icon sprite
    const highlightUnit = (unitId: number, color = 0xffffff) => {
      const u = this.world.units.get(unitId);
      if (u) this.applyUnitGlow(u, color);
    };

    if (st.kind === 'tileSelected') {
      // Don't draw tile highlight if there's a unit on the tile
      if (!this.getUnitAt(st.col, st.row)) {
        this.applyTerrainGlow(st.col, st.row, 0xffffff);
        // Fallback diamond if no terrain overlay on this tile
        if (!this.terrainOverlayImages.has(`${st.col},${st.row}`)) {
          this.drawTileHighlight(g, st.col, st.row, 0xffffff, 0.3);
        }
      }
    }

    if (st.kind === 'unitSelected') {
      // Show movement range immediately when a unit is selected — same logic
      // as orderMove but a subtler alpha because we're not yet committing to
      // a move target.
      if (!st._cachedNormalKeys) {
        const normal: string[] = []; const zoc: string[] = [];
        for (const k of st.range.keys()) { (st.zoc.has(k) ? zoc : normal).push(k); }
        st._cachedNormalKeys = normal;
        st._cachedZocKeys = zoc;
      }
      // Movement range = soft WHITE glow. ZoC tiles = amber (caution: contested).
      // Attack range is deliberately NOT drawn here — it overlapped the move
      // grid and misled players. It shows on RMB-hold and once an attack order
      // is chosen (distinct red style — see drawAttackRange).
      if (st._cachedNormalKeys.length > 0) {
        this.drawRangeContour(g, st._cachedNormalKeys, 0xffffff, 0.14, false);
      }
      if (st._cachedZocKeys!.length > 0) this.drawRangeContour(g, st._cachedZocKeys!, 0xe0af68, 0.14, false);
      highlightUnit(st.unitId);

      // Glow every enemy the unit could attack THIS turn — including those it
      // can reach by moving — so the player sees targets the instant they select.
      const selectedUnit = this.world.units.get(st.unitId);
      if (selectedUnit) {
        if (!st._cachedAttackableEnemies) {
          st._cachedAttackableEnemies = this.computeReachableAttackableEnemies(selectedUnit, st.range);
        }
        this.drawTargetGlow(st._cachedAttackableEnemies);

        // Hover-on-enemy damage preview (Fire-Emblem-style HP gradient bars).
        if (this.hoveredEnemyId != null && st._cachedAttackableEnemies.has(this.hoveredEnemyId)) {
          const hovered = this.world.units.get(this.hoveredEnemyId);
          if (hovered) this.drawBattlePreviewPair(selectedUnit, hovered);
        }
      }
    }

    if (st.kind === 'pendingMoveAttack') {
      // Picking a post-move attack target: glow the moved unit, blink targets,
      // and show its attack range (the red zone) so the player sees the reach.
      highlightUnit(st.unitId);
      this.drawTargetGlow(st.attackableEnemies);
      const mover = this.world.units.get(st.unitId);
      if (mover) this.drawAttackRange(g, this.computeAttackRangeKeys(mover), true);
      if (mover && this.hoveredEnemyId != null && st.attackableEnemies.has(this.hoveredEnemyId)) {
        const hovered = this.world.units.get(this.hoveredEnemyId);
        if (hovered) this.drawBattlePreviewPair(mover, hovered);
      }
    }

    if (st.kind === 'orderMove') {
      // Cache split keys on first frame to avoid spread+filter on every frame
      if (!st._cachedNormalKeys) {
        const normal: string[] = []; const zoc: string[] = [];
        for (const k of st.range.keys()) { (st.zoc.has(k) ? zoc : normal).push(k); }
        st._cachedNormalKeys = normal;
        st._cachedZocKeys = zoc;
      }
      this.drawRangeContour(g, st._cachedNormalKeys, 0xf0860a, 0.40, true);
      if (st._cachedZocKeys!.length > 0) this.drawRangeContour(g, st._cachedZocKeys!, 0xf7768e, 0.40, true);
      highlightUnit(st.unitId);
    }

    if (st.kind === 'confirmMove') {
      // Cache split keys on first frame to avoid spread+filter on every frame
      if (!st._cachedNormalKeys) {
        const normal: string[] = []; const zoc: string[] = [];
        for (const k of st.range.keys()) { (st.zoc.has(k) ? zoc : normal).push(k); }
        st._cachedNormalKeys = normal;
        st._cachedZocKeys = zoc;
      }
      this.drawRangeContour(g, st._cachedNormalKeys, 0xc06808, 0.20, false);
      if (st._cachedZocKeys!.length > 0) this.drawRangeContour(g, st._cachedZocKeys!, 0xb04040, 0.20, false);
      for (const step of st.path) {
        this.drawTileHighlight(g, step.col, step.row, 0x00ddff, 0.4, true);
      }
      this.drawTileHighlight(g, st.targetCol, st.targetRow, 0x00ddff, 0.6);
      highlightUnit(st.unitId);
    }

    if (st.kind === 'orderAttack') {
      this.drawAttackRange(g, st.attackRangeKeys, true);
      const unit = this.world.units.get(st.unitId);
      for (const enemyId of st.attackableEnemies) {
        const enemy = this.world.units.get(enemyId);
        if (enemy) {
          this.drawTargetGlow([enemy.id]);
          if (unit) this.drawPreviewBar(enemy, calculateDamage(this.world, unit, enemy));
        }
      }
      highlightUnit(st.unitId);
    }

    if (st.kind === 'confirmAttack') {
      const enemy = this.world.units.get(st.targetEnemyId);
      if (enemy) {
        this.drawTargetGlow([enemy.id]);
        const unit = this.world.units.get(st.unitId);
        if (unit) this.drawBattlePreviewPair(unit, enemy);
      }
      highlightUnit(st.unitId);
    }

    if (st.kind === 'moved') {
      const unit = this.world.units.get(st.unitId);
      if (unit) {
        for (const [, enemy] of this.world.units) {
          if (enemy.owner === unit.owner) continue;
          if (canAttack(unit, enemy)) {
            this.drawTargetGlow([enemy.id]);
          }
        }
      }
      highlightUnit(st.unitId);
    }

    if (st.kind === 'movedOrderAttack') {
      const unit = this.world.units.get(st.unitId);
      if (unit) this.drawAttackRange(g, this.computeAttackRangeKeys(unit), true);
      for (const enemyId of st.attackableEnemies) {
        const enemy = this.world.units.get(enemyId);
        if (enemy) {
          this.drawTargetGlow([enemy.id]);
          if (unit) this.drawPreviewBar(enemy, calculateDamage(this.world, unit, enemy));
        }
      }
      highlightUnit(st.unitId);
    }

    if (st.kind === 'confirmPostMoveAttack') {
      const enemy = this.world.units.get(st.targetEnemyId);
      if (enemy) {
        this.drawTargetGlow([enemy.id]);
        const unit = this.world.units.get(st.unitId);
        if (unit) this.drawBattlePreviewPair(unit, enemy);
      }
      highlightUnit(st.unitId);
    }

    if (st.kind === 'orderUnload') {
      const keys = st.unloadTiles.map(t => `${t.col},${t.row}`);
      this.drawRangeContour(g, keys, 0x4a90e2, 0.35, true);
      highlightUnit(st.unitId);
    }

    // buildingSelected glow is handled above (selBuildingId)

    // Hover: show enemy unit attack range preview (only in passive states)
    if (this.hoveredEnemyId != null && (
      st.kind === 'idle' || st.kind === 'tileSelected' || st.kind === 'unitSelected' || st.kind === 'buildingSelected'
    )) {
      const enemy = this.world.units.get(this.hoveredEnemyId);
      if (enemy && this.playerVisible.has(`${enemy.col},${enemy.row}`)) {
        const rangeKeys = this.computeAttackRangeKeys(enemy);
        this.drawAttackRange(g, rangeKeys, false);
      }
    }

    // RMB attack-range preview for one of my own units — its reach + any enemies
    // currently within it.
    if (this.attackRangePreviewId != null) {
      const u = this.world.units.get(this.attackRangePreviewId);
      if (u && u.owner === this.localPlayer && UNIT_DEFS[u.type].attackRange > 0) {
        const keys = this.computeAttackRangeKeys(u);
        this.drawAttackRange(g, keys, true);
        const keySet = new Set(keys);
        for (const [, e] of this.world.units) {
          if (e.owner === u.owner) continue;
          if (keySet.has(`${e.col},${e.row}`) && this.playerVisible.has(`${e.col},${e.row}`)) {
            this.drawTargetGlow([e.id]);
          }
        }
        highlightUnit(u.id);
      } else {
        this.attackRangePreviewId = null;
      }
    }

    // Tutorial highlight: pulsing marker on target tile/unit/building
    this.drawTutorialHighlight(g);

    // Interactive confirm-move marker
    this.updateConfirmMarker();

    // Remove postFX only from objects that are no longer glowed this frame
    this.finalizeGlows();
    // Create/update/drop the blinking red silhouette outlines for this frame's
    // attackable targets (accumulated via drawTargetGlow).
    this.syncTargetOutlines();
  }

  /**
   * Show / hide the interactive green ✓ marker over the current move target.
   * Created lazily, reused across confirmMove states. Click on it = execute
   * the move; hover scales it up; pointer-down depresses it briefly.
   */
  private updateConfirmMarker(): void {
    const st = this.inputState;
    // Determine target tile and marker style for the current confirm state.
    let target: { col: number; row: number } | null = null;
    let textureKey = 'confirm-marker';
    if (st.kind === 'confirmMove') {
      target = { col: st.targetCol, row: st.targetRow };
      textureKey = 'confirm-marker';
    } else if (st.kind === 'confirmAttack' || st.kind === 'confirmPostMoveAttack') {
      const enemy = this.world.units.get(st.targetEnemyId);
      if (enemy) {
        target = { col: enemy.col, row: enemy.row };
        textureKey = 'confirm-attack-marker';
      }
    }
    if (!target) {
      if (this.confirmMarker) {
        this.confirmMarker.setVisible(false);
        // Hidden game objects still receive pointer events unless their
        // interactivity is explicitly removed — otherwise a click on a unit
        // that happens to be on the old target tile fires the marker's
        // pointerup handler. Disable here, re-enable when we re-show.
        this.confirmMarker.disableInteractive();
      }
      return;
    }
    const { x, y } = this.gridToScreen(target.col, target.row);
    if (!this.confirmMarker) {
      const img = this.add.image(x, y, 'confirm-marker');
      img.setOrigin(0.5, 0.5);
      img.setDepth(99997); // above units, below explosions
      img.setInteractive({ useHandCursor: true });
      // Texture is 4× the display size (supersampled for sharp edges); the
      // hover/press scales below are relative to that downsampled base.
      const baseScale = 22 / img.width;
      img.setScale(baseScale);
      img.setData('baseScale', baseScale);
      const restScale = baseScale;
      const hoverScale = baseScale * 1.22;
      const pressScale = baseScale * 0.85;
      const tween = (scale: number, tint: number, duration: number): void => {
        this.tweens.killTweensOf(img);
        this.tweens.add({ targets: img, scale, duration, ease: 'Sine.easeOut' });
        img.setTint(tint);
      };
      const isConfirmState = (): boolean => {
        const k = this.inputState.kind;
        return k === 'confirmMove' || k === 'confirmAttack' || k === 'confirmPostMoveAttack';
      };
      // All marker handlers no-op when we're not in any confirm-* state —
      // protects against stale events delivered after the marker was hidden.
      img.on('pointerover', () => {
        if (!isConfirmState()) return;
        tween(hoverScale, 0xeeffee, 90);
      });
      img.on('pointerout',  () => {
        if (!isConfirmState()) return;
        tween(restScale, 0xffffff, 120);
      });
      img.on('pointerdown', (p: Phaser.Input.Pointer) => {
        if (!isConfirmState()) return;
        if (p.button !== 0) return; // only left button presses the marker
        tween(pressScale, 0xbfd9bf, 60);
      });
      img.on('pointerup', (p: Phaser.Input.Pointer) => {
        if (!isConfirmState()) return;
        // Right click on the marker cancels the order; left click confirms.
        if (p.button === 2) {
          this.cancelBack();
          return;
        }
        if (p.button !== 0) return;
        tween(restScale, 0xffffff, 90);
        const cur = this.inputState;
        if (cur.kind === 'confirmMove') {
          this.handleConfirmMoveClick(cur.targetCol, cur.targetRow);
        } else if (cur.kind === 'confirmAttack' || cur.kind === 'confirmPostMoveAttack') {
          const enemy = this.world.units.get(cur.targetEnemyId);
          if (!enemy) return;
          if (cur.kind === 'confirmAttack') {
            this.handleConfirmAttackClick(enemy.col, enemy.row);
          } else {
            this.handleConfirmPostMoveAttackClick(enemy.col, enemy.row);
          }
        }
      });
      this.confirmMarker = img;
    }
    if (this.confirmMarker.texture.key !== textureKey) {
      this.confirmMarker.setTexture(textureKey);
    }
    this.confirmMarker.setPosition(x, y); // centered on the tile
    this.confirmMarker.setVisible(true);
    if (!this.confirmMarker.input?.enabled) {
      this.confirmMarker.setInteractive({ useHandCursor: true });
    }
    this.confirmMarker.setAlpha(1);
    // Reset to base scale on re-show — in case the user cancelled mid-hover
    // and the tween left the marker at the hovered/pressed scale.
    this.tweens.killTweensOf(this.confirmMarker);
    const base = (this.confirmMarker.getData('baseScale') as number | undefined) ?? 1;
    this.confirmMarker.setScale(base);
    this.confirmMarker.clearTint();
  }

  /** Get current pulse multiplier (0.85..1.0 oscillating). */
  private get pulse(): number {
    return 0.85 + 0.15 * Math.sin(this.highlightTime * 3);
  }

  private drawTileHighlight(g: Phaser.GameObjects.Graphics, col: number, row: number, color: number, alpha: number, animated = false): void {
    const { x, y } = this.gridToScreen(col, row);
    const a = animated ? alpha * this.pulse : alpha;
    // Fill — direct path commands (no Point allocations)
    g.fillStyle(color, a);
    g.beginPath();
    g.moveTo(x, y - HH);
    g.lineTo(x + HW, y);
    g.lineTo(x, y + HH);
    g.lineTo(x - HW, y);
    g.closePath();
    g.fillPath();
    // Border — darker, more opaque (cached dark color)
    const darkColor = TurnGameScene.getRangeDarkColor(color);
    g.lineStyle(1, darkColor, Math.min(a + 0.25, 0.9));
    g.beginPath();
    g.moveTo(x, y - HH);
    g.lineTo(x + HW, y);
    g.lineTo(x, y + HH);
    g.lineTo(x - HW, y);
    g.lineTo(x, y - HH);
    g.strokePath();
  }

  /** Draw pulsing highlight for the current tutorial step target. */
  private drawTutorialHighlight(g: Phaser.GameObjects.Graphics): void {
    if (!this.runner) return;
    const state = this.runner.getTutorialState();
    if (!state || state.finished || !state.step.highlight) {
      // No tutorial highlight active — clear any lingering building glow
      if (this.tutorialHighlightBuildingId != null) {
        const oldIcon = this.buildingIcons.get(this.tutorialHighlightBuildingId);
        if (oldIcon?.postFX) oldIcon.postFX.clear();
        this.tutorialHighlightBuildingId = null;
      }
      return;
    }
    const hl = state.step.highlight;
    const pulse = 0.3 + 0.4 * Math.abs(Math.sin(this.highlightTime * 2.5));

    // If previous step highlighted a building but current doesn't, clear glow.
    if (hl.type !== 'building' && this.tutorialHighlightBuildingId != null) {
      const oldIcon = this.buildingIcons.get(this.tutorialHighlightBuildingId);
      if (oldIcon?.postFX) oldIcon.postFX.clear();
      this.tutorialHighlightBuildingId = null;
    }

    if (hl.type === 'tile') {
      const { col, row } = hl.target;
      // Pulsing cyan diamond
      this.drawTileHighlight(g, col, row, 0x00ddff, pulse, true);
      // Arrow marker above
      const { x, y } = this.gridToScreen(col, row);
      const bounce = 4 * Math.sin(this.highlightTime * 4);
      g.fillStyle(0x00ddff, 0.9);
      g.fillTriangle(x, y - HH - 10 + bounce, x - 6, y - HH - 20 + bounce, x + 6, y - HH - 20 + bounce);
    }

    if (hl.type === 'unit') {
      const unitId = this.runner['tagMap'].get(hl.target);
      if (unitId !== undefined) {
        const u = this.world.units.get(unitId);
        if (u) {
          // Pulsing glow on unit
          this.applyUnitGlow(u, 0x00ddff);
          // Arrow above unit
          const { x, y } = this.gridToScreen(u.col, u.row);
          const bounce = 4 * Math.sin(this.highlightTime * 4);
          g.fillStyle(0x00ddff, 0.9);
          g.fillTriangle(x, y - HH - 14 + bounce, x - 6, y - HH - 24 + bounce, x + 6, y - HH - 24 + bounce);
        }
      }
    }

    if (hl.type === 'building') {
      const bId = this.runner['tagMap'].get(hl.target);
      if (bId !== undefined) {
        const b = this.world.buildings.get(bId);
        if (b) {
          this.drawTileHighlight(g, b.col, b.row, 0x00ddff, pulse, true);
          // Apply building tutorial glow (idempotent — only on transition)
          if (this.tutorialHighlightBuildingId !== bId) {
            const icon = this.buildingIcons.get(bId);
            if (icon?.postFX) {
              icon.postFX.clear();
              icon.postFX.addGlow(0x00ddff, 6, 0, false, 0.1, 6);
            }
            this.tutorialHighlightBuildingId = bId;
          }
        }
      }
    }
  }

  /**
   * Draw battle preview overlay HP bar for a single unit showing predicted damage.
   * Green = HP remaining, pulsating red = HP to be lost, dark = already missing.
   */
  private drawPreviewBar(unit: Unit, damage: number): void {
    const g = this.battlePreviewGfx;
    const R = UNIT_TOKEN_R;
    const barW = 32;
    const barH = 4;
    const { x, y } = this.gridToScreen(unit.col, unit.row);
    const def = UNIT_DEFS[unit.type];
    const barX = x - barW / 2;
    const barY = y - 6 - R - 7;

    const hpRatio = unit.hp / def.maxHp;
    const afterHp = Math.max(0, unit.hp - damage);
    const afterRatio = afterHp / def.maxHp;

    // Black border
    g.lineStyle(1, 0x000000, 1);
    g.strokeRect(barX - 0.5, barY - 0.5, barW + 1, barH + 1);
    // Dark background (missing HP)
    g.fillStyle(0x000000, 0.6);
    g.fillRect(barX, barY, barW, barH);
    // Green segment (HP remaining after attack)
    const greenColor = afterRatio > 0.5 ? 0x9ece6a : afterRatio > 0.25 ? 0xe0af68 : 0xf7768e;
    if (afterRatio > 0) {
      g.fillStyle(greenColor, 1);
      g.fillRect(barX, barY, barW * afterRatio, barH);
    }
    // Pulsating red segment (HP to be lost) — fast, wide blink (alpha 0.3..1.0)
    const lostWidth = barW * (hpRatio - afterRatio);
    if (lostWidth > 0) {
      const blink = 0.5 + 0.5 * Math.sin(this.highlightTime * 6);  // 0..1 @ ~1Hz
      const pulseAlpha = 0.3 + 0.7 * blink;
      g.fillStyle(0xf7768e, pulseAlpha);
      g.fillRect(barX + barW * afterRatio, barY, lostWidth, barH);
    }
  }

  /**
   * Draw battle preview for attacker vs one defender.
   * Shows damage on defender + counter-damage on attacker (with defender's post-damage HP).
   */
  private drawBattlePreviewPair(attacker: Unit, defender: Unit): void {
    const dmg = calculateDamage(this.world, attacker, defender);
    this.drawPreviewBar(defender, dmg);

    // Counter-damage: defender counters AFTER taking damage, so use reduced HP
    const defDef = UNIT_DEFS[defender.type];
    const dist = chebyshevDistance(attacker, defender);
    const defMinRange = defDef.minAttackRange ?? 1;
    if (dist >= defMinRange && dist <= defDef.attackRange && defDef.attack > 0) {
      const defenderAfterHp = Math.max(0, defender.hp - dmg);
      if (defenderAfterHp > 0) {
        // Simulate counter with reduced HP: damage formula uses hp/maxHp ratio
        const counterBase = calculateDamage(this.world, defender, attacker, 0.7);
        const counterDmg = Math.max(1, Math.round(counterBase * (defenderAfterHp / defender.hp)));
        this.drawPreviewBar(attacker, counterDmg);
      }
    }
  }

  /**
   * Apply a glow effect on the unit's icon sprite. Since tokens were removed
   * (owner identity is carried by tinting the icon), the icon Image is the
   * only visible mark of the unit — that's where the glow needs to live.
   * Idempotent — skips when the same color is already applied.
   */
  /**
   * Mark each attackable enemy with a cheap pulsing RED glow drawn into the
   * shared highlight Graphics. This replaces the old per-enemy postFX Glow
   * (which spawned one offscreen render target + shader pass per enemy and
   * tanked the frame rate when several units were in range). Any number of
   * targets now costs a single extra draw pass. Pulse is driven by
   * `highlightTime`, so the caller must run on the per-frame highlight path.
   */
  /** Mark ids as attackable this frame; the actual outline sprites are created /
   *  updated once per frame in syncTargetOutlines (called at the end of
   *  drawHighlights) so multiple call sites just accumulate into one set. */
  private drawTargetGlow(ids: Iterable<number>): void {
    for (const id of ids) this.frameTargetIds.add(id);
  }

  /** Reconcile the blinking red silhouette outlines with `frameTargetIds`:
   *  create one per new target, drop stale ones, and pulse their alpha. Each
   *  outline mirrors the enemy icon (texture/pose/flip) scaled up a touch and
   *  filled solid red, sitting just behind the icon so only the rim shows. */
  private syncTargetOutlines(): void {
    const ids = this.frameTargetIds;
    for (const [id, spr] of this.targetOutlines) {
      if (!ids.has(id)) { spr.destroy(); this.targetOutlines.delete(id); }
    }
    if (ids.size === 0) return;
    const blink = 0.25 + 0.40 * (0.5 + 0.5 * Math.sin(this.highlightTime * 4)); // ~0.25..0.65
    for (const id of ids) {
      const icon = this.unitIconImages.get(id);
      if (!icon) {
        const stale = this.targetOutlines.get(id);
        if (stale) { stale.destroy(); this.targetOutlines.delete(id); }
        continue;
      }
      let spr = this.targetOutlines.get(id);
      if (!spr) {
        spr = this.add.image(0, 0, icon.texture.key);
        this.entityContainer.add(spr);
        this.targetOutlines.set(id, spr);
      }
      if (spr.texture.key !== icon.texture.key) spr.setTexture(icon.texture.key);
      spr.setOrigin(icon.originX, icon.originY);
      spr.setPosition(icon.x, icon.y);
      spr.setFlipX(icon.flipX);
      spr.setScale(icon.scaleX * 1.14, icon.scaleY * 1.14); // a touch larger → rim pokes out
      spr.setTintFill(ATTACK_GLOW_COLOR);                   // solid red silhouette
      spr.setDepth(icon.depth - 0.5);
      spr.setAlpha(blink);
      // The container only depth-sorts in syncEntities; force the outline BEHIND
      // its icon now so only the enlarged rim shows (otherwise it'd paint over
      // the whole sprite this frame).
      this.entityContainer.moveBelow(spr, icon);
    }
  }

  /** Destroy all target outlines (used when highlights are wiped for animation). */
  private clearTargetOutlines(): void {
    for (const spr of this.targetOutlines.values()) spr.destroy();
    this.targetOutlines.clear();
    this.frameTargetIds.clear();
  }

  private applyUnitGlow(u: Unit, color: number): void {
    const iconImg = this.unitIconImages.get(u.id);
    if (!iconImg?.postFX) return;
    if (this.unitGlowColors.get(u.id) === color) {
      this.glowedUnitIds.add(u.id);
      return; // already glowing this color
    }
    iconImg.postFX.clear();
    iconImg.postFX.addGlow(color, 4, 0, false, 0.1, 6);
    this.glowedUnitIds.add(u.id);
    this.unitGlowColors.set(u.id, color);
  }

  /** Apply a glow on a terrain overlay image at the given tile (idempotent). */
  private applyTerrainGlow(col: number, row: number, color: number): void {
    const key = `${col},${row}`;
    const img = this.terrainOverlayImages.get(key);
    if (!img?.postFX) return;
    if (this.terrainGlowColors.get(key) === color) {
      this.glowedTerrainKeys.add(key);
      return; // already glowing this color
    }
    img.postFX.clear();
    img.postFX.addGlow(color, 4, 0, false, 0.1, 6);
    this.glowedTerrainKeys.add(key);
    this.terrainGlowColors.set(key, color);
  }

  /** Prepare for glow re-evaluation: swap current→prev tracking sets (no allocations).
   *  Actual postFX removal happens in finalizeGlows() for objects no longer needed. */
  private prepareGlowCycle(): void {
    // Swap references instead of copying — avoids new Set() allocation each frame
    const tmpUnits = this.prevGlowedUnitIds;
    this.prevGlowedUnitIds = this.glowedUnitIds;
    this.glowedUnitIds = tmpUnits;
    this.glowedUnitIds.clear();

    const tmpTerrain = this.prevGlowedTerrainKeys;
    this.prevGlowedTerrainKeys = this.glowedTerrainKeys;
    this.glowedTerrainKeys = tmpTerrain;
    this.glowedTerrainKeys.clear();
  }

  /** Remove postFX only from objects that were glowed last frame but not this frame. */
  private finalizeGlows(): void {
    for (const id of this.prevGlowedUnitIds) {
      if (!this.glowedUnitIds.has(id)) {
        const iconImg = this.unitIconImages.get(id);
        if (iconImg?.postFX) iconImg.postFX.clear();
        this.unitGlowColors.delete(id);
      }
    }
    for (const key of this.prevGlowedTerrainKeys) {
      if (!this.glowedTerrainKeys.has(key)) {
        const img = this.terrainOverlayImages.get(key);
        if (img?.postFX) img.postFX.clear();
        this.terrainGlowColors.delete(key);
      }
    }
  }

  /** Pre-computed dark border colors for range contour drawing (avoids per-frame Color object creation). */
  private static readonly RANGE_DARK_COLORS = new Map<number, number>();
  private static getRangeDarkColor(color: number): number {
    let dark = TurnGameScene.RANGE_DARK_COLORS.get(color);
    if (dark === undefined) {
      const c = Phaser.Display.Color.ValueToColor(color);
      c.darken(30);
      dark = c.color;
      TurnGameScene.RANGE_DARK_COLORS.set(color, dark);
    }
    return dark;
  }

  /** Draw a set of range tiles with fills and only outer contour (no per-tile borders).
   *  Optimized: no per-frame Point/Color allocations — uses direct beginPath/moveTo/lineTo. */
  /** Attack-range overlay — a pale red zone with a darker outer contour and a
   *  per-tile inner-diamond pattern, visually distinct from the white move range. */
  private drawAttackRange(g: Phaser.GameObjects.Graphics, rangeKeys: Iterable<string>, animated: boolean): void {
    const rangeSet = new Set<string>();
    const tiles: { col: number; row: number }[] = [];
    for (const key of rangeKeys) { rangeSet.add(key); tiles.push(parseCoordKey(key)); }
    const dark = 0x5e0a0a;          // dark blood (outer contour)
    const a = animated ? Math.max(0.5, this.pulse) : 1;
    // Subtle blink of the band fill (gentle sine on the pulse timer).
    const blink = animated ? 0.42 + 0.10 * Math.sin(this.highlightTime * 2.5) : 0.5;

    // Wide diagonal hazard BANDS filling each diamond — the same blood red in two
    // subtly different brightnesses, alternating. Bands run at a steep "/" angle
    // (distinct from the iso edges) and are clipped to the diamond by polygon
    // clipping against the two parallel band planes.
    const dlen = Math.hypot(1, 1.6);
    const dx = 1 / dlen, dy = -1.6 / dlen;     // band direction "/"
    const nx = -dy, ny = dx;                   // band axis (perpendicular)
    const bandW = HH * 0.7;                    // band thickness
    const shadeLight = 0xc62828, shadeDark = 0x9a1818;

    const clipHalf = (poly: { x: number; y: number }[], c: number, keepBelow: boolean) => {
      const out: { x: number; y: number }[] = [];
      const dist = (p: { x: number; y: number }) => p.x * nx + p.y * ny - c;
      for (let i = 0; i < poly.length; i++) {
        const A = poly[i], B = poly[(i + 1) % poly.length];
        const dA = dist(A), dB = dist(B);
        const inA = keepBelow ? dA <= 1e-9 : dA >= -1e-9;
        const inB = keepBelow ? dB <= 1e-9 : dB >= -1e-9;
        if (inA) out.push(A);
        if (inA !== inB) { const t = dA / (dA - dB); out.push({ x: A.x + t * (B.x - A.x), y: A.y + t * (B.y - A.y) }); }
      }
      return out;
    };

    for (const { col, row } of tiles) {
      const { x, y } = this.gridToScreen(col, row);
      const diamond = [{ x, y: y - HH }, { x: x + HW, y }, { x, y: y + HH }, { x: x - HW, y }];
      // GLOBAL band grid in world space (band index m = floor(P·n / bandW)) so
      // bands run continuously and line up across adjacent tiles — and the
      // light/dark parity (m % 2) is shared, not reset per tile.
      let qmin = Infinity, qmax = -Infinity;
      for (const p of diamond) { const q = p.x * nx + p.y * ny; if (q < qmin) qmin = q; if (q > qmax) qmax = q; }
      const m0 = Math.floor(qmin / bandW), m1 = Math.ceil(qmax / bandW);
      for (let m = m0; m < m1; m++) {
        let poly = clipHalf(diamond, m * bandW, false);                      // keep P·n >= lo
        if (poly.length >= 3) poly = clipHalf(poly, (m + 1) * bandW, true);  // keep P·n <= hi
        if (poly.length < 3) continue;
        g.fillStyle((((m % 2) + 2) % 2) === 0 ? shadeLight : shadeDark, blink);
        g.beginPath();
        g.moveTo(poly[0].x, poly[0].y);
        for (let i = 1; i < poly.length; i++) g.lineTo(poly[i].x, poly[i].y);
        g.closePath(); g.fillPath();
      }
    }

    // Darker outer contour — only the external edges of the zone.
    g.lineStyle(2, dark, Math.min(0.85 * a + 0.1, 0.95));
    for (const { col, row } of tiles) {
      const { x, y } = this.gridToScreen(col, row);
      const tx = x, ty = y - HH, rx = x + HW, ry = y, bx = x, by = y + HH, lx = x - HW, ly = y;
      if (!rangeSet.has(`${col},${row - 1}`)) { g.beginPath(); g.moveTo(tx, ty); g.lineTo(rx, ry); g.strokePath(); }
      if (!rangeSet.has(`${col + 1},${row}`)) { g.beginPath(); g.moveTo(rx, ry); g.lineTo(bx, by); g.strokePath(); }
      if (!rangeSet.has(`${col},${row + 1}`)) { g.beginPath(); g.moveTo(bx, by); g.lineTo(lx, ly); g.strokePath(); }
      if (!rangeSet.has(`${col - 1},${row}`)) { g.beginPath(); g.moveTo(lx, ly); g.lineTo(tx, ty); g.strokePath(); }
    }
  }

  private drawRangeContour(g: Phaser.GameObjects.Graphics, rangeKeys: Iterable<string>, fillColor: number, fillAlpha: number, animated: boolean): void {
    const rangeSet = new Set<string>();
    const tiles: { col: number; row: number }[] = [];
    for (const key of rangeKeys) {
      rangeSet.add(key);
      tiles.push(parseCoordKey(key));
    }
    const a = animated ? fillAlpha * this.pulse : fillAlpha;

    // Fill all tiles — single batch with direct path commands (no Point allocations)
    g.fillStyle(fillColor, a);
    for (const { col, row } of tiles) {
      const { x, y } = this.gridToScreen(col, row);
      g.beginPath();
      g.moveTo(x, y - HH);
      g.lineTo(x + HW, y);
      g.lineTo(x, y + HH);
      g.lineTo(x - HW, y);
      g.closePath();
      g.fillPath();
    }

    // Draw only external edges — direct line commands (no Point allocations)
    const darkColor = TurnGameScene.getRangeDarkColor(fillColor);
    g.lineStyle(2, darkColor, Math.min(a + 0.25, 0.9));

    for (const { col, row } of tiles) {
      const { x, y } = this.gridToScreen(col, row);
      const tx = x, ty = y - HH;      // top
      const rx = x + HW, ry = y;       // right
      const bx = x, by = y + HH;       // bottom
      const lx = x - HW, ly = y;       // left
      // Top→Right edge shared with (col, row-1)
      if (!rangeSet.has(`${col},${row - 1}`)) { g.beginPath(); g.moveTo(tx, ty); g.lineTo(rx, ry); g.strokePath(); }
      // Right→Bottom edge shared with (col+1, row)
      if (!rangeSet.has(`${col + 1},${row}`)) { g.beginPath(); g.moveTo(rx, ry); g.lineTo(bx, by); g.strokePath(); }
      // Bottom→Left edge shared with (col, row+1)
      if (!rangeSet.has(`${col},${row + 1}`)) { g.beginPath(); g.moveTo(bx, by); g.lineTo(lx, ly); g.strokePath(); }
      // Left→Top edge shared with (col-1, row)
      if (!rangeSet.has(`${col - 1},${row}`)) { g.beginPath(); g.moveTo(lx, ly); g.lineTo(tx, ty); g.strokePath(); }
    }
  }

  // --- Entity rendering ---

  private syncEntities(): void {
    for (const [id, gfx] of this.buildingSprites) {
      if (!this.world.buildings.has(id)) {
        gfx.destroy();
        this.buildingSprites.delete(id);
        this.buildingIcons.get(id)?.destroy();
        this.buildingIcons.delete(id);
        this.buildingBars.get(id)?.destroy();
        this.buildingBars.delete(id);
      }
    }
    for (const [id, gfx] of this.unitSprites) {
      if (!this.world.units.has(id)) {
        gfx.destroy();
        this.unitSprites.delete(id);
        this.unitIconImages.get(id)?.destroy();
        this.unitIconImages.delete(id);
        this.unitShadowImages.get(id)?.destroy();
        this.unitShadowImages.delete(id);
        this.commanderTokens.get(id)?.destroy();
        this.commanderTokens.delete(id);
        this.unitFacing.delete(id);
        this.ammoAlertIcons.get(id)?.destroy();
        this.ammoAlertIcons.delete(id);
        this.fuelAlertIcons.get(id)?.destroy();
        this.fuelAlertIcons.delete(id);
        this.hpPercentTexts.get(id)?.destroy();
        this.hpPercentTexts.delete(id);
        this.hpBars.get(id)?.destroy();
        this.hpBars.delete(id);
        this.cargoBars.get(id)?.destroy();
        this.cargoBars.delete(id);
        this.xpStarTexts.get(id)?.destroy();
        this.xpStarTexts.delete(id);
      }
    }

    for (const [id, b] of this.world.buildings) {
      const bKey = `${b.col},${b.row}`;
      const isOwn = b.owner === this.localPlayer;
      const bVisible = this.playerVisible.has(bKey);

      // Buildings are part of the always-revealed static map: we draw every
      // building regardless of fog. What stays hidden until a unit actually
      // sees the tile is the *ownership* (handled below via `fogDim`).

      let gfx = this.buildingSprites.get(id);
      if (!gfx) {
        gfx = this.add.graphics();
        this.entityContainer.add(gfx);
        this.buildingSprites.set(id, gfx);
      }
      gfx.setVisible(true);
      this.drawBuilding(gfx, b);

      // Dim enemy buildings in explored-but-not-visible fog
      const fogDim = !isOwn && !bVisible;
      if (fogDim) gfx.setAlpha(0.5);
      else gfx.setAlpha(1);

      let icon = this.buildingIcons.get(id);
      const neutralKey = `bldg-${b.type}`;
      if (!icon && this.textures.exists(neutralKey)) {
        icon = this.add.image(0, 0, neutralKey);
        this.entityContainer.add(icon);
        this.buildingIcons.set(id, icon);
      }
      if (icon) {
        icon.setVisible(true);
        const pos = this.gridToScreen(b.col, b.row);
        // Buildings always render with the neutral art; ownership is shown by the
        // flag mast overlay (see syncBuildingMast). No baked owner-coloured variants.
        if (icon.texture.key !== neutralKey) icon.setTexture(neutralKey);
        // Size & centre the MAIN BODY (core) on the tile, ignoring small protrusions
        // (tank/tower/cannon) via per-building "fit" (see fit.json / compute_fit).
        // Core is scaled to BLDG_CORE_FILL × tile width and centred; its bottom sits
        // at the tile's front vertex. Protrusions overflow the tile (intended).
        const fit = TurnGameScene.BLDG_FIT[b.type];
        if (fit) {
          icon.setOrigin(fit[0], fit[1]);   // [originX, originY] — main-body anchor
          icon.displayWidth = TILE_W * fit[2]; // [displayWidthMul]
        } else {
          icon.setOrigin(0.5, 1);
          icon.displayWidth = TILE_W * (TurnGameScene.BLDG_ICON_SCALE[b.type] ?? 1);
        }
        icon.scaleY = icon.scaleX;
        icon.setPosition(
          pos.x,
          pos.y + HH - TurnGameScene.BLDG_RAISE + (TurnGameScene.BLDG_ICON_Y_OFFSET[b.type] ?? 0),
        );
        icon.setDepth((b.col + b.row) * 1000 + 200);

        // Ownership coloring: full owner tint (multiply blend) — matches the
        // unit-icon scheme. Bunker under construction stays semi-transparent;
        // fogged buildings get a dim grey overlay; neutral buildings render
        // with a subdued grey so they don't pretend to belong to anyone.
        if (b.buildProgress > 0) {
          icon.setTint(0x888888);
          icon.setAlpha(0.5);
        } else if (fogDim) {
          // Revealed but not currently in sight: show the building clearly in a
          // neutral grey (same as an unowned building) so the player knows it's
          // there and what it is, without leaking who controls it. Bunkers fall
          // here too — they must read as fogged like every other building.
          icon.setTint(0x999999);
          icon.setAlpha(1);
        } else if (b.type === 'bunker') {
          // In sight: bunkers keep their natural concrete art (no owner tint,
          // fully opaque — they are always neutral and can't be captured).
          icon.clearTint();
          icon.setAlpha(1);
        } else if (b.owner != null) {
          // Natural colours — ownership is shown by the bar above the building.
          // While the building is producing a unit it's dimmed to a slightly
          // greyed tint so busy buildings read at a glance; this reverts to full
          // colour automatically once training completes or is interrupted (the
          // redraw reads `b.training` live every frame).
          if (b.training) icon.setTint(0xb0b0b0);
          else icon.clearTint();
          icon.setAlpha(1);
        } else if (b.captureProgress > 0 && b.capturingPlayer != null) {
          // Neutral building being captured — interpolate from neutral grey
          // toward capturing player's colour as progress climbs from 0 to 1.
          const ratio = b.captureProgress / 2;
          const pColor = PALETTE.player[b.capturingPlayer % PALETTE.player.length];
          const pr = (pColor >> 16) & 0xff;
          const pg = (pColor >> 8) & 0xff;
          const pb = pColor & 0xff;
          const grey = 0x77;
          const r = Math.round(grey + (pr - grey) * ratio);
          const g = Math.round(grey + (pg - grey) * ratio);
          const bl = Math.round(grey + (pb - grey) * ratio);
          icon.setTint(Phaser.Display.Color.GetColor(r, g, bl));
          icon.setAlpha(1);
        } else {
          // Neutral, idle — slightly desaturated.
          icon.setTint(0x999999);
          icon.setAlpha(1);
        }

        // Ownership/capture is shown by the flag mast; the old bar is hidden.
        this.syncBuildingBar(id);
        // Flag mast overlay (for 'overlay' styles): empty pole = neutral,
        // raised coloured flag = owner, half-raised = capture in progress.
        this.syncBuildingMast(b, id, pos, fogDim);
      }
    }

    // Training ghost units — greyed-out unit silhouette on buildings with active training
    // Only show training ghosts for own buildings or visible enemy buildings
    const activeTrainingIds = new Set<number>();
    for (const [id, b] of this.world.buildings) {
      if (b.training && b.owner != null && (b.owner === this.localPlayer || this.playerVisible.has(`${b.col},${b.row}`))) {
        activeTrainingIds.add(id);
        let ghost = this.trainingGhosts.get(id);
        if (!ghost) {
          ghost = this.add.graphics();
          this.entityContainer.add(ghost);
          this.trainingGhosts.set(id, ghost);
        }
        // Create ghost icon image if texture exists. Match the live unit token:
        // directional units use their default-facing ('se') sprite, not the
        // static icon, so the production preview reads the same as the spawned unit.
        const ghostIconKey = this.iconKeyForTypeFacing(b.training.unitType, 'se');
        let ghostIcon = this.trainingGhostIcons.get(id);
        if (!ghostIcon && ghostIconKey && this.textures.exists(ghostIconKey)) {
          ghostIcon = this.add.image(0, 0, ghostIconKey);
          ghostIcon.setOrigin(0.5, 0.5);
          ghostIcon.setTint(0x888888);
          this.entityContainer.add(ghostIcon);
          this.trainingGhostIcons.set(id, ghostIcon);
        }
        const { x, y } = this.gridToScreen(b.col, b.row);
        const uDef = UNIT_DEFS[b.training.unitType];
        const progress = 1 - b.training.turnsLeft / uDef.trainingTime;
        const ghostDepth = (b.col + b.row) * 1000 + 250;
        this.drawGhostUnit(ghost, b.training.unitType, b.owner, x, y, ghostDepth, progress);
        // Position ghost icon — fit to match the live unit token.
        if (ghostIcon) {
          if (ghostIconKey && ghostIcon.texture.key !== ghostIconKey) ghostIcon.setTexture(ghostIconKey);
          ghostIcon.setPosition(x, y - 6);
          fitUnitIconFor(ghostIcon, b.training.unitType);
          ghostIcon.setDepth(ghostDepth + 1);
        }
      }
    }
    // Remove stale ghosts
    for (const [id, ghost] of this.trainingGhosts) {
      if (!activeTrainingIds.has(id)) {
        ghost.destroy();
        this.trainingGhosts.delete(id);
        const ghostIcon = this.trainingGhostIcons.get(id);
        if (ghostIcon) { ghostIcon.destroy(); this.trainingGhostIcons.delete(id); }
      }
    }

    for (const [id, u] of this.world.units) {
      // Fog of war: hide enemy units on non-visible tiles
      const isOwnUnit = u.owner === this.localPlayer;
      const unitVisible = isOwnUnit || this.playerVisible.has(`${u.col},${u.row}`);

      if (!unitVisible) {
        // Hide existing sprites
        const existing = this.unitSprites.get(id);
        if (existing) existing.setVisible(false);
        const existingIcon = this.unitIconImages.get(id);
        if (existingIcon) existingIcon.setVisible(false);
        const existingShadow = this.unitShadowImages.get(id);
        if (existingShadow) existingShadow.setVisible(false);
        const existingAmmo = this.ammoAlertIcons.get(id);
        if (existingAmmo) existingAmmo.setVisible(false);
        const existingFuel = this.fuelAlertIcons.get(id);
        if (existingFuel) existingFuel.setVisible(false);
        const existingHp = this.hpPercentTexts.get(id);
        if (existingHp) existingHp.setVisible(false);
        const existingHpBar = this.hpBars.get(id);
        if (existingHpBar) existingHpBar.setVisible(false);
        const existingCargoBar = this.cargoBars.get(id);
        if (existingCargoBar) existingCargoBar.setVisible(false);
        const existingStars = this.xpStarTexts.get(id);
        if (existingStars) existingStars.setVisible(false);
        this.commanderTokens.get(id)?.setVisible(false);
        continue;
      }

      let gfx = this.unitSprites.get(id);
      if (!gfx) {
        gfx = this.add.graphics();
        this.entityContainer.add(gfx);
        this.unitSprites.set(id, gfx);
      }
      gfx.setVisible(true);

      // Create icon image if texture exists for this unit type
      const iconKey = this.iconKeyForUnit(u);
      let iconImg = this.unitIconImages.get(id);
      if (!iconImg && iconKey && this.textures.exists(iconKey)) {
        iconImg = this.add.image(0, 0, iconKey);
        iconImg.setOrigin(0.5, 0.5);
        this.entityContainer.add(iconImg);
        this.unitIconImages.set(id, iconImg);
      }
      if (iconImg) iconImg.setVisible(true);

      // Pulsing supply alerts — independent low-ammo and low-fuel badges, each
      // using its bottom-bar icon so the player can tell them apart at a glance.
      const uDef = UNIT_DEFS[u.type];
      const lowFuel = uDef.maxFuel > 0 && u.fuel < uDef.maxFuel * 0.25;
      const lowAmmo = uDef.maxAmmo > 0 && u.ammo < uDef.maxAmmo * 0.25;
      const pos = this.gridToScreen(u.col, u.row);
      // Side-by-side fixed slots off to the RIGHT of the unit icon: ammo on the
      // right, fuel just left of it. Each keeps its slot whether or not the
      // other badge is showing.
      this.syncSupplyAlert(this.ammoAlertIcons, id, 'ammo-icon-src', lowAmmo, pos.x + 34, pos.y - 12);
      this.syncSupplyAlert(this.fuelAlertIcons, id, 'fuel-icon-src', lowFuel, pos.x + 22, pos.y - 12);

      this.drawUnit(gfx, u);
    }

    // Sort children by depth — Phaser's default sort is Array.sort (O(n log n)).
    // Skip if the list is already sorted (common case when no entities moved).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const children = this.entityContainer.list as any[];
    let needsSort = false;
    for (let i = 1; i < children.length; i++) {
      if (children[i].depth < children[i - 1].depth) {
        needsSort = true;
        break;
      }
    }
    if (needsSort) this.entityContainer.sort('depth');

    // After all visibilities were set above, apply the viewport filter.
    // syncEntities is the authoritative point where intended fog visibility
    // is computed; cull narrows that to the camera viewport.
    this.cullEntities();

    // Scenario building / group labels — colour reflects owner, visibility
    // follows fog of war. Cheap pass over at most a few dozen labels.
    this.updateBuildingLabels();
  }

  private drawBuilding(g: Phaser.GameObjects.Graphics, b: Building): void {
    g.clear();
    const { x, y } = this.gridToScreen(b.col, b.row);

    // Build progress — fill a grey-green diamond from bottom up
    if (b.buildProgress > 0 && b.buildingPlayerId != null) {
      const s = 0.85;
      const ratio = b.buildProgress / 2;
      g.fillStyle(0x9ece6a, 0.2 + ratio * 0.2);
      const topY = y + HH * s * (1 - ratio * 2);
      const hw = HW * s * Math.min(1, ratio * 2);
      g.beginPath();
      g.moveTo(x, topY);
      g.lineTo(x + hw, y);
      g.lineTo(x, y + HH * s);
      g.lineTo(x - hw, y);
      g.closePath();
      g.fillPath();
    }

    // Capture progress — fill a colored diamond from bottom up
    if (b.captureProgress > 0 && b.capturingPlayer != null) {
      const s = 0.85;
      const ratio = b.captureProgress / 2; // 0..1
      const capColor = PALETTE.player[b.capturingPlayer % PALETTE.player.length];
      g.fillStyle(capColor, 0.3 + ratio * 0.2);
      const topY = y + HH * s * (1 - ratio * 2);
      const hw = HW * s * Math.min(1, ratio * 2);
      g.beginPath();
      g.moveTo(x, topY);
      g.lineTo(x + hw, y);
      g.lineTo(x, y + HH * s);
      g.lineTo(x - hw, y);
      g.closePath();
      g.fillPath();
    }

    g.setDepth((b.col + b.row) * 1000 + 100);
  }

  /** Placeholder symbol for a commander unit, or null for ordinary units. */
  private static commanderSymbol(type: UnitType): string | null {
    if (type === 'officer') return '▲';
    if (type.startsWith('hero-')) return '★';
    return null;
  }

  /** Draw/position/recolour the placeholder token for a commander without a PNG
   *  icon. No-op (and cleans up) for ordinary units or ones that have an icon. */
  private syncCommanderToken(u: Unit, x: number, y: number, depth: number): void {
    const symbol = TurnGameScene.commanderSymbol(u.type);
    if (!symbol || this.unitIconImages.has(u.id)) {
      const old = this.commanderTokens.get(u.id);
      if (old) { old.destroy(); this.commanderTokens.delete(u.id); }
      return;
    }
    let tok = this.commanderTokens.get(u.id);
    if (!tok) {
      tok = this.add.text(0, 0, symbol, {
        fontFamily: '"Arial Black", Arial, sans-serif', fontSize: '24px',
        color: '#ffffff', stroke: '#0a0a0a', strokeThickness: 4,
      });
      tok.setOrigin(0.5, 0.5);
      this.entityContainer.add(tok);
      this.commanderTokens.set(u.id, tok);
    }
    tok.setText(symbol);
    const dim = u.acted && u.owner === currentPlayerId(this.world);
    const color = PALETTE.player[u.owner % PALETTE.player.length];
    tok.setColor(dim ? '#8a8a8a' : '#' + color.toString(16).padStart(6, '0'));
    tok.setPosition(x, y - 6);
    tok.setDepth(depth + 1);
    tok.setVisible(true);
  }

  /** Foot units drawn on the world map as a SQUAD: three down-scaled copies of
   *  the single-figure directional sprite, so the token reads as a small group
   *  rather than one over-sized soldier. (Battle window still uses the single
   *  figure.) Baked once into `squad:<type>:<facing>` by bakeSquadTextures(). */
  private static readonly SQUAD_UNITS: readonly string[] = ['infantry', 'infantry-bazooka', 'cavalry', 'polska-ulani'];

  private squadIconKey(type: string, facing: UnitFacing): string {
    return `squad:${type}:${facing}`;
  }

  /** Per-facing GRID forward vector (col,row deltas) for the squad wedge. The
   *  lead soldier sits ahead of the unit along this, the two others trail behind
   *  it — so the triangle points the way the unit faces. e/w are side-elevation
   *  frames whose on-screen "right/left" is the col−row diagonal in grid space. */
  private static readonly SQUAD_FWD: Record<UnitFacing, readonly [number, number]> = {
    se: [1, 0], nw: [-1, 0], sw: [0, 1], ne: [0, -1], e: [1, -1], w: [-1, 1],
  };

  /** Compose each single-soldier directional frame into a 3-figure squad texture:
   *  a triangle pointing in the unit's FACING direction. The three positions are
   *  laid out in grid space (lead ahead, two trailing ±sideways) and projected
   *  through the same iso transform as the map, so depth reads correctly — the
   *  lead soldier is farther along the facing, the trailing pair nearer the
   *  camera and drawn on top. Idempotent: textures persist across scene restarts,
   *  so re-bakes are skipped and we only re-read dims to patch `ar`. */
  private bakeSquadTextures(): void {
    const S = 0.6;     // per-soldier scale vs. the single-figure source frame
    const LEAD = 0.34; // lead soldier's distance ahead, in tiles
    const BACK = 0.22; // trailing pair's distance behind centre, in tiles
    const SIDE = 0.42; // trailing pair's half-spread sideways, in tiles
    for (const type of TurnGameScene.SQUAD_UNITS) {
      const spec = DIRECTIONAL_UNITS[type];
      if (!spec) continue;
      let arSum = 0, arN = 0;
      for (const facing of spec.dirs) {
        const srcKey = dirIconKey(type, facing);
        if (!this.textures.exists(srcKey)) continue;
        const outKey = this.squadIconKey(type, facing);
        if (this.textures.exists(outKey)) {
          const t = this.textures.get(outKey).getSourceImage() as HTMLImageElement;
          arSum += t.width / t.height; arN++;
          continue;
        }
        const src = this.textures.get(srcKey).getSourceImage() as HTMLImageElement;
        const W0 = src.width, H0 = src.height;
        const sw = S * W0, sh = S * H0;
        // Tile pitch in source px (iso 2:1) — sized so a tile of spread is
        // comparable to one soldier's width, keeping the wedge tight.
        const P = 0.9 * W0, HWp = P / 2, HHp = P / 4;
        const [fc, fr] = TurnGameScene.SQUAD_FWD[facing];
        const [pc, pr] = [-fr, fc]; // grid perpendicular
        // Three grid offsets (col,row) relative to tile centre: lead + two trail.
        let cells: Array<[number, number]> = [
          [fc * LEAD, fr * LEAD],
          [-fc * BACK + pc * SIDE, -fr * BACK + pr * SIDE],
          [-fc * BACK - pc * SIDE, -fr * BACK - pr * SIDE],
        ];
        // NE faces away (up-right): the generic perpendicular throws one trailing
        // soldier up to NW, looking detached. Keep the LEAD as the apex pointing
        // NE, but seat the trailing pair toward the two FRONT edges (SE & SW) and
        // BELOW the lead — so the wedge still points NE while the squad stands forward.
        if (facing === 'ne') {
          cells = [[fc * LEAD, fr * LEAD], [SIDE, BACK], [-BACK, SIDE]];
        }
        // Project to screen px (centre of each soldier).
        const pts = cells.map(([dc, dr]): [number, number] =>
          [(dc - dr) * HWp, (dc + dr) * HHp]);
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const [px, py] of pts) {
          minX = Math.min(minX, px - sw / 2); maxX = Math.max(maxX, px + sw / 2);
          minY = Math.min(minY, py - sh / 2); maxY = Math.max(maxY, py + sh / 2);
        }
        const cw = Math.ceil(maxX - minX), ch = Math.ceil(maxY - minY);
        const tex = this.textures.createCanvas(outKey, cw, ch);
        const ctx = tex?.getContext();
        if (!tex || !ctx) continue;
        // Back-to-front: smaller screen-y (farther) drawn first, nearer on top.
        for (const i of [0, 1, 2].sort((a, b) => pts[a][1] - pts[b][1])) {
          ctx.drawImage(src, pts[i][0] - sw / 2 - minX, pts[i][1] - sh / 2 - minY, sw, sh);
        }
        tex.refresh();
        arSum += cw / ch; arN++;
      }
      // Patch the footprint aspect to the baked group (averaged over facings) so
      // HP-bar / %-overlay / glow bbox placement (which read iconArFor) line up.
      if (arN) spec.ar = arSum / arN;
    }
  }

  /** Texture key for a unit type facing a given direction: the squad variant for
   *  foot squads, else the directional variant, else the static icon. Used for
   *  both live units and previews/ghosts (which default to 'se'). */
  private iconKeyForTypeFacing(type: string, facing: UnitFacing): string | undefined {
    const spec = DIRECTIONAL_UNITS[type];
    if (spec && spec.dirs.includes(facing)) {
      if (TurnGameScene.SQUAD_UNITS.includes(type)) {
        const sk = this.squadIconKey(type, facing);
        if (this.textures.exists(sk)) return sk;
      }
      const key = dirIconKey(type, facing);
      if (this.textures.exists(key)) return key;
    }
    return TurnGameScene.ICON_KEYS[type];
  }

  /** Texture key for a unit's on-map icon: the directional variant for its
   *  current facing when the type has a directional set, else the static icon. */
  private iconKeyForUnit(u: Unit): string | undefined {
    return this.iconKeyForTypeFacing(u.type, this.unitFacing.get(u.id) ?? 'se');
  }

  /** Record a unit's facing and, when its icon is directional, swap the texture now. */
  private setUnitFacing(unit: Unit, iconImg: Phaser.GameObjects.Image | undefined, facing: UnitFacing): void {
    this.unitFacing.set(unit.id, facing);
    if (iconImg && DIRECTIONAL_UNITS[unit.type]) {
      const key = this.iconKeyForUnit(unit);
      if (key && iconImg.texture.key !== key) { iconImg.setTexture(key); fitUnitIconFor(iconImg, unit.type); }
    }
    // Keep the ground shadow's silhouette in sync with the facing mid-move.
    const sh = this.unitShadowImages.get(unit.id);
    const spec = shadowBaseType(unit.type) ? SHADOW_FACINGS[facing] : undefined;
    if (sh && spec) this.applyShadowFrame(sh, unit, spec);
  }

  private drawUnit(g: Phaser.GameObjects.Graphics, u: Unit): void {
    // Reset position offset — may be stale from interrupted animation
    g.setPosition(0, 0);
    const { x, y } = this.gridToScreen(u.col, u.row);
    const depth = (u.col + u.row) * 1000 + 300;
    this.drawUnitAt(g, u, x, y, depth);

    // Position icon image if present. `iconY` carries an optional per-type
    // forward nudge so long-gunned hulls don't hug the tile's rear edge.
    const iconY = y - 6 + iconYNudge(u.type);
    // Aircraft float above their tile and drop a shadow at the ground anchor;
    // the lift raises the sprite while everything attached to it follows.
    const lift = shadowBaseType(u.type) ? HOVER_LIFT : 0;
    const planeY = iconY - lift;
    // Ground shadow sits at the un-lifted anchor (the unit's true footprint).
    this.syncUnitShadow(u, x, iconY, depth);
    const iconImg = this.unitIconImages.get(u.id);
    if (iconImg) {
      const desiredKey = this.iconKeyForUnit(u);
      if (desiredKey && iconImg.texture.key !== desiredKey) iconImg.setTexture(desiredKey);
      iconImg.setPosition(x, planeY);
      fitUnitIconFor(iconImg, u.type);
      // Spent units of the CURRENT player are desaturated so the player sees
      // which of their own units already acted. Enemy `acted` is stale until
      // their turn, so they always render at full strength.
      const dim = u.acted && u.owner === currentPlayerId(this.world);
      // Natural icon colours — own units are marked by the player-coloured HP
      // bar above them; spent units grey down.
      iconImg.setTint(dim ? 0x8a8a8a : 0xffffff);
      iconImg.setAlpha(1);
      iconImg.setDepth(depth + 1);
    }

    // Commander placeholder token (★ hero / ▲ officer) when there's no PNG icon.
    this.syncCommanderToken(u, x, y, depth);

    // HP % overlay — shown ONLY when the unit is wounded. Same place on
    // every icon (bottom-right corner of the standard 46×28 footprint) so
    // the eye learns where to look. Anchored to the (possibly lifted) sprite.
    this.syncHpBar(u, x, planeY, depth + 2);
    this.syncCargoBar(u, x, planeY, depth + 2);
    this.syncHpPercent(u, x, planeY, depth + 2);
    this.syncXpStars(u, x, planeY, depth + 2);
  }

  /** Place/update/remove the ground-shadow silhouette for a hovering aircraft.
   *  `groundY` is the unit's true tile anchor (the sprite itself is lifted). */
  private syncUnitShadow(u: Unit, x: number, groundY: number, depth: number): void {
    const base = shadowBaseType(u.type);
    const spec = base ? SHADOW_FACINGS[this.unitFacing.get(u.id) ?? 'se'] : undefined;
    const key = base && spec ? shadowKey(base, spec.facing) : undefined;
    if (!key || !this.textures.exists(key)) {
      const old = this.unitShadowImages.get(u.id);
      if (old) { old.destroy(); this.unitShadowImages.delete(u.id); }
      return;
    }
    let sh = this.unitShadowImages.get(u.id);
    if (!sh) {
      sh = this.add.image(0, 0, key);
      sh.setOrigin(0.5, 0.5);
      this.entityContainer.add(sh);
      this.unitShadowImages.set(u.id, sh);
    }
    this.applyShadowFrame(sh, u, spec!);
    sh.setPosition(x, groundY);
    sh.setDepth(depth + 0.5); // above the unit graphics, below the lifted sprite
    sh.setVisible(true);
  }

  /** Point a shadow Image at the right silhouette for a facing, mirror it for
   *  se/ne, and size it to roughly the plane's wingspan. Uniform scale keeps
   *  the silhouette's own (foreshortened) aspect so it reads as a shadow. */
  private applyShadowFrame(
    sh: Phaser.GameObjects.Image,
    u: Unit,
    spec: { facing: 'sw' | 'nw'; flip: boolean },
  ): void {
    // Shadow art is keyed by base type so faction planes share it generically.
    const key = shadowKey(shadowBaseType(u.type) ?? u.type, spec.facing);
    if (sh.texture.key !== key) sh.setTexture(key);
    sh.setFlipX(spec.flip);
    sh.displayWidth = iconHeightFor(u.type) * iconArFor(u.type) * SHADOW_SCALE;
    sh.scaleY = sh.scaleX;
  }

  /** Position a unit's lifted sprite + ground shadow at the tile anchor (ix,iy).
   *  Used by the movement tweens so aircraft keep hovering while they slide. */
  private positionUnitVisual(unit: Unit, ix: number, iy: number, depth: number): void {
    const groundY = iy - 6 + iconYNudge(unit.type);
    const lift = shadowBaseType(unit.type) ? HOVER_LIFT : 0;
    const iconImg = this.unitIconImages.get(unit.id);
    if (iconImg) { iconImg.setPosition(ix, groundY - lift); iconImg.setDepth(depth + 1); }
    const sh = this.unitShadowImages.get(unit.id);
    if (sh) { sh.setPosition(ix, groundY); sh.setDepth(depth + 0.5); }
  }

  /** Paint a small horizontal bar into `g` relative to its own origin: a dark
   *  track + a coloured fill of width `ratio`. The caller positions the graphics. */
  private paintBar(g: Phaser.GameObjects.Graphics, ratio: number, color: number, w = 26, h = 4): void {
    const r = Math.max(0, Math.min(1, ratio));
    g.clear();
    g.fillStyle(0x000000, 0.6); g.fillRect(-w / 2 - 1, -1, w + 2, h + 2);
    g.fillStyle(0x0c0f15, 1); g.fillRect(-w / 2, 0, w, h);
    if (r > 0) { g.fillStyle(color, 1); g.fillRect(-w / 2, 0, Math.max(2, w * r), h); }
  }

  /** Player-coloured HP bar above the player's OWN units only — its presence (and
   *  colour) marks a unit as yours; enemies get no bar. Colour tracks the owner,
   *  never HP. */
  private syncHpBar(u: Unit, cx: number, cyIcon: number, depth: number): void {
    if (u.owner !== this.localPlayer) {
      const existing = this.hpBars.get(u.id);
      if (existing) existing.setVisible(false);
      return;
    }
    const def = UNIT_DEFS[u.type];
    let bar = this.hpBars.get(u.id);
    if (!bar) { bar = this.add.graphics(); this.entityContainer.add(bar); this.hpBars.set(u.id, bar); }
    this.paintBar(bar, u.hp / def.maxHp, PALETTE.player[u.owner % PALETTE.player.length], 22, 2);
    bar.setPosition(cx, cyIcon - iconHeightFor(u.type) / 2 - 6);
    bar.setDepth(depth);
    bar.setVisible(true);
  }

  /** Cargo-load bar for carriers — a second bar directly under the HP bar,
   *  split into one segment per cargo slot: filled (loaded) green, empty grey. */
  private syncCargoBar(u: Unit, cx: number, cyIcon: number, depth: number): void {
    const def = UNIT_DEFS[u.type];
    const cap = def.cargoCapacity ?? 0;
    if (cap <= 0) {
      const existing = this.cargoBars.get(u.id);
      if (existing) existing.setVisible(false);
      return;
    }
    let bar = this.cargoBars.get(u.id);
    if (!bar) { bar = this.add.graphics(); this.entityContainer.add(bar); this.cargoBars.set(u.id, bar); }
    // Same width AND thickness (2 px) as the HP bar; sits one row below it
    // (HP bar at -6, height 2 → +1 px gap).
    this.paintSegmentedBar(bar, u.cargo?.length ?? 0, cap, 0x6cc24a, 0x4b515c, 22, 2);
    bar.setPosition(cx, cyIcon - iconHeightFor(u.type) / 2 - 3);
    bar.setDepth(depth);
    bar.setVisible(true);
  }

  /** Paint a segmented bar centred on the graphics origin: `total` equal cells
   *  (1 px gaps), the first `filled` in `filledColor`, the rest in `emptyColor`,
   *  over a dark framed track to match paintBar. */
  private paintSegmentedBar(
    g: Phaser.GameObjects.Graphics, filled: number, total: number,
    filledColor: number, emptyColor: number, w = 22, h = 3,
  ): void {
    g.clear();
    g.fillStyle(0x000000, 0.6); g.fillRect(-w / 2 - 1, -1, w + 2, h + 2);
    const gap = total > 1 ? 1 : 0;
    const segW = (w - gap * (total - 1)) / total;
    for (let i = 0; i < total; i++) {
      g.fillStyle(i < filled ? filledColor : emptyColor, 1);
      g.fillRect(-w / 2 + i * (segW + gap), 0, segW, h);
    }
  }

  /** Possession bar above a building: capture/build progress while in flux, a
   *  full owner-coloured bar when held, an empty grey track when neutral. Under
   *  fog the owner stays hidden (neutral grey). Replaces the old "1/2" text. */
  /** Pole-base anchor within the mast frame (from prep_mast.py) and on-screen height. */
  // Słup wyrównany w klatkach na 0.1585; origin dobrany tak, by FLAGOWE maszty
  // wróciły do swojej (poprawnej) pozycji sprzed wyrównania, a pusty do nich dołączył.
  private static readonly MAST_ORIGIN_X = 0.575;
  private static readonly MAST_DISPLAY_H = TILE_H * 0.62;   // ~4× mniejszy niż wcześniej
  private static readonly MAST_OFF_X = HW * 0.8;           // lekko w prawo od lewego wierzchołka
  private static readonly MAST_OFF_Y = HH * 0.9;           // przy lewym wierzchołku, ciut z przodu

  /**
   * Flag mast overlay for 'overlay' styles (clean buildings + game-drawn flag).
   * Empty wooden pole = neutral, raised flag in owner colour = owned, half-raised
   * in capturer colour = capture in progress. Hidden for other styles and bunkers.
   */
  private syncBuildingMast(b: Building, id: number, pos: { x: number; y: number }, fogDim: boolean): void {
    const wantMast = b.type !== 'bunker';
    let mast = this.buildingMasts.get(id);
    if (!wantMast) { mast?.setVisible(false); return; }

    let key = 'mast-empty';
    if (!fogDim && b.owner != null) key = `mast-full-p${b.owner % 6}`;
    else if (b.owner == null && b.captureProgress > 0 && b.capturingPlayer != null) {
      key = `mast-half-p${b.capturingPlayer % 6}`;
    }
    if (!this.textures.exists(key)) key = 'mast-empty';

    if (!mast) {
      mast = this.add.image(0, 0, key);
      mast.setOrigin(TurnGameScene.MAST_ORIGIN_X, 1);
      this.entityContainer.add(mast);
      this.buildingMasts.set(id, mast);
    }
    mast.setVisible(true);
    if (mast.texture.key !== key) mast.setTexture(key);
    mast.displayHeight = TurnGameScene.MAST_DISPLAY_H;
    mast.scaleX = mast.scaleY;
    // Pole base near the front-left of the tile so the flag stands clear of the building.
    mast.setPosition(pos.x - TurnGameScene.MAST_OFF_X, pos.y + HH - TurnGameScene.MAST_OFF_Y);
    mast.setDepth((b.col + b.row) * 1000 + 250);
    mast.setAlpha(fogDim ? 0.6 : 1);
  }

  private syncBuildingBar(id: number): void {
    // Ownership/capture is shown by the flag mast overlay, so the old bar above
    // the building is always hidden.
    this.buildingBars.get(id)?.setVisible(false);
  }

  /**
   * Create or update the white "%" overlay for a unit. Skipped (and any
   * existing text destroyed) when HP is full — full units stay clean.
   */
  private syncHpPercent(u: Unit, ax: number, ay: number, depth: number): void {
    const def = UNIT_DEFS[u.type];
    const hpRatio = u.hp / def.maxHp;
    let label = this.hpPercentTexts.get(u.id);

    if (hpRatio >= 1) {
      if (label) { label.destroy(); this.hpPercentTexts.delete(u.id); }
      return;
    }

    if (!label) {
      label = this.add.text(0, 0, '', {
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
        fontSize: '8px',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 1.5,
        // Render the glyph texture dense enough to stay crisp at high camera
        // zoom / retina, instead of being upscaled (blurry).
        resolution: Math.min(6, Math.ceil((window.devicePixelRatio || 1) * this.maxZoom)),
      });
      label.setOrigin(1, 1); // anchor bottom-right (matches the old ammo icon spot)
      this.entityContainer.add(label);
      this.hpPercentTexts.set(u.id, label);
    }

    const pct = Math.max(1, Math.round(hpRatio * 100));
    if (label.text !== `${pct}%`) label.setText(`${pct}%`);
    const iconHalfW = (iconHeightFor(u.type) * iconArFor(u.type)) / 2;
    const iconHalfH = iconHeightFor(u.type) / 2;
    label.setPosition(ax + iconHalfW, ay + iconHalfH);
    label.setDepth(depth);
    label.setAlpha(1);
    label.setVisible(true);
  }

  /** Render bronze/silver/gold ★ stars above the icon based on xp level.
   *  Level 0 → no stars (and any existing text is destroyed). */
  private syncXpStars(u: Unit, ax: number, ay: number, depth: number): void {
    const level = levelForXp(u.xp);
    const count = starsForLevel(level);
    let label = this.xpStarTexts.get(u.id);

    if (count <= 0) {
      if (label) { label.destroy(); this.xpStarTexts.delete(u.id); }
      return;
    }

    const colour =
      tierForLevel(level) === 'bronze' ? '#cd7f32' :
      tierForLevel(level) === 'silver' ? '#d8d8d8' :
      '#ffd700';

    if (!label) {
      label = this.add.text(0, 0, '', {
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
        fontSize: '10px',
        color: colour,
        stroke: '#000000',
        strokeThickness: 2,
        resolution: Math.min(6, Math.ceil((window.devicePixelRatio || 1) * this.maxZoom)),
        lineSpacing: -2,
      });
      // Anchor at bottom-left so the FIRST star sits at the icon's bottom-left
      // corner and additional stars stack UPWARD inside the same Text block.
      label.setOrigin(0, 1);
      this.entityContainer.add(label);
      this.xpStarTexts.set(u.id, label);
    } else if (label.style.color !== colour) {
      label.setColor(colour);
    }

    // Multi-line "★\n★\n★" — Phaser Text renders top-to-bottom, but with
    // origin (0, 1) the BOTTOM of the block sits at the anchor, so the
    // first character is on top of the stack. We need the count to grow
    // upward from the anchor, so the count appears as N rows.
    const text = Array(count).fill('★').join('\n');
    if (label.text !== text) label.setText(text);
    const iconHalfW = (iconHeightFor(u.type) * iconArFor(u.type)) / 2;
    const iconHalfH = iconHeightFor(u.type) / 2;
    label.setPosition(ax - iconHalfW, ay + iconHalfH);
    label.setDepth(depth);
    label.setVisible(true);
  }

  private drawUnitAt(g: Phaser.GameObjects.Graphics, _u: Unit, _x: number, _y: number, depth: number): void {
    // Nothing is painted into `g` anymore — owner identity is the tinted icon,
    // HP is the "%" label + bar, and cargo load is the segmented bar under the
    // HP bar (see syncCargoBar). The graphics object only hosts the postFX glow
    // target, so just keep it cleared at the right depth.
    g.clear();
    g.setDepth(depth);
  }

  /** Draw a greyed-out ghost unit on a building (training in progress) with radial clock. */
  private drawGhostUnit(g: Phaser.GameObjects.Graphics, _unitType: string, _owner: number, x: number, y: number, depth: number, progress: number): void {
    g.clear();
    const R = UNIT_TOKEN_R;
    // Token shape removed — the grey-tinted ghost icon (set up by the
    // caller via setTint(0x888888)) carries the visual identity now. The
    // graphics object still hosts the radial clock above the icon.

    // Radial clock icon above the ghost unit
    const clockR = 5;
    const clockX = x;
    const clockY = y - 6 - R - clockR - 2;
    const startAngle = -Math.PI / 2; // 12 o'clock
    const endAngle = startAngle + progress * Math.PI * 2;

    // Grey background circle
    g.fillStyle(0x333333, 1);
    g.fillCircle(clockX, clockY, clockR);
    // Filled arc showing progress
    if (progress > 0) {
      g.fillStyle(0x9ece6a, 1);
      g.beginPath();
      g.arc(clockX, clockY, clockR, startAngle, endAngle, false);
      g.lineTo(clockX, clockY);
      g.closePath();
      g.fillPath();
    }
    // Clock border
    g.lineStyle(1, 0x555555, 1);
    g.strokeCircle(clockX, clockY, clockR);
    // Clock hands (12 o'clock tick)
    g.lineStyle(1, 0x888888, 1);
    g.lineBetween(clockX, clockY - clockR + 1, clockX, clockY - 1);

    g.setDepth(depth);
  }

  /** Play a random explosion animation at screen position. Returns a promise that resolves when done. */
  private playExplosion(x: number, y: number): Promise<void> {
    return new Promise((resolve) => {
      const variant = Phaser.Math.Between(1, 10);
      const key = `explosion-${variant}`;
      // Larger spritesheets (64x64 frames) need less scaling
      const baseScale = (variant === 7 || variant === 10) ? 1.5 : 3;
      const sprite = this.add.sprite(x, y - 8, key);
      sprite.setScale(baseScale);
      sprite.setDepth(99998);
      sprite.play(key);
      sprite.once('animationcomplete', () => {
        sprite.destroy();
        resolve();
      });
    });
  }

  /** Resolve the faction id owning a player, for nation-specific voice packs. */
  private factionOfOwner(owner: PlayerId): string | undefined {
    return this.world.factions?.get(owner);
  }

  /** Resolve the faction id owning a unit, for nation-specific voice packs. */
  private factionOf(unit: Unit): string | undefined {
    return this.factionOfOwner(unit.owner);
  }

  /** Animate combat via the full-screen Advance-Wars-style battle scene:
   *  resolve into a deterministic script → march-in → attack → counter → flash. */
  private animateCombat(
    unit: Unit, enemy: Unit, onComplete: () => void,
  ): void {
    this.animating = true;
    // No attack voice shout here: the battle window plays its own weapon SFX,
    // and the two overlapping read as noise (see battleFireClip).

    const enemyId = enemy.id;
    const attackerId = unit.id;

    // Resolve combat into a script (mutates HP / removes killed units), then
    // do the usual post-combat bookkeeping before the visuals play.
    const script = buildBattleScript(this.world, unit, enemy);
    unit.movePoints = 0;
    validateCaptures(this.world);
    validateBuilds(this.world);
    cancelDisruptedTraining(this.world);

    // Notify scenario runner of kills
    if (script.defender.killed && this.runner) this.runner.onUnitKilled(enemyId);
    if (script.attacker.killed && this.runner) this.runner.onUnitKilled(attackerId);

    void this.playBattle(script).then(() => {
      this.animating = false;
      if (this.processRunnerActions()) return;
      onComplete();
    });
  }

  // ── Advance-Wars-style full-screen battle scene ──────────────────────────
  // The attacker is always on the LEFT, the defender on the RIGHT (mirrored).
  // Squads are up to 5 tokens, each = 20% HP; a force bar floats over the
  // background; the window closes with a white flash. Presentation only — the
  // outcome is fixed by `buildBattleScript` before any of this runs.

  /** Texture key for a side's background, from its domain, structure and terrain. */
  private battleBackgroundKeyFor(side: BattleSide): string {
    if (side.domain === 'air') return 'battle-bg-air';
    if (side.domain === 'water') return 'battle-bg-water';
    // Urban combat when standing on a structure.
    if (side.structure === 'city') return 'battle-bg-city';
    if (side.structure === 'building') return 'battle-bg-building';
    switch (side.terrain) {
      case 'forest': return 'battle-bg-forest';
      case 'mountain': return 'battle-bg-mountain';
      case 'road': return 'battle-bg-road';
      case 'bridge': return 'battle-bg-bridge';
      case 'coast': return 'battle-bg-coast';
      case 'water': return 'battle-bg-water';
      default: return 'battle-bg-plains';
    }
  }

  /** Neutral fallback fill when the matching background texture isn't loaded. */
  private battleFallbackColor(domain: Domain, terrain: TerrainType): number {
    if (domain === 'air') return 0x5b86b5;
    if (domain === 'water') return PALETTE.water;
    switch (terrain) {
      case 'forest': return PALETTE.forest;
      case 'mountain': return PALETTE.mountain;
      case 'water':
      case 'coast': return PALETTE.water;
      default: return PALETTE.grass;
    }
  }

  /** One side's full-height background patch (image if available, else fill).
   *  The window is split down the middle, so each half shows the matching HALF
   *  of its background image (left panel → image's left half, right panel →
   *  right half), scaled to COVER the panel and CROPPED — never squished to
   *  fit the half-width slot. */
  private createBattleBackground(
    side: BattleSide, x: number, y: number, w: number, h: number, half: 'left' | 'right',
  ): Phaser.GameObjects.GameObject {
    const key = this.battleBackgroundKeyFor(side);
    if (this.textures.exists(key)) {
      const src = this.textures.get(key).getSourceImage() as { width: number; height: number };
      const sw = src.width, sh = src.height;
      const halfW = sw / 2;
      // Cover the panel with the chosen half of the image (uniform scale → no
      // distortion); the overflow is cropped away.
      const s = Math.max(w / halfW, h / sh);
      const visW = w / s; // ≤ halfW, guaranteed by the cover scale
      const visH = h / s; // ≤ sh
      const cropX = half === 'left' ? 0 : sw - visW;
      const cropY = (sh - visH) / 2;
      const img = this.add.image(0, 0, key).setOrigin(0.5, 0.5);
      img.setCrop(cropX, cropY, visW, visH);
      img.setScale(s);
      // Place so the cropped region lands exactly over the panel. setCrop keeps
      // the piece anchored within the full frame, so offset by its distance
      // from the frame centre.
      img.x = (x + w / 2) - (cropX + visW / 2 - sw / 2) * s;
      img.y = (y + h / 2) - (cropY + visH / 2 - sh / 2) * s;
      return img;
    }
    return this.add.rectangle(
      x + w / 2, y + h / 2, w, h, this.battleFallbackColor(side.domain, side.terrain),
    );
  }

  /** Commander badge for one side of the battle window: the player's chosen
   *  scientist portrait + their nation flag, pinned to a top corner. `cornerX`
   *  is the outer edge (left edge for 'left', right edge for 'right'); the badge
   *  grows inward and downward from (cornerX, topY). No-op if the player has no
   *  faction pick (e.g. neutral/structure battles). */
  private addBattleCommander(
    layer: Phaser.GameObjects.Container,
    owner: PlayerId,
    cornerX: number,
    topY: number,
    anchor: 'left' | 'right',
    portraitH: number,
  ): void {
    const pick = this.gameConfig?.factions.get(owner);
    if (!pick) return;
    const faction = getFaction(pick.factionId);
    const char = faction?.characters.find((c) => c.id === pick.characterId);

    const portraitW = portraitH * 0.8; // bust portraits are 512×640 (≈0.8 AR)
    const left = anchor === 'left' ? cornerX : cornerX - portraitW;
    const cx = left + portraitW / 2;

    // Portrait — mirrored so the two commanders face EACH OTHER across the
    // battle (toward the centre). Source portraits all face left, so the
    // left-corner (attacker) one is flipped to face right; the right-corner
    // (defender) one stays as-is, facing left.
    const pKey = portraitKey(pick.factionId, pick.characterId);
    if (char?.portrait && this.textures.exists(pKey)) {
      const src = this.textures.get(pKey).getSourceImage() as { width: number; height: number };
      const img = this.add.image(cx, topY + portraitH / 2, pKey).setOrigin(0.5, 0.5);
      img.setScale(portraitH / src.height);
      img.setFlipX(anchor === 'left');
      layer.add(img);
    }

    // Nation flag pinned ON the portrait, in its bottom-right corner.
    const rect = FLAG_RECTS[pick.factionId];
    if (rect && this.textures.exists('flags-sheet')) {
      const src = this.textures.get('flags-sheet').getSourceImage() as { width: number; height: number };
      const flagH = portraitH * 0.3;
      const s = flagH / rect.h;
      const dispW = rect.w * s;
      const inset = portraitH * 0.04;
      const fCenterX = left + portraitW - inset - dispW / 2;
      const fCenterY = topY + portraitH - inset - flagH / 2;
      // Thin dark backing so the flag reads on top of the portrait.
      layer.add(this.add.rectangle(fCenterX, fCenterY, dispW + 2, flagH + 2, 0x000000, 0.6));
      const flag = this.add.image(0, 0, 'flags-sheet').setOrigin(0.5, 0.5);
      flag.setCrop(rect.x, rect.y, rect.w, rect.h);
      flag.setScale(s);
      // setCrop anchors the piece within the FULL frame, so offset by the crop
      // centre's distance from the image centre to land it where we want.
      flag.x = fCenterX - (rect.x + rect.w / 2 - src.width / 2) * s;
      flag.y = fCenterY - (rect.y + rect.h / 2 - src.height / 2) * s;
      layer.add(flag);
    }

    // Commander name beside the portrait, on the inner (centre-facing) edge —
    // left commander's name to the right of the bust, right commander's to the
    // left. Rendered at camera resolution so it stays sharp despite the
    // world-space (zoomed) battle layer.
    const name = t(`faction.${pick.factionId}.${pick.characterId}.name`);
    const gap = portraitH * 0.06;
    const labelX = anchor === 'left' ? left + portraitW + gap : left - gap;
    const label = this.add.text(labelX, topY + portraitH / 2, name, {
      fontFamily: 'sans-serif',
      fontSize: `${Math.max(8, Math.round(portraitH * 0.09))}px`,
      color: '#ffffff',
      align: anchor === 'left' ? 'left' : 'right',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(anchor === 'left' ? 0 : 1, 0.5);
    label.setResolution((window.devicePixelRatio || 1) * Math.max(1, this.cameras.main.zoom));
    label.setWordWrapWidth(portraitW * 1.4);
    layer.add(label);
  }

  /** A single squad token — just the unit's map icon (mirrored for the right
   *  side), sized to `size` px, or a player-colored glyph for icon-less types. */
  /** On-screen content footprint (√(w·h), px) the map-icon path would render a
   *  type at — so directional side-profile tokens can be sized to match and the
   *  whole battle reads at a consistent scale regardless of art source. */
  private battleTokenFootprint(type: string, size: number): number {
    const iconKey = TurnGameScene.ICON_KEYS[type];
    if (iconKey && this.textures.exists(iconKey)) {
      const s = this.textures.get(iconKey).getSourceImage() as { width: number; height: number };
      const m = UNIT_ICON_METRICS[type];
      const base = size / Math.max(s.width, s.height);
      const f = unitIconScale(type);
      const fw = m?.fillW ?? 1, fh = m?.fillH ?? 1;
      return base * f * Math.sqrt((fw * s.width) * (fh * s.height));
    }
    return size;
  }

  private createBattleToken(side: BattleSide, x: number, y: number, mirror: boolean, size: number): Phaser.GameObjects.Container {
    const c = this.add.container(x, y);
    // Fighters use an ANIMATED propeller sprite (the prop keeps spinning through
    // fly-in, idle and dive). The sheet is a side view facing LEFT, so the
    // attacker (left column, faces RIGHT) is flipped; the defender stays as-is.
    if (resolveBaseType(side.type) === 'plane-fighter'
        && this.textures.exists('plane-fighter-prop') && this.anims.exists('fighter-prop')) {
      const spr = this.add.sprite(0, 0, 'plane-fighter-prop');
      // Drawn a touch smaller than the footprint: the Gemini source is low-res,
      // so shrinking it hides the upscaling pixelation.
      const target = this.battleTokenFootprint(side.type, size) * 0.82;
      spr.setScale(target / Math.sqrt(spr.width * spr.height));
      spr.setFlipX(!mirror);
      spr.setOrigin(0.5, 1);
      spr.setY(size * (0.22 + (BATTLE_TOKEN_DROP[resolveBaseType(side.type)] ?? 0)));
      spr.play('fighter-prop');
      c.add(spr);
      return c;
    }
    // Prefer our directional side-profile sprite when the type has one: the
    // attacker (left, mirror=false) faces RIGHT → 'e', the defender (right,
    // mirror=true) faces LEFT → 'w'. The sprites are already oriented, so no
    // flip is applied; they're scaled to match the map-icon footprint.
    const dirSpec = DIRECTIONAL_UNITS[side.type];
    const facing: UnitFacing = mirror ? 'w' : 'e';
    const dirKey = dirSpec && dirSpec.dirs.includes(facing) ? dirIconKey(side.type, facing) : undefined;
    if (dirKey && this.textures.exists(dirKey)) {
      const img = this.add.image(0, 0, dirKey);
      const d = this.textures.get(dirKey).getSourceImage() as { width: number; height: number };
      const target = this.battleTokenFootprint(side.type, size);
      img.setScale(target / Math.sqrt(d.width * d.height));
      // Bottom-grounded with a small drop so feet sit just below the placement
      // row (in the ground band) without the lowest rank spilling past the
      // window's lower edge.
      img.setOrigin(0.5, 1);
      // Base drop (0.22) seats feet in the ground band; some types whose art
      // sits high get an extra push down (still clears the bottom edge).
      img.setY(size * (0.22 + (BATTLE_TOKEN_DROP[resolveBaseType(side.type)] ?? 0)));
      c.add(img);
      return c;
    }
    const iconKey = TurnGameScene.ICON_KEYS[side.type];
    if (iconKey && this.textures.exists(iconKey)) {
      const icon = this.add.image(0, 0, iconKey);
      const src = this.textures.get(iconKey).getSourceImage() as { width: number; height: number };
      const base = size / Math.max(src.width, src.height);
      const f = unitIconScale(side.type); // enlarge under-filled tank/vehicle icons
      icon.setScale(base * f);
      icon.setFlipX(mirror);
      // Ground the (bottom-aligned) art so scaled icons grow upward, not sink.
      icon.setY(-(src.height * base) * (f - 1) / 2);
      c.add(icon);
    } else {
      const color = PALETTE.player[side.owner % PALETTE.player.length];
      const glyph = side.type.startsWith('hero-') ? '★' : side.type === 'officer' ? '▲' : '◆';
      const txt = this.add.text(0, 0, glyph, {
        fontFamily: 'sans-serif', fontSize: `${Math.round(size * 0.85)}px`,
        color: `#${color.toString(16).padStart(6, '0')}`,
      }).setOrigin(0.5);
      c.add(txt);
    }
    return c;
  }

  /** Promise wrapper around a one-shot tween. */
  private battleTween(config: Phaser.Types.Tweens.TweenBuilderConfig): Promise<void> {
    return new Promise((resolve) => {
      this.tweens.add({ ...config, onComplete: () => resolve() });
    });
  }

  /** Promise-based delay used to pace the choreography. */
  private battleWait(ms: number): Promise<void> {
    return new Promise((resolve) => { this.time.delayedCall(ms, () => resolve()); });
  }

  /** Tween the force bar from one HP split to another, redrawing each frame. */
  private animateBattleBar(
    draw: (atkHp: number, defHp: number) => void,
    a0: number, d0: number, a1: number, d1: number,
  ): Promise<void> {
    const obj = { a: a0, d: d0 };
    return new Promise((resolve) => {
      this.tweens.add({
        targets: obj, a: a1, d: d1, duration: 280, ease: 'Quad.easeOut',
        onUpdate: () => draw(obj.a, obj.d),
        onComplete: () => { draw(a1, d1); resolve(); },
      });
    });
  }

  /** Explosion sprite parented into the battle layer (screen-fixed, on top). */
  private battleExplosion(layer: Phaser.GameObjects.Container, x: number, y: number): Promise<void> {
    return new Promise((resolve) => {
      const variant = Phaser.Math.Between(1, 10);
      const key = `explosion-${variant}`;
      const baseScale = (variant === 7 || variant === 10) ? 1.2 : 2.2;
      const sprite = this.add.sprite(x, y - 6, key).setScale(baseScale);
      layer.add(sprite);
      sprite.play(key);
      sprite.once('animationcomplete', () => { sprite.destroy(); resolve(); });
    });
  }

  /** The struck units blink white (gentle, on the icon itself) and shake a touch. */
  private async battleHit(_layer: Phaser.GameObjects.Container, tokens: Phaser.GameObjects.Container[]): Promise<void> {
    for (const tk of tokens) {
      const img = tk.list.find((o): o is Phaser.GameObjects.Image => o instanceof Phaser.GameObjects.Image);
      if (img) {
        // Two quick, gentle white flashes via fill-tint on the icon.
        img.setTintFill(0xffffff);
        this.time.delayedCall(80, () => { if (img.active) img.clearTint(); });
        this.time.delayedCall(170, () => { if (img.active) img.setTintFill(0xffffff); });
        this.time.delayedCall(250, () => { if (img.active) img.clearTint(); });
      }
      const baseX = tk.x;
      this.tweens.add({
        targets: tk, x: baseX + 4, duration: 42, yoyo: true, repeat: 3,
        onComplete: () => { tk.x = baseX; },
      });
    }
    await this.battleWait(340);
  }

  /** Destroy the outermost `n` tokens of a squad. Types with a dedicated death
   *  sheet (vehicles) play their side-view burn-to-wreck animation in place;
   *  everything else gets the generic puff + shrink. `mirror` is the dying side
   *  (true = defender, faces 'w'; false = attacker, faces 'e') so the death art
   *  is flipped to match the token it replaces; `size` is the token size. */
  private async killBattleTokens(
    layer: Phaser.GameObjects.Container, tokens: Phaser.GameObjects.Container[], n: number,
    type: UnitType, mirror: boolean, size: number,
  ): Promise<void> {
    const deaths: Promise<void>[] = [];
    // One hit/death sound per killing volley (per-class; see battleHitClip).
    if (n > 0 && tokens.length > 0) {
      const hit = battleHitClip(type);
      if (hit) audio.fileSfx.play(hit);
    }
    // The dedicated death sheet plays for EVERY dying token (per-sprite), not
    // only when the whole unit is wiped — each lost sprite burns down to a wreck
    // in place, staggered slightly so a multi-token loss reads as a cascade of
    // explosions rather than one. Types without a sheet (infantry, planes, ships)
    // fall back to the generic puff for all their tokens.
    const sheet = deathSheetFor(type);
    const hasSheet = !!sheet && this.anims.exists(`death-anim-${sheet.key}`);
    for (let k = 0; k < n && tokens.length > 0; k++) {
      const tk = tokens.pop()!; // outermost token dies first
      if (hasSheet) {
        deaths.push(this.battleDeath(layer, tk, sheet, type, mirror, size, k * 110));
      } else {
        deaths.push(this.battleExplosion(layer, tk.x, tk.y));
        this.tweens.add({
          targets: tk, alpha: 0, scaleX: 0.3, scaleY: 0.3, duration: 220,
          onComplete: () => tk.destroy(),
        });
      }
    }
    if (deaths.length > 0) await Promise.all(deaths);
  }

  /** Play a unit's side-view death sequence over its token: hide the live token
   *  (the sheet's first frame is the intact unit, so the swap is seamless), burn
   *  it down to a wreck on the same ground line, hold a beat, then fade. */
  private battleDeath(
    layer: Phaser.GameObjects.Container, tk: Phaser.GameObjects.Container,
    sheet: { key: string; facing: UnitFacing; hfrac: number }, type: UnitType, mirror: boolean, size: number,
    startDelay = 0,
  ): Promise<void> {
    const facing: UnitFacing = mirror ? 'w' : 'e';
    const drop = BATTLE_TOKEN_DROP[resolveBaseType(type)] ?? 0;
    const groundY = tk.y + size * (0.22 + drop);
    const spr = this.add.sprite(tk.x, groundY, `death-${sheet.key}`);
    spr.setVisible(false); // shown when this token's turn to burn arrives
    spr.setOrigin(0.5, 1);
    // Match the live token's on-screen height: replicate createBattleToken's
    // dir-sprite sizing, then scale the whole frame so the unit portion (hfrac of
    // frame height) lands at that height. The explosion just grows past the top.
    // Faction variants (Sherman, T-34, …) have no directional sprite and render
    // as a flat map-icon token; fall back to their footprint so the death sprite
    // isn't mis-sized (the source aspect of the flat icon keys the height).
    const dKey = dirIconKey(type, facing);
    const iconKey = TurnGameScene.ICON_KEYS[type];
    const foot = this.battleTokenFootprint(type, size);
    let liveH = foot;
    if (this.textures.exists(dKey)) {
      const d = this.textures.get(dKey).getSourceImage() as { width: number; height: number };
      liveH = foot * Math.sqrt(d.height / d.width);
    } else if (iconKey && this.textures.exists(iconKey)) {
      const s = this.textures.get(iconKey).getSourceImage() as { width: number; height: number };
      liveH = foot * Math.sqrt(s.height / s.width);
    }
    spr.displayHeight = liveH / sheet.hfrac;
    spr.scaleX = spr.scaleY;
    if (facing !== sheet.facing) spr.setFlipX(true);
    spr.setDepth(99997);
    layer.add(spr);
    return new Promise((resolve) => {
      this.time.delayedCall(startDelay, () => {
        tk.setVisible(false); // hand off from live token to the burning sprite
        spr.setVisible(true);
        spr.play(`death-anim-${sheet.key}`);
        spr.once('animationcomplete', () => {
          this.tweens.add({
            targets: spr, alpha: 0, duration: 240, delay: 200,
            onComplete: () => { spr.destroy(); tk.destroy(); resolve(); },
          });
        });
      });
    });
  }

  /** True for bomber aircraft (base or faction variant) — only these drop bombs. */
  private isBattleBomber(type: UnitType): boolean {
    return type === 'plane-bomber' || resolveBaseType(type) === 'plane-bomber';
  }

  /** True for high-angle weapons (flak, indirect artillery) whose shots arc up. */
  private isArcWeapon(type: UnitType): boolean {
    if (type === 'aa-flakpanzer') return true;
    const def = UNIT_DEFS[type] ?? UNIT_DEFS[resolveBaseType(type)];
    return (def?.minAttackRange ?? 1) >= 2;
  }

  /** True for submarines — they lurk submerged and surface only to fire. */
  private isBattleSub(type: UnitType): boolean {
    return type === 'ship-submarine' || resolveBaseType(type) === 'ship-submarine';
  }

  /** A quick water splash (ring + droplets) at a point. */
  private battleSplash(layer: Phaser.GameObjects.Container, x: number, y: number, size: number): void {
    const ring = this.add.ellipse(x, y, size * 0.5, size * 0.22, 0xdfeeff, 0.85);
    layer.add(ring);
    this.tweens.add({ targets: ring, scaleX: 2.6, scaleY: 1.8, alpha: 0, duration: 380, ease: 'Quad.easeOut', onComplete: () => ring.destroy() });
    for (let k = 0; k < 4; k++) {
      const drop = this.add.circle(x, y, size * 0.06, 0xeaf4ff, 0.95);
      layer.add(drop);
      this.tweens.add({
        targets: drop, x: x + (k - 1.5) * size * 0.2, y: y - size * 0.45, alpha: 0,
        duration: 340, ease: 'Quad.easeOut', onComplete: () => drop.destroy(),
      });
    }
  }

  /** Surface any submerged submarine tokens in the list (rise + splash). */
  private async surfaceBattleSubs(layer: Phaser.GameObjects.Container, tokens: Phaser.GameObjects.Container[], size: number): Promise<void> {
    const subs = tokens.filter((tk) => tk.active && tk.getData('isSub') && tk.getData('submerged'));
    if (subs.length === 0) return;
    await Promise.all(subs.map((tk) => new Promise<void>((resolve) => {
      const surfaceY = tk.getData('surfaceY') as number;
      tk.setData('submerged', false);
      const delay = Phaser.Math.Between(0, 180);
      this.time.delayedCall(delay + 140, () => { if (tk.active) this.battleSplash(layer, tk.x, surfaceY + size * 0.35, size); });
      this.tweens.add({ targets: tk, y: surfaceY, alpha: 1, duration: 340, delay, ease: 'Back.easeOut', onComplete: () => resolve() });
    })));
  }

  /** Submerge any surfaced submarine tokens in the list (sink + hide). */
  private async submergeBattleSubs(tokens: Phaser.GameObjects.Container[], size: number): Promise<void> {
    const subs = tokens.filter((tk) => tk.active && tk.getData('isSub') && !tk.getData('submerged'));
    if (subs.length === 0) return;
    await Promise.all(subs.map((tk) => new Promise<void>((resolve) => {
      const surfaceY = tk.getData('surfaceY') as number;
      tk.setData('submerged', true);
      this.tweens.add({
        targets: tk, y: surfaceY + size * 1.2, alpha: 0, duration: 320, delay: Phaser.Math.Between(0, 140),
        ease: 'Quad.easeIn', onComplete: () => resolve(),
      });
    })));
  }

  /** A child container of `parent`, clipped to the rectangle [x,y,w,h] in world
   *  coords. Used so a fighter's strafing rounds render ONLY on the enemy half —
   *  they appear from behind the dividing seam and never spill onto the planes'
   *  half. Call cleanup() once the volley is done. */
  private makeBattleHalfClip(
    parent: Phaser.GameObjects.Container, x: number, y: number, w: number, h: number,
  ): { container: Phaser.GameObjects.Container; cleanup: () => void } {
    const container = this.add.container(0, 0);
    parent.add(container);
    const g = this.make.graphics({}, false);
    g.fillStyle(0xffffff, 1).fillRect(x, y, w, h);
    container.setMask(g.createGeometryMask());
    return { container, cleanup: () => { container.clearMask(true); container.destroy(); g.destroy(); } };
  }

  /** A white impact burst at a point. */
  private battleBurst(layer: Phaser.GameObjects.Container, x: number, y: number, size: number): void {
    const burst = this.add.circle(x, y, size * 0.12, 0xffffff, 0.95);
    layer.add(burst);
    this.tweens.add({ targets: burst, scale: 2.6, alpha: 0, duration: 190, onComplete: () => burst.destroy() });
  }

  /**
   * Deliver ordnance from `shooters` to `targets`.
   *  - 'volley': tracers fly to the centre seam and vanish, a beat passes
   *    (travel time), then they reappear on the enemy half and strike the
   *    targets. `rounds` > 1 fires a rapid burst per shooter (aircraft strafe).
   *    Handles ground-vs-ground, air-vs-air dogfights, and AA-vs-air (the angle
   *    follows the actual token heights).
   *  - 'bombing': bombers attacking land/water — bombs are released from the
   *    bombers and fall onto each target, detonating on impact.
   */
  private async battleStrike(
    layer: Phaser.GameObjects.Container,
    shooters: Phaser.GameObjects.Container[],
    targets: Phaser.GameObjects.Container[],
    mode: 'volley' | 'bombing',
    seamX: number, size: number, rounds = 1, arc = false, topY = 0, projectileKey = 'tank-shell',
    projThicknessFrac = 0.22,
  ): Promise<void> {
    if (shooters.length === 0 || targets.length === 0) return;

    if (mode === 'bombing') {
      // A real bombing run: EACH bomber releases a stick of several black bombs
      // that fall from its altitude onto the enemy positions below and detonate.
      // A bombing run: the bombers climb and fly OVER the enemy (toward the top
      // edge), each releases a few small bombs that fall STRAIGHT DOWN from
      // beneath it onto the enemy, then they fly back to formation.
      const BOMBS_PER_BOMBER = 3;
      const groundY = Math.max(...targets.map((t) => t.y));
      const enemyX = targets.reduce((s, t) => s + t.x, 0) / targets.length;
      const origins = shooters.map((b) => ({ x: b.x, y: b.y }));
      // 1. Fly over the enemy, up near the top edge.
      await Promise.all(shooters.map((b, bi) => this.battleTween({
        targets: b, x: enemyX + (bi - (shooters.length - 1) / 2) * size * 0.6, y: topY + size * 0.8,
        duration: 520, delay: bi * 90, ease: 'Sine.easeInOut',
      })));
      // 2. Each bomber drops bombs straight down from under itself.
      // Falling-bomb whistle, once for the whole stick as it starts to fall.
      audio.fileSfx.play('battle-bomb-falling');
      const drops: Promise<void>[] = [];
      shooters.forEach((b, bi) => {
        for (let k = 0; k < BOMBS_PER_BOMBER; k++) {
          const sx = b.x + Phaser.Math.Between(-Math.round(size * 0.28), Math.round(size * 0.28));
          const ty = groundY + Phaser.Math.Between(-Math.round(size * 0.15), Math.round(size * 0.15));
          const fall = Phaser.Math.Between(720, 920);
          const delay = bi * 120 + k * 200 + Phaser.Math.Between(0, 120);
          drops.push(new Promise<void>((resolve) => {
            const startY = b.y + size * 0.28;
            const bomb: Phaser.GameObjects.Image | Phaser.GameObjects.Ellipse = this.textures.exists('bomb')
              ? this.add.image(sx, startY, 'bomb').setRotation(Math.PI / 2).setScale((size * 0.42) / 157)
              : this.add.ellipse(sx, startY, size * 0.14, size * 0.075, 0x3a3a3a, 1);
            layer.add(bomb);
            this.tweens.add({
              targets: bomb, y: ty, duration: fall, delay, ease: 'Quad.easeIn',
              onComplete: () => { bomb.destroy(); this.battleBurst(layer, sx, ty, size * 1.0); resolve(); },
            });
          }));
        }
      });
      await Promise.all(drops);
      // 3. Fly back to formation.
      await Promise.all(shooters.map((b, bi) => this.battleTween({
        targets: b, x: origins[bi].x, y: origins[bi].y, duration: 460, ease: 'Sine.easeInOut',
      })));
      return;
    }

    // Volley — pair each shooter with a target (wrap if counts differ).
    const pairs = shooters.map((sh, i) => ({ sh, tgt: targets[i % targets.length] }));
    // Seam crossing point; for arcing weapons (flak / artillery / shots at
    // aircraft) the apex is lifted ABOVE the straight line so the round rises to
    // the seam then descends onto the target — a high-angle lob.
    const seamYOf = (sh: Phaser.GameObjects.Container, tgt: Phaser.GameObjects.Container) => {
      const dx = tgt.x - sh.x;
      const frac = Math.abs(dx) < 1 ? 0.5 : Phaser.Math.Clamp((seamX - sh.x) / dx, 0.1, 0.95);
      const straight = sh.y + (tgt.y - sh.y) * frac;
      const lift = arc ? Math.max(size * 1.1, Math.abs(dx) * 0.2) : 0;
      return straight - lift;
    };
    // Projectile sprite (per-type, from /projectiles). Right-anchored nose, so
    // origin (1, 0.5) + rotation toward the aim point; scaled to the token size.
    const projCfg = BATTLE_PROJECTILES[projectileKey] ?? BATTLE_PROJECTILES['tank-shell'];
    // Per-calibre thickness (see battleProjectileThicknessFrac): tiny bullets
    // for small arms up to fat shells for heavy guns / battleships.
    const projThickness = size * projThicknessFrac;
    const projScale = projThickness / projCfg.frameH;
    const makeShot = (x: number, y: number, aimX: number, aimY: number): Phaser.GameObjects.Sprite => {
      const s = this.add.sprite(x, y, projectileKey).setOrigin(1, 0.5).setScale(projScale);
      s.rotation = Phaser.Math.Angle.Between(x, y, aimX, aimY);
      if (this.anims.exists(`${projectileKey}-fly`)) s.play(`${projectileKey}-fly`);
      return s;
    };

    // Each shooter fires `rounds` rounds; timing is randomised so the muzzles
    // don't flash in lockstep — rounds within a burst keep a loose order.
    const shots = pairs.flatMap(({ sh, tgt }) =>
      Array.from({ length: rounds }, (_, r) => ({ sh, tgt, r, first: r === 0 })));

    // Stage 1: shooter → seam apex, fading out as it crosses the divide.
    await Promise.all(shots.map(({ sh, tgt, r, first }) => new Promise<void>((resolve) => {
      const delay = r * 45 + Phaser.Math.Between(0, 200);
      const seamY = seamYOf(sh, tgt);
      if (first) {
        const muzzle = this.add.circle(sh.x, sh.y, size * Math.max(0.07, projThicknessFrac * 0.9), 0xfff1a8, 0.95);
        layer.add(muzzle);
        this.tweens.add({
          targets: muzzle, scale: 1.8, alpha: 0, duration: 150, delay,
          onComplete: () => muzzle.destroy(),
        });
      }
      const tracer = makeShot(sh.x, sh.y, seamX, seamY);
      layer.add(tracer);
      this.tweens.add({
        targets: tracer, x: seamX, y: seamY, alpha: 0, duration: 190, delay, ease: 'Quad.easeIn',
        onComplete: () => { tracer.destroy(); resolve(); },
      });
    })));

    // Travel-time beat while the rounds are "in flight" across the divide.
    await this.battleWait(150);

    // Stage 2: reappear at the seam apex and descend onto the targets.
    await Promise.all(shots.map(({ sh, tgt, r, first }) => new Promise<void>((resolve) => {
      const delay = r * 45 + Phaser.Math.Between(0, 200);
      const seamY = seamYOf(sh, tgt);
      const tracer = makeShot(seamX, seamY, tgt.x, tgt.y);
      layer.add(tracer);
      this.tweens.add({
        targets: tracer, x: tgt.x, y: tgt.y, duration: 170, delay, ease: 'Quad.easeOut',
        onComplete: () => { tracer.destroy(); if (first) this.battleBurst(layer, tgt.x, tgt.y, size); resolve(); },
      });
    })));
  }

  /** Always-on DOM HUD panels — hidden while the battle window is up so it
   *  reads as a separate window over the whole screen (the Phaser canvas sits
   *  below the DOM, so these would otherwise show over the battle). */
  private battleHudPanels(): HTMLDivElement[] {
    return [
      this.topBar, this.bottomBar, this.infoPanel, this.actionsPanel,
      this.minimapPanel, this.playerListPanel, this.messagePanel,
    ].filter(Boolean);
  }

  /** Hide the DOM HUD for the battle, run it, then restore the HUD. */
  private async playBattle(script: BattleScript): Promise<void> {
    const panels = this.battleHudPanels();
    const prevVis = panels.map((p) => p.style.visibility);
    panels.forEach((p) => { p.style.visibility = 'hidden'; });
    // Normal cursor over the battle window (drop any aim crosshair etc.).
    const canvas = this.game.canvas;
    const prevCursor = canvas.style.cursor;
    canvas.style.cursor = 'default';
    // Freeze the camera: the window is captured from the current view, so any
    // pan/zoom while it's up desyncs the overlay from the world behind it.
    this.battleActive = true;
    // Drop any in-progress RMB/touch drag so it can't resume after the battle.
    this.rmbPan.active = false;
    this.touch.active = false;
    try {
      await this.runBattle(script);
    } finally {
      this.battleActive = false;
      panels.forEach((p, i) => { p.style.visibility = prevVis[i]; });
      canvas.style.cursor = prevCursor;
    }
  }

  /**
   * Play the battle as a window over the dimmed game: fade in → both squads
   * DEPLOY into a loose bottom-to-top formation (clear, unhurried phase) →
   * pause → attack (defender shakes/blinks, loses tokens) → counter if any →
   * white flash. Resolves when the window has closed. Player and AI attacks.
   */
  private async runBattle(script: BattleScript): Promise<void> {
    const len = PALETTE.player.length;

    // Build the window in WORLD space from the camera's visible rect — this
    // already accounts for camera zoom and scroll, so the window stays centered
    // and correctly scaled at any resolution (scrollFactor(0) would be zoomed).
    // The camera does not move during a battle (input is blocked), so the
    // captured view stays valid for the whole sequence.
    const view = this.cameras.main.worldView;
    const W = view.width, H = view.height;
    const ox = view.x, oy = view.y;
    const cx = ox + W / 2;

    const layer = this.add.container(0, 0);
    layer.setDepth(100000);

    // Dim the whole game behind the window.
    layer.add(this.add.rectangle(cx, oy + H / 2, W, H, 0x000000, 0.62));

    // Full-screen battle. Per-side backgrounds fill each half (left = attacker,
    // right = defender). The window now spans the whole viewport, so every
    // layout metric below derives from the full width/height.
    const winW = W, winH = H;
    const winX = ox, winY = oy;
    const winCy = winY + winH / 2;
    layer.add(this.createBattleBackground(script.attacker, winX, winY, winW / 2, winH, 'left'));
    layer.add(this.createBattleBackground(script.defender, winX + winW / 2, winY, winW / 2, winH, 'right'));
    layer.add(this.add.rectangle(cx, winCy, 2, winH, 0x000000, 0.45));
    const frame = this.add.rectangle(cx, winCy, winW, winH);
    frame.setStrokeStyle(3, PALETTE.panelEdge, 1);
    layer.add(frame);

    // Force-advantage bar floating over the background, at the top of the window.
    const barW = winW * 0.5, barX = winX + (winW - barW) / 2;
    const barY = winY + winH * 0.045, barH = winH * 0.025;
    const atkColor = PALETTE.player[script.attacker.owner % len];
    const defColor = PALETTE.player[script.defender.owner % len];
    const barG = this.add.graphics();
    layer.add(barG);
    const drawBar = (atkHp: number, defHp: number) => {
      barG.clear();
      barG.fillStyle(0x000000, 0.55);
      barG.fillRoundedRect(barX - 4, barY - 4, barW + 8, barH + 8, 6);
      const total = atkHp + defHp;
      const atkFrac = total > 0 ? atkHp / total : 0.5;
      const atkW = barW * atkFrac;
      barG.fillStyle(atkColor, 1);
      barG.fillRect(barX, barY, atkW, barH);
      barG.fillStyle(defColor, 1);
      barG.fillRect(barX + atkW, barY, barW - atkW, barH);
      barG.fillStyle(0xffffff, 0.85);
      barG.fillRect(barX + atkW - 1, barY - 2, 2, barH + 4);
    };

    // Loose formation: tokens stacked bottom-to-top, each nudged sideways so
    // the column reads as a scattered squad rather than a neat line. They enter
    // by driving in from beyond the window's left / right side edges.
    // Token size as a fraction of the (now full-screen) height — re-tuned down
    // from the old windowed 0.27 so units keep roughly their previous on-screen
    // size now that winH spans the whole viewport (~0.17·H ≈ old 0.27·0.64·H).
    const tokenSize = winH * 0.17;
    // Squads hug their own side edge (left attacker, right defender), leaving the
    // centre open for projectiles to cross.
    const leftColX = winX + winW * 0.13, rightColX = winX + winW * 0.87;
    const colStep = winW * 0.09;
    // Checkerboard (quincunx) formation growing DOWNWARD within a per-domain
    // vertical band: land/sea stay in the lower ground band (below the horizon);
    // aircraft fill the sky, stretched down toward the horizon. Two columns
    // alternate, the second nudged half a step down so no two share a row.
    const bandFor = (s: BattleSide) => s.domain === 'air'
      ? { top: winY + winH * 0.14, bottom: winY + winH * 0.5 }
      // Ground band sits low so even the rear ranks stay below the horizon line
      // in the backgrounds (tokens grow upward from their row, so the top rank
      // must start well under mid-screen).
      : { top: winY + winH * 0.66, bottom: winY + winH * 0.91 };
    const leftTokens: Phaser.GameObjects.Container[] = [];
    const rightTokens: Phaser.GameObjects.Container[] = [];
    const deploy: Array<{ tk: Phaser.GameObjects.Container; targetX: number }> = [];
    const place = (
      side: BattleSide, count: number, colX: number, mirror: boolean, fromX: number, arr: Phaser.GameObjects.Container[],
    ) => {
      const band = bandFor(side);
      const rowStep = (band.bottom - band.top) / 2; // up to 3 ranks span the band
      const dir = mirror ? -1 : 1;
      const isSub = this.isBattleSub(side.type);
      for (let i = 0; i < count; i++) {
        const col = i % 2;            // alternate between the two columns
        const rank = Math.floor(i / 2);
        const targetX = colX + dir * col * colStep;
        const targetY = band.top + rank * rowStep + col * rowStep * 0.5;
        if (isSub) {
          // Submarines lurk submerged at their station — no sailing-in. They
          // surface only to fire (see surface/submergeBattleSubs).
          const tk = this.createBattleToken(side, targetX, targetY + tokenSize * 1.2, mirror, tokenSize);
          tk.setAlpha(0);
          tk.setData('isSub', true);
          tk.setData('submerged', true);
          tk.setData('surfaceY', targetY);
          layer.add(tk);
          arr.push(tk);
        } else {
          const tk = this.createBattleToken(side, fromX, targetY, mirror, tokenSize);
          layer.add(tk);
          arr.push(tk);
          deploy.push({ tk, targetX });
        }
      }
    };
    place(script.attacker, script.attacker.spritesBefore, leftColX, false, winX - tokenSize, leftTokens);
    place(script.defender, script.defender.spritesBefore, rightColX, true, winX + winW + tokenSize, rightTokens);

    // Commander portraits + nation flags pinned to the top corners (attacker
    // left, defender right). Added after the tokens so the HUD stays on top.
    const cmdH = winH * 0.11;
    const cmdMargin = winW * 0.015, cmdTop = winY + winH * 0.02;
    this.addBattleCommander(layer, script.attacker.owner, winX + cmdMargin, cmdTop, 'left', cmdH);
    this.addBattleCommander(layer, script.defender.owner, winX + winW - cmdMargin, cmdTop, 'right', cmdH);

    // Fade the window in.
    layer.setAlpha(0);
    drawBar(script.attacker.hpBefore, script.defender.hpBefore);
    await this.battleTween({ targets: layer, alpha: 1, duration: 320 });

    // ── DEPLOYMENT ── squads drive in from the side edges at random times
    // (not in lockstep) for a more natural, ragged advance. Only the ATTACKER's
    // single roll-in SFX plays (the defender is already in place); both sides'
    // weapons are still heard later during the volleys.
    const deployClip = battleDeployClip(script.attacker.type);
    if (deployClip) audio.fileSfx.play(deployClip);
    await Promise.all(deploy.map(({ tk, targetX }) => this.battleTween({
      targets: tk, x: targetX, duration: Phaser.Math.Between(480, 640), ease: 'Back.easeOut',
      delay: Phaser.Math.Between(0, 550),
    })));
    // Hold so the assembled formation is clearly visible before any fighting.
    await this.battleWait(800);

    // Only BOMBERS attacking land/water drop bombs from above. Fighters (and
    // ground/sea units, and air-vs-air dogfights) fire horizontal volleys —
    // aircraft strafe in rapid bursts. Same rule for the counter.
    const atkAir = script.attacker.domain === 'air';
    const defAir = script.defender.domain === 'air';
    const attackMode: 'volley' | 'bombing' = this.isBattleBomber(script.attacker.type) && !defAir ? 'bombing' : 'volley';
    const counterMode: 'volley' | 'bombing' = this.isBattleBomber(script.defender.type) && !atkAir ? 'bombing' : 'volley';
    const atkProj = battleProjectileKey(script.attacker.type);
    const defProj = battleProjectileKey(script.defender.type);
    // Rapid-fire weapons (rifles, MGs, autocannon → rifle-bullet) rattle off a
    // long burst of tiny rounds; aircraft strafe in pairs; everything else fires
    // a single shell.
    const atkRounds = atkProj === 'rifle-bullet' ? 3 : atkAir ? 2 : 1;
    const defRounds = defProj === 'rifle-bullet' ? 3 : defAir ? 2 : 1;
    const atkSub = this.isBattleSub(script.attacker.type);
    const defSub = this.isBattleSub(script.defender.type);
    // Flak / indirect artillery LOB an arc over GROUND/SEA targets. Against
    // aircraft there's no lob — the shot rises straight up and hits the plane
    // from below (so ground fire keeps its upward direction).
    const atkArc = this.isArcWeapon(script.attacker.type) && !defAir;
    const counterArc = this.isArcWeapon(script.defender.type) && !atkAir;

    // Each token lunges toward the enemy at a random moment (no lockstep).
    const lunge = (tokens: Phaser.GameObjects.Container[], dx: number) => Promise.all(tokens.map((tk) => {
      const bx = tk.x;
      return this.battleTween({
        targets: tk, x: bx + dx, duration: Phaser.Math.Between(150, 220), delay: Phaser.Math.Between(0, 150),
        yoyo: true, ease: 'Quad.easeIn',
      });
    }));

    // Fighters vs GROUND/SEA: no firing pose. They DIVE diagonally toward + past
    // the seam (down-right for the left attacker, down-left for the right
    // defender); a half-window mask hides the part that crosses, so the plane
    // TUCKS BEHIND the enemy half piece-by-piece instead of fading. The rounds
    // then land on the enemy on their own, and the planes sweep back in before
    // the counter.
    const atkFighter = atkAir && !this.isBattleBomber(script.attacker.type) && !atkSub && !defAir;
    const defFighter = defAir && !this.isBattleBomber(script.defender.type) && !defSub && !atkAir;
    // Clip a set of fighter tokens to their OWN half of the window. The seam edge
    // of the mask occludes them as they dive across it (and, on the way back,
    // their outer border occludes them so they slide in from behind it). Returns
    // a release fn that drops the mask and frees its stencil graphics.
    const halfMaskFor = (dir: number, tokens: Phaser.GameObjects.Container[]) => {
      const g = this.make.graphics({}, false);
      g.fillStyle(0xffffff, 1).fillRect(dir > 0 ? winX : cx, winY, winW / 2, winH);
      const mask = g.createGeometryMask();
      tokens.forEach((tk) => tk.setMask(mask));
      return () => { tokens.forEach((tk) => tk.clearMask()); mask.destroy(); g.destroy(); };
    };
    // Bank the plane to point along the dive vector: nose-right sprite (left
    // attacker) tips clockwise by +diveAngle, nose-left sprite (right defender)
    // tips the other way — hence dir * diveAngle.
    const diveAngle = Math.atan2(winH * 0.45, winW * 0.9);
    const fighterDiveAway = (tokens: Phaser.GameObjects.Container[], dir: number) =>
      Promise.all(tokens.map((tk) => this.battleTween({
        targets: tk, x: tk.x + dir * winW * 0.9, y: tk.y + winH * 0.45, rotation: dir * diveAngle,
        duration: 460, ease: 'Quad.easeIn', delay: Phaser.Math.Between(0, 160),
      })));
    // While tucked behind the seam, park the planes ON it (invisible) so the
    // volley's rounds originate from the dividing edge, never the planes' half.
    const fighterFireOrigin = (tokens: Phaser.GameObjects.Container[]) =>
      tokens.forEach((tk) => { tk.x = cx; tk.y = winY + winH * 0.4; tk.setAlpha(0); });
    // Re-enter from behind the OUTER side edge (attacker from the left, defender
    // from the right); the mask occludes them at that edge so they slide out
    // from behind it back into the formation slot.
    const fighterReturn = (
      tokens: Phaser.GameObjects.Container[], dir: number, homeX: number[], homeY: number[],
    ) => Promise.all(tokens.map((tk, i) => {
      tk.setAlpha(1); tk.rotation = 0; tk.y = homeY[i]; // level out for the run home
      tk.x = dir > 0 ? winX - tokenSize * 2 : winX + winW + tokenSize * 2;
      return this.battleTween({
        targets: tk, x: homeX[i], duration: 420, ease: 'Quad.easeOut',
        delay: Phaser.Math.Between(0, 160),
      });
    }));
    let leftHomeX: number[] = [], leftHomeY: number[] = [];
    let rightHomeX: number[] = [], rightHomeY: number[] = [];
    let releaseLeftMask: (() => void) | null = null;
    let releaseRightMask: (() => void) | null = null;

    // ── ATTACK ── (no SFX here — sounds play at the player's order confirmation)
    // Submarines surface (with a splash) instead of lunging; the target must be
    // visible too. Then the volley fires from the (now surfaced) shooters.
    if (atkSub) await this.surfaceBattleSubs(layer, leftTokens, tokenSize);
    else if (atkFighter) {
      leftHomeX = leftTokens.map((t) => t.x); leftHomeY = leftTokens.map((t) => t.y);
      releaseLeftMask = halfMaskFor(1, leftTokens);
      await fighterDiveAway(leftTokens, 1); // dive down-right, tucking behind the enemy half
      fighterFireOrigin(leftTokens);
    } else if (attackMode !== 'bombing') await lunge(leftTokens, winW * 0.035); // bombers fly over instead
    if (defSub) await this.surfaceBattleSubs(layer, rightTokens, tokenSize);
    { const fire = battleFireClip(script.attacker.type); if (fire) audio.fileSfx.play(fire); }
    // Fighter rounds are clipped to the enemy (right) half so they appear from
    // behind the seam and never bleed onto the planes' own half.
    const atkClip = atkFighter ? this.makeBattleHalfClip(layer, cx, winY, winW / 2, winH) : null;
    await this.battleStrike(atkClip?.container ?? layer, leftTokens, rightTokens, attackMode, cx, tokenSize, atkRounds, atkArc, winY, atkProj, battleProjectileThicknessFrac(script.attacker.type));
    atkClip?.cleanup();
    await this.battleHit(layer, rightTokens);
    await this.killBattleTokens(layer, rightTokens, script.defender.spritesBefore - script.defender.spritesAfter, script.defender.type, true, tokenSize);
    // Fighters sweep back in from off-board BEFORE the counter can fire at them.
    if (atkFighter) { await fighterReturn(leftTokens, 1, leftHomeX, leftHomeY); releaseLeftMask?.(); }
    await this.animateBattleBar(
      drawBar, script.attacker.hpBefore, script.defender.hpBefore, script.attacker.hpBefore, script.defender.hpAfter,
    );

    // ── COUNTER (only when the defender survived and dealt damage) ──
    if (script.counterHappened) {
      await this.battleWait(450);
      if (defSub) await this.surfaceBattleSubs(layer, rightTokens, tokenSize);
      else if (defFighter) {
        rightHomeX = rightTokens.map((t) => t.x); rightHomeY = rightTokens.map((t) => t.y);
        releaseRightMask = halfMaskFor(-1, rightTokens);
        await fighterDiveAway(rightTokens, -1); // dive down-left, tucking behind the enemy half
        fighterFireOrigin(rightTokens);
      } else if (counterMode !== 'bombing') await lunge(rightTokens, -winW * 0.035);
      if (atkSub) await this.surfaceBattleSubs(layer, leftTokens, tokenSize);
      { const fire = battleFireClip(script.defender.type); if (fire) audio.fileSfx.play(fire); }
      // Counter-fighter rounds clipped to the enemy (left) half — same seam trick.
      const defClip = defFighter ? this.makeBattleHalfClip(layer, winX, winY, winW / 2, winH) : null;
      await this.battleStrike(defClip?.container ?? layer, rightTokens, leftTokens, counterMode, cx, tokenSize, defRounds, counterArc, winY, defProj, battleProjectileThicknessFrac(script.defender.type));
      defClip?.cleanup();
      await this.battleHit(layer, leftTokens);
      await this.killBattleTokens(layer, leftTokens, script.attacker.spritesBefore - script.attacker.spritesAfter, script.attacker.type, false, tokenSize);
      if (defFighter) { await fighterReturn(rightTokens, -1, rightHomeX, rightHomeY); releaseRightMask?.(); }
      await this.animateBattleBar(
        drawBar, script.attacker.hpBefore, script.defender.hpAfter, script.attacker.hpAfter, script.defender.hpAfter,
      );
    }

    // Submarines slip back under the surface before the window closes.
    await Promise.all([
      this.submergeBattleSubs(leftTokens, tokenSize),
      this.submergeBattleSubs(rightTokens, tokenSize),
    ]);
    await this.battleWait(350);

    // ── Close with a white flash ──
    const flash = this.add.rectangle(cx, oy + H / 2, W, H, 0xffffff, 0);
    flash.setDepth(100001);
    await this.battleTween({ targets: flash, alpha: 1, duration: 160, ease: 'Quad.easeIn' });
    layer.destroy(); // remove the battle window under cover of the flash
    await this.battleTween({ targets: flash, alpha: 0, duration: 240, ease: 'Quad.easeOut' });
    flash.destroy();
  }

  /** Animate green "+" over a unit at grid position. Scale 0→1 then fade out. ~600ms. */
  private animateHealPlus(col: number, row: number, onComplete: () => void): void {
    const pos = this.gridToScreen(col, row);
    const gfx = this.add.graphics();
    gfx.setPosition(pos.x, pos.y - 12);
    gfx.setDepth(99999);
    gfx.setScale(0);
    gfx.setAlpha(1);

    // Draw green "+" shape
    const c = 0x4ade80;
    const w = 4, h = 14;
    gfx.fillStyle(c, 1);
    gfx.fillRect(-w / 2, -h / 2, w, h); // vertical bar
    gfx.fillRect(-h / 2, -w / 2, h, w); // horizontal bar

    // Scale up
    this.tweens.add({
      targets: gfx,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: 200,
      ease: 'Back.easeOut',
      onComplete: () => {
        // Hold then fade out
        this.tweens.add({
          targets: gfx,
          alpha: 0,
          scaleX: 1.5,
          scaleY: 1.5,
          duration: 400,
          ease: 'Sine.easeIn',
          onComplete: () => {
            gfx.destroy();
            onComplete();
          },
        });
      },
    });
  }

  /** Animate heal results sequentially — one green "+" per healed unit. */
  private animateHealSequence(results: HealResult[], onComplete: () => void): void {
    if (results.length === 0) { onComplete(); return; }
    this.animating = true;
    let idx = 0;
    const next = () => {
      if (idx >= results.length) {
        this.animating = false;
        onComplete();
        return;
      }
      const r = results[idx];
      const unit = this.world.units.get(r.unitId);
      if (!unit) { idx++; next(); return; }
      this.animateHealPlus(unit.col, unit.row, () => {
        this.syncEntities();
        idx++;
        next();
      });
    };
    next();
  }

  /** Animate building repair results. Pan to each building, then show heal "+" on repaired units. */
  private animateRepairSequence(results: BuildingRepairResult[], onComplete: () => void): void {
    if (results.length === 0) { onComplete(); return; }
    this.animating = true;
    let bIdx = 0;
    const nextBuilding = () => {
      if (bIdx >= results.length) {
        this.animating = false;
        onComplete();
        return;
      }
      const br = results[bIdx];
      const building = this.world.buildings.get(br.buildingId);
      if (!building || br.repairs.length === 0) { bIdx++; nextBuilding(); return; }

      const pos = this.gridToScreen(br.col, br.row);
      this.cameras.main.pan(pos.x, pos.y, 200, 'Sine.easeInOut', false, (_cam, progress) => {
        if (progress === 1) {
          this.addMessage(t('msg.repaired', t(`building.${building.type}`)));
          this.animateHealSequenceInner(br.repairs, () => {
            bIdx++;
            nextBuilding();
          });
        }
      });
    };
    nextBuilding();
  }

  /** Inner heal sequence without setting animating flag (called from animateRepairSequence). */
  private animateHealSequenceInner(results: HealResult[], onComplete: () => void): void {
    if (results.length === 0) { onComplete(); return; }
    let idx = 0;
    const next = () => {
      if (idx >= results.length) {
        onComplete();
        return;
      }
      const r = results[idx];
      const unit = this.world.units.get(r.unitId);
      if (!unit) { idx++; next(); return; }
      this.animateHealPlus(unit.col, unit.row, () => {
        this.syncEntities();
        idx++;
        next();
      });
    };
    next();
  }

  /** Animate a green ammo icon (scale up → fade out) at a grid position. */
  private animateResupplyIcon(col: number, row: number, onComplete: () => void): void {
    if (!this.textures.exists('ammo-icon-src')) { onComplete(); return; }
    const pos = this.gridToScreen(col, row);
    const icon = this.add.image(pos.x, pos.y - 12, 'ammo-icon-src');
    icon.setOrigin(0.5, 0.5);
    icon.setTint(0x4ade80);
    icon.displayWidth = 14;
    icon.displayHeight = 14;
    icon.setDepth(99999);
    // Save target scale, then start from 0
    const targetSX = icon.scaleX;
    const targetSY = icon.scaleY;
    icon.setScale(0);
    icon.setAlpha(1);

    this.tweens.add({
      targets: icon,
      scaleX: targetSX * 1.2,
      scaleY: targetSY * 1.2,
      duration: 200,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: icon,
          alpha: 0,
          scaleX: targetSX * 1.5,
          scaleY: targetSY * 1.5,
          duration: 400,
          ease: 'Sine.easeIn',
          onComplete: () => {
            icon.destroy();
            onComplete();
          },
        });
      },
    });
  }

  /** Animate truck resupply results sequentially — one green ammo icon per target. */
  private animateResupplyPlus(results: ResupplyResult[], onComplete: () => void): void {
    if (results.length === 0) { onComplete(); return; }
    this.animating = true;
    let idx = 0;
    const next = () => {
      if (idx >= results.length) {
        this.animating = false;
        onComplete();
        return;
      }
      const r = results[idx];
      const unit = this.world.units.get(r.unitId);
      if (!unit) { idx++; next(); return; }
      this.animateResupplyIcon(unit.col, unit.row, () => {
        this.syncEntities();
        idx++;
        next();
      });
    };
    next();
  }

  /** Animate building resupply at end of turn. Pan to each building, show ammo icon on resupplied units. */
  private animateResupplySequence(results: BuildingResupplyResult[], onComplete: () => void): void {
    if (results.length === 0) { onComplete(); return; }
    this.animating = true;
    let bIdx = 0;
    const nextBuilding = () => {
      if (bIdx >= results.length) {
        this.animating = false;
        onComplete();
        return;
      }
      const br = results[bIdx];
      const building = this.world.buildings.get(br.buildingId);
      if (!building || br.resupplies.length === 0) { bIdx++; nextBuilding(); return; }

      const pos = this.gridToScreen(br.col, br.row);
      this.cameras.main.pan(pos.x, pos.y, 200, 'Sine.easeInOut', false, (_cam, progress) => {
        if (progress === 1) {
          this.addMessage(t('msg.resupplied'));
          let uIdx = 0;
          const nextUnit = () => {
            if (uIdx >= br.resupplies.length) {
              bIdx++;
              nextBuilding();
              return;
            }
            const r = br.resupplies[uIdx];
            const unit = this.world.units.get(r.unitId);
            if (!unit) { uIdx++; nextUnit(); return; }
            this.animateResupplyIcon(unit.col, unit.row, () => {
              this.syncEntities();
              uIdx++;
              nextUnit();
            });
          };
          nextUnit();
        }
      });
    };
    nextBuilding();
  }

  /**
   * Two bounces on the building tile — visible acceleration on the way down.
   * onLanded is called once after the last landing (before final sync) — use
   * it to apply the capture state change so the color fill appears after the bounce.
   */
  private animateCaptureBounce(unit: Unit, onComplete: () => void, onLanded?: () => void): void {
    const gfx = this.unitSprites.get(unit.id);
    if (!gfx) {
      if (onLanded) onLanded();
      onComplete();
      return;
    }

    this.animating = true;
    this.highlightGfx.clear();
    this.clearTargetOutlines();

    const basePos = this.gridToScreen(unit.col, unit.row);
    const depth = (unit.col + unit.row) * 1000 + 300;
    const iconImg = this.unitIconImages.get(unit.id);
    const heights = [14, 9]; // pixels up for each bounce
    let bounce = 0;

    const moveUnit = (yOffset: number) => {
      this.drawUnitAt(gfx, unit, basePos.x, basePos.y + yOffset, depth);
      if (iconImg) {
        iconImg.setPosition(basePos.x, basePos.y + yOffset - 4);
        iconImg.setDepth(depth + 1);
      }
    };

    const doBounce = () => {
      if (bounce >= heights.length) {
        // Apply capture state change, then sync to show color fill
        if (onLanded) onLanded();
        this.animating = false;
        this.syncEntities();
        this.fullRefresh();
        onComplete();
        return;
      }
      const h = heights[bounce];
      // Jump up: decelerate (Quad.easeOut)
      this.tweens.add({
        targets: { t: 0 },
        t: 1,
        duration: 120,
        ease: 'Quad.easeOut',
        onUpdate: (_tw, obj) => {
          moveUnit(-h * (obj as { t: number }).t);
        },
        onComplete: () => {
          // Fall down: accelerate (Quad.easeIn)
          this.tweens.add({
            targets: { t: 0 },
            t: 1,
            duration: 100,
            ease: 'Quad.easeIn',
            onUpdate: (_tw, obj) => {
              moveUnit(-h * (1 - (obj as { t: number }).t));
            },
            onComplete: () => {
              bounce++;
              this.time.delayedCall(60, doBounce);
            },
          });
        },
      });
    };
    doBounce();
  }

  /** Animate a unit moving along a path, tile by tile. Calls onComplete when done. */
  private animateMove(unit: Unit, path: Coord[], onComplete: (ambushed: boolean) => void, ambushTiles?: Set<string>, revealFog = true): void {
    const gfx = this.unitSprites.get(unit.id);
    if (!gfx || path.length === 0) {
      onComplete(false); return;
    }
    // No SFX during the move itself — the unit's order-confirm bark plays only
    // when its turn is finalized (see finishUnitTurn).

    this.animating = true;
    // Hide highlights during animation
    this.highlightGfx.clear();
    this.clearTargetOutlines();

    const positions = [
      this.gridToScreen(unit.col, unit.row),
      ...path.map((p) => this.gridToScreen(p.col, p.row)),
    ];

    // Pre-compute base visibility (all units except the moving one + buildings).
    // During animation only the moving unit changes position, so we reuse
    // this constant set and only BFS the moving unit at each step.
    const baseVisible = new Set<string>();
    for (const [, u] of this.world.units) {
      if (u.owner !== this.localPlayer || u.id === unit.id) continue;
      for (const key of computeUnitVision(u, this.world.map)) baseVisible.add(key);
    }
    for (const [, b] of this.world.buildings) {
      if (b.owner !== this.localPlayer) continue;
      for (const key of computeBuildingVision(b, this.world.map)) baseVisible.add(key);
    }

    // Track moving unit's vision per step for incremental fog updates.
    let prevMovingVision = computeUnitVision(unit, this.world.map);

    // Build initial playerVisible = base + starting vision. For a provisional
    // (pending) move we freeze the fog instead: the unit slides blind and fog
    // is only revealed once the player confirms (or on ambush). So leave
    // this.playerVisible untouched when revealFog is false.
    const workingVisible = new Set(baseVisible);
    for (const key of prevMovingVision) workingVisible.add(key);
    if (revealFog) this.playerVisible = workingVisible;

    // Draw unit once at starting position — animate via position offset (no per-frame redraw)
    const drawOrigin = positions[0];
    const startDepth = (unit.col + unit.row) * 1000 + 300;
    this.drawUnitAt(gfx, unit, drawOrigin.x, drawOrigin.y, startDepth);
    const iconImg = this.unitIconImages.get(unit.id);

    // Ambush tiles the unit has SPOTTED earlier in this move. Spotting an enemy
    // a step before contact means the player can see it coming — so it no longer
    // counts as an ambush. Only enemies first revealed at the contact tile spring.
    const seenAmbushers = new Set<string>();

    let seg = 0;
    const nextSegment = () => {
      if (seg >= path.length) {
        unit.col = path[path.length - 1].col;
        unit.row = path[path.length - 1].row;
        gfx.setPosition(0, 0); // reset offset
        this.animating = false;
        this.syncEntities();
        onComplete(false);
        return;
      }

      const from = positions[seg];
      const to = positions[seg + 1];
      const target = path[seg];
      // Face the direction of this step (unit.col/row is still the previous tile here).
      this.setUnitFacing(unit, iconImg, facingFromStep(target.col - unit.col, target.row - unit.row));
      const depthAtTarget = (target.col + target.row) * 1000 + 300;

      this.tweens.add({
        targets: { t: 0 },
        t: 1,
        duration: 90,
        ease: 'Linear',
        onUpdate: (_tw, obj) => {
          const t = (obj as { t: number }).t;
          const ix = from.x + (to.x - from.x) * t;
          const iy = from.y + (to.y - from.y) * t;
          // Offset the pre-drawn graphics — no g.clear()/redraw per frame
          gfx.setPosition(ix - drawOrigin.x, iy - drawOrigin.y);
          gfx.setDepth(depthAtTarget);
          this.positionUnitVisual(unit, ix, iy, depthAtTarget);
        },
        onComplete: () => {
          unit.col = target.col;
          unit.row = target.row;
          // Optimized fog reveal: compute delta between old and new moving-unit vision,
          // then apply only the changed tiles via drawFogDelta (no Set copy or diffing).
          // Skipped entirely for a provisional move (revealFog=false) — fog stays
          // frozen until the move is confirmed (see beginPendingMove).
          if (revealFog) {
            const newMovingVision = computeUnitVision(unit, this.world.map);

            const hidden: string[] = [];
            for (const key of prevMovingVision) {
              if (!newMovingVision.has(key) && !baseVisible.has(key)) hidden.push(key);
            }
            const revealed: string[] = [];
            for (const key of newMovingVision) {
              if (!prevMovingVision.has(key)) revealed.push(key);
            }

            // Mutate workingVisible in-place
            for (const key of hidden) workingVisible.delete(key);
            for (const key of revealed) workingVisible.add(key);
            this.playerVisible = workingVisible;

            updateExplored(this.localPlayer, this.playerVisible, this.world);
            this.drawFogDelta(revealed, hidden);
            prevMovingVision = newMovingVision;
          }

          // Ambush: the moving unit just landed next to an enemy that was hidden
          // at move start (Chebyshev adjacency — diagonals included) and that the
          // unit had NOT already spotted on an earlier step. An enemy the player
          // saw coming during the approach is not an ambush.
          let ambushed = false;
          if (ambushTiles) {
            for (let dc = -1; dc <= 1 && !ambushed; dc++) {
              for (let dr = -1; dr <= 1 && !ambushed; dr++) {
                if (dc === 0 && dr === 0) continue;
                const nk = `${unit.col + dc},${unit.row + dr}`;
                if (ambushTiles.has(nk) && !seenAmbushers.has(nk)) ambushed = true;
              }
            }
            // Record every ambush enemy now in view, so later steps know it was
            // already spotted (checked AFTER the trigger so a first-contact reveal
            // still springs the trap on this step).
            for (const tk of ambushTiles) {
              if (this.playerVisible.has(tk)) seenAmbushers.add(tk);
            }
          }
          if (ambushed) {
            gfx.setPosition(0, 0);
            this.animating = false;
            this.syncEntities();
            onComplete(true);
            return;
          }

          seg++;
          nextSegment();
        },
      });
    };
    nextSegment();
  }

  // --- Helper: compute available orders ---

  private computeAvailableOrders(unit: Unit, range: Map<string, number>): OrderEntry[] {
    const orders: OrderEntry[] = [];
    // A unit that already spent its turn can be reviewed (stats in info panel)
    // but cannot issue new orders — every action button is disabled below.
    const idle = !unit.acted;

    // Every unit can move; attack only if the unit has an attack range
    orders.push({ kind: 'move', enabled: idle && range.size > 0 });
    if (UNIT_DEFS[unit.type].attackRange > 0) {
      orders.push({ kind: 'attack', enabled: idle && this.computeAttackableEnemies(unit).size > 0 });
    }

    // Capture — only for capture-capable unit types
    if (canUnitCapture(unit)) {
      orders.push({ kind: 'capture', enabled: idle && canCapture(this.world, unit) != null });
    }
    // Heal — medic only
    const baseType = resolveBaseType(unit.type);
    if (baseType === 'medic') {
      orders.push({ kind: 'heal', enabled: idle && computeHealTargets(this.world, unit.id).length > 0 });
    }
    // Resupply — any mobile resupplier (truck→land, carrier/plane-transport→air,
    // ship-transport→water). Targets are already domain-filtered.
    if (resolveResupplyDomain(unit.type) !== null) {
      orders.push({ kind: 'resupply', enabled: idle && computeResupplyTargets(this.world, unit.id).length > 0 });
    }
    // Build (bunkers) — supply-truck only.
    if (baseType === 'supply-truck') {
      orders.push({ kind: 'build', enabled: idle && canBuildOrContinue(this.world, unit) });
    }
    return orders;
  }

  private computeAttackableEnemies(unit: Unit): Set<number> {
    const result = new Set<number>();
    for (const [, e] of this.world.units) {
      if (e.owner === unit.owner) continue;
      // Fog of war: only target visible enemies
      if (!this.playerVisible.has(`${e.col},${e.row}`)) continue;
      if (canAttack(unit, e)) result.add(e.id);
    }
    return result;
  }

  /** Enemies the unit could attack THIS TURN — from its current tile, or, for
   *  direct-fire units, after moving anywhere in `range` (indirect/artillery
   *  can't move-and-fire, so they only count their current tile). Used to glow
   *  reachable targets the moment a unit is selected, so the player immediately
   *  sees who they can hit. Cheap: scans each enemy's small attack-range ring
   *  against the set of tiles the unit can fire from. */
  private computeReachableAttackableEnemies(unit: Unit, range: Map<string, number>): Set<number> {
    const result = new Set<number>();
    if (unit.acted) return result;
    const def = UNIT_DEFS[unit.type];
    const maxR = def.attackRange;
    if (maxR <= 0) return result;
    const minR = def.minAttackRange ?? 1;
    const indirect = minR >= 2; // artillery: no move-and-fire
    const fireTiles = new Set<string>();
    fireTiles.add(`${unit.col},${unit.row}`);
    if (!indirect) { for (const k of range.keys()) fireTiles.add(k); }
    for (const [, e] of this.world.units) {
      if (e.owner === unit.owner) continue;
      if (!this.playerVisible.has(`${e.col},${e.row}`)) continue;
      let hit = false;
      for (let dr = -maxR; dr <= maxR && !hit; dr++) {
        for (let dc = -maxR; dc <= maxR; dc++) {
          const dist = Math.max(Math.abs(dr), Math.abs(dc));
          if (dist < minR || dist > maxR) continue;
          if (fireTiles.has(`${e.col + dc},${e.row + dr}`)) { hit = true; break; }
        }
      }
      if (hit) result.add(e.id);
    }
    return result;
  }

  private computePostMoveAttackableEnemies(unit: Unit): Set<number> {
    const result = new Set<number>();
    for (const [, e] of this.world.units) {
      if (e.owner === unit.owner) continue;
      // Fog of war: only target visible enemies
      if (!this.playerVisible.has(`${e.col},${e.row}`)) continue;
      if (canAttack(unit, e)) result.add(e.id);
    }
    return result;
  }

  /** Compute all tile keys within attack range from unit's current position. */
  private computeAttackRangeKeys(unit: Unit): string[] {
    const atkDef = UNIT_DEFS[unit.type];
    const minRange = atkDef.minAttackRange ?? 1;
    const maxRange = atkDef.attackRange;
    const keys: string[] = [];
    for (let dr = -maxRange; dr <= maxRange; dr++) {
      for (let dc = -maxRange; dc <= maxRange; dc++) {
        const dist = Math.max(Math.abs(dr), Math.abs(dc));
        if (dist < minRange || dist > maxRange) continue;
        const c = unit.col + dc;
        const r = unit.row + dr;
        if (c >= 0 && r >= 0 && c < this.mapWidth && r < this.mapHeight) {
          keys.push(`${c},${r}`);
        }
      }
    }
    return keys;
  }

  // --- Input handling ---

  private bindInput(): void {
    this.input.mouse?.disableContextMenu();

    const cursors = this.input.keyboard!.createCursorKeys();
    const wasd = {
      up: this.input.keyboard!.addKey('W'),
      down: this.input.keyboard!.addKey('S'),
      left: this.input.keyboard!.addKey('A'),
      right: this.input.keyboard!.addKey('D'),
    };
    const escKey = this.input.keyboard!.addKey('ESC');

    this.events.on('update', () => {
      if (this.battleActive) return; // camera frozen while a battle window is up
      const speed = 8 / this.cameras.main.zoom;
      if (cursors.left.isDown || wasd.left.isDown) this.cameras.main.scrollX -= speed;
      if (cursors.right.isDown || wasd.right.isDown) this.cameras.main.scrollX += speed;
      if (cursors.up.isDown || wasd.up.isDown) this.cameras.main.scrollY -= speed;
      if (cursors.down.isDown || wasd.down.isDown) this.cameras.main.scrollY += speed;
      this.clampCamera();

      if (Phaser.Input.Keyboard.JustDown(escKey)) {
        this.cancelBack(true);
      }
    });

    // ── Zoom: mouse wheel (desktop) — zoom toward mouse cursor ──
    // Use native DOM event for reliable screen-space cursor coordinates.
    // Phaser scroll model: midPoint = (scrollX + width/2, scrollY + height/2)
    // regardless of zoom. World-space top-left = midPoint - (width, height) / (2 * zoom).
    this.game.canvas.addEventListener('wheel', (e: WheelEvent) => {
      e.preventDefault();
      if (this.battleActive) return; // no zoom while a battle window is up
      const cam = this.cameras.main;
      const oldZoom = cam.zoom;
      const newZoom = Phaser.Math.Clamp(oldZoom - e.deltaY * 0.001, this.minZoom, this.maxZoom);
      if (newZoom === oldZoom) return;

      // Screen position of cursor in RENDER pixels relative to canvas.
      // Pod Scale.FIT rozmiar wyświetlany (CSS) różni się od rozmiaru renderu,
      // więc skalujemy o cam.width/rect.width (pod RESIZE współczynnik = 1).
      const rect = this.game.canvas.getBoundingClientRect();
      const sx = (e.clientX - rect.left) * (cam.width / rect.width);
      const sy = (e.clientY - rect.top) * (cam.height / rect.height);

      // World point under cursor: top-left in world = midPoint - size/(2*zoom)
      // then offset by sx/zoom, sy/zoom
      const midX = cam.scrollX + cam.width / 2;
      const midY = cam.scrollY + cam.height / 2;
      const worldX = midX - cam.width / (2 * oldZoom) + sx / oldZoom;
      const worldY = midY - cam.height / (2 * oldZoom) + sy / oldZoom;

      // After zoom: want same worldX at same sx
      // worldX = newMidX - width/(2*newZoom) + sx/newZoom
      // newMidX = worldX + width/(2*newZoom) - sx/newZoom
      // newScrollX = newMidX - width/2
      cam.zoom = newZoom;
      const newMidX = worldX + cam.width / (2 * newZoom) - sx / newZoom;
      const newMidY = worldY + cam.height / (2 * newZoom) - sy / newZoom;
      cam.scrollX = newMidX - cam.width / 2;
      cam.scrollY = newMidY - cam.height / 2;
      this.clampCamera();
    }, { passive: false });

    // ── Pointer down ──
    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      if (this.battleActive) return; // ignore all pointer input during a battle
      // Desktop RMB pan — unchanged
      if (p.rightButtonDown()) {
        this.rmbPan.active = true;
        this.rmbPan.startX = p.x;
        this.rmbPan.startY = p.y;
        this.rmbPan.camStartX = this.cameras.main.scrollX;
        this.rmbPan.camStartY = this.cameras.main.scrollY;
        this.rmbPan.dragged = false;
        // Hold RMB over one of my own units → peek its attack range while held
        // (cleared on release). Pure preview, not a selection.
        if (!this.animating && this.inputState.kind !== 'aiTurn') {
          const wp = this.cameras.main.getWorldPoint(p.x, p.y);
          const { col, row } = this.screenToGrid(wp.x, wp.y);
          const own = this.inBounds(col, row) ? this.getUnitAt(col, row) : null;
          // RMB peeks an own unit's attack range — UNLESS it's the currently
          // selected unit on its own tile: there RMB must reach tryIssueOrder so
          // the in-place order menu (capture/heal/resupply/build) opens. Peeking
          // the selected unit would swallow that click (see `hadPeek` on release).
          const isSelectedUnit = this.inputState.kind === 'unitSelected'
            && this.inputState.unitId === own?.id;
          if (own && own.owner === this.localPlayer && UNIT_DEFS[own.type].attackRange > 0
              && !isSelectedUnit) {
            this.attackRangePreviewId = own.id;
            this.refresh();
          }
        }
        return;
      }

      // Check if a second pointer is arriving → start pinch
      const p1Down = this.input.pointer1?.isDown ?? false;
      const p2Down = this.input.pointer2?.isDown ?? false;
      if (p1Down && p2Down) {
        const p1 = this.input.pointer1;
        const p2 = this.input.pointer2;
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        this.touch.pinching = true;
        this.touch.startDist = Math.sqrt(dx * dx + dy * dy) || 1;
        this.touch.startZoom = this.cameras.main.zoom;
        this.touch.pinchMidX = (p1.x + p2.x) / 2;
        this.touch.pinchMidY = (p1.y + p2.y) / 2;
        this.touch.camStartX = this.cameras.main.scrollX;
        this.touch.camStartY = this.cameras.main.scrollY;
        return;
      }

      // Single touch / LMB — record start for drag detection
      const cam = this.cameras.main;
      this.touch.active = true;
      this.touch.dragged = false;
      this.touch.startX = p.x;
      this.touch.startY = p.y;
      this.touch.camStartX = cam.scrollX;
      this.touch.camStartY = cam.scrollY;
    });

    // ── Pointer move ──
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (this.battleActive) return; // camera frozen while a battle window is up
      // Desktop RMB pan
      if (this.rmbPan.active) {
        const dx = p.x - this.rmbPan.startX;
        const dy = p.y - this.rmbPan.startY;
        if (Math.abs(dx) > 6 || Math.abs(dy) > 6) this.rmbPan.dragged = true;
        if (this.rmbPan.dragged) {
          const cam = this.cameras.main;
          cam.scrollX = this.rmbPan.camStartX - dx / cam.zoom;
          cam.scrollY = this.rmbPan.camStartY - dy / cam.zoom;
        }
        return;
      }

      // Pinch zoom (2 fingers)
      if (this.touch.pinching && this.input.pointer1?.isDown && this.input.pointer2?.isDown) {
        const p1 = this.input.pointer1;
        const p2 = this.input.pointer2;
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const cam = this.cameras.main;
        cam.zoom = Phaser.Math.Clamp(
          this.touch.startZoom * (dist / this.touch.startDist), this.minZoom, this.maxZoom,
        );
        // Pan to follow pinch midpoint
        const midX = (p1.x + p2.x) / 2;
        const midY = (p1.y + p2.y) / 2;
        cam.scrollX = this.touch.camStartX - (midX - this.touch.pinchMidX) / cam.zoom;
        cam.scrollY = this.touch.camStartY - (midY - this.touch.pinchMidY) / cam.zoom;
        return;
      }

      // Single touch pan (1 finger drag)
      if (this.touch.active) {
        const dx = p.x - this.touch.startX;
        const dy = p.y - this.touch.startY;
        if (!this.touch.dragged && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
          this.touch.dragged = true;
        }
        if (this.touch.dragged) {
          const cam = this.cameras.main;
          cam.scrollX = this.touch.camStartX - dx / cam.zoom;
          cam.scrollY = this.touch.camStartY - dy / cam.zoom;
        }
        return;
      }

      // Hover detection — show enemy attack range preview + update cursor
      // so the player sees at a glance what RMB will do on the hovered tile.
      if (!this.animating) {
        const wp = this.cameras.main.getWorldPoint(p.x, p.y);
        const { col, row } = this.screenToGrid(wp.x, wp.y);
        let newHover: number | null = null;
        if (this.inBounds(col, row)) {
          const u = this.getUnitAt(col, row);
          if (u && u.owner !== this.localPlayer) newHover = u.id;
        }
        if (newHover !== this.hoveredEnemyId) {
          this.hoveredEnemyId = newHover;
          this.drawHighlights();
        }
        // Air-strike targeting — track the hovered tile for the 3×3 preview.
        if (this.inputState.kind === 'orderOfficerAbility') {
          const tile = this.inBounds(col, row) ? { col, row } : null;
          if (tile?.col !== this.abilityHoverTile?.col || tile?.row !== this.abilityHoverTile?.row) {
            this.abilityHoverTile = tile;
            this.drawHighlights();
          }
        }
        this.updateMapCursor(col, row);
      }
    });

    // ── Pointer up ──
    this.input.on('pointerup', (p: Phaser.Input.Pointer) => {
      if (this.battleActive) return; // swallow releases while a battle window is up
      // Desktop RMB release — RMB is purely "cancel/deselect" now. Orders are
      // issued with LMB (select → move/attack target → context menu).
      if (p.button === 2 && this.rmbPan.active) {
        const wasDrag = this.rmbPan.dragged;
        this.rmbPan.active = false;
        // Releasing RMB ends the attack-range peek (hold-to-show preview).
        const hadPeek = this.attackRangePreviewId != null;
        if (hadPeek) { this.attackRangePreviewId = null; this.refresh(); }
        if (wasDrag) return;
        // A peek over a unit consumes the click — don't also cancel/order.
        if (hadPeek) return;
        // RMB while a context menu is open → dismiss it (cancel pending move).
        if (this.contextMenu) { this.contextMenuDismiss?.(); return; }
        if (this.inputState.kind === 'aiTurn' || this.animating) return;
        if (this.menuOverlay || this.unitInfoOverlay || this.recruitWindow) return;
        // RMB issues orders for the selected unit (move / attack / load).
        if (this.inputState.kind === 'unitSelected') {
          const wp = this.cameras.main.getWorldPoint(p.x, p.y);
          const { col, row } = this.screenToGrid(wp.x, wp.y);
          if (this.inBounds(col, row) && this.tryIssueOrder(col, row)) return;
        }
        // RMB while picking a post-move attack target: pick it (or reopen menu).
        if (this.inputState.kind === 'pendingMoveAttack') {
          const wp = this.cameras.main.getWorldPoint(p.x, p.y);
          const { col, row } = this.screenToGrid(wp.x, wp.y);
          if (this.inBounds(col, row)) this.handlePendingMoveAttackClick(col, row);
          return;
        }
        this.cancelBack();
        return;
      }

      // End pinch when a finger lifts
      if (this.touch.pinching) {
        this.touch.pinching = false;
        this.touch.active = false;
        this.touch.dragged = true; // suppress click after pinch
        return;
      }

      // Single touch / LMB release
      if (this.touch.active) {
        const wasDrag = this.touch.dragged;
        this.touch.active = false;
        this.touch.dragged = false;

        // Tap (no drag) → handle as click
        if (!wasDrag) {
          if (this.inputState.kind === 'aiTurn') return;
          if (this.animating) return;
          if (this.menuOverlay) return;
          if (this.unitInfoOverlay) return;
          // LMB on the map while a context menu is open → dismiss it.
          if (this.contextMenu) { this.contextMenuDismiss?.(); return; }

          const wp = this.cameras.main.getWorldPoint(p.x, p.y);
          const { col, row } = this.screenToGrid(wp.x, wp.y);
          if (!this.inBounds(col, row)) return;
          this.handleClick(col, row);
        }
      }
    });
  }

  /** Context-sensitive cancel/back. ESC and RMB (without drag) trigger this.
   *  `allowMenu` gates opening the game menu when nothing is selected — only ESC
   *  may do that; RMB on empty space should never pop the menu mid-game. */
  private cancelBack(allowMenu = false): void {
    if (this.animating) return;
    const st = this.inputState;
    switch (st.kind) {
      case 'orderMove':
      case 'orderAttack': {
        const unit = this.world.units.get(st.unitId);
        if (unit) this.selectUnit(unit);
        else this.deselect();
        break;
      }
      case 'confirmMove': {
        // RMB on the marker (or anywhere else) should cancel the move and
        // return to the previous unitSelected state with its range still
        // visible — not back to the older orderMove intermediate step.
        const unit = this.world.units.get(st.unitId);
        if (unit) this.selectUnit(unit);
        else this.deselect();
        break;
      }
      case 'confirmAttack': {
        // Same logic as confirmMove — go straight back to unitSelected so
        // the player keeps the unit highlighted and can pick a new target.
        const unit = this.world.units.get(st.unitId);
        if (unit) this.selectUnit(unit);
        else this.deselect();
        break;
      }
      case 'confirmPostMoveAttack': {
        // Cancel post-move attack — return to the post-move chooser.
        const unit = this.world.units.get(st.unitId);
        if (unit) {
          const orders = this.computePostMoveOrders(unit);
          this.inputState = { kind: 'moved', unitId: st.unitId, availableOrders: orders };
          this.refresh();
        } else {
          this.deselect();
        }
        break;
      }
      case 'unitSelected':
        this.deselect();
        break;
      case 'pendingMove':
        this.cancelPendingMove(st);
        break;
      case 'pendingMoveAttack':
        this.reopenPendingMenu(st);
        break;
      case 'moved': {
        // Can't undo move — just wait
        const unit = this.world.units.get(st.unitId);
        if (unit) this.finishUnitTurn(unit);
        this.deselect();
        break;
      }
      case 'movedOrderAttack': {
        const unit = this.world.units.get(st.unitId);
        if (unit) {
          const orders = this.computePostMoveOrders(unit);
          this.inputState = { kind: 'moved', unitId: st.unitId, availableOrders: orders };
        } else {
          this.deselect();
        }
        this.refresh();
        break;
      }
      case 'orderUnload': {
        const unit = this.world.units.get(st.unitId);
        if (!unit) { this.deselect(); break; }
        // If the move is still provisional (unload picked from the post-move
        // menu), reopen that menu so the unit can act again — committing here
        // would strand it (move spent, no action taken). Otherwise just reselect.
        if (st.pending) this.reopenPendingMenu({ unitId: unit.id, ...st.pending });
        else this.selectUnit(unit);
        break;
      }
      case 'orderOfficerAbility': {
        const unit = this.world.units.get(st.unitId);
        if (!unit) { this.deselect(); break; }
        if (st.pending) this.reopenPendingMenu({ unitId: unit.id, ...st.pending });
        else this.selectUnit(unit);
        break;
      }
      case 'buildingSelected':
      case 'tileSelected':
        this.deselect();
        break;
      case 'idle':
        if (this.settingsDialog.isOpen()) this.settingsDialog.close();
        else if (this.menuOverlay) this.closeMenu();
        else if (allowMenu) this.openMenu();
        break;
      default:
        break;
    }
  }

  private handleClick(col: number, row: number): void {
    const pid = currentPlayerId(this.world);
    if (pid !== this.localPlayer) return;

    // A left click starts a fresh interaction — drop any attack-range preview.
    this.attackRangePreviewId = null;

    const st = this.inputState;
    switch (st.kind) {
      case 'idle':
      case 'tileSelected': this.handleIdleClick(col, row); break;
      case 'unitSelected': this.handleUnitSelectedClick(col, row); break;
      case 'orderMove': this.handleOrderMoveClick(col, row); break;
      case 'confirmMove': this.handleConfirmMoveClick(col, row); break;
      case 'orderAttack': this.handleOrderAttackClick(col, row); break;
      case 'confirmAttack': this.handleConfirmAttackClick(col, row); break;
      case 'moved': this.handleMovedClick(col, row); break;
      case 'pendingMoveAttack': this.handlePendingMoveAttackClick(col, row); break;
      case 'movedOrderAttack': this.handleMovedOrderAttackClick(col, row); break;
      case 'confirmPostMoveAttack': this.handleConfirmPostMoveAttackClick(col, row); break;
      case 'orderUnload': this.handleOrderUnloadClick(col, row); break;
      case 'orderOfficerAbility': this.handleOfficerAbilityClick(col, row); break;
      case 'buildingSelected': this.handleBuildingSelectedClick(col, row); break;
    }
  }

  /** Resolve an officer air-strike: deduct gold, damage the 3×3 area, animate. */
  private handleOfficerAbilityClick(col: number, row: number): void {
    const st = this.inputState;
    if (st.kind !== 'orderOfficerAbility') return;
    const unit = this.world.units.get(st.unitId);
    if (!unit) { this.deselect(); return; }
    if (!this.inBounds(col, row)) return;
    const ability = UNIT_DEFS[unit.type].abilities?.find((a) => a.id === st.abilityId);
    if (!ability || !canUseAbility(unit, st.abilityId)) { this.deselect(); return; }
    const player = currentPlayer(this.world);
    if (player.gold < ability.cost) { this.deselect(); return; }

    // Commit the provisional move now (officer holds position), then act.
    if (st.pending) this.commitPendingMove({ unitId: unit.id, cost: st.pending.cost, steps: st.pending.steps });
    player.gold -= ability.cost;

    if (ability.effect === 'recon') {
      executeRecon(unit);
      this.addMessage(t('order.recon'));
      this.deselect();
      // A lone fighter overflies the area and lifts the fog there while it passes.
      this.animating = true;
      void this.playReconFlyover(col, row).then(() => { this.animating = false; });
      return;
    }

    const result = executeAirStrike(this.world, unit, col, row);
    this.addMessage(t('order.airStrike'));
    this.deselect();

    // Three bombers fly across the map in a vic; explosions + casualties resolve
    // as they pass over the target, then they continue off the far edge.
    this.animating = true;
    void this.playAirstrikeFlyover(col, row, result).then(() => { this.animating = false; });
  }

  /** Bombers-in-formation airstrike animation. A vic of three bombers crosses
   *  the whole viewport horizontally; when the lead passes over the target the
   *  3×3 explosions play and the casualties resolve, then the bombers fly on and
   *  off the opposite edge. */
  private async playAirstrikeFlyover(col: number, row: number, result: AirStrikeResult): Promise<void> {
    const target = this.gridToScreen(col, row);
    const view = this.cameras.main.worldView;

    const key = 'dir:plane-bomber:e'; // side profile, nose pointing right (east)
    const haveTex = this.textures.exists(key);
    const planeH = HH * 3.4; // a touch larger than the map sprite, for drama
    const scale = haveTex
      ? planeH / (this.textures.get(key).getSourceImage() as { height: number }).height
      : 1;
    const back = planeH * 0.95;   // wingmen trail this far behind the lead
    const spread = planeH * 0.75; // …and sit this far above / below it
    const altitude = HH * 2.6;    // bombers fly above the ground tiles
    const baseY = target.y - altitude;
    const margin = planeH * 3 + back;
    const startX = view.x - margin;
    const endX = view.x + view.width + margin;

    // Vic formation: lead at the apex, two wingmen trailing left + above/below.
    const formation = [{ dx: 0, dy: 0 }, { dx: -back, dy: -spread }, { dx: -back, dy: spread }];
    const bombers = formation.map((f) => {
      const spr = haveTex
        ? this.add.sprite(startX + f.dx, baseY + f.dy, key).setScale(scale)
        : this.add.rectangle(startX + f.dx, baseY + f.dy, planeH * 1.6, planeH * 0.5, 0x3a3a30);
      spr.setDepth(99999); // above the explosions (99998)
      return spr;
    });

    const duration = 2600;
    const span = endX - startX;
    const flights = bombers.map((spr, i) => this.battleTween({
      targets: spr, x: endX + formation[i].dx, duration, ease: 'Linear',
    }));

    // Linear flight → the lead is over screen-x `x` at this time. Each tile's
    // bomb lands as the lead passes over it, so the 3×3 detonates as a sweep
    // following the formation rather than all at once.
    const timeAtX = (x: number) => Phaser.Math.Clamp((duration * (x - startX)) / span, 0, duration);
    let lastT = 0;
    for (const dc of [-1, 0, 1]) {
      for (const dr of [-1, 0, 1]) {
        const p = this.gridToScreen(col + dc, row + dr);
        const t = timeAtX(p.x);
        lastT = Math.max(lastT, t);
        this.time.delayedCall(t, () => { void this.playExplosion(p.x, p.y); });
      }
    }
    // Resolve casualties + victory once the sweep's last bomb has landed.
    this.time.delayedCall(lastT, () => {
      if (this.runner) {
        for (const id of result.killed) this.runner.onUnitKilled(id);
      }
      checkElimination(this.world);
      const winner = checkVictory(this.world);
      this.fullRefresh();
      if (winner != null) this.showVictory(winner);
    });

    await Promise.all(flights);
    bombers.forEach((b) => b.destroy());
  }

  /** Air-recon animation. A single fighter crosses the viewport horizontally;
   *  while it passes over the indicated area the fog there is lifted (enemies
   *  become visible), and it closes back in once the fighter has gone by. */
  private async playReconFlyover(col: number, row: number): Promise<void> {
    const target = this.gridToScreen(col, row);
    const view = this.cameras.main.worldView;
    const radius = RECON_RADIUS;

    const planeH = HH * 2.8;
    let plane: Phaser.GameObjects.Sprite | Phaser.GameObjects.Rectangle;
    if (this.textures.exists('plane-fighter-prop') && this.anims.exists('fighter-prop')) {
      // Animated propeller sheet: side view facing LEFT → flip to face east.
      const s = this.add.sprite(0, 0, 'plane-fighter-prop');
      s.setScale(planeH / s.height);
      s.setFlipX(true);
      s.play('fighter-prop');
      plane = s;
    } else if (this.textures.exists('dir:plane-fighter:e')) {
      const s = this.add.sprite(0, 0, 'dir:plane-fighter:e');
      s.setScale(planeH / (this.textures.get('dir:plane-fighter:e').getSourceImage() as { height: number }).height);
      plane = s;
    } else {
      plane = this.add.rectangle(0, 0, planeH * 1.5, planeH * 0.45, 0x9aa0a6);
    }
    plane.setDepth(99999);

    const altitude = HH * 2.6;
    const baseY = target.y - altitude;
    const margin = planeH * 3;
    const startX = view.x - margin;
    const endX = view.x + view.width + margin;
    const span = endX - startX;
    plane.setPosition(startX, baseY);

    const duration = 2600;
    const flight = this.battleTween({ targets: plane, x: endX, duration, ease: 'Linear' });

    // Moving reveal window: each fogged tile is uncovered as the fighter reaches
    // its screen-x and re-fogged once the fighter has flown `trail` px past it,
    // so a band of clear sky follows the plane across the area (no `explored`
    // change → the fog fully returns afterwards).
    const timeAtX = (x: number) => Phaser.Math.Clamp((duration * (x - startX)) / span, 0, duration);
    const lead = TILE_W * 0.5;  // open a touch before the plane is overhead
    const trail = TILE_W * 2.5; // …and close this far behind it
    const tempRevealed = new Set<string>();
    for (let dc = -radius; dc <= radius; dc++) {
      for (let dr = -radius; dr <= radius; dr++) {
        const c = col + dc, r = row + dr;
        if (!this.inBounds(c, r)) continue;
        const key = `${c},${r}`;
        if (this.playerVisible.has(key)) continue; // already seen for real
        const tx = this.gridToScreen(c, r).x;
        const tReveal = timeAtX(tx - lead);
        const tHide = Math.max(tReveal + 1, timeAtX(tx + trail));
        this.time.delayedCall(tReveal, () => {
          if (this.playerVisible.has(key)) return;
          this.playerVisible.add(key);
          tempRevealed.add(key);
          this.drawFogDelta([key], []);
          this.syncEntities(); // spawn enemies/buildings now in sight
        });
        this.time.delayedCall(tHide, () => {
          if (!tempRevealed.has(key)) return;
          this.playerVisible.delete(key);
          tempRevealed.delete(key);
          this.drawFogDelta([], [key]);
          this.syncEntities(); // re-fog as the plane moves on
        });
      }
    }

    await flight;
    plane.destroy();
    // Safety net: re-fog anything the moving window left open, then repaint HUD
    // (spent gold) + the officer's spent-turn state.
    if (tempRevealed.size) {
      const rest = [...tempRevealed];
      for (const k of rest) this.playerVisible.delete(k);
      this.drawFogDelta([], rest);
      tempRevealed.clear();
    }
    this.fullRefresh();
  }

  private handleIdleClick(col: number, row: number): void {
    const unit = this.getUnitAt(col, row);
    // Acted units are still selectable — selecting them just won't offer any
    // orders, but the player can still inspect their info / hp / supply.
    if (unit && unit.owner === this.localPlayer) {
      this.selectUnit(unit); return;
    }
    const building = getBuildingAt(this.world, col, row);
    if (building) {
      // Tutorial blocking for building selection
      if (this.runner && !this.runner.isActionAllowed({ type: 'select-building', buildingId: building.id })) return;
      // Tutorial advancement for building selection
      if (this.runner) {
        this.runner.onActionCompleted({ type: 'select-building', buildingId: building.id });
        this.processRunnerActions();
      }
      this.inputState = { kind: 'buildingSelected', buildingId: building.id };
      // Building selection SFX
      const bType = building.type;
      if (bType === 'barracks') audio.fileSfx.play('select-barracks');
      else if (bType === 'vehicle-factory') audio.fileSfx.play('select-factory');
      else if (bType === 'port') audio.fileSfx.play('select-shipyard');
      this.refresh(); return;
    }
    // Select empty tile to show terrain info
    this.inputState = { kind: 'tileSelected', col, row };
    this.refresh();
  }

  private handleUnitSelectedClick(col: number, row: number): void {
    // LMB is selection/deselection only — orders are issued with RMB.
    // Clicking another own unit switches selection; a building selects it;
    // empty terrain deselects (shows tile info).
    this.handleIdleClick(col, row);
  }

  /**
   * Issue an order for the selected unit at (col,row). RMB-driven:
   *  - RMB on a reachable tile → provisional move + context menu.
   *  - RMB on a friendly carrier (loadable, in range) → move there and load.
   *  - RMB on an in-range enemy → "Atak / Anuluj" menu.
   *  - RMB on a visible enemy out of range → flash feedback.
   * Returns true if an order (or feedback) was issued.
   */
  private tryIssueOrder(col: number, row: number): boolean {
    const st = this.inputState;
    if (st.kind !== 'unitSelected') return false;
    const unit = this.world.units.get(st.unitId);
    if (!unit) return false;
    // One order per unit per turn: a spent unit can be inspected but issues no
    // further orders, even if it has leftover movement points.
    if (unit.acted) return false;

    const target = this.getUnitAt(col, row);
    const key = `${col},${row}`;
    // RMB on the unit's own tile → open the order menu in place, without moving
    // (Advance Wars "wait here": capture/heal/build/attack from the current tile).
    if (col === unit.col && row === unit.row) {
      this.beginPendingMove(unit, col, row, []);
      return true;
    }
    // Load onto a friendly carrier — its tile is a reachable "loadable" tile.
    if (target && target.owner === this.localPlayer && target.id !== unit.id
        && st.range.has(key) && canLoadInto(unit, target)) {
      const path = findPath(this.world, unit, { col, row }, st.zoc, this.playerVisible);
      if (path) { this.beginPendingMove(unit, col, row, path); return true; }
    }
    // Attack an in-range enemy.
    if (target && target.owner !== this.localPlayer) {
      if (canAttack(unit, target) && this.computeAttackableEnemies(unit).has(target.id)) {
        this.openAttackMenu(unit, target); return true;
      }
      this.flashOutOfRange(target); return true;
    }
    // Move to a reachable tile.
    if (st.range.has(key)) {
      const path = findPath(this.world, unit, { col, row }, st.zoc, this.playerVisible);
      if (path) { this.beginPendingMove(unit, col, row, path); return true; }
    }
    return false;
  }

  /** Show the "Atak / Anuluj" menu for attacking `enemy` from the current tile. */
  private openAttackMenu(unit: Unit, enemy: Unit): void {
    this.showContextMenu(enemy.id, [
      { label: t('order.attack'), color: '#f7768e', icon: ORDER_MENU_ICONS.attack, onClick: () => {
        this.hideContextMenu();
        if (this.runner && !this.runner.isActionAllowed({ type: 'attack', unitId: unit.id, targetId: enemy.id })) return;
        this.executeAttack(unit, enemy);
      } },
      { label: t('order.cancel'), icon: ORDER_MENU_ICONS.cancel, onClick: () => { this.hideContextMenu(); } },
    ], () => { this.hideContextMenu(); });
  }

  /**
   * Briefly flash an enemy unit's icon red + slight shrink, then back to
   * normal. Used to acknowledge an RMB click on an enemy outside the
   * selected unit's attack range — input state is unchanged.
   */
  private flashOutOfRange(enemy: Unit): void {
    const icon = this.unitIconImages.get(enemy.id);
    if (!icon) return;
    this.tweens.killTweensOf(icon);
    icon.setTint(0xff3030);
    this.tweens.add({
      targets: icon,
      scale: { from: icon.scale * 0.85, to: icon.scale },
      duration: 180,
      ease: 'Sine.easeOut',
      onComplete: () => icon.clearTint(),
    });
    this.requestRedraw();
  }

  /**
   * Update the canvas cursor to reflect what an RMB click on the hovered
   * tile would do while a unit is selected. Outside `unitSelected` the
   * cursor stays default — selection-mode UX hint only.
   */
  // Custom cursors (hotspot ≈ image centre). RMB on the hovered tile would
  // perform the hinted action.
  private static readonly CURSOR_ATTACK = "url('/graphics/attack-cursor.png') 16 16, crosshair";
  private static readonly CURSOR_MOVE = "url('/graphics/move-cursor.png') 16 16, pointer";
  private static readonly CURSOR_UNLOAD = "url('/graphics/unload-cursor.png') 16 24, copy";

  private updateMapCursor(col: number, row: number): void {
    const canvas = this.game.canvas;
    if (!canvas) return;
    const st = this.inputState;
    let cur = '';
    if (st.kind === 'unitSelected') {
      cur = 'default';
      // Only hint movement while a unit is merely selected. Attacking is a
      // separate order (chosen from the context menu after moving, or in the
      // dedicated post-move attack mode), so we deliberately do NOT show the
      // attack crosshair on passive enemy hover — it misled players into
      // thinking the hover itself had armed an attack. Attackable enemies are
      // already signalled by the white blink in drawHighlights.
      if (this.inBounds(col, row) && st.range.has(`${col},${row}`)) {
        cur = TurnGameScene.CURSOR_MOVE;
      }
    } else if (st.kind === 'pendingMoveAttack') {
      cur = 'default';
      if (this.inBounds(col, row)) {
        const enemyHere = this.getUnitAt(col, row);
        if (enemyHere && st.attackableEnemies.has(enemyHere.id)) cur = TurnGameScene.CURSOR_ATTACK;
      }
    } else if (st.kind === 'orderUnload') {
      cur = 'default';
      if (this.inBounds(col, row) && st.unloadTiles.some((tl) => tl.col === col && tl.row === row)) {
        cur = TurnGameScene.CURSOR_UNLOAD;
      }
    } else if (st.kind === 'orderOfficerAbility') {
      cur = this.inBounds(col, row) ? TurnGameScene.CURSOR_ATTACK : 'default';
    }
    if (canvas.style.cursor !== cur) canvas.style.cursor = cur;
  }

  /** Run the attack immediately: animate, resolve combat, post-actions. */
  private executeAttack(unit: Unit, enemy: Unit): void {
    this.lastCommandPos = { col: unit.col, row: unit.row };
    const msg = t('msg.attacks', t(`unit.${unit.type}`), t(`unit.${enemy.type}`));
    if (this.runner) {
      this.runner.onActionCompleted({ type: 'attack', unitId: unit.id, targetId: enemy.id });
    }
    this.animateCombat(unit, enemy, () => {
      this.addMessage(msg);
      this.deselect();
      this.fullRefresh();
    });
  }


  /** Run the move immediately: animate, then handle carrier load / post-move
   *  chooser / movePoints + supply cost. Extracted from the former two-step
   *  confirm flow so RMB can dispatch a single action. */
  private executeMove(unit: Unit, targetCol: number, targetRow: number, path: Coord[]): void {
    this.lastCommandPos = { col: targetCol, row: targetRow };
    const pathCopy = [...path];
    const fromPos = { col: unit.col, row: unit.row };
    this.animateMove(unit, pathCopy, () => {
      this.recomputeVisible();

      if (this.runner) {
        this.runner.onActionCompleted({ type: 'move', unitId: unit.id, to: { col: targetCol, row: targetRow } });
        this.runner.onUnitMoved(unit.id, fromPos, { col: targetCol, row: targetRow });
        if (this.processRunnerActions()) return;
      }

      // Carrier auto-load — same destination as a friendly carrier.
      const carrier = [...this.world.units.values()].find(
        u => u.col === targetCol && u.row === targetRow && u.id !== unit.id,
      );
      if (carrier && canLoadInto(unit, carrier)) {
        executeLoad(this.world, unit, carrier);
        if (this.runner) {
          this.runner.onActionCompleted({ type: 'load', unitId: unit.id, carrierId: carrier.id });
          if (this.processRunnerActions()) return;
        }
        this.addMessage(t('msg.loaded', t(`unit.${unit.type}`), t(`unit.${carrier.type}`)));
        this.syncEntities();
        this.deselect();
        this.fullRefresh();
        return;
      }

      // Subtract movement cost + supply for the path.
      const def = UNIT_DEFS[unit.type];
      let cost = 0;
      for (const step of pathCopy) {
        const terrain = this.world.map.tiles[step.row][step.col].type;
        cost += getMovementCost(terrain, def.domain, unit.type);
      }
      unit.movePoints = Math.max(0, Math.floor(unit.movePoints - cost));
      consumeMoveSupply(unit, pathCopy.length);

      // Air/water units that hit fuel=0 mid-move are destroyed immediately
      // (proposal §3 — plane crashes, ship sinks). Land units stay alive
      // but immobilised; the end-of-turn flow handles them next.
      if (def.maxFuel > 0 && unit.fuel <= 0 && def.domain !== 'land') {
        const pos = this.gridToScreen(unit.col, unit.row);
        const msgKey = def.domain === 'air' ? 'msg.plane.outOfFuel' : 'msg.ship.outOfFuel';
        this.addMessage(t(msgKey, t(`unit.${unit.type}`)));
        this.world.units.delete(unit.id);
        if (this.runner) this.runner.onUnitKilled(unit.id);
        void this.playExplosion(pos.x, pos.y).then(() => {
          this.deselect();
          this.fullRefresh();
        });
        return;
      }

      // Stay selected when there's still juice in the unit; otherwise show
      // the post-move chooser or end its turn.
      if (unit.movePoints > 0) {
        this.selectUnit(unit);
      } else {
        const orders = this.computePostMoveOrders(unit);
        if (orders.length > 0) {
          this.inputState = { kind: 'moved', unitId: unit.id, availableOrders: orders };
        } else {
          this.finishUnitTurn(unit);
          this.deselect();
        }
      }
      this.fullRefresh();
    });
  }

  private handleOrderMoveClick(col: number, row: number): void {
    const st = this.inputState as { kind: 'orderMove'; unitId: number; range: Map<string, number>; zoc: Set<string> };
    const unit = this.world.units.get(st.unitId);
    if (!unit) { this.deselect(); return; }

    const key = `${col},${row}`;
    if (st.range.has(key)) {
      const path = findPath(this.world, unit, { col, row }, st.zoc, this.playerVisible);
      if (path) {
        if (this.runner && !this.runner.isActionAllowed({ type: 'move', unitId: unit.id, to: { col, row } })) return;
        this.executeMove(unit, col, row, path);
      }
    }
    // Click outside range — ignore
  }

  private handleConfirmMoveClick(col: number, row: number): void {
    const st = this.inputState as { kind: 'confirmMove'; unitId: number; range: Map<string, number>; zoc: Set<string>; targetCol: number; targetRow: number; path: Coord[] };
    const unit = this.world.units.get(st.unitId);
    if (!unit) { this.deselect(); return; }

    if (col === st.targetCol && row === st.targetRow) {
      // Tutorial blocking for move
      if (this.runner && !this.runner.isActionAllowed({ type: 'move', unitId: unit.id, to: { col: st.targetCol, row: st.targetRow } })) return;
      // Confirm — animate move, then subtract movePoints
      this.lastCommandPos = { col: st.targetCol, row: st.targetRow };
      const pathCopy = [...st.path];
      const fromPos = { col: unit.col, row: unit.row };
      this.animateMove(unit, pathCopy, () => {
        // Recompute fog after movement
        this.recomputeVisible();

        // Tutorial advancement for move
        if (this.runner) {
          this.runner.onActionCompleted({ type: 'move', unitId: unit.id, to: { col: st.targetCol, row: st.targetRow } });
        }

        // Notify scenario runner of movement
        if (this.runner) {
          this.runner.onUnitMoved(unit.id, fromPos, { col: st.targetCol, row: st.targetRow });
          if (this.processRunnerActions()) return;
        }

        // Check if target tile has a friendly carrier — auto-load
        const carrier = [...this.world.units.values()].find(
          u => u.col === st.targetCol && u.row === st.targetRow && u.id !== unit.id,
        );
        if (carrier && canLoadInto(unit, carrier)) {
          executeLoad(this.world, unit, carrier);
          if (this.runner) {
            this.runner.onActionCompleted({ type: 'load', unitId: unit.id, carrierId: carrier.id });
            if (this.processRunnerActions()) return;
          }
          this.addMessage(t('msg.loaded', t(`unit.${unit.type}`), t(`unit.${carrier.type}`)));
          this.syncEntities();
          this.deselect();
          this.fullRefresh();
          return;
        }

        // Subtract movement cost for the path
        const def = UNIT_DEFS[unit.type];
        let cost = 0;
        for (const step of pathCopy) {
          const terrain = this.world.map.tiles[step.row][step.col].type;
          cost += getMovementCost(terrain, def.domain, unit.type);
        }
        unit.movePoints = Math.max(0, Math.floor(unit.movePoints - cost));

        // Consume supply for movement (per tile)
        consumeMoveSupply(unit, pathCopy.length);

        // Re-select unit if it still has move points or can do actions
        if (unit.movePoints > 0) {
          this.selectUnit(unit);
        } else {
          const orders = this.computePostMoveOrders(unit);
          if (orders.length > 0) {
            this.inputState = { kind: 'moved', unitId: unit.id, availableOrders: orders };
          } else {
            this.finishUnitTurn(unit);
            this.deselect();
          }
        }
        this.fullRefresh();
      });
      return;
    }

    // The green ✓ marker handles its own confirmation clicks. Any LMB tap
    // that reaches us means the player clicked the map, not the marker —
    // treat it as "cancel the current move", with two convenience cases:
    //  - clicking another own unit switches selection to it
    //  - clicking a building selects it (mirroring the idle-click flow)
    // Otherwise we re-select the current unit so its range stays visible.
    const otherUnit = this.getUnitAt(col, row);
    if (otherUnit && otherUnit.owner === this.localPlayer && otherUnit.id !== unit.id) {
      this.selectUnit(otherUnit);
      return;
    }
    const bld = getBuildingAt(this.world, col, row);
    if (bld) {
      this.inputState = { kind: 'buildingSelected', buildingId: bld.id };
      this.refresh();
      return;
    }
    this.selectUnit(unit);
  }

  private handleOrderAttackClick(col: number, row: number): void {
    const st = this.inputState as { kind: 'orderAttack'; unitId: number; attackableEnemies: Set<number>; attackRangeKeys: string[] };
    const unit = this.world.units.get(st.unitId);
    if (!unit) { this.deselect(); return; }

    const target = this.getUnitAt(col, row);
    if (target && st.attackableEnemies.has(target.id)) {
      if (this.runner && !this.runner.isActionAllowed({ type: 'attack', unitId: unit.id, targetId: target.id })) return;
      this.executeAttack(unit, target);
    }
  }

  private handleConfirmAttackClick(col: number, row: number): void {
    const st = this.inputState as { kind: 'confirmAttack'; unitId: number; targetEnemyId: number };
    const unit = this.world.units.get(st.unitId);
    if (!unit) { this.deselect(); return; }
    const enemy = this.world.units.get(st.targetEnemyId);
    if (!enemy) { this.deselect(); return; }

    // Click on same enemy tile → confirm attack (fallback for clicks that
    // landed on the tile but missed the marker hitbox).
    if (col === enemy.col && row === enemy.row) {
      // Tutorial blocking for attack
      if (this.runner && !this.runner.isActionAllowed({ type: 'attack', unitId: unit.id, targetId: enemy.id })) return;
      this.lastCommandPos = { col: unit.col, row: unit.row };
      const msg = t('msg.attacks', t(`unit.${unit.type}`), t(`unit.${enemy.type}`));
      // Tutorial advancement for attack
      if (this.runner) {
        this.runner.onActionCompleted({ type: 'attack', unitId: unit.id, targetId: enemy.id });
      }
      this.animateCombat(unit, enemy, () => {
        this.addMessage(msg);
        this.deselect(); this.fullRefresh();
      });
      return;
    }

    // Any other LMB click cancels the attack order — mirrors the move flow.
    // Convenience: clicking another own unit / a building selects it.
    const otherUnit = this.getUnitAt(col, row);
    if (otherUnit && otherUnit.owner === this.localPlayer && otherUnit.id !== unit.id) {
      this.selectUnit(otherUnit);
      return;
    }
    const bld = getBuildingAt(this.world, col, row);
    if (bld) {
      this.inputState = { kind: 'buildingSelected', buildingId: bld.id };
      this.refresh();
      return;
    }
    this.selectUnit(unit);
  }

  private handleMovedClick(col: number, row: number): void {
    const st = this.inputState as { kind: 'moved'; unitId: number };
    const unit = this.world.units.get(st.unitId);
    if (!unit) { this.deselect(); return; }

    const target = this.getUnitAt(col, row);

    // Clicking another own unit → finish current, select new
    if (target && target.owner === this.localPlayer && !target.acted && target.id !== unit.id) {
      this.finishUnitTurn(unit);
      this.selectUnit(target);
      return;
    }

    // Clicking enemy in attack range → execute attack immediately.
    if (target && target.owner !== unit.owner && canAttack(unit, target)) {
      const enemies = this.computePostMoveAttackableEnemies(unit);
      if (enemies.has(target.id)) {
        if (this.runner && !this.runner.isActionAllowed({ type: 'attack', unitId: unit.id, targetId: target.id })) return;
        this.executeAttack(unit, target);
        return;
      }
    }

    // Clicking empty ground / building / tile → finish unit, deselect
    this.finishUnitTurn(unit);
    this.handleIdleClick(col, row);
  }

  private handleMovedOrderAttackClick(col: number, row: number): void {
    const st = this.inputState as { kind: 'movedOrderAttack'; unitId: number; attackableEnemies: Set<number> };
    const unit = this.world.units.get(st.unitId);
    if (!unit) { this.deselect(); return; }

    const target = this.getUnitAt(col, row);
    if (target && st.attackableEnemies.has(target.id)) {
      if (this.runner && !this.runner.isActionAllowed({ type: 'attack', unitId: unit.id, targetId: target.id })) return;
      this.executeAttack(unit, target);
    }
  }

  private handleConfirmPostMoveAttackClick(col: number, row: number): void {
    const st = this.inputState as { kind: 'confirmPostMoveAttack'; unitId: number; targetEnemyId: number };
    const unit = this.world.units.get(st.unitId);
    if (!unit) { this.deselect(); return; }
    const enemy = this.world.units.get(st.targetEnemyId);
    if (!enemy) { this.deselect(); return; }

    if (col === enemy.col && row === enemy.row) {
      // Tutorial blocking for post-move attack
      if (this.runner && !this.runner.isActionAllowed({ type: 'attack', unitId: unit.id, targetId: enemy.id })) return;
      this.lastCommandPos = { col: unit.col, row: unit.row };
      const msg = t('msg.attacks', t(`unit.${unit.type}`), t(`unit.${enemy.type}`));
      // Tutorial advancement for post-move attack
      if (this.runner) {
        this.runner.onActionCompleted({ type: 'attack', unitId: unit.id, targetId: enemy.id });
      }
      this.animateCombat(unit, enemy, () => {
        this.addMessage(msg);
        this.deselect(); this.fullRefresh();
      });
      return;
    }

    // Any other LMB click cancels — return to `moved` (post-move chooser).
    const otherUnit = this.getUnitAt(col, row);
    if (otherUnit && otherUnit.owner === this.localPlayer && otherUnit.id !== unit.id) {
      this.selectUnit(otherUnit);
      return;
    }
    const bld = getBuildingAt(this.world, col, row);
    if (bld) {
      this.inputState = { kind: 'buildingSelected', buildingId: bld.id };
      this.refresh();
      return;
    }
    const orders = this.computePostMoveOrders(unit);
    this.inputState = { kind: 'moved', unitId: st.unitId, availableOrders: orders };
    this.refresh();
  }

  private handleOrderUnloadClick(col: number, row: number): void {
    const st = this.inputState as { kind: 'orderUnload'; unitId: number; cargoIndex: number; unloadTiles: { col: number; row: number }[]; pending?: { originCol: number; originRow: number; cost: number; steps: number } };
    const unit = this.world.units.get(st.unitId);
    if (!unit) { this.deselect(); return; }

    const isValid = st.unloadTiles.some(t => t.col === col && t.row === row);
    if (isValid) {
      // The move was kept provisional while picking a tile — commit it now.
      if (st.pending) this.commitPendingMove({ unitId: unit.id, cost: st.pending.cost, steps: st.pending.steps });
      executeUnload(this.world, unit, col, row, st.cargoIndex);
      if (this.runner) {
        this.runner.onActionCompleted({ type: 'unload', carrierId: unit.id });
        this.processRunnerActions();
      }
      this.recomputeVisible();
      this.addMessage(t('msg.unloads', t(`unit.${unit.type}`)));

      // Unloading ends the carrier's turn — a unit moves/acts only once, so it
      // must NOT be re-selectable for another move after dropping cargo.
      if (this.world.units.has(unit.id)) {
        this.finishUnitTurn(unit);
      }
      this.deselect();
      // Refresh first so syncEntities creates the new unit sprite + icon at correct pos
      this.fullRefresh();

      // Animate the icon appearing (don't animate Graphics — it draws at absolute
      // coords so scaling it from 0 makes it fly from (0,0) to the real position)
      const newUnit = this.getUnitAt(col, row);
      if (newUnit) {
        const newIcon = this.unitIconImages.get(newUnit.id);
        if (newIcon) {
          const targetSX = newIcon.scaleX;
          const targetSY = newIcon.scaleY;
          newIcon.setScale(0);
          this.tweens.add({ targets: newIcon, scaleX: targetSX, scaleY: targetSY, duration: 200, ease: 'Back.easeOut' });
        }
      }
      return;
    }

    // Click outside valid tiles — cancel back to unit selection
    this.cancelBack();
  }

  private handleBuildingSelectedClick(col: number, row: number): void {
    const b = this.world.buildings.get((this.inputState as { buildingId: number }).buildingId);
    if (!b || (col !== b.col || row !== b.row)) {
      this.handleIdleClick(col, row); return;
    }
    this.deselect();
  }

  private selectUnit(unit: Unit): void {
    // Tutorial blocking for unit selection
    if (this.runner && !this.runner.isActionAllowed({ type: 'select-unit', unitId: unit.id })) return;
    // Tutorial advancement for unit selection
    if (this.runner) {
      this.runner.onActionCompleted({ type: 'select-unit', unitId: unit.id });
      this.processRunnerActions();
    }
    const zoc = computeZocTiles(this.world, unit, this.playerVisible);
    // A spent unit (already acted this turn) shows no movement range — it can
    // be selected for info only, never re-ordered.
    const range = unit.acted ? new Map<string, number>() : getMovementRange(this.world, unit, zoc, this.playerVisible);
    const orders = this.computeAvailableOrders(unit, range);
    this.inputState = { kind: 'unitSelected', unitId: unit.id, range, zoc, availableOrders: orders };
    audio.fileSfx.playUnitSfx(unit.type, 'selected', this.factionOf(unit));
    this.refresh();
  }

  private deselect(): void {
    this.hideContextMenu();
    this.attackRangePreviewId = null;
    this.abilityHoverTile = null;
    this.inputState = { kind: 'idle' };
    if (this.game.canvas) this.game.canvas.style.cursor = '';
    this.refresh();
  }

  /** Finalize a unit's turn — mark it spent and play its order-confirm voice
   *  ONCE, when the unit is "done" (not during the move). Use at move / wait /
   *  end-orders finalizations; attack and capture already bark at their own. */
  private finishUnitTurn(unit: Unit): void {
    unit.acted = true;
    unit.movePoints = 0;
    audio.fileSfx.playUnitSfx(unit.type, 'move', this.factionOf(unit));
  }

  // ── In-world context menu (Advance Wars-style order chooser) ──────────────

  /** Convert iso world coordinates to page (client) pixels for DOM overlays. */
  private worldToPage(worldX: number, worldY: number): { x: number; y: number } {
    const cam = this.cameras.main;
    const rect = this.game.canvas.getBoundingClientRect();
    return {
      x: rect.left + (worldX - cam.worldView.x) * cam.zoom,
      y: rect.top + (worldY - cam.worldView.y) * cam.zoom,
    };
  }

  /** Show a small context menu next to `unitId` with the given items. Clicking
   *  away or right-clicking invokes `onDismiss`. */
  private showContextMenu(
    unitId: number,
    items: CtxMenuItem[],
    onDismiss: () => void,
  ): void {
    this.hideContextMenu();
    const unit = this.world.units.get(unitId);
    if (!unit) return;
    const w = this.gridToScreen(unit.col, unit.row);
    const p = this.worldToPage(w.x, w.y);

    const menu = document.createElement('div');
    menu.style.cssText = [
      'position:fixed', `left:${Math.round(p.x + 24)}px`, `top:${Math.round(p.y - 20)}px`,
      'z-index:120', 'display:flex', 'flex-direction:column', 'gap:3px',
      'background:rgba(20,24,33,0.96)', 'border:1px solid #3a4252', 'border-radius:8px',
      'padding:5px', 'box-shadow:0 6px 18px rgba(0,0,0,0.55)', 'min-width:128px',
      "font-family:inherit",
    ].join(';');
    menu.addEventListener('pointerdown', (e) => e.stopPropagation());
    menu.addEventListener('pointerup', (e) => e.stopPropagation());

    for (const it of items) {
      if (it.divider) {
        const sep = document.createElement('div');
        sep.style.cssText = 'height:1px;margin:3px 6px;background:#3a4252';
        menu.appendChild(sep);
        continue;
      }
      const btn = document.createElement('button');
      const enabled = it.enabled !== false;
      const labelColor = enabled ? (it.color ?? '#e6e8ec') : '#5a6273';
      // Custom html (e.g. a cargo unit row) overrides the icon+label layout.
      // Otherwise: framed icon (recessed slot) + label, with a same-width spacer
      // when the order has no icon, so every entry lines up.
      btn.innerHTML = it.html
        ?? ((it.icon ? menuIconSlot(it.icon) : '<span style="flex:0 0 auto;width:32px"></span>')
          + `<span style="flex:1 1 auto;color:${labelColor}">${it.label}</span>`);
      btn.style.cssText = [
        'display:flex', 'align-items:center', 'gap:9px',
        'text-align:left', 'padding:5px 10px', 'border-radius:5px', 'border:none',
        'font-size:15px', 'font-weight:600', 'cursor:' + (enabled ? 'pointer' : 'default'),
        'background:' + (enabled ? 'rgba(255,255,255,0.06)' : 'transparent'),
        'color:' + labelColor,
        'opacity:' + (enabled ? '1' : '0.5'),
      ].join(';');
      if (enabled) {
        btn.addEventListener('mouseenter', () => { btn.style.background = 'rgba(255,255,255,0.16)'; });
        btn.addEventListener('mouseleave', () => { btn.style.background = 'rgba(255,255,255,0.06)'; });
        btn.addEventListener('click', () => { it.onClick?.(); });
      }
      menu.appendChild(btn);
    }

    document.body.appendChild(menu);
    this.contextMenu = menu;
    this.contextMenuDismiss = onDismiss;
  }

  private hideContextMenu(): void {
    if (this.contextMenu) { this.contextMenu.remove(); this.contextMenu = null; }
    this.contextMenuDismiss = null;
  }

  /** Available post-move orders at the unit's (provisional) position. */
  private pendingMoveItems(unit: Unit): CtxMenuItem[] {
    const items: CtxMenuItem[] = [];
    const commit = (run: () => void) => {
      this.hideContextMenu();
      const st = this.inputState;
      if (st.kind === 'pendingMove') this.commitPendingMove(st);
      run();
    };
    // Moved onto a friendly carrier → the only action is to confirm loading
    // (or cancel). Loading is the commit; no other order applies here.
    const carrier = this.loadableCarrierAt(unit);
    if (carrier) {
      items.push({ label: t('order.load'), color: '#7dcfff', icon: ORDER_MENU_ICONS.load, onClick: () => {
        this.hideContextMenu();
        this.doLoad(unit, carrier);
      } });
      items.push({ label: t('order.cancel'), color: '#f7768e', icon: ORDER_MENU_ICONS.cancel, onClick: () => {
        const st = this.inputState;
        if (st.kind === 'pendingMove') this.cancelPendingMove(st);
      } });
      return items;
    }
    // Attack (post-move) — excluded for indirect units that have moved (canAttack guards it).
    if (UNIT_DEFS[unit.type].attackRange > 0) {
      const enemies = this.computePostMoveAttackableEnemies(unit);
      if (enemies.size > 0) {
        // Enter target-picking WITHOUT committing the move yet, so a misclick
        // can reopen this menu and the move stays cancellable until the attack.
        items.push({ label: t('order.attack'), color: '#f7768e', icon: ORDER_MENU_ICONS.attack, onClick: () => {
          this.hideContextMenu();
          const s = this.inputState;
          if (s.kind !== 'pendingMove') return;
          this.inputState = {
            kind: 'pendingMoveAttack', unitId: unit.id,
            originCol: s.originCol, originRow: s.originRow, cost: s.cost, steps: s.steps,
            attackableEnemies: this.computePostMoveAttackableEnemies(unit),
          };
          this.refresh();
        } });
      }
    }
    if (canUnitCapture(unit) && canCapture(this.world, unit) != null) {
      items.push({ label: t('order.capture'), color: '#e0af68', icon: ORDER_MENU_ICONS.capture, onClick: () => commit(() => this.doCapture(unit)) });
    }
    const baseType = resolveBaseType(unit.type);
    if (baseType === 'medic' && computeHealTargets(this.world, unit.id).length > 0) {
      items.push({ label: t('order.heal'), color: '#9ece6a', icon: ORDER_MENU_ICONS.heal, onClick: () => commit(() => this.doHeal(unit)) });
    }
    // Resupply — any mobile resupplier with matching-domain targets adjacent.
    if (resolveResupplyDomain(unit.type) !== null && computeResupplyTargets(this.world, unit.id).length > 0) {
      items.push({ label: t('order.resupply'), color: '#9ece6a', icon: ORDER_MENU_ICONS.resupply, onClick: () => commit(() => this.doResupply(unit)) });
    }
    // Build (bunkers) — supply-truck only.
    if (baseType === 'supply-truck' && canBuildOrContinue(this.world, unit)) {
      items.push({ label: t('order.build'), color: '#7aa2f7', icon: ORDER_MENU_ICONS.build, onClick: () => commit(() => this.doBuild(unit)) });
    }
    // Air strike — officer ability. Shown with remaining cooldown; enabled only
    // when off cooldown and affordable. Picking it commits the move and enters
    // a tile-targeting state for the 3×3 impact area.
    const airStrike = UNIT_DEFS[unit.type].abilities?.find((a) => a.effect === 'air-strike');
    if (airStrike) {
      const cd = unit.cooldownState?.[airStrike.id] ?? 0;
      const ready = canUseAbility(unit, airStrike.id);
      const affordable = currentPlayer(this.world).gold >= airStrike.cost;
      const label = ready ? `${t('order.airStrike')} (${airStrike.cost})` : `${t('order.airStrike')} (${cd})`;
      items.push({
        label, color: '#f7768e', icon: ORDER_MENU_ICONS.attack, enabled: ready && affordable,
        onClick: () => {
          this.hideContextMenu();
          const s = this.inputState;
          // Keep the move provisional so cancelling reopens the menu intact.
          const pending = s.kind === 'pendingMove'
            ? { originCol: s.originCol, originRow: s.originRow, cost: s.cost, steps: s.steps }
            : undefined;
          this.inputState = { kind: 'orderOfficerAbility', unitId: unit.id, abilityId: airStrike.id, pending };
          this.refresh();
        },
      });
    }
    // Air recon — officer ability. Same targeting flow as the air strike, but a
    // single fighter overflies the indicated area and lifts the fog there for the
    // duration of its pass.
    const recon = UNIT_DEFS[unit.type].abilities?.find((a) => a.effect === 'recon');
    if (recon) {
      const cd = unit.cooldownState?.[recon.id] ?? 0;
      const ready = canUseAbility(unit, recon.id);
      const affordable = currentPlayer(this.world).gold >= recon.cost;
      const label = ready ? `${t('order.recon')} (${recon.cost})` : `${t('order.recon')} (${cd})`;
      items.push({
        label, color: '#7dcfff', icon: ORDER_MENU_ICONS.capture, enabled: ready && affordable,
        onClick: () => {
          this.hideContextMenu();
          const s = this.inputState;
          const pending = s.kind === 'pendingMove'
            ? { originCol: s.originCol, originRow: s.originRow, cost: s.cost, steps: s.steps }
            : undefined;
          this.inputState = { kind: 'orderOfficerAbility', unitId: unit.id, abilityId: recon.id, pending };
          this.refresh();
        },
      });
    }
    // Unload — one entry per carried unit (icon + name + HP%), so the player
    // picks exactly which unit to drop. Separated from the other orders above.
    if (unit.cargo && unit.cargo.length > 0 && getUnloadTiles(this.world, unit).length > 0) {
      if (items.length) items.push({ divider: true });
      for (let i = 0; i < unit.cargo.length; i++) {
        const c = unit.cargo[i];
        const cDef = UNIT_DEFS[c.type];
        const idx = i;
        // Unload opens a cancellable tile-picker, so DON'T commit the move yet —
        // carry the provisional-move context and commit only when a drop tile is
        // chosen. Cancelling (RMB) returns to this menu with the move intact.
        items.push({ html: this.cargoMenuRow(c.type, c.hp, cDef.maxHp), onClick: () => {
          this.hideContextMenu();
          const s = this.inputState;
          const pending = s.kind === 'pendingMove'
            ? { originCol: s.originCol, originRow: s.originRow, cost: s.cost, steps: s.steps }
            : undefined;
          this.doUnload(unit, idx, pending);
        } });
      }
    }
    // Terminal actions, separated from the orders above. "Koniec" sits second
    // from the bottom, and "Anuluj" is always the very last item.
    if (items.length) items.push({ divider: true });
    // Koniec — commit the move and end the unit's turn.
    items.push({ label: t('order.end'), icon: ORDER_MENU_ICONS.end, onClick: () => {
      this.hideContextMenu();
      const st = this.inputState;
      if (st.kind === 'pendingMove') this.commitPendingMove(st);
      this.finishUnitTurn(unit);
      this.deselect();
    } });
    // Anuluj — revert the provisional move. Always the bottom item.
    items.push({ label: t('order.cancel'), color: '#f7768e', icon: ORDER_MENU_ICONS.cancel, onClick: () => {
      const st = this.inputState;
      if (st.kind === 'pendingMove') this.cancelPendingMove(st);
    } });
    return items;
  }

  /** LMB on a reachable tile: provisionally move the unit there, then open the
   *  context menu. The move isn't committed (movePoints/supply) until an action
   *  or "Koniec"; "Anuluj" returns the unit to its origin. */
  private beginPendingMove(unit: Unit, col: number, row: number, path: Coord[]): void {
    if (this.runner && !this.runner.isActionAllowed({ type: 'move', unitId: unit.id, to: { col, row } })) return;
    const originCol = unit.col, originRow = unit.row;
    const def = UNIT_DEFS[unit.type];
    let cost = 0;
    for (const step of path) cost += getMovementCost(this.world.map.tiles[step.row][step.col].type, def.domain, unit.type);
    const steps = path.length;
    this.lastCommandPos = { col, row };
    // Remember the facing before the slide so a cancel can restore the sprite.
    this.movePreFacing.set(unit.id, this.unitFacing.get(unit.id) ?? 'se');

    // Snapshot enemies that are currently HIDDEN to the player (their tile isn't
    // in our vision). Becoming adjacent to one of these mid-move springs an
    // ambush — discouraging blind charges into fog.
    const hiddenEnemyTiles = new Set<string>();
    for (const [, u] of this.world.units) {
      if (u.owner === this.localPlayer) continue;
      const k = `${u.col},${u.row}`;
      if (!this.playerVisible.has(k)) hiddenEnemyTiles.add(k);
    }

    this.animateMove(unit, [...path], (ambushed) => {
      // Fog stays frozen through the provisional slide (revealFog=false below);
      // it's revealed on commit, or along the whole route on ambush.
      // Notify the runner with the ACTUAL final position (an ambush stops short).
      if (steps > 0 && this.runner) {
        this.runner.onUnitMoved(unit.id, { col: originCol, row: originRow }, { col: unit.col, row: unit.row });
        if (this.processRunnerActions()) return;
      }

      if (ambushed) {
        // Walked next to a hidden enemy: the move is locked in here and the
        // unit's turn ends — no reverse, no further orders.
        unit.hasMoved = true;
        const movedSteps = path.findIndex(p => p.col === unit.col && p.row === unit.row) + 1;
        this.commitPendingMove({ unitId: unit.id, cost, steps: movedSteps || steps });
        unit.acted = true;
        // Reveal the fog over the entire route the unit blindly travelled.
        this.revealVisionAlongPath(unit, originCol, originRow, path);

        // The hidden enemies we ended up next to each land a small reaction shot.
        const ambushers: Unit[] = [];
        for (const [, e] of this.world.units) {
          if (e.owner === this.localPlayer) continue;
          if (hiddenEnemyTiles.has(`${e.col},${e.row}`) && chebyshevDistance(unit, e) <= 1) ambushers.push(e);
        }
        const victimPos = this.gridToScreen(unit.col, unit.row);
        const victimId = unit.id;
        const res = applyAmbushDamage(this.world, unit, ambushers);

        this.addMessage(t('msg.ambushed', t(`unit.${unit.type}`)));
        this.showAmbushLabel(unit);

        if (res.died) {
          if (this.runner) this.runner.onUnitKilled(victimId);
          void this.playExplosion(victimPos.x, victimPos.y);
          checkElimination(this.world);
          const winner = checkVictory(this.world);
          this.deselect();
          this.fullRefresh();
          if (winner != null) this.showVictory(winner);
        } else {
          this.deselect();
          this.fullRefresh();
        }
        return;
      }

      // Moving onto a friendly carrier is a normal (reversible) move now: the
      // unit waits on the carrier tile and the context menu offers "Załaduj".
      // Loading only happens when the player confirms it (see pendingMoveItems).
      // "Wait here" (steps === 0) is not a move — keep hasMoved false so indirect
      // units can still fire and the order menu reflects the current tile.
      unit.hasMoved = steps > 0;
      this.inputState = { kind: 'pendingMove', unitId: unit.id, originCol, originRow, cost, steps };
      this.fullRefresh();
      this.showContextMenu(unit.id, this.pendingMoveItems(unit), () => {
        const st = this.inputState;
        if (st.kind === 'pendingMove') this.cancelPendingMove(st);
      });
    }, hiddenEnemyTiles, false);
  }

  /** Animated "Zasadzka!" label that rises and fades above an ambushed unit. */
  private showAmbushLabel(unit: Unit): void {
    const pos = this.gridToScreen(unit.col, unit.row);
    const label = this.add.text(pos.x, pos.y - HH - 6, t('combat.ambush'), {
      fontFamily: '"Arial Black", "Trebuchet MS", Impact, sans-serif',
      fontSize: '26px', color: '#f7768e', fontStyle: 'bold',
      stroke: '#1a0608', strokeThickness: 6,
    });
    label.setOrigin(0.5, 1);
    label.setDepth(100000);
    label.setScale(0.4);
    label.setAlpha(0);
    this.tweens.add({ targets: label, scale: 1, alpha: 1, duration: 180, ease: 'Back.easeOut' });
    this.tweens.add({
      targets: label, y: pos.y - HH - 48, alpha: 0, delay: 750, duration: 480, ease: 'Quad.easeIn',
      onComplete: () => label.destroy(),
    });
  }

  /** Apply the deferred movement cost/supply for a committed pending move. */
  private commitPendingMove(st: { unitId: number; cost: number; steps: number }): void {
    const unit = this.world.units.get(st.unitId);
    if (!unit) return;
    consumeMoveSupply(unit, st.steps);
    // One order per unit per turn — moving spends the unit's whole turn, so it
    // can't move again with leftover points.
    unit.movePoints = 0;
    // Confirming the move is when fog finally updates: the provisional slide kept
    // it frozen, so reveal the unit's vision from its committed position now.
    this.recomputeVisible();
    if (this.runner) {
      this.runner.onActionCompleted({ type: 'move', unitId: unit.id, to: { col: unit.col, row: unit.row } });
    }
  }

  /** Reveal the moving unit's vision from every tile it actually traversed on a
   *  provisional move — used when an ambush ends the move blind, so the player
   *  sees the whole route (and where the trap sprang from), per the AW model. */
  private revealVisionAlongPath(unit: Unit, originCol: number, originRow: number, path: Coord[]): void {
    const tiles: Coord[] = [{ col: originCol, row: originRow }];
    for (const p of path) {
      tiles.push(p);
      if (p.col === unit.col && p.row === unit.row) break; // stop where the unit halted
    }
    const savedCol = unit.col, savedRow = unit.row;
    for (const tl of tiles) {
      unit.col = tl.col; unit.row = tl.row;
      for (const k of computeUnitVision(unit, this.world.map)) this.playerVisible.add(k);
    }
    unit.col = savedCol; unit.row = savedRow;
    updateExplored(this.localPlayer, this.playerVisible, this.world);
    // Vision was mutated directly here (not via drawFogDelta) — flag a full
    // repaint for the next refresh so the blindly-travelled route un-fogs.
    this.fogDirty = true;
  }

  /** Revert a provisional move: put the unit back on its origin tile. */
  private cancelPendingMove(st: { unitId: number; originCol: number; originRow: number }): void {
    this.hideContextMenu();
    const unit = this.world.units.get(st.unitId);
    if (!unit) { this.deselect(); return; }
    unit.col = st.originCol;
    unit.row = st.originRow;
    unit.hasMoved = false;
    // Restore the pre-move sprite orientation (the slide rotated it).
    const pre = this.movePreFacing.get(unit.id);
    if (pre) this.setUnitFacing(unit, this.unitIconImages.get(unit.id), pre);
    this.movePreFacing.delete(unit.id);
    this.recomputeVisible();
    this.syncEntities();
    this.selectUnit(unit);
  }

  /** A friendly carrier sharing `unit`'s tile that can still load it, or null. */
  private loadableCarrierAt(unit: Unit): Unit | null {
    for (const [, u] of this.world.units) {
      if (u.id !== unit.id && u.col === unit.col && u.row === unit.row && canLoadInto(unit, u)) return u;
    }
    return null;
  }

  /** Confirm loading `unit` into `carrier` (commits the provisional move). */
  private doLoad(unit: Unit, carrier: Unit): void {
    executeLoad(this.world, unit, carrier);
    if (this.runner) {
      this.runner.onActionCompleted({ type: 'load', unitId: unit.id, carrierId: carrier.id });
      if (this.processRunnerActions()) return;
    }
    this.addMessage(t('msg.loaded', t(`unit.${unit.type}`), t(`unit.${carrier.type}`)));
    // Loading confirms the provisional move; the unit becomes cargo (no vision),
    // so settle the fog from the resulting unit set instead of the frozen state.
    this.recomputeVisible();
    this.syncEntities();
    this.deselect();
    this.fullRefresh();
  }

  /** Re-open the post-move context menu (after a stray click in attack-target
   *  mode). The move stays provisional — nothing is committed or lost. */
  private reopenPendingMenu(st: { unitId: number; originCol: number; originRow: number; cost: number; steps: number }): void {
    const unit = this.world.units.get(st.unitId);
    if (!unit) { this.deselect(); return; }
    this.inputState = { kind: 'pendingMove', unitId: st.unitId, originCol: st.originCol, originRow: st.originRow, cost: st.cost, steps: st.steps };
    this.fullRefresh();
    this.showContextMenu(unit.id, this.pendingMoveItems(unit), () => {
      const s = this.inputState;
      if (s.kind === 'pendingMove') this.cancelPendingMove(s);
    });
  }

  /** Click handling while picking a post-move attack target. Clicking an
   *  attackable enemy commits the move and resolves combat; any other click
   *  reopens the post-move menu (the unit and its move are not lost). */
  private handlePendingMoveAttackClick(col: number, row: number): void {
    const st = this.inputState;
    if (st.kind !== 'pendingMoveAttack') return;
    const unit = this.world.units.get(st.unitId);
    if (!unit) { this.deselect(); return; }
    const target = this.getUnitAt(col, row);
    if (target && st.attackableEnemies.has(target.id)) {
      if (this.runner && !this.runner.isActionAllowed({ type: 'attack', unitId: unit.id, targetId: target.id })) return;
      this.commitPendingMove(st);
      this.executeAttack(unit, target);
      return;
    }
    this.reopenPendingMenu(st);
  }

  private getUnitAt(col: number, row: number): Unit | null {
    for (const [, u] of this.world.units) {
      if (u.col === col && u.row === row) {
        // Skip enemy units on non-visible tiles (fog of war)
        if (u.owner !== this.localPlayer && !this.playerVisible.has(`${col},${row}`)) continue;
        return u;
      }
    }
    return null;
  }

  private computePostMoveOrders(unit: Unit): OrderEntry[] {
    const orders: OrderEntry[] = [];
    if (UNIT_DEFS[unit.type].attackRange > 0) {
      const hasEnemy = [...this.world.units.values()].some(
        (e) => e.owner !== unit.owner && this.playerVisible.has(`${e.col},${e.row}`) && canAttack(unit, e),
      );
      orders.push({ kind: 'attack', enabled: hasEnemy });
    }
    if (canUnitCapture(unit)) {
      orders.push({ kind: 'capture', enabled: canCapture(this.world, unit) != null });
    }
    const baseType = resolveBaseType(unit.type);
    if (baseType === 'supply-truck') {
      orders.push({ kind: 'build', enabled: canBuildOrContinue(this.world, unit) });
    }
    return orders;
  }

  // --- End Turn ---

  /** Finalize human player's end of turn: run repairs (animated), then call engine endTurn. */
  private endTurn(): void {
    // Tutorial blocking: check if end-turn is allowed
    if (this.runner && !this.runner.isActionAllowed({ type: 'end-turn' })) return;

    // Immediately disable the end-turn button to prevent double-clicks
    // and to give visual feedback that the turn is being processed.
    const endBtn = document.getElementById('btn-end-turn-top') as HTMLButtonElement | null;
    if (endBtn) {
      endBtn.disabled = true;
      endBtn.style.opacity = '0.4';
      endBtn.style.pointerEvents = 'none';
      endBtn.style.filter = 'grayscale(0.6)';
    }
    // Tutorial advancement
    if (this.runner) {
      this.runner.onActionCompleted({ type: 'end-turn' });
      this.processRunnerActions();
    }

    // Reset report for the upcoming AI turns + next human turn
    this.turnReport = emptyTurnReport();

    // Snapshot our units now. Losses are derived by diffing this against the
    // world at the next turn-report — so every casualty (enemy fire, attrition,
    // supply) is counted exactly once, animated or fast-forwarded alike.
    this.reportLossSnapshot.clear();
    for (const u of this.world.units.values()) {
      if (u.owner === this.localPlayer) {
        this.reportLossSnapshot.set(u.id, { type: u.type, col: u.col, row: u.row });
      }
    }

    const pid = currentPlayerId(this.world);

    // Supply phase: consume idle → attrition → commander heal auras → building resupply
    consumeIdleSupply(this.world, pid);
    const attritionResults = applyAttrition(this.world, pid);
    if (attritionResults.length > 0) {
      this.syncEntities();
    }
    const auraHeals = applyAuraHealing(this.world, pid);
    if (auraHeals.length > 0) {
      this.syncEntities();
    }
    const resupplyResults = executeBuildingResupply(this.world, pid);

    // Animate building resupply, then repairs, then completeEndTurn
    const afterResupply = () => {
      // Run building repair phase for the current (human) player — animate results
      const repairResults = executeRepairs(this.world, pid);
      if (repairResults.length > 0) {
        this.animateRepairSequence(repairResults, () => {
          this.completeEndTurn();
        });
      } else {
        this.completeEndTurn();
      }
    };

    if (resupplyResults.length > 0) {
      this.animateResupplySequence(resupplyResults, afterResupply);
    } else {
      afterResupply();
    }
  }

  /** Complete end-of-turn after repairs are done (and animated). */
  private completeEndTurn(): void {
    // Scenario runner: end-of-turn hook
    if (this.runner) {
      this.runner.onTurnEnd(this.world.turnNumber, currentPlayerId(this.world));
      if (this.processRunnerActions()) return;
    }

    // Snapshot training about to complete (turnsLeft === 1 → will finish in advanceTraining)
    const trainingSnapshot = new Map<number, { unitType: UnitType; buildingType: BuildingType }>();
    for (const b of this.world.buildings.values()) {
      if (b.owner === this.localPlayer && b.training && b.training.turnsLeft <= 1) {
        trainingSnapshot.set(b.id, { unitType: b.training.unitType, buildingType: b.type });
      }
    }

    const winner = executeEndTurn(this.world);

    // Detect completed training
    for (const [bid, snap] of trainingSnapshot) {
      const b = this.world.buildings.get(bid);
      if (b && !b.training) {
        this.turnReport.unitsRecruited.push({ type: snap.unitType, buildingType: snap.buildingType });
      }
    }

    if (winner != null) { this.showVictory(winner); return; }
    if (this.checkHumanEliminated()) return;

    if (currentPlayerId(this.world) !== this.localPlayer) {
      this.runAiTurn();
    } else {
      // Scenario runner: start-of-turn hook for human (queues event messages
      // for the dispatch screen shown before the turn report).
      if (this.fireTurnStartEvents()) return;
      audio.fileSfx.play('turn-start');
      this.recomputeVisible();
      this.detectNewlySpottedEnemies();
      this.addMessage(t('msg.turn', this.world.turnNumber));
      this.deselect();
      this.updateHud();
      this.drawMinimap();
      this.panToTurnStart();
      if (this.runner?.getTutorialState()) {
        this.updateTutorialBanner();
        this.fullRefresh();
      } else {
        this.showTurnStartModal();
      }
    }
  }

  private runAiTurn(): void {
    this.inputState = { kind: 'aiTurn' };
    this.skipAiTurn = false;
    this.fullRefresh();

    const initDelay = AI_ANIM_BASE_DELAY_MS / gameSettings.aiAnimSpeed;
    this.time.delayedCall(initDelay, () => this.runAiTurnStep());
  }

  /** Skip all remaining AI animations immediately — called from skip button. */
  private executeSkipAi(): void {
    this.skipAiTurn = true;
    // Kill all running tweens/delays/camera effects to abort in-progress animations.
    // tweens.killAll() does NOT fire onComplete, so reset animating flag manually.
    this.tweens.killAll();
    this.time.removeAllEvents();
    this.cameras.main.resetFX();
    this.animating = false;
    // Fast-forward current AI player
    // eslint-disable-next-line no-empty
    while (aiActNextUnit(this.world) !== null) {}
    checkElimination(this.world);
    const w0 = checkVictory(this.world);
    if (w0 != null) { this.syncEntities(); this.showVictory(w0); return; }
    this.syncEntities();
    this.finishAiPlayerTurn();
  }

  private runAiTurnStep(): void {
    // Guard against stale callbacks (e.g. camera pan firing after skip)
    if (this.inputState.kind !== 'aiTurn') return;

    if (this.skipAiTurn) {
      // Fast-forward all remaining unit actions
      // eslint-disable-next-line no-empty
      while (aiActNextUnit(this.world) !== null) {}
      checkElimination(this.world);
      const w1 = checkVictory(this.world);
      if (w1 != null) { this.syncEntities(); this.showVictory(w1); return; }
      this.finishAiPlayerTurn();
      return;
    }

    // Snapshot human unit HP to detect enemy attacks on our units (for the
    // camera pan only — losses are tallied centrally via reportLossSnapshot).
    const hpBefore = new Map<number, { hp: number; col: number; row: number }>();
    for (const u of this.world.units.values()) {
      if (u.owner === this.localPlayer) hpBefore.set(u.id, { hp: u.hp, col: u.col, row: u.row });
    }

    const result = aiActNextUnit(this.world);
    if (!result) {
      this.finishAiPlayerTurn();
      return;
    }

    // Pan the camera to the first of our units the AI just hit (damaged or killed).
    let aiAttackPos: { col: number; row: number } | null = null;
    for (const [uid, before] of hpBefore) {
      const after = this.world.units.get(uid);
      if (!after || after.hp < before.hp) {
        this.lastEnemyAttackPos = { col: before.col, row: before.row };
        aiAttackPos = { col: before.col, row: before.row };
        break;
      }
    }

    // Fog of war: skip camera/animation if neither start nor end tile is visible
    const fromKey = `${result.fromCol},${result.fromRow}`;
    const toKey = `${result.toCol},${result.toRow}`;
    const pathVisible = result.path.some(p => this.playerVisible.has(`${p.col},${p.row}`));
    const actionVisible = this.playerVisible.has(fromKey) || this.playerVisible.has(toKey) || pathVisible;

    if (!actionVisible) {
      // Invisible action — just sync state silently and proceed
      this.syncEntities();
      this.fullRefresh();
      this.time.delayedCall(0, () => this.runAiTurnStep());
      return;
    }

    // AI medic heal — animate green "+" over healed units, then proceed
    if (result.healResults && result.healResults.length > 0) {
      const speed = gameSettings.aiAnimSpeed;
      const panMs = AI_ANIM_BASE_PAN_MS / speed;
      const delayMs = AI_ANIM_BASE_DELAY_MS / speed;
      this.syncEntities();
      this.fullRefresh();
      const pos = this.gridToScreen(result.toCol, result.toRow);
      this.cameras.main.pan(pos.x, pos.y, panMs, 'Sine.easeInOut', false, (_cam, progress) => {
        if (progress >= 1) {
          this.animateHealSequence(result.healResults!, () => {
            this.time.delayedCall(delayMs, () => this.runAiTurnStep());
          });
        }
      });
      return;
    }

    // AI supply-truck resupply — animate green ammo icons over resupplied units, then proceed
    if (result.resupplyResults && result.resupplyResults.length > 0) {
      const speed = gameSettings.aiAnimSpeed;
      const panMs = AI_ANIM_BASE_PAN_MS / speed;
      const delayMs = AI_ANIM_BASE_DELAY_MS / speed;
      this.syncEntities();
      this.fullRefresh();
      const pos = this.gridToScreen(result.toCol, result.toRow);
      this.cameras.main.pan(pos.x, pos.y, panMs, 'Sine.easeInOut', false, (_cam, progress) => {
        if (progress >= 1) {
          this.animateResupplyPlus(result.resupplyResults!, () => {
            this.time.delayedCall(delayMs, () => this.runAiTurnStep());
          });
        }
      });
      return;
    }

    const speed = gameSettings.aiAnimSpeed;
    const panMs = AI_ANIM_BASE_PAN_MS / speed;
    const delayMs = AI_ANIM_BASE_DELAY_MS / speed;

    // After movement/sync, optionally play the battle scene / explosion + capture
    // bounce, then proceed. The action is visible here (we returned early above
    // when it wasn't), so an AI attack always gets the full-screen battle.
    const afterSync = () => {
      // AI attack — play the full-screen Advance-Wars-style battle scene.
      // `animating = true` keeps the render loop awake for the whole sequence:
      // without it the idle-sleep check in update() can put the loop to sleep
      // mid-battle (AI state isn't "pulse-active"), stalling the tweens and
      // making the deployment/volleys stutter badly. The player path sets this
      // in animateCombat — the AI path must mirror it.
      if (result.battle) {
        this.animating = true;
        void this.playBattle(result.battle).then(() => { this.animating = false; afterExplosion(); });
        return;
      }
      // Non-attack action that still damaged our unit (e.g. ambush) — explosion only.
      if (aiAttackPos && this.playerVisible.has(`${aiAttackPos.col},${aiAttackPos.row}`)) {
        const explosionPos = this.gridToScreen(aiAttackPos.col, aiAttackPos.row);
        this.animating = true;
        void this.playExplosion(explosionPos.x, explosionPos.y).then(() => { this.animating = false; afterExplosion(); });
        return;
      }
      afterExplosion();
    };

    const afterExplosion = () => {
      if (result.captured) {
        const unit = this.world.units.get(result.unitId);
        if (unit) {
          // AI already executed the capture — temporarily revert the building's
          // visual state so the color fill appears only after the bounce lands.
          const bld = getBuildingAt(this.world, unit.col, unit.row);
          let savedOwner: PlayerId | null = null;
          let savedProgress = 0;
          let savedCapturing: PlayerId | null = null;
          if (bld) {
            savedOwner = bld.owner;
            savedProgress = bld.captureProgress;
            savedCapturing = bld.capturingPlayer;
            // Revert to pre-capture visual: if progress is 0 and owner changed,
            // the capture just completed — show as the previous owner (neutral or enemy).
            // If progress > 0, partial capture — show previous progress.
            if (savedProgress === 0 && savedOwner === unit.owner) {
              // Full capture just completed — temporarily show as neutral-ish
              bld.owner = null;
              bld.capturingPlayer = unit.owner;
              bld.captureProgress = 1; // show partial fill before bounce
            }
            this.syncEntities();
            this.fullRefresh();
          }

          this.animateCaptureBounce(unit, () => {
            // HQ capture → instant elimination + victory check
            checkElimination(this.world);
            const winner = checkVictory(this.world);
            if (winner != null) { this.showVictory(winner); return; }
            this.time.delayedCall(delayMs, () => this.runAiTurnStep());
          }, () => {
            // Restore actual state after bounce
            if (bld) {
              bld.owner = savedOwner;
              bld.captureProgress = savedProgress;
              bld.capturingPlayer = savedCapturing;
            }
          });
          return;
        }
      }
      this.time.delayedCall(delayMs, () => this.runAiTurnStep());
    };

    if (result.path.length > 0) {
      // Unit moved — animate it tile-by-tile like player moves
      const gfx = this.unitSprites.get(result.unitId);
      const unit = this.world.units.get(result.unitId);

      // Pan camera to starting position, then animate movement
      const fromPos = this.gridToScreen(result.fromCol, result.fromRow);
      this.cameras.main.pan(fromPos.x, fromPos.y, panMs, 'Sine.easeInOut', false, (_cam, progress) => {
        if (progress < 1) return;

        if (gfx && unit) {
          // Temporarily move unit to start for animation
          const savedCol = unit.col;
          const savedRow = unit.row;
          unit.col = result.fromCol;
          unit.row = result.fromRow;

          this.animateAiMove(gfx, unit, result.path, speed, () => {
            unit.col = savedCol;
            unit.row = savedRow;
            this.syncEntities();
            this.fullRefresh();
            afterSync();
          });
        } else {
          this.syncEntities();
          this.fullRefresh();
          afterSync();
        }
      });
    } else {
      // No movement (attack from current position, etc.) — just sync and show
      this.syncEntities();
      this.fullRefresh();

      const pos = this.gridToScreen(result.toCol, result.toRow);
      this.cameras.main.pan(pos.x, pos.y, panMs, 'Sine.easeInOut', false, (_cam, progress) => {
        if (progress >= 1) {
          afterSync();
        }
      });
    }
  }

  /** Animate AI unit sprite tile-by-tile along a path (doesn't modify world state). */
  private animateAiMove(
    gfx: Phaser.GameObjects.Graphics,
    unit: Unit,
    path: { col: number; row: number }[],
    speed: number,
    onComplete: () => void,
  ): void {
    const positions = [
      this.gridToScreen(unit.col, unit.row),
      ...path.map((p) => this.gridToScreen(p.col, p.row)),
    ];

    // Draw unit once at starting position — animate via position offset
    const drawOrigin = positions[0];
    const startDepth = (unit.col + unit.row) * 1000 + 300;
    this.drawUnitAt(gfx, unit, drawOrigin.x, drawOrigin.y, startDepth);
    const iconImg = this.unitIconImages.get(unit.id);

    // Scale segment duration with speed (90ms base at speed 1)
    const segMs = Math.max(20, Math.round(90 / speed));
    let seg = 0;

    const nextSegment = () => {
      if (seg >= path.length) {
        gfx.setPosition(0, 0); // reset offset
        onComplete();
        return;
      }

      const from = positions[seg];
      const to = positions[seg + 1];
      const target = path[seg];
      // Face the direction of this step (unit.col/row is still the previous tile here).
      this.setUnitFacing(unit, iconImg, facingFromStep(target.col - unit.col, target.row - unit.row));
      const depthAtTarget = (target.col + target.row) * 1000 + 300;

      this.tweens.add({
        targets: { t: 0 },
        t: 1,
        duration: segMs,
        ease: 'Linear',
        onUpdate: (_tw, obj) => {
          const t = (obj as { t: number }).t;
          const ix = from.x + (to.x - from.x) * t;
          const iy = from.y + (to.y - from.y) * t;
          gfx.setPosition(ix - drawOrigin.x, iy - drawOrigin.y);
          gfx.setDepth(depthAtTarget);
          this.positionUnitVisual(unit, ix, iy, depthAtTarget);
        },
        onComplete: () => {
          unit.col = target.col;
          unit.row = target.row;
          seg++;
          nextSegment();
        },
      });
    };
    nextSegment();
  }

  private finishAiPlayerTurn(): void {
    const aiPid = currentPlayerId(this.world);
    aiRecruitAll(this.world);
    validateCaptures(this.world);
    validateBuilds(this.world);
    cancelDisruptedTraining(this.world);

    // Supply phase for AI player
    consumeIdleSupply(this.world, aiPid);
    applyAttrition(this.world, aiPid);
    executeBuildingResupply(this.world, aiPid);

    // Run building repairs for AI player
    const repairResults = executeRepairs(this.world, aiPid);
    // Filter to only visible repairs for animation
    const visibleRepairs = repairResults.filter(r => this.playerVisible.has(`${r.col},${r.row}`));

    const continueAfterRepairs = () => {
      const winner = executeEndTurn(this.world);
      if (winner != null) { this.showVictory(winner); return; }
      if (this.checkHumanEliminated()) return;

      if (currentPlayerId(this.world) !== this.localPlayer) {
        if (this.skipAiTurn) {
          // Fast-forward remaining AI players without animation.
          // Re-check after each player to avoid acting on the human player.
          while (currentPlayerId(this.world) !== this.localPlayer) {
            if (!this.world.players[this.localPlayer].alive) break;
            // Losses are tallied centrally (reportLossSnapshot diff), so just run.
            // eslint-disable-next-line no-empty
            while (aiActNextUnit(this.world) !== null) {}
            aiRecruitAll(this.world);
            validateCaptures(this.world);
            validateBuilds(this.world);
            cancelDisruptedTraining(this.world);
            // Supply + Repairs for fast-forwarded AI players (no animation)
            const ffPid = currentPlayerId(this.world);
            consumeIdleSupply(this.world, ffPid);
            applyAttrition(this.world, ffPid);
            executeBuildingResupply(this.world, ffPid);
            executeRepairs(this.world, ffPid);
            const w = executeEndTurn(this.world);
            if (w != null) { this.showVictory(w); return; }
          }
          if (this.checkHumanEliminated()) return;
          // Now it's the human player's turn
          if (this.fireTurnStartEvents()) return;
          audio.fileSfx.play('turn-start');
          this.recomputeVisible();
          this.detectNewlySpottedEnemies();
          this.addMessage(t('msg.turn', this.world.turnNumber));
          this.inputState = { kind: 'idle' };
          this.skipAiTurn = false;
          this.syncEntities();
          this.fullRefresh();
          this.drawMinimap();
          this.panToTurnStart();
          if (this.runner?.getTutorialState()) {
            this.updateTutorialBanner();
          } else {
            this.showTurnStartModal();
          }
          return;
        }
        this.runAiTurn();
      } else {
        // Scenario runner: start-of-turn hook for the human (fires turn-start
        // events like messages / reinforcements). This path — returning to the
        // human after the AI's turn — previously skipped it, so human turn-start
        // events never triggered. Messages are queued for the dispatch screen.
        if (this.fireTurnStartEvents()) return;
        audio.fileSfx.play('turn-start');
        this.recomputeVisible();
        this.detectNewlySpottedEnemies();
        this.addMessage(t('msg.turn', this.world.turnNumber));
        this.inputState = { kind: 'idle' };
        this.skipAiTurn = false;
        this.syncEntities();
        this.fullRefresh();
        this.drawMinimap();
        this.panToTurnStart();
        if (this.runner?.getTutorialState()) {
          this.updateTutorialBanner();
        } else {
          this.showTurnStartModal();
        }
      }
    };

    if (visibleRepairs.length > 0 && !this.skipAiTurn) {
      this.animateRepairSequence(visibleRepairs, continueAfterRepairs);
    } else {
      continueAfterRepairs();
    }
  }

  private recruitUnit(building: Building, unitType?: string): void {
    const def = BUILDING_DEFS[building.type];
    if (!def.recruits) return;
    const type = unitType ?? def.recruits[0];
    // Tutorial blocking for produce
    if (this.runner && !this.runner.isActionAllowed({ type: 'produce', buildingId: building.id, unitType: type })) return;
    if (startTraining(this.world, building, type as import('../turn/types').UnitType)) {
      // Tutorial advancement for produce
      if (this.runner) {
        this.runner.onActionCompleted({ type: 'produce', buildingId: building.id, unitType: type });
        this.processRunnerActions();
      }
      this.addMessage(t('msg.trainingStarted', t(`unit.${type}`)));
      this.refresh();
    }
  }

  private showCancelTrainingConfirm(building: Building): void {
    if (!building.training) return;
    const trainDef = UNIT_DEFS[building.training.unitType];
    const elapsed = building.training.totalTurns - building.training.turnsLeft;
    const refund = elapsed > 0 ? Math.floor(trainDef.cost / 2) : trainDef.cost;
    const refundPct = elapsed > 0 ? '50%' : '100%';

    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
      position: 'fixed', inset: '0', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.6)', zIndex: '500', fontFamily: 'system-ui,-apple-system,sans-serif',
    });

    const dialog = document.createElement('div');
    Object.assign(dialog.style, {
      background: PANEL_BG, border: '1px solid #2a3140', borderRadius: '10px',
      padding: '24px 32px', color: '#d8dee9', maxWidth: '340px', textAlign: 'center',
    });
    dialog.innerHTML = `
      <div style="font-size:16px;font-weight:700;margin-bottom:12px">${t('modal.cancelTraining')}</div>
      <div style="font-size:14px;opacity:0.8;margin-bottom:6px">${t('modal.trainingInfo', t(`unit.${building.training.unitType}`), building.training.turnsLeft)}</div>
      <div style="font-size:13px;color:#e0af68;margin-bottom:20px">${t('modal.refund', refund, refundPct)}</div>
      <style>.train-btn:hover{filter:brightness(1.2)}.train-btn:active{filter:brightness(0.9);transform:scale(0.97)}</style>
      <div style="display:flex;gap:10px;justify-content:center">
        <button id="confirm-cancel-yes" class="train-btn" style="display:inline-block;width:150px;height:46px;border:none;cursor:pointer;background:url('/graphics/ui/confirmation-button-background.png') no-repeat;background-size:100% 100%;background-color:transparent;color:#E8DCC2;font-family:'Arial Black','Trebuchet MS',Impact,sans-serif;font-weight:bold;font-size:13px;letter-spacing:2px;text-transform:uppercase;line-height:46px;text-align:center;transition:filter 0.15s,transform 0.08s">${t('menu.yes')}</button>
        <button id="confirm-cancel-no" class="train-btn" style="display:inline-block;width:150px;height:46px;border:none;cursor:pointer;background:url('/graphics/ui/cancel-button-background.png') no-repeat;background-size:100% 100%;background-color:transparent;color:#E8DCC2;font-family:'Arial Black','Trebuchet MS',Impact,sans-serif;font-weight:bold;font-size:13px;letter-spacing:2px;text-transform:uppercase;line-height:46px;text-align:center;transition:filter 0.15s,transform 0.08s">${t('menu.no')}</button>
      </div>
    `;
    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    const close = () => overlay.remove();

    document.getElementById('confirm-cancel-no')?.addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

    document.getElementById('confirm-cancel-yes')?.addEventListener('click', () => {
      const refunded = cancelTraining(this.world, building);
      this.addMessage(t('msg.trainingCancelled', refunded));
      close();
      this.refresh();
    });
  }

  // --- Victory ---

  private showVictory(winner: PlayerId): void {
    // Guard: if overlay already shown (e.g. double processRunnerActions), skip
    if (this.victoryOverlay) return;

    const isWin = winner === this.localPlayer;
    const isTutorial = !!this.gameConfig?.scenario.tutorial;

    // Evaluate star rating if applicable
    const stars = isWin && this.runner ? this.runner.evaluateOutcome() : 0;
    const starsHtml = stars > 0
      ? `<div style="color:#e0af68;font-size:36px;margin-top:12px">${'★'.repeat(stars)}${'☆'.repeat(3 - stars)}</div>`
      : '';

    // For tutorials: find current lesson index and whether there's a next one
    let nextTutorialIndex = -1;
    const tutorialEntries = getTutorialCatalog().entries();
    if (isTutorial && isWin) {
      const currentScenario = this.gameConfig!.scenario;
      const currentIdx = tutorialEntries.findIndex((e) => e.scenario === currentScenario);
      if (currentIdx >= 0 && currentIdx < tutorialEntries.length - 1) {
        nextTutorialIndex = currentIdx + 1;
      }
    }

    const titleText = isTutorial && isWin ? t('tutorial.victoryTitle') : (isWin ? t('modal.victory') : t('modal.defeat'));
    const titleColor = isWin ? '#9ece6a' : '#f7768e';
    const subtitleHtml = isTutorial
      ? '' // skip generic "Player X wins" for tutorials
      : `<div style="color:#d8dee9;font-size:18px;margin-top:16px">${isWin ? t('modal.winMessage', winner + 1, this.world.turnNumber) : t('modal.loseMessage', winner + 1, this.world.turnNumber)}</div>`;

    // Tutorial: "Next lesson" (green) + "Back to lessons" (gray)
    // Regular game: "Main menu"
    const vicBtnCss = (bg: string, w: number) => `display:inline-block;width:${w}px;height:46px;border:none;cursor:pointer;background:url('${bg}') no-repeat;background-size:100% 100%;background-color:transparent;color:#E8DCC2;font-family:'Arial Black','Trebuchet MS',Impact,sans-serif;font-weight:bold;font-size:13px;letter-spacing:2px;text-transform:uppercase;line-height:46px;text-align:center;transition:filter 0.15s,transform 0.08s`;
    const vicBtnStyle = `<style>.vic-btn:hover{filter:brightness(1.2)}.vic-btn:active{filter:brightness(0.9);transform:scale(0.97)}</style>`;

    let buttonsHtml: string;
    if (isTutorial && isWin) {
      const nextBtnHtml = nextTutorialIndex >= 0
        ? `<button id="turn-next-lesson" class="vic-btn" style="margin-top:32px;${vicBtnCss('/graphics/ui/confirmation-button-background.png', 200)}">
            ${t('tutorial.nextLesson')}</button>`
        : '';
      buttonsHtml = `${vicBtnStyle}
        ${nextBtnHtml}
        <button id="turn-restart" class="vic-btn" style="margin-top:${nextTutorialIndex >= 0 ? '12' : '32'}px;${vicBtnCss('/graphics/ui/cancel-button-background.png', 200)}">
          ${t('tutorial.backToLessons')}</button>`;
    } else {
      buttonsHtml = `${vicBtnStyle}<button id="turn-restart" class="vic-btn" style="margin-top:32px;${vicBtnCss('/graphics/ui/exit-button-background.png', 200)}">
        ${t('modal.quitBtn')}</button>`;
    }

    this.victoryOverlay = document.createElement('div');
    Object.assign(this.victoryOverlay.style, {
      position: 'fixed', top: '0', left: '0', width: '100%', height: '100%',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.7)', zIndex: '1000', fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
    });
    this.victoryOverlay.innerHTML = `
      <div style="color:${titleColor};font-size:48px;font-weight:bold">${titleText}</div>
      ${starsHtml}
      ${subtitleHtml}
      ${buttonsHtml}
    `;
    document.body.appendChild(this.victoryOverlay);

    // "Next lesson" button — launch next tutorial directly
    if (nextTutorialIndex >= 0) {
      document.getElementById('turn-next-lesson')?.addEventListener('click', () => {
        this.victoryOverlay?.remove();
        const nextScenario = tutorialEntries[nextTutorialIndex]?.scenario;
        if (!nextScenario) return;
        const config: GameConfig = {
          scenario: nextScenario,
          humanPlayer: 0,
          factions: new Map([[0, this.gameConfig!.factions.get(0)!]]),
        };
        this.scene.start('TurnGame', { config });
      });
    }

    // "Back to lessons" / "Main menu" button
    document.getElementById('turn-restart')?.addEventListener('click', () => {
      this.victoryOverlay?.remove();
      this.scene.start(isTutorial ? 'Tutorial' : 'MainMenu');
    });
  }

  /** Check if the human player was eliminated; if so, show defeat and return true. */
  private checkHumanEliminated(): boolean {
    if (!this.world.players[this.localPlayer].alive) {
      const leader = this.world.players.find(p => p.alive);
      this.showVictory(leader?.id ?? this.localPlayer);
      return true;
    }
    return false;
  }

  // ─── Scenario runner integration ──────────────────────────────────

  /** Fire the human player's start-of-turn scenario events, collecting any
   *  event messages into the dispatch queue (shown before the turn report).
   *  Returns true if the game ended (victory/defeat) — caller should bail. */
  private fireTurnStartEvents(): boolean {
    if (!this.runner) return false;
    this.collectingTurnStartDispatch = true;
    this.runner.onTurnStart(this.world.turnNumber, this.localPlayer);
    const ended = this.processRunnerActions();
    this.collectingTurnStartDispatch = false;
    return ended;
  }

  /** Process pending actions from scenario runner (messages, spawns, etc). Returns true if game ended. */
  private processRunnerActions(): boolean {
    if (!this.runner) return false;
    const result = this.runner.checkResult();
    if (result === 'victory') { this.showVictory(this.localPlayer); return true; }
    if (result === 'defeat') {
      const enemy = this.world.players.find(p => p.id !== this.localPlayer && p.alive);
      this.showVictory(enemy?.id ?? 1);
      return true;
    }
    const actions = this.runner.getPendingActions();
    for (const action of actions) {
      this.executeScenarioAction(action);
    }
    return false;
  }

  private executeScenarioAction(action: EventAction): void {
    switch (action.type) {
      case 'message': {
        const text = localize(action.text);
        if (this.collectingTurnStartDispatch) {
          // Defer to the dispatch screen shown before the turn report.
          this.addMessage(text);
          this.pendingDispatchMessages.push(text);
        } else {
          this.showScenarioMessage(text);
        }
        break;
      }
      case 'spawn-units':
        for (const su of action.units) {
          const id = this.world.nextId++;
          const def = UNIT_DEFS[su.type];
          const unit: Unit = {
            id, type: su.type, owner: su.owner,
            col: su.col, row: su.row,
            hp: su.hp ?? def.maxHp, acted: false, movePoints: def.movementRange,
            fuel: su.fuel ?? def.maxFuel, ammo: su.ammo ?? def.maxAmmo, xp: 0,
          };
          // Seed carrier cargo so reinforcement carriers arrive loaded (mirrors
          // scenarioLoader). Owner is inherited; hp defaults to the cargo maxHp.
          if (su.cargo && su.cargo.length > 0) {
            unit.cargo = su.cargo.map((c) => ({
              type: c.type,
              hp: c.hp ?? UNIT_DEFS[c.type].maxHp,
              owner: su.owner,
              acted: false,
              xp: 0,
            }));
          }
          this.world.units.set(id, unit);
        }
        this.syncEntities();
        break;
      case 'remove-unit': {
        // Find unit by tag (reverse lookup through tagMap — not stored here, so search by ID from runner)
        // The runner stores tagMap internally; for remove we need to iterate
        for (const [uid, u] of this.world.units) {
          // We don't have direct tag access here, so remove-unit is a no-op in integration
          // TODO: expose tag lookup from runner if needed
          void uid; void u;
        }
        break;
      }
      case 'change-owner':
        // Would need tag lookup — skip for now
        break;
      case 'add-gold': {
        const p = this.world.players[action.player];
        if (p) p.gold += action.amount;
        break;
      }
    }
  }

  private showScenarioMessage(text: string): void {
    this.addMessage(text);
    // Also show as a brief overlay
    if (this.scenarioMessageOverlay) {
      this.scenarioMessageOverlay.remove();
    }
    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
      position: 'fixed', top: '60px', left: '50%', transform: 'translateX(-50%)',
      background: 'rgba(0,0,0,0.85)', color: '#d8dee9', padding: '12px 24px',
      borderRadius: '6px', fontSize: '16px', fontFamily: 'monospace',
      zIndex: '900', maxWidth: '500px', textAlign: 'center',
      border: '1px solid #4a5568', pointerEvents: 'auto', cursor: 'pointer',
    });
    overlay.textContent = text;
    overlay.addEventListener('click', () => overlay.remove());
    document.body.appendChild(overlay);
    this.scenarioMessageOverlay = overlay;
    // Auto-dismiss after 5s
    setTimeout(() => { if (overlay.parentNode) overlay.remove(); }, 5000);
  }

  private beginGameplay(): void {
    this.updateTutorialBanner();
    if (currentPlayerId(this.world) !== this.localPlayer) {
      this.runAiTurn();
      return;
    }
    this.panToTurnStart();
    const proceed = () => {
      // In tutorials skip the "Your turn" modal — go straight to gameplay
      if (this.runner?.getTutorialState()) {
        this.fullRefresh();
      } else {
        this.showTurnStartModal();
      }
    };
    // Once per scenario, open with a dispatch-style briefing listing the
    // victory objectives and defeat conditions, then resume the normal flow.
    if (!this.briefingShown && this.objectiveStatuses().length > 0) {
      this.briefingShown = true;
      this.showBriefingOverlay(proceed);
    } else {
      proceed();
    }
  }

  /** Wire up the scenario intro. The normal path (via LoadingScene) already has
   *  the intro overlay on screen — masking the asset load + map build — so we
   *  just hand it the "begin gameplay" callback and start the moment the player
   *  has acknowledged it. Tutorial / campaign start TurnGame directly (no
   *  LoadingScene), so there we show the intro overlay ourselves as a fallback. */
  private presentScenarioIntro(scenario: Scenario): void {
    const intro = document.getElementById('scenario-intro-overlay');
    if (intro) {
      const begin = () => {
        document.getElementById('scenario-intro-overlay')?.remove();
        this.dismissLoadingScreen(false);
        this.beginGameplay();
      };
      // Everything is now loaded and the map is built: hand the intro its start
      // callback, then signal "ready" so it swaps the progress bar for the
      // start button.
      this.game.registry.set('introBegin', begin);
      this.game.events.emit('intro-ready');
      return;
    }
    // Fallback: no pre-shown intro (tutorial / campaign direct start).
    this.showStoryOverlay(scenario.story ? localize(scenario.story) : '', () => this.beginGameplay());
    this.dismissLoadingScreen(false);
  }

  private showStoryOverlay(text: string, onDismiss: () => void): void {
    // One-time keyframes for the "click to continue" pulse.
    if (!document.getElementById('story-overlay-style')) {
      const style = document.createElement('style');
      style.id = 'story-overlay-style';
      style.textContent = `
        @keyframes story-hint-pulse { 0%,100% { opacity: 0.35; } 50% { opacity: 0.9; } }
        @keyframes story-card-in { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
      `;
      document.head.appendChild(style);
    }

    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
      position: 'fixed', inset: '0',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      // Themed wallpaper behind a dark scrim so long prose stays readable.
      background: 'linear-gradient(160deg, rgba(8,12,18,0.82), rgba(6,10,16,0.94)), #0b0d10 url("wallpaper.png") center/cover no-repeat',
      zIndex: '1000', cursor: 'pointer', padding: '24px',
    });

    const title = this.scenario ? localize(this.scenario.name) : '';

    const card = document.createElement('div');
    Object.assign(card.style, {
      maxWidth: '680px', maxHeight: '80vh', overflowY: 'auto', boxSizing: 'border-box',
      padding: '34px 44px',
      background: 'rgba(12,17,25,0.86)',
      border: '1px solid rgba(190,165,110,0.35)',
      borderRadius: '10px',
      boxShadow: '0 18px 60px rgba(0,0,0,0.6), inset 0 0 60px rgba(0,0,0,0.35)',
      animation: 'story-card-in 0.45s ease-out',
    });
    const hasBody = text.trim().length > 0;
    card.innerHTML = `
      ${title ? `<div style="
          font-family: Georgia, 'Times New Roman', serif;
          color:#e8dcc2; font-size:27px; font-weight:700; letter-spacing:1px;
          text-align:center; line-height:1.25; ${hasBody ? 'margin-bottom:14px' : ''};
          text-shadow:0 2px 8px rgba(0,0,0,0.6);">${title}</div>
        ${hasBody ? `<div style="height:1px;background:linear-gradient(90deg,transparent,rgba(190,165,110,0.55),transparent);margin:0 0 22px"></div>` : ''}` : ''}
      ${hasBody ? `<div class="story-body" style="
          font-family: Georgia, 'Times New Roman', serif;
          color:#e3e8ef; font-size:18px; line-height:1.85; text-align:left;
          text-shadow:0 1px 3px rgba(0,0,0,0.5);"></div>` : ''}
    `;
    // Set the story text via textContent to avoid HTML injection from scenario data.
    if (hasBody) {
      const body = card.querySelector('.story-body') as HTMLDivElement;
      body.textContent = text;
    }

    const hint = document.createElement('div');
    hint.textContent = t('modal.clickToContinue');
    Object.assign(hint.style, {
      color: '#cdb98a', fontSize: '13px', letterSpacing: '1.5px', textTransform: 'uppercase',
      marginTop: '22px', fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
      animation: 'story-hint-pulse 1.8s ease-in-out infinite',
    });

    overlay.appendChild(card);
    overlay.appendChild(hint);
    overlay.addEventListener('click', () => {
      overlay.remove();
      onDismiss();
    });
    document.body.appendChild(overlay);
  }

  /** Dismiss the LoadingScene overlay that persists into this scene. With
   *  `fade`, it eases out to reveal the map (no story); otherwise it's removed
   *  instantly because the briefing already covers it. */
  private dismissLoadingScreen(fade: boolean): void {
    const el = document.getElementById('scenario-loading-overlay');
    if (!el) return;
    if (!fade) { el.remove(); return; }
    el.style.transition = 'opacity 0.45s ease';
    el.style.opacity = '0';
    window.setTimeout(() => el.remove(), 480);
  }

  /** Update the tutorial instruction banner (shown at top when tutorial is active). */
  private updateTutorialBanner(): void {
    if (!this.runner) return;
    const state = this.runner.getTutorialState();
    if (!state || state.finished) {
      if (this.tutorialBanner) { this.tutorialBanner.remove(); this.tutorialBanner = null; }
      return;
    }
    if (!this.tutorialBanner) {
      this.tutorialBanner = document.createElement('div');
      Object.assign(this.tutorialBanner.style, {
        position: 'fixed', top: '46px', left: '50%', transform: 'translateX(-50%)',
        background: '#1a2744', color: '#e8eef5', padding: '14px 28px',
        borderRadius: '0 0 10px 10px', fontSize: '18px', fontWeight: '600',
        fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
        zIndex: '850', maxWidth: '650px', textAlign: 'center',
        border: '2px solid #4a90e2', borderTop: 'none',
        boxShadow: '0 4px 20px rgba(74,144,226,0.4)',
        animation: 'tutorial-banner-in 0.3s ease-out',
      });
      // Add animation keyframes if not already present
      if (!document.getElementById('tutorial-banner-style')) {
        const style = document.createElement('style');
        style.id = 'tutorial-banner-style';
        style.textContent = `
          @keyframes tutorial-banner-in {
            from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
            to   { opacity: 1; transform: translateX(-50%) translateY(0); }
          }
        `;
        document.head.appendChild(style);
      }
      document.body.appendChild(this.tutorialBanner);
    }
    this.tutorialBanner.innerHTML = `
      <div style="color:#4a90e2;font-size:12px;letter-spacing:1px;margin-bottom:4px">${t('tutorial.stepCounter', state.stepIndex + 1, state.totalSteps)}</div>
      <div>${localize(state.step.instruction)}</div>
    `;
  }

  /** Center camera on the human player's HQ building (or map center as fallback). */
  /** Detect newly spotted enemy units after recomputeVisible. */
  private detectNewlySpottedEnemies(): void {
    for (const u of this.world.units.values()) {
      if (u.owner === this.localPlayer) continue;
      const key = `${u.col},${u.row}`;
      if (this.playerVisible.has(key)) {
        this.lastSpottedEnemyPos = { col: u.col, row: u.row };
      }
    }
  }

  /** Clamp camera so the visible area stays within map bounds.
   *
   *  Phaser scroll is a world-space offset applied BEFORE the zoom matrix,
   *  so midPoint = (scrollX + width/2, scrollY + height/2) regardless of zoom.
   *  worldView = (midX - displayW/2, midY - displayH/2, displayW, displayH)
   *  where displayW = width/zoom, displayH = height/zoom.
   */
  private clampCamera(): void {
    const cam = this.cameras.main;
    const halfW = cam.width / 2;
    const halfH = cam.height / 2;
    const displayW = cam.width / cam.zoom;
    const displayH = cam.height / cam.zoom;
    const topPad = HUD_TOP_PX / cam.zoom;
    const bottomPad = HUD_BOTTOM_PX / cam.zoom;
    const visibleH = displayH - topPad - bottomPad;

    // Extra scroll margin so map edges can approach the center of the screen,
    // but not so much that corners escape the viewport entirely.
    const padX = displayW * 0.3;
    const padY = visibleH * 0.3;

    // X: clamp so map edges can reach screen center
    const midX = Phaser.Math.Clamp(
      cam.scrollX + halfW, -padX, this.worldPxW + padX,
    );
    cam.scrollX = midX - halfW;

    // Y: clamp so map edges can reach screen center (offset for top/bottom HUD)
    const midY = Phaser.Math.Clamp(
      cam.scrollY + halfH,
      -padY + (bottomPad - topPad) / 2,
      this.worldPxH + padY + (bottomPad - topPad) / 2,
    );
    cam.scrollY = midY - halfH;
  }

  /** Pan camera with priority: cameraFocus → last enemy attack → last spotted enemy → last command → HQ. */
  private panToTurnStart(): void {
    const scenario = this.gameConfig?.scenario;

    // Scenario-defined camera focus (tutorials etc.) — always use it
    if (scenario?.cameraFocus) {
      const t = this.gridToScreen(scenario.cameraFocus.col, scenario.cameraFocus.row);
      this.centerCameraOn(t.x, t.y);
      this.clampCamera();
      return;
    }

    let target: { x: number; y: number } | null = null;

    if (this.lastEnemyAttackPos) {
      target = this.gridToScreen(this.lastEnemyAttackPos.col, this.lastEnemyAttackPos.row);
    } else if (this.lastSpottedEnemyPos) {
      target = this.gridToScreen(this.lastSpottedEnemyPos.col, this.lastSpottedEnemyPos.row);
    } else if (this.lastCommandPos) {
      target = this.gridToScreen(this.lastCommandPos.col, this.lastCommandPos.row);
    } else {
      // Fallback: HQ or map center
      target = this.gridToScreen(this.mapWidth / 2, this.mapHeight / 2);
      for (const b of this.world.buildings.values()) {
        if (b.type === 'hq' && b.owner === this.localPlayer) {
          target = this.gridToScreen(b.col, b.row);
          break;
        }
      }
    }

    // Reset tracking for next turn cycle
    this.lastEnemyAttackPos = null;
    this.lastSpottedEnemyPos = null;

    this.centerCameraOn(target.x, target.y);
    this.clampCamera();
  }

  /** Compute the turn report shown at the start of the human turn: losses
   *  (snapshot diff) and income. Recruits are accumulated at end-of-turn. */
  private computeTurnReport(): void {
    // Losses: units present when we ended our turn that no longer exist.
    this.turnReport.unitsLost = [];
    for (const [uid, snap] of this.reportLossSnapshot) {
      if (!this.world.units.has(uid)) {
        this.turnReport.unitsLost.push({ type: snap.type, col: snap.col, row: snap.row });
      }
    }

    const incomeMap = new Map<BuildingType, { count: number; perBuilding: number }>();
    for (const b of this.world.buildings.values()) {
      if (b.owner !== this.localPlayer) continue;
      const def = BUILDING_DEFS[b.type];
      if (def.income <= 0) continue;
      const entry = incomeMap.get(b.type);
      if (entry) { entry.count++; }
      else { incomeMap.set(b.type, { count: 1, perBuilding: def.income }); }
    }
    this.turnReport.income = [];
    this.turnReport.totalIncome = 0;
    for (const [bt, data] of incomeMap) {
      const total = data.count * data.perBuilding;
      this.turnReport.income.push({ buildingType: bt, count: data.count, perBuilding: data.perBuilding, total });
      this.turnReport.totalIncome += total;
    }
  }

  /** Blocking "incoming dispatch" screen for scenario event messages that fired
   *  at turn-start. Shown before the turn report; the player acknowledges with a
   *  button. A morse cue plays so an arriving message is unmistakable. */
  /** Shared chrome for the comms-family modals (turn report, dispatch,
   *  briefing) so they all share one footprint and chrome. The card is sized to
   *  its content and capped at a viewport fraction — it only scrolls when a
   *  report genuinely overflows, so the common case shows no scrollbar. Returns
   *  the body element to fill; the button removes the overlay and runs onProceed. */
  private buildCommsModal(buttonLabel: string, onProceed: () => void): { overlay: HTMLDivElement; body: HTMLDivElement } {
    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
      position: 'fixed', inset: '0',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.72)', zIndex: '950', fontFamily: 'monospace',
      padding: '24px', boxSizing: 'border-box',
    });

    const card = document.createElement('div');
    // Identical footprint across all three: same width AND a fixed height, so a
    // short briefing and a long report are the same size. Capped at a viewport
    // fraction for small screens; the body scrolls only if content overflows
    // (rare now that the header/padding are compact).
    Object.assign(card.style, {
      background: PANEL_BG, border: '1px solid #2a3140', borderRadius: '10px',
      padding: '40px 52px', width: '600px', maxWidth: '90vw',
      height: '600px', maxHeight: '86vh',
      boxSizing: 'border-box', display: 'flex', flexDirection: 'column', overflow: 'hidden',
      boxShadow: '0 18px 60px rgba(0,0,0,0.6)',
    });

    const body = document.createElement('div');
    // grow 1 so the body fills the card (button pinned to the bottom); shrink 1
    // + overflow auto so it yields and scrolls only when content exceeds the cap.
    Object.assign(body.style, { flex: '1 1 auto', overflowY: 'auto', minHeight: '0' });
    card.appendChild(body);

    const btnWrap = document.createElement('div');
    Object.assign(btnWrap.style, { textAlign: 'right', marginTop: '20px', flexShrink: '0' });
    const btn = document.createElement('button');
    btn.className = 'rpt-btn';
    btn.textContent = buttonLabel;
    btn.style.cssText = "display:inline-block;width:280px;height:46px;border:none;cursor:pointer;background:url('/graphics/ui/confirmation-button-background.png') no-repeat;background-size:100% 100%;background-color:transparent;color:#E8DCC2;font-family:'Arial Black','Trebuchet MS',Impact,sans-serif;font-weight:bold;font-size:13px;letter-spacing:2px;text-transform:uppercase;line-height:46px;text-align:center;transition:filter 0.15s,transform 0.08s";
    if (!document.getElementById('rpt-btn-style')) {
      const style = document.createElement('style');
      style.id = 'rpt-btn-style';
      style.textContent = '.rpt-btn:hover{filter:brightness(1.2)}.rpt-btn:active{filter:brightness(0.9);transform:scale(0.97)}';
      document.head.appendChild(style);
    }
    btn.addEventListener('click', () => { audio.sfx.play('ui-click'); overlay.remove(); onProceed(); });
    btnWrap.appendChild(btn);
    card.appendChild(btnWrap);

    overlay.appendChild(card);
    document.body.appendChild(overlay);
    return { overlay, body };
  }

  /** The shared header for every comms-family modal (report, dispatch,
   *  briefing): a gold ❦ title with an optional muted subtitle and a divider —
   *  identical chrome across all three so they read as one window family. */
  private commsHeader(title: string, subtitle?: string): HTMLDivElement {
    const wrap = document.createElement('div');
    Object.assign(wrap.style, {
      textAlign: 'center', marginBottom: '24px',
      borderBottom: '1px solid #2a3140', paddingBottom: '16px',
    });
    const h = document.createElement('div');
    h.textContent = `❦ ${title}`;
    Object.assign(h.style, {
      color: '#e5c07b', fontSize: '20px', fontWeight: 'bold', letterSpacing: '3px',
      textTransform: 'uppercase',
    });
    wrap.appendChild(h);
    if (subtitle) {
      const s = document.createElement('div');
      s.textContent = subtitle;
      Object.assign(s.style, { color: '#7e8696', fontSize: '15px', marginTop: '8px' });
      wrap.appendChild(s);
    }
    return wrap;
  }

  private showDispatchOverlay(messages: string[], onProceed: () => void): void {
    audio.fileSfx.play('message');
    const { body } = this.buildCommsModal(t('dispatch.proceed'), onProceed);
    body.appendChild(this.commsHeader(t('dispatch.title')));
    for (const msg of messages) {
      // Same dark panel the report uses for its sections.
      const p = document.createElement('div');
      p.textContent = msg; // textContent — no HTML injection from scenario data
      Object.assign(p.style, {
        color: '#e3e8ef', fontSize: '17px', lineHeight: '1.6', textAlign: 'left',
        marginBottom: '20px',
        background: 'rgba(10,12,16,0.6)', border: '1px solid #2a3140',
        borderRadius: '6px', padding: '14px 18px',
      });
      body.appendChild(p);
    }
  }

  /** Scenario briefing — victory objectives and defeat conditions, presented
   *  dispatch-style. Shown once before the player's first turn, and reopened
   *  from the top-bar objectives button (with a close-style label). */
  private showBriefingOverlay(onProceed: () => void, buttonLabel: string = t('briefing.proceed')): void {
    const all = this.objectiveStatuses();
    const victory = all.filter(o => o.kind === 'victory');
    const defeat = all.filter(o => o.kind === 'defeat');
    audio.fileSfx.play('message');

    const { body } = this.buildCommsModal(buttonLabel, onProceed);
    body.appendChild(this.commsHeader(t('briefing.title')));

    // One labelled section (victory / defeat) listing its objective lines.
    const section = (label: string, color: string, items: ObjectiveStatus[]) => {
      if (items.length === 0) return;
      const h = document.createElement('div');
      h.textContent = label;
      Object.assign(h.style, {
        color, fontSize: '13px', fontWeight: 'bold', letterSpacing: '2px',
        textTransform: 'uppercase', marginBottom: '10px',
      });
      body.appendChild(h);

      const box = document.createElement('div');
      Object.assign(box.style, {
        background: 'rgba(10,12,16,0.6)', border: '1px solid #2a3140',
        borderRadius: '6px', padding: '14px 18px', marginBottom: '24px',
      });
      for (const o of items) {
        const row = document.createElement('div');
        // textContent — objective text comes from scenario data, no HTML.
        row.textContent = `• ${this.objectiveText(o)}`;
        Object.assign(row.style, {
          color: '#e3e8ef', fontSize: '16px', lineHeight: '1.55', marginBottom: '6px',
        });
        box.appendChild(row);
      }
      body.appendChild(box);
    };
    section(t('objective.victoryHeader'), '#9ece6a', victory);
    section(t('objective.defeatHeader'), '#f7768e', defeat);

    if (victory.length === 0 && defeat.length === 0) {
      const none = document.createElement('div');
      none.textContent = t('objective.none');
      Object.assign(none.style, { color: '#9ea6b4', fontSize: '16px', fontStyle: 'italic' });
      body.appendChild(none);
    }
  }

  /** Show a blocking modal at the start of the human player's turn — military-style field report. */
  private showTurnStartModal(): void {
    if (this.turnStartOverlay) return;
    // Scenario event messages that fired this turn-start are shown first, on a
    // dispatch screen the player must acknowledge before seeing the report.
    if (this.pendingDispatchMessages.length > 0) {
      const messages = this.pendingDispatchMessages;
      this.pendingDispatchMessages = [];
      this.showDispatchOverlay(messages, () => this.showTurnStartModal());
      return;
    }
    this.computeTurnReport();

    const { overlay, body } = this.buildCommsModal(t('report.acknowledge'), () => { this.turnStartOverlay = null; });
    this.turnStartOverlay = overlay;

    const report = this.turnReport;

    // Count occurrences of each unit type for compact "\u00d7 N" listing.
    const tally = (units: { type: UnitType }[]): Map<UnitType, number> => {
      const m = new Map<UnitType, number>();
      for (const u of units) m.set(u.type, (m.get(u.type) ?? 0) + 1);
      return m;
    };
    const listRows = (counts: Map<UnitType, number>, color: string): string =>
      [...counts.entries()]
        .map(([utype, count]) => `<div style="color:${color};font-size:17px;padding:3px 0">\u2022 ${t(`unit.${utype}`)}${count > 1 ? ` \u00d7${count}` : ''}</div>`)
        .join('');

    const sectionStyle = 'text-align:left;margin-bottom:20px;background:rgba(10,12,16,0.6);border:1px solid #2a3140;border-radius:6px;padding:14px 18px';
    const headerStyle = 'color:#7e8696;font-size:15px;text-transform:uppercase;letter-spacing:2px;margin-bottom:8px;border-bottom:1px solid #2a3140;padding-bottom:6px';
    const emptyStyle = 'color:#7e8696;font-size:16px;font-style:italic;padding:6px 2px;margin-bottom:16px';
    // A populated category gets a full boxed section; an empty one collapses to
    // a single plain line so the report only shows what actually happened.
    const box = (icon: string, title: string, inner: string) =>
      `<div style="${sectionStyle}"><div style="${headerStyle}">${icon} ${title}</div>${inner}</div>`;
    const emptyLine = (msg: string) => `<div style="${emptyStyle}">${msg}</div>`;

    const lossesBlock = report.unitsLost.length === 0
      ? emptyLine(t('report.noLosses'))
      : box('\u2620', t('report.losses'), listRows(tally(report.unitsLost), '#e06c75'));

    const recruitedBlock = report.unitsRecruited.length === 0
      ? emptyLine(t('report.noRecruited'))
      : box('\u2694', t('report.recruited'), listRows(tally(report.unitsRecruited), '#98c379'));

    const incomeBlock = report.income.length === 0
      ? emptyLine(t('report.noIncome'))
      : box('\u25cf', t('report.income'),
          report.income.map((inc) => `<div style="color:#d8dee9;font-size:17px;padding:3px 0">${t(`building.${inc.buildingType}`)} \u00d7${inc.count}: <span style="color:#e5c07b">+${inc.total}g</span></div>`).join('')
          + `<div style="color:#e5c07b;font-size:18px;font-weight:bold;margin-top:8px;text-align:right">${t('report.incomeTotal', report.totalIncome)}</div>`);

    body.appendChild(this.commsHeader(t('report.title'), t('report.turn', this.world.turnNumber)));
    const sections = document.createElement('div');
    sections.innerHTML = lossesBlock + recruitedBlock + incomeBlock;
    body.appendChild(sections);
  }

  // --- Messages ---

  private addMessage(msg: string): void {
    this.messages.push(msg);
    if (this.messages.length > 8) this.messages.shift();
    this.updateMessagePanel();
  }

  private updateMessagePanel(): void {
    if (!this.messagePanel) return;
    if (this.messages.length === 0) {
      this.messagePanel.innerHTML = `<div style="color:#6f7787;font-style:italic">${t('msg.noMessages')}</div>`;
    } else {
      this.messagePanel.innerHTML = this.messages
        .slice(-5)
        .map((m) => `<div style="padding:2px 0;border-bottom:1px solid #1a1f2a;font-size:12px">${m}</div>`)
        .join('');
    }
  }

  // --- Menu overlay ---

  private openMenu(): void {
    if (this.menuOverlay) return;

    // Shared button style for game menu — image background with military label
    const btnBase = `
      display:block;width:245px;height:46px;border:none;cursor:pointer;
      background-size:100% 100%;background-repeat:no-repeat;background-color:transparent;
      color:#E8DCC2;font-family:'Arial Black','Trebuchet MS',Impact,sans-serif;
      font-weight:bold;font-size:14px;letter-spacing:3px;text-transform:uppercase;
      padding:0 0 0 56px;text-align:center;line-height:46px;
      transition:filter 0.15s;
    `.replace(/\n\s*/g, '');

    this.menuOverlay = document.createElement('div');
    Object.assign(this.menuOverlay.style, {
      position: 'fixed', top: '0', left: '0', width: '100%', height: '100%',
      background: 'rgba(8,10,14,0.78)', zIndex: '1000',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'monospace',
    });

    const card = document.createElement('div');
    Object.assign(card.style, {
      background: PANEL_BG, border: '1px solid #2a3140', borderRadius: '8px',
      padding: '36px 48px',
      display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center',
    });

    card.innerHTML = `
      <style>
        .game-menu-btn:hover { filter: brightness(1.2); }
        .game-menu-btn:active { filter: brightness(0.9); }
      </style>
      <div style="font-size:22px;font-weight:600;color:#E8DCC2;text-align:center;margin-bottom:8px;
        font-family:'Trebuchet MS','Arial Black',Impact,sans-serif;letter-spacing:3px;text-transform:uppercase">${t('modal.gameMenu')}</div>
      <button id="menu-resume" class="game-menu-btn" style="${btnBase}background-image:url('/graphics/ui/new-game-button-background.png')">
        ${t('modal.resumeGame')}
      </button>
      <button id="menu-save" class="game-menu-btn" disabled title="${t('campaign.locked')}" style="${btnBase}background-image:url('/graphics/ui/save-game-button-background.png');opacity:0.4;filter:grayscale(1);cursor:not-allowed;pointer-events:none">
        ${t('modal.saveGame')}
      </button>
      <button id="menu-load" class="game-menu-btn" disabled title="${t('campaign.locked')}" style="${btnBase}background-image:url('/graphics/ui/load-game-button-background.png');opacity:0.4;filter:grayscale(1);cursor:not-allowed;pointer-events:none">
        ${t('modal.loadGame')}
      </button>
      <button id="menu-settings" class="game-menu-btn" style="${btnBase}background-image:url('/graphics/ui/settings-button-background.png')">
        ${t('settings.title')}
      </button>
      <button id="menu-quit" class="game-menu-btn" style="${btnBase}background-image:url('/graphics/ui/exit-button-background.png')">
        ${t('modal.quitToMenu')}
      </button>
    `;

    this.menuOverlay.appendChild(card);
    document.body.appendChild(this.menuOverlay);

    this.menuOverlay.addEventListener('click', (e) => {
      if (e.target === this.menuOverlay) this.closeMenu();
    });

    document.getElementById('menu-resume')?.addEventListener('click', () => this.closeMenu());
    document.getElementById('menu-save')?.addEventListener('click', () => {
      // TODO: save game implementation
      audio.sfx.play('ui-click');
    });
    document.getElementById('menu-load')?.addEventListener('click', () => {
      // TODO: load game implementation
      audio.sfx.play('ui-click');
    });
    document.getElementById('menu-settings')?.addEventListener('click', () => {
      this.settingsDialog.open();
    });
    document.getElementById('menu-quit')?.addEventListener('click', () => this.showQuitConfirm());
  }

  private closeMenu(): void {
    this.menuOverlay?.remove();
    this.menuOverlay = null;
  }

  private showQuitConfirm(): void {
    if (!this.menuOverlay) return;
    const card = this.menuOverlay.querySelector('div') as HTMLDivElement;
    if (!card) return;

    const smallBtnBase = `
      display:inline-block;height:57px;border:none;cursor:pointer;
      background-size:100% 100%;background-repeat:no-repeat;background-color:transparent;
      color:#E8DCC2;font-family:'Trebuchet MS','Arial Black',Impact,sans-serif;
      font-weight:bold;font-size:14px;letter-spacing:2px;text-transform:uppercase;
      line-height:57px;text-align:center;transition:filter 0.15s;
    `.replace(/\n\s*/g, '');

    card.innerHTML = `
      <style>
        .confirm-btn:hover { filter: brightness(1.2); }
        .confirm-btn:active { filter: brightness(0.9); }
      </style>
      <div style="font-size:20px;font-weight:bold;color:#E8DCC2;text-align:center;
        font-family:'Trebuchet MS','Arial Black',Impact,sans-serif;letter-spacing:2px">${t('modal.quitConfirmTitle')}</div>
      <div style="color:#d8dee9;text-align:center;font-style:italic;font-size:13px">
        ${t('modal.quitConfirmBody')}
      </div>
      <div style="display:flex;gap:12px;justify-content:center;margin-top:8px">
        <button id="confirm-cancel" class="confirm-btn" style="${smallBtnBase}width:188px;background-image:url('/graphics/ui/confirmation-button-background.png')">${t('modal.cancelBtn')}</button>
        <button id="confirm-quit" class="confirm-btn" style="${smallBtnBase}width:178px;background-image:url('/graphics/ui/cancel-button-background.png')">${t('modal.quitBtn')}</button>
      </div>
    `;

    document.getElementById('confirm-cancel')?.addEventListener('click', () => {
      this.closeMenu();
    });
    document.getElementById('confirm-quit')?.addEventListener('click', () => {
      this.closeMenu();
      this.scene.start('MainMenu');
    });
  }

  // --- Minimap ---

  /** Mark the cached minimap layer as needing a rebuild on the next draw. */
  private invalidateMinimapCache(): void {
    this.minimapStaticDirty = true;
  }

  /** Build the static minimap layer (background + terrain + bevel) into an
   *  offscreen canvas. Heavy: O(W×H) fills + 2 shadowBlur passes. We only
   *  redo it when fog/scenario/canvas-size changes. */
  private buildMinimapStaticCache(scale: number, ox: number, oy: number, dw: number, dh: number): HTMLCanvasElement {
    const src = this.minimapCanvas;
    const cv = document.createElement('canvas');
    cv.width = src.width;
    cv.height = src.height;
    const ctx = cv.getContext('2d');
    if (!ctx) return cv;
    const cw = cv.width;
    const ch = cv.height;

    // Recessed diamond background
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(ox, oy - dh);
    ctx.lineTo(ox + dw, oy);
    ctx.lineTo(ox, oy + dh);
    ctx.lineTo(ox - dw, oy);
    ctx.closePath();
    ctx.fillStyle = '#0a0d12';
    ctx.fill();
    ctx.clip();

    // Terrain tiles with fog of war — a single dim level: bright where a unit
    // currently sees the tile, otherwise uniformly dimmed (the whole map layout
    // is readable from the start).
    for (let row = 0; row < this.mapHeight; row++) {
      for (let col = 0; col < this.mapWidth; col++) {
        const tile = this.world.map.tiles[row][col];
        const color = TERRAIN_COLORS[tile.type] ?? TERRAIN_DEFS[tile.type].color;
        const mx = ox + (col - row) * scale * 0.5;
        const my = oy + (col + row - this.mapWidth) * scale * 0.25;
        const key = `${col},${row}`;
        const isVisible = this.playerVisible.has(key);
        let r = (color >> 16) & 0xff;
        let g = (color >> 8) & 0xff;
        let bl = color & 0xff;
        if (!isVisible) {
          const dim = 0.5;
          r = Math.round(r * dim);
          g = Math.round(g * dim);
          bl = Math.round(bl * dim);
        }
        ctx.fillStyle = `rgb(${r},${g},${bl})`;
        ctx.fillRect(mx - scale * 0.5, my - scale * 0.25, scale, scale * 0.5);
      }
    }
    ctx.restore();

    // Bevel — recessed shadow effect. Expensive shadowBlur, but baked in
    // here so per-frame draws don't pay for it.
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(ox, oy - dh);
    ctx.lineTo(ox + dw, oy);
    ctx.lineTo(ox, oy + dh);
    ctx.lineTo(ox - dw, oy);
    ctx.closePath();
    ctx.clip();
    ctx.beginPath();
    ctx.moveTo(-cw, -ch);
    ctx.lineTo(cw * 2, -ch);
    ctx.lineTo(cw * 2, ch * 2);
    ctx.lineTo(-cw, ch * 2);
    ctx.closePath();
    ctx.moveTo(ox, oy - dh);
    ctx.lineTo(ox - dw, oy);
    ctx.lineTo(ox, oy + dh);
    ctx.lineTo(ox + dw, oy);
    ctx.closePath();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.85)';
    ctx.shadowBlur = 14;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 4;
    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.fill('evenodd');
    ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
    ctx.shadowBlur = 6;
    ctx.shadowOffsetY = 2;
    ctx.fill('evenodd');
    ctx.restore();

    // Top/bottom bevel highlights
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(ox - dw, oy);
    ctx.lineTo(ox, oy - dh);
    ctx.lineTo(ox + dw, oy);
    ctx.strokeStyle = 'rgba(160, 180, 200, 0.2)';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(ox + dw, oy);
    ctx.lineTo(ox, oy + dh);
    ctx.lineTo(ox - dw, oy);
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.restore();

    return cv;
  }

  private drawMinimap(): void {
    const canvas = this.minimapCanvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const cw = canvas.width;
    const ch = canvas.height;
    ctx.clearRect(0, 0, cw, ch);

    // Fit the isometric diamond (2:1 aspect) into the canvas
    const span = this.mapWidth + this.mapHeight;
    const scaleByW = (2 * cw) / span;
    const scaleByH = (4 * ch) / span;
    const scale = Math.min(scaleByW, scaleByH) * 0.92;
    const ox = cw / 2;
    const oy = ch / 2;
    const dw = span * scale * 0.25;
    const dh = span * scale * 0.125;

    // Background + terrain + bevel: cached static layer.
    if (this.minimapStaticDirty || !this.minimapStaticCache
        || this.minimapStaticCache.width !== cw
        || this.minimapStaticCache.height !== ch) {
      this.minimapStaticCache = this.buildMinimapStaticCache(scale, ox, oy, dw, dh);
      this.minimapStaticDirty = false;
    }
    ctx.drawImage(this.minimapStaticCache, 0, 0);

    // Dynamic layers (buildings, units, viewport indicator) drawn fresh —
    // these change with camera movement and turn state.
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(ox, oy - dh);
    ctx.lineTo(ox + dw, oy);
    ctx.lineTo(ox, oy + dh);
    ctx.lineTo(ox - dw, oy);
    ctx.closePath();
    ctx.clip();

    // Buildings — always shown (part of the revealed static map). The owner's
    // colour only shows while a unit currently sees the tile; otherwise the
    // dot is neutral grey so ownership stays hidden until spotted.
    for (const [, b] of this.world.buildings) {
      const bKey = `${b.col},${b.row}`;
      const bVisible = b.owner === this.localPlayer || this.playerVisible.has(bKey);
      const mx = ox + (b.col - b.row) * scale * 0.5;
      const my = oy + (b.col + b.row - this.mapWidth) * scale * 0.25;
      const bColor = bVisible && b.owner != null ? PLAYER_COLORS[b.owner % PLAYER_COLORS.length] : '#888';
      ctx.fillStyle = bColor;
      ctx.fillRect(mx - 2, my - 2, 4, 4);
    }

    // Draw units (own always, enemy only on visible tiles)
    for (const [, u] of this.world.units) {
      if (u.owner !== this.localPlayer && !this.playerVisible.has(`${u.col},${u.row}`)) continue;
      const mx = ox + (u.col - u.row) * scale * 0.5;
      const my = oy + (u.col + u.row - this.mapWidth) * scale * 0.25;
      ctx.fillStyle = PLAYER_COLORS[u.owner % PLAYER_COLORS.length];
      ctx.beginPath();
      ctx.arc(mx, my, 2, 0, Math.PI * 2);
      ctx.fill();
    }

    // Draw camera viewport indicator — zoom-aware
    const cam = this.cameras.main;
    const topLeft = cam.getWorldPoint(0, HUD_TOP_PX);
    const bottomRight = cam.getWorldPoint(cam.width, cam.height - HUD_BOTTOM_PX);
    const tlGrid = this.screenToGrid(topLeft.x, topLeft.y);
    const brGrid = this.screenToGrid(bottomRight.x, bottomRight.y);

    const v1x = ox + (tlGrid.col - tlGrid.row) * scale * 0.5;
    const v1y = oy + (tlGrid.col + tlGrid.row - this.mapWidth) * scale * 0.25;
    const v2x = ox + (brGrid.col - brGrid.row) * scale * 0.5;
    const v2y = oy + (brGrid.col + brGrid.row - this.mapWidth) * scale * 0.25;

    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1;
    ctx.strokeRect(v1x, v1y, v2x - v1x, v2y - v1y);

    ctx.restore();
  }

  /** Sync minimap canvas resolution with its CSS-rendered size. */
  private resizeMinimap(): void {
    const rect = this.minimapPanel.getBoundingClientRect();
    const w = Math.round(rect.width * window.devicePixelRatio);
    const h = Math.round(rect.height * window.devicePixelRatio);
    if (w > 0 && h > 0 && (this.minimapCanvas.width !== w || this.minimapCanvas.height !== h)) {
      this.minimapCanvas.width = w;
      this.minimapCanvas.height = h;
      this.invalidateMinimapCache();
      this.drawMinimap();
    }
  }

  // --- HUD creation (RTS-style) ---

  private createHud(): void {
    // Inject hover styles for order/recruit cards (only once)
    if (!document.getElementById('card-hover-styles')) {
      const style = document.createElement('style');
      style.id = 'card-hover-styles';
      style.textContent = `
        .order-card:hover:not(:disabled) { filter: brightness(1.35); border-color: #7a8a9a !important; }
        /* Browsers set pointer-events: none on disabled form controls by default,
           which swallows mouseenter/mouseleave. Re-enable them so hover previews
           (e.g. attack range when no enemy is in range) still fire. The HTML
           \`disabled\` attribute still blocks the click action. */
        .order-card:disabled { pointer-events: auto; }
        .actions-cards-row { scrollbar-width: thin; scrollbar-color: #2a3140 #0a0c10; }
        .actions-cards-row::-webkit-scrollbar { height: 8px; }
        .actions-cards-row::-webkit-scrollbar-track { background: #0a0c10; border-radius: 4px; }
        .actions-cards-row::-webkit-scrollbar-thumb { background: #2a3140; border-radius: 4px; }
        .actions-cards-row::-webkit-scrollbar-thumb:hover { background: #3a4a5a; }
      `;
      document.head.appendChild(style);
    }

    // === TOP BAR (resource bar) ===
    this.topBar = document.createElement('div');
    Object.assign(this.topBar.style, {
      position: 'fixed', top: '0', left: '0', width: '100%', height: `${HUD_TOP_PX}px`,
      background: PANEL_BG, borderBottom: '1px solid #2a3140',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 16px', fontFamily: 'system-ui,-apple-system,Segoe UI,sans-serif',
      color: '#d8dee9', fontSize: '15px', zIndex: '100', boxSizing: 'border-box',
      fontVariantNumeric: 'tabular-nums',
    });
    document.body.appendChild(this.topBar);

    // === BOTTOM BAR (3-column panel) ===
    this.bottomBar = document.createElement('div');
    Object.assign(this.bottomBar.style, {
      position: 'fixed', bottom: '0', left: '0', right: '0',
      height: `${HUD_BOTTOM_PX}px`, display: 'flex',
      background: PANEL_BG, borderTop: '1px solid #2a3140',
      fontFamily: 'monospace', color: '#d8dee9', fontSize: '14px',
      zIndex: '100', boxSizing: 'border-box',
    });

    // Left column: selection info (wider to fit all unit stats)
    this.infoPanel = document.createElement('div');
    Object.assign(this.infoPanel.style, {
      flex: '1.2', padding: '8px 14px',
      borderRight: '1px solid #2a3140',
      display: 'flex', flexDirection: 'column', gap: '6px',
      overflow: 'hidden',
    });

    // Center column: actions
    this.actionsPanel = document.createElement('div');
    Object.assign(this.actionsPanel.style, {
      flex: '1', padding: '8px 10px',
      borderRight: '1px solid #2a3140',
      display: 'flex', flexWrap: 'wrap', gap: '6px',
      alignItems: 'flex-start', alignContent: 'flex-start',
      overflow: 'hidden', minWidth: '0',
    });

    // Right column: minimap + toggle buttons (narrower to give info panel more room)
    this.minimapPanel = document.createElement('div');
    Object.assign(this.minimapPanel.style, {
      flex: '0.8', position: 'relative',
      display: 'flex', flexDirection: 'column',
      minWidth: '0',
    });

    this.minimapCanvas = document.createElement('canvas');
    // Canvas resolution set dynamically in resizeMinimap()
    this.minimapCanvas.width = 360;
    this.minimapCanvas.height = HUD_BOTTOM_PX - 2;
    Object.assign(this.minimapCanvas.style, {
      width: '100%', height: '100%', cursor: 'pointer',
    });
    this.minimapPanel.appendChild(this.minimapCanvas);

    // Minimap click + drag → pan camera (matches RTS minimap behavior)
    const onMinimapClick = (e: MouseEvent) => {
      const rect = this.minimapCanvas.getBoundingClientRect();
      const clickX = (e.clientX - rect.left) * (this.minimapCanvas.width / rect.width);
      const clickY = (e.clientY - rect.top) * (this.minimapCanvas.height / rect.height);

      const cw = this.minimapCanvas.width;
      const ch = this.minimapCanvas.height;
      const span = this.mapWidth + this.mapHeight;
      const scaleByW = (2 * cw) / span;
      const scaleByH = (4 * ch) / span;
      const scale = Math.min(scaleByW, scaleByH) * 0.92;
      const ox = cw / 2;
      const oy = ch / 2;

      const relX = clickX - ox;
      const relY = clickY - oy;
      const col = relX / (scale * 0.5) + (relY / (scale * 0.25) + this.mapWidth);
      const row = (relY / (scale * 0.25) + this.mapWidth) - relX / (scale * 0.5);
      const gc = col / 2;
      const gr = row / 2;

      if (gc >= 0 && gc < this.mapWidth && gr >= 0 && gr < this.mapHeight) {
        const screenPos = this.gridToScreen(gc, gr);
        this.centerCameraOn(screenPos.x, screenPos.y);
        this.clampCamera();
        this.drawMinimap();
      }
    };
    this.minimapCanvas.addEventListener('mousedown', onMinimapClick);
    this.minimapCanvas.addEventListener('mousemove', (e) => {
      if (e.buttons & 1) onMinimapClick(e);
    });

    // Toggle buttons (top-right of minimap panel)
    const toggleContainer = document.createElement('div');
    Object.assign(toggleContainer.style, {
      position: 'absolute', top: '4px', right: '4px',
      display: 'flex', gap: '4px', zIndex: '10',
    });

    const btnPlayerList = this.createToggleBtn('/graphics/ui/players-button.png', t('hud.playerList'));
    const btnMessages = this.createToggleBtn('/graphics/ui/messages-button.png', t('hud.messages'));
    const btnFrontLine = this.createToggleBtn('/graphics/ui/frontline-button.png', t('hud.frontLine'));
    // Mirror the default-off state of `showFrontLine` so the icon doesn't
    // mislead the player into thinking the layer is active.
    btnFrontLine.style.opacity = this.showFrontLine ? '1' : '0.4';
    toggleContainer.appendChild(btnFrontLine);
    toggleContainer.appendChild(btnMessages);
    toggleContainer.appendChild(btnPlayerList);
    this.minimapPanel.appendChild(toggleContainer);

    this.bottomBar.appendChild(this.infoPanel);
    this.bottomBar.appendChild(this.actionsPanel);
    this.bottomBar.appendChild(this.minimapPanel);
    document.body.appendChild(this.bottomBar);

    // === FLOATING PANELS ===

    // Player list panel (bottom-right, above HUD) — default ON
    this.playerListPanel = document.createElement('div');
    Object.assign(this.playerListPanel.style, {
      position: 'fixed', bottom: `${HUD_BOTTOM_PX + 8}px`, right: '8px',
      background: 'rgba(20,24,33,0.96)', border: '1px solid #2a3140',
      borderRadius: '6px', padding: '8px 12px', minWidth: '200px',
      fontFamily: 'monospace', color: '#d8dee9', fontSize: '13px',
      zIndex: '101', display: 'block',
    });
    document.body.appendChild(this.playerListPanel);
    this.updatePlayerList();

    // Message panel (bottom-left, above HUD) — default ON
    this.messagePanel = document.createElement('div');
    Object.assign(this.messagePanel.style, {
      position: 'fixed', bottom: `${HUD_BOTTOM_PX + 8}px`, left: '8px',
      width: '260px', maxHeight: '130px', overflowY: 'auto',
      background: 'rgba(20,24,33,0.96)', border: '1px solid #2a3140',
      borderRadius: '6px', padding: '8px 10px',
      fontFamily: 'monospace', color: '#d8dee9', fontSize: '12px',
      zIndex: '101', display: 'block',
    });
    document.body.appendChild(this.messagePanel);
    this.updateMessagePanel();

    // Toggle handlers
    btnPlayerList.addEventListener('click', () => {
      const vis = this.playerListPanel.style.display;
      this.playerListPanel.style.display = vis === 'none' ? 'block' : 'none';
      if (vis === 'none') this.updatePlayerList();
    });
    btnMessages.addEventListener('click', () => {
      const vis = this.messagePanel.style.display;
      this.messagePanel.style.display = vis === 'none' ? 'block' : 'none';
      if (vis === 'none') this.updateMessagePanel();
    });
    btnFrontLine.addEventListener('click', () => {
      this.showFrontLine = !this.showFrontLine;
      btnFrontLine.style.opacity = this.showFrontLine ? '1' : '0.4';
      if (this.showFrontLine) {
        this.recomputeFrontLine();
        this.drawFrontLine();
      } else {
        this.frontLineGfx.clear();
      }
    });
  }

  private createToggleBtn(iconUrl: string, title: string): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.title = title;
    Object.assign(btn.style, {
      width: '36px', height: '30px',
      background: `url('${iconUrl}') center/contain no-repeat`,
      backgroundColor: 'transparent',
      border: 'none', cursor: 'pointer', borderRadius: '4px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '0',
      transition: 'filter 0.15s, transform 0.08s',
    });
    btn.addEventListener('mouseenter', () => { btn.style.filter = 'brightness(1.3)'; });
    btn.addEventListener('mouseleave', () => { btn.style.filter = btn.style.opacity === '0.4' ? 'brightness(0.7)' : ''; });
    btn.addEventListener('mousedown', () => { btn.style.transform = 'scale(0.9)'; });
    btn.addEventListener('mouseup', () => { btn.style.transform = ''; });
    return btn;
  }

  // --- HUD updates ---

  private updateHud(): void {
    this.updateTopBar();
    this.updateInfoPanel();
    this.updateActionsPanel();

    // Whenever the actions panel has no content — a unit is selected (all its
    // orders live in the in-world context menu) or an own building shows
    // recruitment in the info panel — collapse it and let the info panel absorb
    // the freed width so the unit/recruitment section can spread into it. Info
    // sub-sections keep their widths so nothing jumps; the minimap is unaffected
    // (2.2 + 0.8 = 3.0 as before).
    const actionsEmpty = this.actionsPanel.innerHTML.trim() === '';
    this.actionsPanel.style.display = actionsEmpty ? 'none' : '';
    this.infoPanel.style.flex = actionsEmpty ? '2.2' : '1.2';

    // Open/close the recruitment window to match the current selection.
    this.syncRecruitWindow();
  }

  private updateTopBar(): void {
    const pid = currentPlayerId(this.world);
    const player = currentPlayer(this.world);
    const playerColor = PLAYER_COLORS[pid % PLAYER_COLORS.length];

    const income = [...this.world.buildings.values()]
      .filter((b) => b.owner === pid)
      .reduce((sum, b) => sum + BUILDING_DEFS[b.type].income, 0);

    const isAiTurn = this.inputState.kind === 'aiTurn';
    const topBtnStyle = `
      display:inline-block;width:185px;height:36px;border:none;cursor:pointer;
      background-size:100% 100%;background-repeat:no-repeat;background-color:transparent;
      color:#E8DCC2;font-family:'Arial Black','Trebuchet MS',Impact,sans-serif;
      font-weight:bold;font-size:11px;letter-spacing:2px;text-transform:uppercase;
      line-height:36px;text-align:center;padding:0 0 0 28px;
      transition:filter 0.15s;
    `.replace(/\n\s*/g, '');

    const actionBtn = isAiTurn
      ? `<button id="btn-skip-ai" style="${topBtnStyle}background-image:url('/graphics/ui/skip-moves-button-background.png')">${t('hud.skipAi')}</button>`
      : `<button id="btn-end-turn-top" style="${topBtnStyle}background-image:url('/graphics/ui/end-turn-button-background.png')">${t('hud.endTurn')}</button>`;

    this.topBar.innerHTML = `
      <style>
        #btn-end-turn-top:hover, #btn-skip-ai:hover, #btn-menu-top:hover { filter: brightness(1.2); }
        #btn-end-turn-top:active, #btn-skip-ai:active, #btn-menu-top:active { filter: brightness(0.9); }
        #btn-objectives-top:hover { color:#7dcfff; }
      </style>
      <div style="flex:1;display:flex;gap:18px;align-items:center">
        <span style="font-weight:bold;color:${playerColor}">${t('hud.player', pid + 1)}</span>
        <span style="color:#7e8696">${t('hud.turn')}</span> <span>${this.world.turnNumber}</span>
        <span style="color:#7e8696">|</span>
        <span style="color:#e0af68;display:inline-flex;align-items:center;gap:4px"><img src="/graphics/gold-icon.png" style="width:16px;height:16px;object-fit:contain">${t('hud.gold', player.gold, income)}</span>
        <button id="btn-objectives-top" title="${t('objective.panelTitle')}" style="display:inline-flex;align-items:center;gap:5px;padding:0;background:transparent;color:#d8dee9;border:none;cursor:pointer;font-family:inherit;font-size:14px">🎯 ${this.objectiveBadge()}</button>
      </div>
      <div style="flex:1;display:flex;align-items:center;justify-content:center">
        ${actionBtn}
      </div>
      <div style="flex:1;display:flex;align-items:center;justify-content:flex-end">
        <button id="btn-menu-top" style="padding:4px 12px;font-size:16px;
          background:transparent;color:#d8dee9;border:1px solid #2a3140;
          cursor:pointer;border-radius:4px">☰</button>
      </div>
    `;

    document.getElementById('btn-end-turn-top')?.addEventListener('click', () => {
      if (currentPlayerId(this.world) === this.localPlayer && !isAiTurn) {
        this.endTurn();
      }
    });
    document.getElementById('btn-skip-ai')?.addEventListener('click', () => {
      this.executeSkipAi();
    });
    document.getElementById('btn-menu-top')?.addEventListener('click', () => this.openMenu());
    document.getElementById('btn-objectives-top')?.addEventListener('click', () => this.showBriefingOverlay(() => {}, t('briefing.close')));
  }

  /** Runner objectives (empty when there's no scenario runner). */
  private objectiveStatuses(): ObjectiveStatus[] {
    return this.runner ? this.runner.getObjectives() : [];
  }

  /** Top-bar badge: "Cele n/m" over victory objectives (or just "Cele"). */
  private objectiveBadge(): string {
    const vic = this.objectiveStatuses().filter(o => o.kind === 'victory');
    if (vic.length === 0) return t('hud.objectives');
    const done = vic.filter(o => o.done).length;
    return `${t('hud.objectives')} ${done}/${vic.length}`;
  }

  /** Friendly name for a building tag (scenario name if set, else the tag). */
  private buildingTagName(tag: string): string {
    const b = this.scenario?.buildings.find(x => x.tag === tag);
    return b?.name ? localize(b.name) : tag;
  }

  /** Friendly name for a unit tag. */
  private unitTagName(tag: string): string {
    const u = this.scenario?.units.find(x => x.tag === tag);
    return u?.name ? localize(u.name) : tag;
  }

  /** Friendly name for a building group. */
  private groupName(groupId: string): string {
    const g = this.scenario?.buildingGroups?.find(x => x.id === groupId);
    return g ? localize(g.name) : groupId;
  }

  /** Localized one-line description of an objective condition. */
  private objectiveText(o: ObjectiveStatus): string {
    const c = o.condition;
    switch (c.type) {
      case 'capture-hq': return t('objective.captureHq');
      case 'eliminate-all': return t('objective.eliminateAll');
      case 'survive': return t('objective.survive', c.turns);
      case 'hold-buildings': return t('objective.holdBuildings');
      case 'kill-unit': return t('objective.killUnit', this.unitTagName(c.unitTag));
      case 'reach-tile': return t('objective.reachTile', this.unitTagName(c.unitTag));
      case 'protect-unit': return t('objective.protectUnit', this.unitTagName(c.unitTag), c.turns);
      case 'capture-building': return t('objective.captureBuilding', this.buildingTagName(c.buildingTag));
      case 'capture-group': return t('objective.captureGroup', this.groupName(c.groupId));
      case 'hold-building': return t('objective.holdBuilding', this.buildingTagName(c.buildingTag));
      case 'hold-group': return t('objective.holdGroup', this.groupName(c.groupId));
      case 'unit-killed': return t('objective.fail.unitKilled', this.unitTagName(c.unitTag));
      case 'building-lost': return t('objective.fail.buildingLost', this.buildingTagName(c.buildingTag));
      case 'turn-limit': return t('objective.fail.turnLimit', c.turns);
      case 'all-units-killed': return t('objective.fail.allUnitsKilled');
      case 'lose-group': return t('objective.fail.loseGroup', this.groupName(c.groupId));
      case 'fail-hold-building': return t('objective.fail.holdBuilding', this.buildingTagName(c.buildingTag));
      case 'fail-hold-group': return t('objective.fail.holdGroup', this.groupName(c.groupId));
    }
  }

  /** Get tile position for the current selection state. */
  private getSelectionPosition(): { col: number; row: number } | null {
    const st = this.inputState;
    if (st.kind === 'tileSelected') return { col: st.col, row: st.row };
    if (st.kind === 'buildingSelected') {
      const b = this.world.buildings.get(st.buildingId);
      return b ? { col: b.col, row: b.row } : null;
    }
    if ('unitId' in st) {
      const u = this.world.units.get((st as { unitId: number }).unitId);
      return u ? { col: u.col, row: u.row } : null;
    }
    return null;
  }

  /**
   * Info panel — always 3 columns: Teren | Budynek | Jednostka.
   * Shows data for the currently selected tile position.
   */
  /** Terrain type for the info panel: the first land tile bordering water reads
   *  as 'coast' (shoreline), matching `isCoast`. Display-only — stats unchanged. */
  private panelTerrainType(col: number, row: number, type: TerrainType): TerrainType {
    const tiles = this.world.map.tiles;
    const getTerrain = (c: number, r: number): TerrainType | undefined =>
      (r < 0 || r >= this.mapHeight || c < 0 || c >= this.mapWidth) ? undefined : tiles[r][c].type;
    return isCoast(getTerrain, col, row) ? 'coast' : type;
  }

  private updateInfoPanel(): void {
    const st = this.inputState;
    const pos = this.getSelectionPosition();

    if (!pos) {
      this.infoPanel.innerHTML = `<div style="color:#6f7787;padding-top:16px;font-size:14px">${t('info.clickMap')}</div>`;
      return;
    }

    const { col, row } = pos;
    const tile = this.world.map.tiles[row][col];
    const tDef = TERRAIN_DEFS[tile.type];
    // Display-only: a water tile touching land reads as 'coast' (shoreline) so
    // the dedicated coast icon/name shows. Gameplay stats stay from `tDef`.
    const displayTerrain = this.panelTerrainType(col, row, tile.type);
    const building = getBuildingAt(this.world, col, row);

    // Unit: use selected unitId if available, otherwise check tile
    let displayUnit: Unit | null = null;
    if ('unitId' in st) {
      displayUnit = this.world.units.get((st as { unitId: number }).unitId) ?? null;
    } else {
      displayUnit = this.getUnitAt(col, row);
    }

    // Fixed-width info sections so the panel never jumps as selection changes.
    const terrainColStyle = 'flex:0 0 230px;min-width:0;overflow:hidden';
    const unitColStyle = 'flex:1 1 0;min-width:0;overflow:hidden';
    const recruitColStyle = 'flex:1 1 0;min-width:0;overflow:hidden';
    const sepStyle = 'border-left:1px solid #2a3140;padding-left:10px';
    const headerStyle = INFO_SECTION_HEADER;
    // Small recessed "carved slot" for a stat-row icon — same depth/frame look
    // as the big terrain/building slot, sized for inline stat rows.
    const statSlot = (src: string) => `<span style="display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:6px;background:linear-gradient(160deg,#10141b,#1b212c);box-shadow:inset 0 1px 2px rgba(0,0,0,0.65),inset 0 -1px 1px rgba(255,255,255,0.06);border:1px solid #2b3342;vertical-align:middle"><img src="${src}" style="max-width:20px;max-height:20px;width:auto;height:auto"></span>`;
    const si = (file: string) => statSlot(`/graphics/stats/${file}`);
    const gi = (file: string) => statSlot(`/graphics/${file}`);

    // --- Column 1: Teren + Budynek (merged) ---
    const infraLevel = tDef.movementCost <= 0.5 ? 3 : tDef.movementCost <= 1 ? 2 : tDef.movementCost <= 2 ? 1 : 0;
    // Defense comes from a SINGLE source: a completed building overrides the
    // terrain bonus; otherwise the terrain's own bonus applies (matches combat).
    const effectiveDefense = building && building.buildProgress === 0
      ? (BUILDING_DEFS[building.type].defenseBonus ?? 1.0)
      : tDef.defenseBonus;
    // Section title = the terrain or building NAME (shown in the section header).
    // The body keeps just the icon + (building) owner/training info + the stats.
    let headerTitle = t(`terrain.${displayTerrain}`);
    let metaIcon = iconSlot(terrainInfoIcon(displayTerrain), 108, 66);
    let buildingInfo = '';
    if (building) {
      const bDef = BUILDING_DEFS[building.type];
      headerTitle = t(`building.${bDef.type}`);
      metaIcon = iconSlot(buildingInfoIcon(building.type, bDef.icon), 108, 66);
      const ownerHex = building.owner != null
        ? PLAYER_COLORS[building.owner % PLAYER_COLORS.length]
        : '#cccccc';
      const ownerLabel = building.owner != null
        ? `<span style="color:${PLAYER_COLORS[building.owner % PLAYER_COLORS.length]}">${t('hud.player', building.owner + 1)}</span>`
        : `<span style="color:#e0af68">${t('info.neutral')}</span>`;
      // (Currently-training unit is shown in the recruitment section, so it's
      // intentionally omitted here to avoid duplicating the same info.)
      // Custom scenario name (if author tagged this building).
      const nameLs = this.buildingIdToScenarioName.get(building.id);
      const nameLine = nameLs
        ? `<div style="font-size:13px;color:${ownerHex};font-style:italic;margin-top:1px">${localize(nameLs)}</div>`
        : '';

      // Group membership — colour by group unanimity, not this building alone.
      const group = this.buildingIdToGroup.get(building.id);
      let groupLine = '';
      if (group) {
        const tagMap = (this.runner as unknown as { tagMap?: Map<string, number> })?.tagMap;
        let unanimous: number | null | undefined = undefined;
        if (tagMap) {
          for (const tag of group.buildingTags) {
            const bid = tagMap.get(tag);
            if (bid === undefined) continue;
            const bm = this.world.buildings.get(bid);
            if (!bm) continue;
            if (unanimous === undefined) unanimous = bm.owner;
            else if (unanimous !== bm.owner) { unanimous = null; break; }
          }
        }
        const groupColor = unanimous != null
          ? PLAYER_COLORS[unanimous % PLAYER_COLORS.length]
          : '#cccccc';
        groupLine = `<div style="font-size:13px;color:${groupColor};margin-top:1px">${localize(group.name)}</div>`;
      }

      buildingInfo = `
        <div style="min-width:0;display:flex;flex-direction:column;gap:1px">
          <div style="font-size:16px">${ownerLabel}</div>
          ${nameLine}
          ${groupLine}
        </div>
      `;
    }

    // Icon on top with the (building) owner/name BELOW it, so the meta column is
    // only as wide as the icon — leaving room for the stats column on the right
    // and for the cargo section further along.
    const terrainMeta = `<div style="display:flex;flex-direction:column;align-items:flex-start;gap:4px;min-width:0;max-width:140px">${metaIcon}${buildingInfo}</div>`;
    const terrainStats = `
      <div style="flex:0 0 auto;font-size:16px;display:flex;flex-direction:column;gap:4px;border-left:1px solid #2a3140;padding-left:14px">
        <span>${gi('infrastructure-icon.png')} ${'★'.repeat(infraLevel)}${'☆'.repeat(3 - infraLevel)}</span>
        <span>${si('defense-icon.png')} x${effectiveDefense}</span>
        ${building && building.buildProgress === 0 && BUILDING_DEFS[building.type].income > 0
          ? `<span style="color:#e0af68">${gi('gold-icon.png')} ${BUILDING_DEFS[building.type].income}</span>` : ''}
      </div>
    `;
    const terrainBuildingCol = `
      <div style="${terrainColStyle}">
        <div style="${headerStyle}">${headerTitle}</div>
        <div style="display:flex;align-items:flex-start;gap:14px">
          ${terrainMeta}
          ${terrainStats}
        </div>
      </div>
    `;

    // --- Column 2: Jednostka (stats) ---
    let unitCol: string;
    if (displayUnit) {
      const def = UNIT_DEFS[displayUnit.type];
      const hpPct = Math.round((displayUnit.hp / def.maxHp) * 100);
      const hpColor = hpPct > 50 ? '#9ece6a' : hpPct > 25 ? '#e0af68' : '#f7768e';
      const ownerColor = PLAYER_COLORS[displayUnit.owner % PLAYER_COLORS.length];
      // Unit icon at the same size + recessed frame as the terrain/building slot.
      const icon = iconSlot(unitInfoIcon(displayUnit.type), 108, 66);

      let dmgPreview = '';
      if (st.kind === 'confirmAttack' || st.kind === 'confirmPostMoveAttack') {
        const enemy = this.world.units.get(st.targetEnemyId);
        if (enemy) {
          const dmg = calculateDamage(this.world, displayUnit, enemy);
          const eDef = UNIT_DEFS[enemy.type];
          const canCounter = chebyshevDistance(displayUnit, enemy) <= eDef.attackRange || st.kind === 'confirmPostMoveAttack';
          const counter = canCounter ? calculateDamage(this.world, enemy, displayUnit, 0.7) : 0;
          dmgPreview = `
            <div style="margin-top:4px;padding-top:4px;border-top:1px solid #2a3140;font-size:15px">
              <div style="color:#f7768e">${t('info.damage', dmg)}</div>
              ${counter > 0 ? `<div style="color:#e0af68">${t('info.counter', counter)}</div>` : ''}
            </div>
          `;
        }
      }

      const moveColor = displayUnit.acted ? '#6f7787' : (displayUnit.movePoints > 0 ? '#9ece6a' : '#e0af68');

      const xpLevel = levelForXp(displayUnit.xp);
      const xpStars = starsForLevel(xpLevel);
      const xpTier = tierForLevel(xpLevel);
      const xpColor = xpTier === 'bronze' ? '#cd7f32' : xpTier === 'silver' ? '#d8d8d8' : '#ffd700';

      unitCol = `
        <div style="${unitColStyle};${sepStyle}">
          <div style="${headerStyle}">${t(`unit.${def.type}`)}</div>
          <div style="display:flex;align-items:flex-start;gap:14px">
            <!-- Icon with owner + HP stacked BELOW it, so the meta column is only
                 as wide as the icon and the section can be narrowed for cargo. -->
            <div style="flex:0 0 auto;display:flex;flex-direction:column;align-items:flex-start;gap:4px;width:108px">
              ${icon}
              <div style="width:100%;font-size:15px;color:${ownerColor};white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${t('hud.player', displayUnit.owner + 1)}</div>
              <div style="display:flex;align-items:center;gap:4px;width:100%">
                <div style="flex:1;height:8px;background:#1a1d24;border-radius:3px;overflow:hidden">
                  <div style="width:${hpPct}%;height:100%;background:${hpColor}"></div>
                </div>
                <span style="font-size:12px;font-weight:bold">${displayUnit.hp}/${def.maxHp}</span>
              </div>
            </div>
            <div style="flex:1;min-width:0;font-size:16px;display:flex;align-items:flex-start;gap:30px;border-left:1px solid #2a3140;padding-left:16px">
              <div style="flex:0 0 auto;display:flex;flex-direction:column;gap:4px">
                <span title="${t('stat.attack')}">${si('attack-icon.png')} ${def.attack > 0 ? def.attack : '—'}</span>
                <span title="${t('stat.defense')}">${si('defense-icon.png')} ${def.defense}</span>
                <span title="${t('stat.range')}">${si('range-icon.png')} ${def.attackRange > 0 ? def.attackRange : '—'}</span>
              </div>
              <div style="flex:0 0 auto;display:flex;flex-direction:column;gap:4px">
                <span title="${t('stat.sight')}">${si('sight-icon.png')} ${def.sightRange}</span>
                <span title="${t('stat.move')}" style="color:${moveColor}">${si('move-points-icon.png')} ${displayUnit.movePoints}/${def.movementRange}</span>
                <span title="${t('stat.fuel')}" style="color:${def.maxFuel > 0 && displayUnit.fuel < def.maxFuel * 0.25 ? '#f7768e' : '#9ece6a'}">${si('fuel-icon.png')} ${displayUnit.fuel}/${def.maxFuel}</span>
              </div>
              <div style="flex:0 0 auto;display:flex;flex-direction:column;gap:4px">
                <span title="${t('stat.ammo')}" style="color:${def.maxAmmo > 0 && displayUnit.ammo < def.maxAmmo * 0.25 ? '#f7768e' : '#e0af68'}">${si('ammo-icon.png')} ${displayUnit.ammo}/${def.maxAmmo}</span>
                <span title="${t('stat.exp')}">${si('exp-icon.png')} <span style="color:${xpColor};font-weight:bold">${xpLevel > 0 ? '★'.repeat(xpStars) : '—'}</span></span>
                <span title="${t('stat.cargo')}"><span style="display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;font-size:18px;vertical-align:middle">📦</span> ${displayUnit.cargo?.length ?? 0}/${def.cargoCapacity ?? 0}</span>
              </div>
            </div>
          </div>
          ${dmgPreview}
        </div>
        ${this.renderCargoSection(displayUnit, sepStyle, headerStyle)}
      `;
    } else if (st.kind === 'buildingSelected') {
      // Own building: recruiting now happens in the centered window. The bottom
      // panel only shows what's CURRENTLY being trained (with a cancel button).
      const b = this.world.buildings.get(st.buildingId);
      unitCol = (b && b.owner === this.localPlayer && b.training)
        ? `<div style="${recruitColStyle};${sepStyle}">${this.renderTrainingPanel(b)}</div>`
        : '';
    } else {
      // No unit on the tile (empty terrain / enemy building) — omit the column.
      unitCol = '';
    }

    this.infoPanel.innerHTML = `
      <div style="display:flex;gap:10px;height:100%">
        ${terrainBuildingCol}
        ${unitCol}
      </div>
    `;

    // Recruitment / cargo card rows (now hosted here) get vertical-wheel →
    // horizontal scroll.
    this.infoPanel.querySelectorAll('.actions-cards-row').forEach((cardsRow) => {
      cardsRow.addEventListener('wheel', (e) => {
        const we = e as WheelEvent;
        if (we.deltaY !== 0) { e.preventDefault(); cardsRow.scrollLeft += we.deltaY; }
      }, { passive: false });
    });
  }

  /** Wrap actions panel content in a uniform layout: header + full-height cards row. */
  private wrapActionsLayout(header: string, cardsHtml: string): string {
    return `
      <div style="display:flex;flex-direction:column;width:100%;height:100%;overflow:hidden">
        <div style="color:#7e8696;font-size:13px;margin-bottom:6px;font-weight:bold;text-transform:uppercase;letter-spacing:0.5px">${header}</div>
        <div class="actions-cards-row" style="display:flex;gap:8px;align-items:stretch;flex:1;min-height:0;overflow-x:scroll;overflow-y:hidden">${cardsHtml}</div>
      </div>
    `;
  }

  private updateActionsPanel(): void {
    const pid = currentPlayerId(this.world);
    let html = '';
    const st = this.inputState;

    if (pid === this.localPlayer && st.kind !== 'aiTurn') {
      // unitSelected: no order cards — all orders are issued via the in-world
      // context menu (RMB a tile to move+menu, or RMB the unit to act in place).

      if (st.kind === 'orderMove') {
        html = this.wrapActionsLayout(t('order.move'), `
          <div style="display:flex;flex-direction:column;justify-content:center;gap:8px">
            <div style="color:#4a90e2;font-size:14px">${t('order.selectTarget')}</div>
            <button id="btn-cancel-order" style="${this.cancelBtnStyle()}">${t('order.cancel')}</button>
          </div>
        `);
      }

      if (st.kind === 'confirmMove') {
        html = this.wrapActionsLayout(t('order.move'), `
          <div style="display:flex;flex-direction:column;justify-content:center;gap:8px">
            <div style="color:#00ddff;font-size:14px">${t('order.confirmMove')}</div>
            <div style="color:#7e8696;font-size:12px">${t('order.pathLength', tp(st.path.length, 'tile'))}</div>
            <button id="btn-cancel-order" style="${this.cancelBtnStyle()}">${t('order.cancel')}</button>
          </div>
        `);
      }

      if (st.kind === 'orderAttack') {
        html = this.wrapActionsLayout(t('order.attack'), `
          <div style="display:flex;flex-direction:column;justify-content:center;gap:8px">
            <div style="color:#f7768e;font-size:14px">${t('order.selectAttackTarget')}</div>
            <button id="btn-cancel-order" style="${this.cancelBtnStyle()}">${t('order.cancel')}</button>
          </div>
        `);
      }

      if (st.kind === 'confirmAttack') {
        const enemy = this.world.units.get(st.targetEnemyId);
        if (enemy) {
          const eDef = UNIT_DEFS[enemy.type];
          html = this.wrapActionsLayout(t('order.attack'), `
            <div style="display:flex;flex-direction:column;justify-content:center;gap:8px">
              <div style="color:#f7768e;font-size:14px">${t('order.target', t(`unit.${eDef.type}`), enemy.hp, eDef.maxHp)}</div>
              <div style="color:#7e8696;font-size:12px">${t('order.confirmAttack')}</div>
              <button id="btn-cancel-order" style="${this.cancelBtnStyle()}">${t('order.cancel')}</button>
            </div>
          `);
        }
      }

      // After a move the orders are issued through the in-world context menu,
      // so the actions panel shows nothing for `moved` / `pendingMove`.

      if (st.kind === 'orderUnload') {
        html = this.wrapActionsLayout(t('order.unload'), `
          <div style="display:flex;flex-direction:column;justify-content:center;gap:8px">
            <div style="color:#9ece6a;font-size:14px">${t('order.selectUnloadTile')}</div>
            <div style="color:#7e8696;font-size:12px">${tp(st.unloadTiles.length, 'availableTile')}</div>
            <button id="btn-cancel-order" style="${this.cancelBtnStyle()}">${t('order.cancel')}</button>
          </div>
        `);
      }

      if (st.kind === 'movedOrderAttack') {
        html = this.wrapActionsLayout(t('order.attack'), `
          <div style="display:flex;flex-direction:column;justify-content:center;gap:8px">
            <div style="color:#f7768e;font-size:14px">${t('order.selectAttackTarget')}</div>
            <button id="btn-cancel-order" style="${this.cancelBtnStyle()}">${t('order.cancel')}</button>
          </div>
        `);
      }

      if (st.kind === 'confirmPostMoveAttack') {
        const enemy = this.world.units.get(st.targetEnemyId);
        if (enemy) {
          const eDef = UNIT_DEFS[enemy.type];
          html = this.wrapActionsLayout(t('order.attack'), `
            <div style="display:flex;flex-direction:column;justify-content:center;gap:8px">
              <div style="color:#f7768e;font-size:14px">${t('order.target', t(`unit.${eDef.type}`), enemy.hp, eDef.maxHp)}</div>
              <div style="color:#7e8696;font-size:12px">${t('order.confirmAttack')}</div>
              <button id="btn-cancel-order" style="${this.cancelBtnStyle()}">${t('order.cancel')}</button>
            </div>
          `);
        }
      }

      // buildingSelected: recruitment now renders in the info panel (in the
      // freed unit slot), so the actions panel stays empty here.

      if (!html && st.kind === 'idle') {
        html = `<div style="color:#6f7787;padding-top:16px;font-size:14px">${t('order.selectUnit')}</div>`;
      }
    } else if (st.kind === 'aiTurn') {
      html = `<div style="color:#e0af68;font-size:16px;padding-top:16px">${t('order.aiTurn')}</div>`;
    }

    this.actionsPanel.innerHTML = html;

    // Convert vertical mouse wheel to horizontal scroll on the cards row
    const cardsRow = this.actionsPanel.querySelector('.actions-cards-row');
    if (cardsRow) {
      cardsRow.addEventListener('wheel', (e) => {
        const we = e as WheelEvent;
        if (we.deltaY !== 0) {
          e.preventDefault();
          cardsRow.scrollLeft += we.deltaY;
        }
      }, { passive: false });
    }

    this.bindActionHandlers();
  }

  private cancelBtnStyle(): string {
    return 'padding:8px 16px;font-size:13px;background:#2a3140;color:#d8dee9;border:1px solid #4a5568;cursor:pointer;font-family:monospace;border-radius:4px';
  }


  /** Cargo section for the info panel: a "Cargo N/M" column of narrow horizontal
   *  cards (icon + name + HP%, hover-lit) for each loaded unit, plus dim empty
   *  slots. Unloading is issued from the in-world context menu, not here. '' for
   *  non-carriers. */
  private renderCargoSection(unit: Unit, sepStyle: string, headerStyle: string): string {
    const def = UNIT_DEFS[unit.type];
    const capacity = def.cargoCapacity ?? 0;
    if (capacity <= 0) return '';
    const filled = unit.cargo?.length ?? 0;
    let cargoCards = '';

    if (unit.cargo) {
      for (const c of unit.cargo) {
        const cDef = UNIT_DEFS[c.type];
        cargoCards += `
          <div style="display:flex;align-items:center;gap:8px;padding:5px 9px;background:#16241a;border:1px solid #2a4a2a;border-radius:8px;cursor:default;transition:filter 0.15s,border-color 0.15s"
            onmouseenter="this.style.filter='brightness(1.3)';this.style.borderColor='#3f7a3f'"
            onmouseleave="this.style.filter='';this.style.borderColor='#2a4a2a'">
            ${this.cargoMenuRow(c.type, c.hp, cDef.maxHp)}
          </div>`;
      }
    }
    for (let i = filled; i < capacity; i++) {
      cargoCards += `
        <div style="padding:6px 9px;background:#14171d;border:1px dashed #2a3140;border-radius:8px;color:#555;font-size:12px">${t('order.noCargo')}</div>`;
    }

    return `
      <div style="flex:0 0 auto;min-width:190px;max-width:240px;${sepStyle};display:flex;flex-direction:column">
        <div style="${headerStyle}">${t('order.cargo')} ${filled}/${capacity}</div>
        <div style="display:flex;flex-direction:column;gap:6px;overflow-y:auto;overflow-x:hidden">${cargoCards}</div>
      </div>
    `;
  }

  /** Bottom-panel "Rekrutacja" section — now only shows the unit CURRENTLY being
   *  trained: one horizontal card with a recessed depth frame, a progress bar
   *  and a labelled cancel button. No scroll row. Picking new units is the
   *  window's job. */
  private renderTrainingPanel(b: Building): string {
    if (!b.training) return '';
    const tr = b.training;
    const pct = Math.round((1 - tr.turnsLeft / tr.totalTurns) * 100);
    const def = UNIT_DEFS[tr.unitType];
    const elapsed = tr.totalTurns - tr.turnsLeft;
    const refundPct = elapsed > 0 ? '50%' : '100%';
    const slot = (src: string) => `<span style="display:inline-flex;align-items:center;justify-content:center;flex:0 0 auto;width:26px;height:26px;border-radius:5px;background:linear-gradient(160deg,#10141b,#1b212c);box-shadow:inset 0 1px 2px rgba(0,0,0,0.65),inset 0 -1px 1px rgba(255,255,255,0.06);border:1px solid #2b3342"><img src="${src}" style="max-width:17px;max-height:17px;width:auto;height:auto"></span>`;
    const stat = (file: string, val: string | number) => `<span style="display:inline-flex;align-items:center;gap:6px">${slot(`/graphics/stats/${file}`)}<b>${val}</b></span>`;
    return `
      <div style="display:flex;flex-direction:column;width:100%;height:100%;overflow:hidden">
        <div style="${INFO_SECTION_HEADER}">${t('recruit.title')}</div>
        <div style="flex:0 0 auto;display:flex;align-items:flex-start;gap:18px;padding:0 2px">
          ${iconSlot(unitInfoIcon(tr.unitType), 220, 140)}
          <div style="flex:1;min-width:0;display:flex;flex-direction:column;gap:9px">
            <div style="display:flex;align-items:baseline;gap:10px;min-width:0">
              <span style="font-weight:bold;font-size:18px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${t(`unit.${def.type}`)}</span>
              <span style="flex:0 0 auto;font-size:13px;color:#7e8696;text-transform:uppercase;letter-spacing:0.5px">${t('recruit.trains')}</span>
            </div>
            <div style="display:flex;align-items:center;gap:18px;font-size:15px;color:#cfd6e2;flex-wrap:wrap">
              ${stat('attack-icon.png', def.attack)}
              ${stat('defense-icon.png', def.defense)}
              ${stat('move-points-icon.png', def.movementRange)}
              ${stat('range-icon.png', def.attackRange)}
            </div>
            <div style="display:flex;align-items:center;gap:10px">
              <div style="flex:1;height:9px;background:#0a0d12;border-radius:4px;overflow:hidden;box-shadow:inset 0 1px 2px rgba(0,0,0,0.6)"><div style="width:${pct}%;height:100%;background:#9ece6a"></div></div>
              <span style="flex:0 0 auto;font-size:14px;color:#9ece6a;font-weight:bold;white-space:nowrap">${tp(tr.turnsLeft, 'turn')}</span>
            </div>
          </div>
          <button id="btn-cancel-training" title="${t('recruit.cancelTraining', refundPct)}" style="flex:0 0 auto;align-self:center;height:46px;display:flex;align-items:center;justify-content:center;padding:0 20px;font-size:14px;font-weight:bold;border-radius:7px;cursor:pointer;background:#2a1a1a;border:1px solid #4a2a2a;color:#f7768e;transition:background 0.15s,border-color 0.15s"
            onmouseenter="this.style.background='#4a1a1a';this.style.borderColor='#7a3a3a'"
            onmouseleave="this.style.background='#2a1a1a';this.style.borderColor='#4a2a2a'">${t('recruit.cancelBtn')}</button>
        </div>
      </div>`;
  }

  // ── Recruitment window ──────────────────────────────────────────────────

  /** Open or close the centered recruitment window to match the selection:
   *  shown for an own, idle (not-training) production building on the local
   *  player's turn. Called from updateHud, so it tracks every state change. */
  private syncRecruitWindow(): void {
    const st = this.inputState;
    const b = st.kind === 'buildingSelected' ? this.world.buildings.get(st.buildingId) : null;
    const factionId = b ? this.world.factions?.get(b.owner ?? -1) : undefined;
    const canRecruit = !!b && b.owner === this.localPlayer && !b.training
      && currentPlayerId(this.world) === this.localPlayer
      && getRecruitList(b.type, factionId, this.world, b.owner ?? undefined).length > 0;
    if (canRecruit && b && !this.recruitWindow) {
      this.openRecruitWindow(b);
    } else if (this.recruitWindow && (!canRecruit || this.recruitWindowBuildingId !== (b ? b.id : null))) {
      this.closeRecruitWindow();
    }
  }

  private closeRecruitWindow(): void {
    if (this.recruitWindow) { this.recruitWindow.remove(); this.recruitWindow = null; }
    this.recruitWindowBuildingId = null;
    this.recruitSelectedType = null;
  }

  /** Close the window AND drop the building selection (the close semantics:
   *  X / backdrop / RMB / ESC all deselect). */
  private closeAndDeselectRecruit(): void {
    this.closeRecruitWindow();
    if (this.inputState.kind === 'buildingSelected') this.deselect();
  }

  private openRecruitWindow(b: Building): void {
    this.closeRecruitWindow();
    this.recruitWindowBuildingId = b.id;
    const recruits = getRecruitList(b.type, this.world.factions?.get(b.owner ?? -1), this.world, b.owner ?? undefined);
    this.recruitSelectedType = recruits[0] ?? null;

    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
      position: 'fixed', inset: '0', background: 'rgba(0,0,0,0.6)', zIndex: '480',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
    });
    // Backdrop LMB (outside the card) or RMB anywhere → close + deselect.
    overlay.addEventListener('pointerdown', (e) => {
      if (e.target === overlay || e.button === 2) this.closeAndDeselectRecruit();
    });
    overlay.addEventListener('contextmenu', (e) => { e.preventDefault(); this.closeAndDeselectRecruit(); });

    const card = document.createElement('div');
    Object.assign(card.style, {
      // Fixed shape so the window never resizes as the selection changes; inner
      // panes are fixed/scrolling, so nothing jumps in front of the player.
      background: PANEL_BG, border: '1px solid #2a3140', borderRadius: '10px',
      width: '860px', maxWidth: '94vw', height: '560px', maxHeight: '90vh',
      color: '#d8dee9', display: 'flex', flexDirection: 'column',
      position: 'relative', overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
    });
    overlay.appendChild(card);
    document.body.appendChild(overlay);
    this.recruitWindow = overlay;
    this.renderRecruitWindow();
  }

  /** (Re)build the window's content for the current building + selected unit and
   *  wire its interactions. Called on open and on every selection change. */
  private renderRecruitWindow(): void {
    const card = this.recruitWindow?.firstElementChild as HTMLDivElement | undefined;
    const b = this.recruitWindowBuildingId != null ? this.world.buildings.get(this.recruitWindowBuildingId) : null;
    if (!card || !b) return;
    const factionId = this.world.factions?.get(b.owner ?? -1);
    const recruits = getRecruitList(b.type, factionId, this.world, b.owner ?? undefined);
    const gold = currentPlayer(this.world).gold;
    const occupied = isTileOccupied(this.world, b.col, b.row);
    const sel = this.recruitSelectedType && recruits.includes(this.recruitSelectedType)
      ? this.recruitSelectedType : (recruits[0] ?? null);
    this.recruitSelectedType = sel;

    const listHtml = recruits.map(ut => this.recruitListItemHtml(ut, gold, ut === sel)).join('');
    const detailHtml = sel ? this.recruitDetailHtml(b, sel, gold, occupied) : '';

    card.innerHTML = `
      <div style="display:flex;align-items:center;gap:10px;padding:14px 18px;border-bottom:1px solid #2a3140">
        <div style="font-size:20px;font-weight:bold">${t(`building.${b.type}`)}</div>
        ${factionId ? `<div style="font-size:14px;color:#7e8696">· ${factionId}</div>` : ''}
        <div style="margin-left:auto;display:flex;align-items:center;gap:6px;color:#e0af68;font-weight:bold">
          <img src="/graphics/gold-icon.png" style="width:18px;height:18px;object-fit:contain"> ${gold}
        </div>
        <button id="recruit-close" style="width:32px;height:32px;font-size:16px;background:transparent;border:1px solid #2a3140;color:#7e8696;cursor:pointer;border-radius:5px;display:flex;align-items:center;justify-content:center;padding:0;transition:background 0.15s,color 0.15s" onmouseenter="this.style.background='#2a3140';this.style.color='#d8dee9'" onmouseleave="this.style.background='transparent';this.style.color='#7e8696'">✕</button>
      </div>
      <div style="display:flex;min-height:0;flex:1">
        <div style="flex:0 0 244px;overflow-y:auto;padding:14px;display:flex;flex-direction:column;gap:8px;border-right:1px solid #2a3140">${listHtml}</div>
        <div style="flex:1;min-width:0;overflow-y:auto">${detailHtml}</div>
      </div>
    `;

    card.querySelector('#recruit-close')?.addEventListener('click', () => this.closeAndDeselectRecruit());
    card.querySelectorAll('.recruit-item').forEach(el => {
      el.addEventListener('click', () => {
        const ut = (el as HTMLElement).dataset.ut as UnitType | undefined;
        if (ut && ut !== this.recruitSelectedType) { this.recruitSelectedType = ut; this.renderRecruitWindow(); }
      });
    });
    const confirm = card.querySelector<HTMLButtonElement>('#recruit-confirm');
    if (confirm && sel && gold >= UNIT_DEFS[sel].cost && !occupied) {
      confirm.addEventListener('mouseenter', () => { confirm.style.filter = 'brightness(1.2)'; });
      confirm.addEventListener('mouseleave', () => { confirm.style.filter = ''; });
      confirm.addEventListener('click', () => this.recruitUnit(b, sel));
    }
  }

  /** One unit entry in the window's left list — framed (recessed depth), hover-lit. */
  private recruitListItemHtml(ut: UnitType, gold: number, selected: boolean): string {
    const def = UNIT_DEFS[ut];
    const afford = gold >= def.cost;
    return `
      <div class="recruit-item" data-ut="${ut}" style="display:flex;align-items:center;gap:10px;padding:8px 10px;border-radius:8px;cursor:pointer;background:linear-gradient(160deg,#10141b,#1b212c);box-shadow:inset 0 2px 5px rgba(0,0,0,0.6),inset 0 -1px 2px rgba(255,255,255,0.06);border:1px solid ${selected ? '#6a8fb5' : '#2c3442'};${afford ? '' : 'opacity:0.55;'}transition:border-color 0.12s,filter 0.12s"
        onmouseenter="this.style.filter='brightness(1.25)';${selected ? '' : "this.style.borderColor='#41506a'"}"
        onmouseleave="this.style.filter='';${selected ? '' : "this.style.borderColor='#2c3442'"}">
        <div style="flex:0 0 auto;width:58px;height:40px;display:flex;align-items:flex-end;justify-content:center">${unitInfoIcon(ut)}</div>
        <div style="flex:1;min-width:0">
          <div style="font-weight:bold;font-size:15px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${t(`unit.${ut}`)}</div>
          <div style="font-size:13px;display:flex;align-items:center;gap:8px">
            <span style="color:${afford ? '#e0af68' : '#f7768e'}"><img src="/graphics/gold-icon.png" style="width:13px;height:13px;object-fit:contain;vertical-align:-2px"> ${def.cost}</span>
            <span style="color:#7e8696"><img src="/graphics/hourglass-icon.png" style="width:13px;height:13px;object-fit:contain;vertical-align:-2px"> ${def.trainingTime}t</span>
          </div>
        </div>
      </div>`;
  }

  /** Right-hand detail pane for the selected recruit. */
  private recruitDetailHtml(b: Building, ut: UnitType, gold: number, occupied: boolean): string {
    const def = UNIT_DEFS[ut];
    void b;
    const afford = gold >= def.cost;
    const blocked = occupied || !afford;
    const m = this.recruitMatchups(ut);
    const slot = (inner: string) => `<span style="display:inline-flex;align-items:center;justify-content:center;flex:0 0 auto;width:30px;height:30px;font-size:18px;border-radius:6px;background:linear-gradient(160deg,#10141b,#1b212c);box-shadow:inset 0 1px 2px rgba(0,0,0,0.65),inset 0 -1px 1px rgba(255,255,255,0.06);border:1px solid #2b3342">${inner}</span>`;
    const simg = (file: string) => `<img src="/graphics/stats/${file}" style="max-width:20px;max-height:20px;width:auto;height:auto">`;
    const stat = (icon: string, label: string, val: string | number) =>
      `<div style="display:flex;align-items:center;gap:8px">${slot(icon)}<span style="color:#9ea6b4;font-size:14px;min-width:74px">${label}</span><span style="font-weight:bold;font-size:16px;margin-left:auto">${val}</span></div>`;
    const matchItem = (mut: UnitType) =>
      `<span style="display:inline-flex;align-items:center;gap:6px;background:rgba(10,12,16,0.5);border:1px solid #2a3140;border-radius:6px;padding:3px 9px 3px 4px"><span style="flex:0 0 auto;width:34px;height:24px;display:inline-flex;align-items:flex-end;justify-content:center">${unitInfoIcon(mut)}</span><span style="font-size:13px;white-space:nowrap">${t(`unit.${mut}`)}</span></span>`;
    const dash = '<span style="color:#5a6273">—</span>';
    const confirmStyle = `margin-left:auto;width:210px;height:48px;border:none;cursor:${blocked ? 'not-allowed' : 'pointer'};background:url('/graphics/ui/confirmation-button-background.png') no-repeat;background-size:100% 100%;background-color:transparent;color:#E8DCC2;font-family:'Arial Black','Trebuchet MS',Impact,sans-serif;font-weight:bold;font-size:14px;letter-spacing:2px;text-transform:uppercase;line-height:48px;text-align:center;transition:filter 0.15s,transform 0.08s${blocked ? ';filter:grayscale(0.8) brightness(0.55);opacity:0.7' : ''}`;
    return `
      <div style="padding:18px 22px;display:flex;flex-direction:column;gap:16px;height:100%;box-sizing:border-box">
        <div style="display:flex;gap:16px;align-items:flex-start;flex:0 0 auto">
          ${iconSlot(unitInfoIcon(ut), 150, 96)}
          <div style="flex:1;min-width:0">
            <div style="font-size:24px;font-weight:bold;margin-bottom:4px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${t(`unit.${ut}`)}</div>
            <div style="font-size:14px;color:#9ea6b4;line-height:1.5;height:63px;overflow:hidden;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical">${unitDescription(ut)}</div>
          </div>
        </div>
        <div style="flex:0 0 auto;display:grid;grid-template-columns:1fr 1fr;gap:8px 28px;background:rgba(10,12,16,0.5);border:1px solid #2a3140;border-radius:8px;padding:14px 18px">
          ${stat(simg('attack-icon.png'), t('stat.attack'), def.attack > 0 ? def.attack : dash)}
          ${stat(simg('defense-icon.png'), t('stat.defense'), def.defense)}
          ${stat('❤', t('stat.hp'), def.maxHp)}
          ${stat(simg('move-points-icon.png'), t('stat.move'), def.movementRange)}
          ${stat(simg('range-icon.png'), t('stat.range'), def.attackRange > 0 ? def.attackRange : dash)}
          ${stat(simg('sight-icon.png'), t('stat.sight'), def.sightRange)}
          ${stat(simg('ammo-icon.png'), t('stat.ammo'), def.maxAmmo > 0 ? def.maxAmmo : '∞')}
          ${stat('📦', t('stat.cargo'), def.cargoCapacity && def.cargoCapacity > 0 ? def.cargoCapacity : dash)}
        </div>
        <div style="display:flex;flex-direction:column;gap:8px">
          <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap"><span style="color:#9ece6a;font-weight:bold;font-size:14px;min-width:140px">${t('recruit.strongVs')}</span>${m.strong.length ? m.strong.map(matchItem).join('') : dash}</div>
          <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap"><span style="color:#f7768e;font-weight:bold;font-size:14px;min-width:140px">${t('recruit.weakVs')}</span>${m.weak.length ? m.weak.map(matchItem).join('') : dash}</div>
        </div>
        <div style="display:flex;align-items:stretch;gap:16px;margin-top:auto;padding-top:8px">
          <div style="display:flex;align-items:center;gap:12px;background:rgba(8,10,14,0.7);border:1px solid #2a3140;border-radius:8px;padding:10px 16px;box-shadow:inset 0 2px 6px rgba(0,0,0,0.55)">
            <div style="display:flex;flex-direction:column;gap:3px">
              <span style="color:#9ea6b4;font-size:11px;text-transform:uppercase;letter-spacing:1px;font-weight:bold">${t('recruit.costLabel')}</span>
              <span style="color:${afford ? '#e0af68' : '#f7768e'};font-weight:bold;font-size:24px;display:flex;align-items:center;gap:6px"><img src="/graphics/gold-icon.png" style="width:24px;height:24px;object-fit:contain"> ${def.cost}</span>
            </div>
            <div style="width:1px;align-self:stretch;background:#2a3140;margin:2px 4px"></div>
            <div style="display:flex;flex-direction:column;gap:3px">
              <span style="color:#9ea6b4;font-size:11px;text-transform:uppercase;letter-spacing:1px;font-weight:bold">${t('recruit.timeLabel')}</span>
              <span style="color:#d8dee9;font-weight:bold;font-size:24px;display:flex;align-items:center;gap:6px"><img src="/graphics/hourglass-icon.png" style="width:22px;height:22px;object-fit:contain"> ${tp(def.trainingTime, 'turn')}</span>
            </div>
          </div>
          <button id="recruit-confirm" ${blocked ? 'disabled' : ''} title="${occupied ? t('recruit.blocked') : (!afford ? t('recruit.cantAfford') : '')}" style="${confirmStyle}">${t('recruit.recruit')}</button>
        </div>
      </div>`;
  }

  /** Two units this recruit hits hardest, and two that hard-counter it. */
  private recruitMatchups(ut: UnitType): { strong: UnitType[]; weak: UnitType[] } {
    const pool: UnitType[] = ['infantry', 'recon', 'cavalry', 'jeep', 'light-tank', 'medium-tank', 'heavy-tank', 'apc', 'tank-destroyer', 'aa-flakpanzer', 'long-range-artillery', 'anti-tank-artillery', 'plane-fighter', 'plane-bomber', 'ship-battleship', 'ship-submarine'];
    const strong = pool.filter(c => c !== ut && getEffectiveness(ut, c) > 1.0)
      .sort((a, c) => getEffectiveness(ut, c) - getEffectiveness(ut, a)).slice(0, 2);
    const weak = pool.filter(c => c !== ut && getEffectiveness(c, ut) > 1.0)
      .sort((a, c) => getEffectiveness(c, ut) - getEffectiveness(a, ut)).slice(0, 2);
    return { strong, weak };
  }

  /** Execute the capture order for `unit` (animated). Marks the unit acted. */
  private doCapture(unit: Unit): void {
    const building = canCapture(this.world, unit);
    if (!building) return;
    if (this.runner && !this.runner.isActionAllowed({ type: 'capture', unitId: unit.id, buildingId: building.id })) return;
    if (this.runner) {
      this.runner.onActionCompleted({ type: 'capture', unitId: unit.id, buildingId: building.id });
      if (this.processRunnerActions()) return;
    }
    audio.fileSfx.playUnitSfx(unit.type, 'capture', this.factionOf(unit));
    unit.acted = true;
    unit.movePoints = 0;
    const progressBefore = building.captureProgress;
    this.animateCaptureBounce(unit, () => {
      this.deselect(); this.fullRefresh();
    }, () => {
      const completed = executeCapture(this.world, unit, building);
      if (completed) {
        this.addMessage(t('msg.captured', t(`building.${building.type}`)));
        if (this.runner) {
          this.runner.onBuildingCaptured(building.id, unit.owner);
          if (this.processRunnerActions()) return;
        }
        checkElimination(this.world);
        const winner = checkVictory(this.world);
        if (winner != null) { this.showVictory(winner); return; }
      } else {
        this.addMessage(t('msg.capturing', t(`building.${building.type}`)) + ` (${progressBefore + 1}/2)`);
      }
    });
  }

  /** Execute the build (bunker) order for `unit` (animated). Marks the unit acted. */
  private doBuild(unit: Unit): void {
    if (!canBuildOrContinue(this.world, unit)) return;
    if (this.runner && !this.runner.isActionAllowed({ type: 'build', unitId: unit.id })) return;
    this.finishUnitTurn(unit);
    const inProgress = getBuildInProgress(this.world, unit);
    const progressBefore = inProgress ? inProgress.buildProgress : 0;
    this.animateCaptureBounce(unit, () => {
      this.deselect(); this.fullRefresh();
    }, () => {
      const completed = executeBuild(this.world, unit);
      if (completed) this.addMessage(t('msg.buildComplete'));
      else this.addMessage(t('msg.building') + ` (${progressBefore + 1}/2)`);
      if (this.runner) {
        this.runner.onActionCompleted({ type: 'build', unitId: unit.id });
        if (this.processRunnerActions()) return;
      }
    });
  }

  /** Execute the medic heal order (animated). executeHeal marks the medic acted. */
  private doHeal(unit: Unit): void {
    if (unit.type !== 'medic') return;
    if (this.runner && !this.runner.isActionAllowed({ type: 'heal', unitId: unit.id })) return;
    const results = executeHeal(this.world, unit.id);
    if (results.length > 0) {
      if (this.runner) {
        this.runner.onActionCompleted({ type: 'heal', unitId: unit.id });
        if (this.processRunnerActions()) return;
      }
      this.addMessage(t('msg.healed', t(`unit.medic-truck`)));
      const pos = this.gridToScreen(unit.col, unit.row);
      this.cameras.main.pan(pos.x, pos.y, 200, 'Sine.easeInOut', false, (_cam, progress) => {
        if (progress === 1) {
          this.animateHealSequence(results, () => { this.syncEntities(); this.deselect(); });
        }
      });
    } else {
      this.syncEntities();
      this.deselect();
    }
  }

  /** Execute the supply-truck resupply order (animated). executeResupply marks it acted. */
  private doResupply(unit: Unit): void {
    // Any mobile resupplier (truck→land, carrier/plane-transport→air,
    // ship-transport→water) — not just the supply-truck. Guarding on type here
    // softlocked the carrier/transports: the pending move was already committed
    // by the menu wrapper, so an early return left the input stuck in pendingMove.
    if (resolveResupplyDomain(unit.type) === null) return;
    if (this.runner && !this.runner.isActionAllowed({ type: 'resupply', unitId: unit.id })) return;
    const results = executeResupply(this.world, unit.id);
    if (results.length > 0) {
      if (this.runner) {
        this.runner.onActionCompleted({ type: 'resupply', unitId: unit.id });
        if (this.processRunnerActions()) return;
      }
      this.addMessage(t('msg.resupplied'));
      const pos = this.gridToScreen(unit.col, unit.row);
      this.cameras.main.pan(pos.x, pos.y, 200, 'Sine.easeInOut', false, (_cam, progress) => {
        if (progress === 1) {
          this.animateResupplyPlus(results, () => { this.syncEntities(); this.deselect(); });
        }
      });
    } else {
      this.syncEntities();
      this.deselect();
    }
  }

  /** Begin the unload flow for `unit`: highlight adjacent drop tiles valid for
   *  the chosen cargo unit (its own domain — planes may launch onto water). */
  private doUnload(unit: Unit, cargoIndex = 0, pending?: { originCol: number; originRow: number; cost: number; steps: number }): void {
    const tiles = getUnloadTiles(this.world, unit, cargoIndex);
    if (tiles.length > 0) {
      this.inputState = { kind: 'orderUnload', unitId: unit.id, cargoIndex, unloadTiles: tiles, pending };
      this.refresh();
    }
  }

  /** Inline row for a carried unit — icon + name + HP% — shared by the cargo
   *  info section and the unload entries in the context menu. */
  private cargoMenuRow(type: string, hp: number, maxHp: number): string {
    const hpPct = Math.round((hp / maxHp) * 100);
    const hpColor = hpPct > 50 ? '#9ece6a' : hpPct > 25 ? '#e0af68' : '#f7768e';
    const src = UNIT_ICON_PATHS[type] ?? '';
    return `<img src="${src}" style="flex:0 0 auto;width:34px;height:28px;object-fit:contain;object-position:center bottom">`
      + `<span style="flex:1 1 auto;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;color:#cfe8ff">${t(`unit.${type}`)}</span>`
      + `<span style="flex:0 0 auto;font-size:13px;font-weight:bold;color:${hpColor}">${hpPct}%</span>`;
  }

  private bindActionHandlers(): void {
    const st = this.inputState;

    document.getElementById('btn-cancel-order')?.addEventListener('click', () => {
      this.cancelBack();
    });

    // Move + attack orders have no HUD cards — both are RMB-driven.

    document.getElementById('btn-order-capture')?.addEventListener('click', () => {
      if (st.kind === 'unitSelected' || st.kind === 'moved') {
        const unit = this.world.units.get((st as { unitId: number }).unitId);
        if (unit) this.doCapture(unit);
      }
    });

    document.getElementById('btn-order-build')?.addEventListener('click', () => {
      if (st.kind === 'unitSelected' || st.kind === 'moved') {
        const unit = this.world.units.get((st as { unitId: number }).unitId);
        if (unit) this.doBuild(unit);
      }
    });

    document.getElementById('btn-order-heal')?.addEventListener('click', () => {
      if (st.kind === 'unitSelected') {
        const unit = this.world.units.get(st.unitId);
        if (unit) this.doHeal(unit);
      }
    });

    document.getElementById('btn-order-resupply')?.addEventListener('click', () => {
      if (st.kind === 'unitSelected') {
        const unit = this.world.units.get(st.unitId);
        if (unit) this.doResupply(unit);
      }
    });

    // Recruitment itself now lives in the centered window; the bottom panel only
    // keeps the cancel-training control for the in-progress unit.
    document.getElementById('btn-cancel-training')?.addEventListener('click', () => {
      if (st.kind === 'buildingSelected') {
        const b = this.world.buildings.get(st.buildingId);
        if (b?.training) this.showCancelTrainingConfirm(b);
      }
    });
  }

  private updatePlayerList(): void {
    this.playerListPanel.innerHTML = '';

    const title = document.createElement('div');
    title.textContent = t('playerList.title');
    Object.assign(title.style, { fontWeight: 'bold', marginBottom: '6px' });
    this.playerListPanel.appendChild(title);

    for (const p of this.world.players) {
      const color = PLAYER_COLORS[p.id % PLAYER_COLORS.length];
      const units = [...this.world.units.values()].filter((u) => u.owner === p.id).length;
      const buildings = [...this.world.buildings.values()].filter((b) => b.owner === p.id).length;
      const isLocal = p.id === this.localPlayer;
      const isActive = p.id === currentPlayerId(this.world);

      const row = document.createElement('div');
      Object.assign(row.style, {
        display: 'flex', alignItems: 'center', gap: '6px', padding: '3px 0',
        opacity: p.alive ? '1' : '0.4',
        textDecoration: p.alive ? 'none' : 'line-through',
        fontWeight: isActive ? 'bold' : 'normal',
      });

      // Flag
      const factionPick = this.gameConfig?.factions.get(p.id);
      if (factionPick) {
        const flag = createFlagElement(factionPick.factionId, 24, 16);
        Object.assign(flag.style, { border: '1px solid #2a3140' });
        row.appendChild(flag);
      } else {
        // Fallback: color swatch
        const swatch = document.createElement('span');
        Object.assign(swatch.style, {
          display: 'inline-block', width: '10px', height: '10px',
          background: color, borderRadius: '2px', flexShrink: '0',
        });
        row.appendChild(swatch);
      }

      const nameSpan = document.createElement('span');
      nameSpan.textContent = `${t('hud.player', p.id + 1)}${isLocal ? ` ${t('playerList.you')}` : ''}`;
      nameSpan.style.color = color;
      row.appendChild(nameSpan);

      const goldSpan = document.createElement('span');
      goldSpan.textContent = `${p.gold}g`;
      Object.assign(goldSpan.style, { color: '#e0af68', marginLeft: 'auto' });
      row.appendChild(goldSpan);

      const statsSpan = document.createElement('span');
      statsSpan.textContent = `${t('hud.unitsShort', units)} ${t('hud.buildingsShort', buildings)}`;
      Object.assign(statsSpan.style, { fontSize: '11px', color: '#7e8696' });
      row.appendChild(statsSpan);

      this.playerListPanel.appendChild(row);
    }
  }

  // --- Fog of War ---

  /** Recompute visible tiles for the human player and update explored. Paints
   *  the fog change INCREMENTALLY (only the tiles that flipped) — a full
   *  `drawFog()` over the whole map was the big stall after every move/commit. */
  private recomputeVisible(): void {
    const prev = this.playerVisible;
    const next = computePlayerVisible(this.localPlayer, this.world);
    const revealed: string[] = [];
    for (const k of next) { if (!prev.has(k)) revealed.push(k); }
    const hidden: string[] = [];
    for (const k of prev) { if (!next.has(k)) hidden.push(k); }
    this.playerVisible = next;
    updateExplored(this.localPlayer, this.playerVisible, this.world);
    this.drawFogDelta(revealed, hidden);
    // Selection-mode attackable-enemies cache depends on visibility — bust
    // it so the next drawHighlights recomputes from the fresh fog state.
    const st = this.inputState;
    if (st.kind === 'unitSelected') {
      st._cachedAttackableEnemies = undefined;
    }
  }

  /** Full fog repaint — clears the offscreen canvas and redraws every fogged tile. */
  private drawFog(): void {
    // The minimap's terrain shading depends on fog state — invalidate the
    // cached layer so the next minimap repaint picks up the new visibility.
    this.invalidateMinimapCache();
    const ctx = this.fogCanvasCtx;
    const canvas = this.fogCanvas;
    if (ctx && canvas) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#000000';
      for (let row = 0; row < this.mapHeight; row++) {
        for (let col = 0; col < this.mapWidth; col++) {
          const key = `${col},${row}`;
          if (this.playerVisible.has(key)) continue;
          // Single dim level everywhere a unit isn't currently looking.
          this.fogPaintTile(ctx, col, row, TurnGameScene.FOG_DIM);
        }
      }
      ctx.globalAlpha = 1;
      this.refreshFogTexture();
    }
    // Terrain overlays (forest/mountain) are always-revealed objects: bright
    // when a unit currently sees the tile, otherwise a uniform "revealed" dim —
    // never the near-black unexplored tint.
    for (const [key, img] of this.terrainOverlayImages) {
      if (this.playerVisible.has(key)) {
        img.clearTint();
        img.setAlpha(1);
      } else {
        img.setTint(0x555555);
        img.setAlpha(1);
      }
    }
  }

  /**
   * Direct fog update from known delta arrays — repaints only the changed
   * tiles on the offscreen canvas (cheaper than a full clear+redraw).
   * Used during movement animation where we know exactly which tiles changed.
   */
  private drawFogDelta(revealed: string[], hidden: string[]): void {
    // Same invalidation reason as drawFog — fog changed, cached terrain
    // layer needs a redo.
    if (revealed.length === 0 && hidden.length === 0) return;
    this.invalidateMinimapCache();
    const ctx = this.fogCanvasCtx;
    if (ctx) {
      ctx.fillStyle = '#000000';
      for (const key of revealed) {
        const idx = key.indexOf(',');
        const col = +key.slice(0, idx); const row = +key.slice(idx + 1);
        this.fogClearTile(ctx, col, row);
      }
      for (const key of hidden) {
        const idx = key.indexOf(',');
        const col = +key.slice(0, idx); const row = +key.slice(idx + 1);
        this.fogPaintTile(ctx, col, row, TurnGameScene.FOG_DIM);
      }
      ctx.globalAlpha = 1;
      this.refreshFogTexture();
    }

    for (const key of revealed) {
      const overlay = this.terrainOverlayImages.get(key);
      if (overlay) { overlay.clearTint(); overlay.setAlpha(1); }
    }
    // Always-revealed objects: dim to the "revealed" tint when no longer in
    // sight, never the near-black unexplored tint.
    for (const key of hidden) {
      const overlay = this.terrainOverlayImages.get(key);
      if (overlay) { overlay.setTint(0x555555); overlay.setAlpha(1); }
    }
  }

  /** Re-upload the fog canvas to its GPU texture. */
  private refreshFogTexture(): void {
    if (!this.fogTextureKey) return;
    const tex = this.textures.get(this.fogTextureKey) as Phaser.Textures.CanvasTexture;
    tex.refresh();
  }

  // --- Front Line ---

  /** Recompute influence map + front line segments (called from refresh()). */
  private recomputeFrontLine(): void {
    this.cachedInfluenceMap = computeInfluenceMap(this.world);
    this.cachedFrontLines = extractFrontLines(
      this.cachedInfluenceMap, this.world,
      (c, r) => this.gridToScreen(c, r),
    );
  }

  /** Draw cached front lines with glow effect in player colors. */
  private drawFrontLine(): void {
    const g = this.frontLineGfx;
    g.clear();

    for (const seg of this.cachedFrontLines) {
      const rp = seg.renderPoints;
      const colorA = PALETTE.player[seg.playerA % PALETTE.player.length];
      const offsetA = seg.sideSignA * 3;

      if (seg.playerB != null) {
        const colorB = PALETTE.player[seg.playerB % PALETTE.player.length];
        this.drawGlowPolyline(g, rp, colorA, -offsetA); // inside (toward territory)
        this.drawGlowPolyline(g, rp, colorB, offsetA);  // outside (away from territory)
      } else {
        this.drawGlowPolyline(g, rp, colorA, 0);
      }
    }
  }

  // Glow layers (constant) — outer → inner (wide faint → narrow bright)
  private static readonly GLOW_LAYERS: readonly [number, number][] = [
    [14, 0.06], [10, 0.10], [6, 0.20], [3, 0.70],
  ];

  /** Draw a glowing polyline using pre-computed normals — no sqrt per point. */
  private drawGlowPolyline(
    g: Phaser.GameObjects.Graphics,
    points: { x: number; y: number; nx: number; ny: number }[],
    color: number, offset: number,
  ): void {
    if (points.length < 2) return;
    for (const [width, alpha] of TurnGameScene.GLOW_LAYERS) {
      g.lineStyle(width, color, alpha);
      g.beginPath();
      if (offset === 0) {
        g.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) g.lineTo(points[i].x, points[i].y);
      } else {
        g.moveTo(points[0].x + points[0].nx * offset, points[0].y + points[0].ny * offset);
        for (let i = 1; i < points.length; i++) {
          g.lineTo(points[i].x + points[i].nx * offset, points[i].y + points[i].ny * offset);
        }
      }
      g.strokePath();
    }
  }

  // --- Refresh ---

  /** Light refresh — sync display without recomputing front lines. */
  private refresh(): void {
    this.syncEntities();
    this.drawHighlights();
    if (this.fogDirty) { this.drawFog(); this.fogDirty = false; }
    if (this.showFrontLine) this.drawFrontLine();
    this.updateHud();
    this.updateTutorialBanner();
  }

  /** Full refresh — recompute influence map + front lines (after territory changes). */
  private fullRefresh(): void {
    this.syncEntities();
    this.drawHighlights();
    if (this.fogDirty) { this.drawFog(); this.fogDirty = false; }
    if (this.showFrontLine) {
      this.recomputeFrontLine();
      this.drawFrontLine();
    }
    this.updateHud();
    this.updateTutorialBanner();
  }

  // --- Cleanup ---

  private teardown(): void {
    if (this.onResizeMinimap) {
      window.removeEventListener('resize', this.onResizeMinimap);
      this.onResizeMinimap = null;
    }
    if (this.onIdleInput) {
      const wake = this.onIdleInput;
      const canvas = this.game.canvas;
      canvas.removeEventListener('pointermove', wake);
      canvas.removeEventListener('pointerdown', wake);
      canvas.removeEventListener('pointerup', wake);
      canvas.removeEventListener('wheel', wake);
      window.removeEventListener('keydown', wake);
      window.removeEventListener('keyup', wake);
      window.removeEventListener('touchstart', wake);
      window.removeEventListener('touchmove', wake);
      // Make sure the loop is awake when the scene shuts down — otherwise the
      // next scene's first frame may never fire.
      if (!this.game.loop.running) this.game.loop.wake();
      this.onIdleInput = null;
    }
    if (this.fogTextureKey && this.textures.exists(this.fogTextureKey)) {
      this.textures.remove(this.fogTextureKey);
    }
    this.fogCanvas = null;
    this.fogCanvasCtx = null;
    this.fogTextureKey = '';
    if (this.terrainBakedKey && this.textures.exists(this.terrainBakedKey)) {
      this.textures.remove(this.terrainBakedKey);
    }
    this.terrainBakedKey = '';
    for (const t of this.buildingNameLabels.values()) t.destroy();
    for (const t of this.buildingGroupLabels.values()) t.destroy();
    this.buildingNameLabels.clear();
    this.buildingGroupLabels.clear();
    this.buildingIdToScenarioName.clear();
    this.buildingIdToGroup.clear();
    this.buildingLabelLayer?.destroy();
    this.buildingLabelLayer = null;
    this.scenario = null;
    this.topBar?.remove();
    this.bottomBar?.remove();
    this.playerListPanel?.remove();
    this.messagePanel?.remove();
    this.menuOverlay?.remove();
    this.victoryOverlay?.remove();
    this.unitInfoOverlay?.remove();
    this.turnStartOverlay?.remove();
    this.scenarioMessageOverlay?.remove();
    this.tutorialBanner?.remove();
    this.tutorialBanner = null;
  }

  /**
   * Hide entities that lie outside the current camera viewport. Cheaper than
   * relying on Phaser's per-object camera culling because we skip the matrix
   * transform calculations entirely for off-screen sprites. Final visibility
   * combines the fog rules used by `syncEntities` with viewport bounds.
   *
   * Called after every `syncEntities` (intended-state may have changed) and
   * after every camera pan/zoom (viewport bounds changed). Iteration cost is
   * O(units + buildings + terrain overlays) which is much cheaper than
   * Phaser's per-frame display-list traversal of all those same sprites.
   */
  private cullEntities(): void {
    const cam = this.cameras.main;
    // Phaser's worldView Rect factors in zoom + camera origin (default
    // 0.5, 0.5). We mirror that calculation manually so the cull box matches
    // what's actually rendered. The margin must be generous because:
    //  - Building/forest/mountain icons use origin (0.5, 1) and extend upward
    //    from their grid position by up to ~2 tiles worth of pixels.
    //  - Position-based check uses tile center but the rendered sprite
    //    bounding box can extend well beyond that.
    const w = cam.width / cam.zoom;
    const h = cam.height / cam.zoom;
    const cx = cam.scrollX + cam.width * cam.originX;
    const cy = cam.scrollY + cam.height * cam.originY;
    // Margin: 2 tile widths horizontally, 4 tile heights vertically (top side
    // extra because icons render upward). At zoom=2.5 this is 128px / 128px
    // in world coords — a small fraction of the viewport but enough to keep
    // tall sprites from popping at the edges.
    const marginX = TILE_W * 2;
    const marginTop = TILE_H * 6;
    const marginBottom = TILE_H * 2;
    const left = cx - w * cam.originX - marginX;
    const top = cy - h * cam.originY - marginTop;
    const right = left + w + marginX * 2;
    const bottom = top + h + marginTop + marginBottom;
    const inView = (x: number, y: number): boolean =>
      x >= left && x <= right && y >= top && y <= bottom;

    // Buildings are part of the always-revealed static map — never hidden by
    // fog, only culled when off-screen. (Ownership stays hidden via tinting in
    // syncEntities, not by hiding the sprite.)
    for (const [id, b] of this.world.buildings) {
      const sprite = this.buildingSprites.get(id);
      const icon = this.buildingIcons.get(id);
      if (!sprite || !icon) continue;
      const { x, y } = this.gridToScreen(b.col, b.row);
      const visible = inView(x, y);
      if (sprite.visible !== visible) sprite.setVisible(visible);
      if (icon.visible !== visible) icon.setVisible(visible);
    }

    // Units: hide enemy units on non-visible tiles OR off-screen.
    for (const [id, u] of this.world.units) {
      const sprite = this.unitSprites.get(id);
      const icon = this.unitIconImages.get(id);
      if (!sprite || !icon) continue;
      const isOwn = u.owner === this.localPlayer;
      const fogVisible = isOwn || this.playerVisible.has(`${u.col},${u.row}`);
      const { x, y } = this.gridToScreen(u.col, u.row);
      const visible = fogVisible && inView(x, y);
      if (sprite.visible !== visible) sprite.setVisible(visible);
      if (icon.visible !== visible) icon.setVisible(visible);
      const shadow = this.unitShadowImages.get(id);
      if (shadow && shadow.visible !== visible) shadow.setVisible(visible);
      const ammo = this.ammoAlertIcons.get(id);
      if (ammo) {
        // Supply-alert visibility is independently driven elsewhere — only hide
        // when off-screen, never force-show.
        if (!visible && ammo.visible) ammo.setVisible(false);
      }
      const fuel = this.fuelAlertIcons.get(id);
      if (fuel) {
        if (!visible && fuel.visible) fuel.setVisible(false);
      }
    }

    // Terrain overlays (forest, mountain): always rendered (no fog hiding),
    // but cull off-screen ones.
    for (const [key, img] of this.terrainOverlayImages) {
      const idx = key.indexOf(',');
      const col = +key.slice(0, idx); const row = +key.slice(idx + 1);
      const { x, y } = this.gridToScreen(col, row);
      const visible = inView(x, y);
      if (img.visible !== visible) img.setVisible(visible);
    }

    // Road overlays — same deal.
    for (const [key, img] of this.roadOverlayImages) {
      const idx = key.indexOf(',');
      const col = +key.slice(0, idx); const row = +key.slice(idx + 1);
      const { x, y } = this.gridToScreen(col, row);
      const visible = inView(x, y);
      if (img.visible !== visible) img.setVisible(visible);
    }
  }

  /**
   * Request a re-render on the next frame. Cheap to call; idempotent within a
   * frame. Wakes the global TimeStep if it was put to sleep by the idle path
   * below.
   */
  private requestRedraw(): void {
    this.redrawPending = true;
    this.lastActivityTime = performance.now();
    const loop = this.game.loop;
    if (!loop.running) loop.wake();
  }

  /**
   * Install DOM-level event listeners that wake the game loop on any user
   * activity. Phaser's input plugins only deliver events while the loop is
   * running — we need a wake before they fire. Bound to canvas (pointer
   * events) and window (keyboard / touch) since the HUD lives in the DOM
   * above the canvas and a click on a HUD button shouldn't keep the engine
   * asleep.
   */
  private installIdleWakeHooks(): void {
    const wake = (): void => { this.requestRedraw(); };
    this.onIdleInput = wake;
    const canvas = this.game.canvas;
    canvas.addEventListener('pointermove', wake);
    canvas.addEventListener('pointerdown', wake);
    canvas.addEventListener('pointerup', wake);
    canvas.addEventListener('wheel', wake, { passive: true });
    window.addEventListener('keydown', wake);
    window.addEventListener('keyup', wake);
    window.addEventListener('touchstart', wake);
    window.addEventListener('touchmove', wake);
  }

  /** Create/position/remove one pulsing supply-alert badge (low ammo or low
   *  fuel) for a unit. The pulse tween is created once and reused. */
  private syncSupplyAlert(
    store: Map<number, Phaser.GameObjects.Image>,
    id: number,
    textureKey: string,
    active: boolean,
    x: number,
    y: number,
  ): void {
    if (active && this.textures.exists(textureKey)) {
      let icon = store.get(id);
      if (!icon) {
        icon = this.add.image(0, 0, textureKey);
        icon.setOrigin(0.5, 0.5);
        icon.displayWidth = 10;
        icon.displayHeight = 10;
        store.set(id, icon);
        // Pulsing scale animation — base scale captured after sizing.
        const baseScaleX = icon.scaleX;
        const baseScaleY = icon.scaleY;
        this.tweens.add({
          targets: icon,
          scaleX: { from: baseScaleX * 0.85, to: baseScaleX * 1.15 },
          scaleY: { from: baseScaleY * 0.85, to: baseScaleY * 1.15 },
          duration: 600,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }
      icon.setPosition(x, y);
      icon.setDepth(99990);
      icon.setAlpha(1);
      icon.setVisible(true);
    } else {
      const icon = store.get(id);
      if (icon) {
        icon.destroy();
        store.delete(id);
      }
    }
  }

  /**
   * True while the scene needs a continuous 60Hz loop for pulse animations
   * (range previews, attack indicators, tutorial highlights).
   */
  private isPulseActive(): boolean {
    const st = this.inputState;
    if (
      st.kind === 'orderMove' || st.kind === 'confirmMove' ||
      st.kind === 'orderAttack' || st.kind === 'movedOrderAttack' ||
      st.kind === 'confirmAttack' || st.kind === 'confirmPostMoveAttack' ||
      st.kind === 'pendingMoveAttack' ||
      this.runner?.getTutorialState() != null
    ) return true;
    // Keep the loop awake so the pulsing red target glow animates while a unit
    // is selected and has enemies in range.
    if (st.kind === 'unitSelected' && (st._cachedAttackableEnemies?.size ?? 0) > 0) return true;
    // Low-supply alerts (ammo/fuel) pulse via yoyo tweens that only tick while
    // the game loop is awake. Keep it running whenever any alert badge EXISTS —
    // not merely while visible — so a brief off-screen cull can't put the loop
    // to sleep and freeze the tween mid-cycle (which made it jump/stutter on
    // wake). Burning a frame while a unit is low on supply is well worth it.
    if (this.ammoAlertIcons.size > 0 || this.fuelAlertIcons.size > 0) return true;
    return false;
  }

  override update(_time: number, delta: number): void {
    // Detect camera mutations from any source (touch/RMB pan, wheel zoom,
    // keyboard, AI follow, scenario events) — treat as redraw trigger and
    // re-run viewport culling so newly-revealed entities show up.
    const cam = this.cameras.main;
    if (cam.scrollX !== this.camTrack.x || cam.scrollY !== this.camTrack.y || cam.zoom !== this.camTrack.zoom) {
      this.camTrack.x = cam.scrollX;
      this.camTrack.y = cam.scrollY;
      this.camTrack.zoom = cam.zoom;
      this.redrawPending = true;
      this.lastActivityTime = performance.now();
      this.cullEntities();
    }

    // Advance highlight pulse timer
    this.highlightTime += delta / 1000;

    // Skip expensive per-frame work during movement/combat animations
    if (this.animating) {
      this.redrawPending = true;
      this.lastActivityTime = performance.now();
      return;
    }

    const pulseActive = this.isPulseActive();
    const wasDirty = this.redrawPending;
    this.redrawPending = false;

    // Redraw highlights every frame when animated tiles are shown (pulse effect)
    const st = this.inputState;
    if (
      st.kind === 'orderMove' || st.kind === 'confirmMove' ||
      st.kind === 'orderAttack' || st.kind === 'movedOrderAttack' ||
      st.kind === 'confirmAttack' || st.kind === 'confirmPostMoveAttack' ||
      st.kind === 'pendingMoveAttack' || st.kind === 'unitSelected' ||
      this.attackRangePreviewId != null ||
      this.runner?.getTutorialState() != null
    ) {
      this.drawHighlights();
    }

    // Redraw minimap periodically (every 10 frames) to keep viewport indicator current
    this.minimapFrame++;
    if (this.minimapFrame % 10 === 0) {
      this.drawMinimap();
    }

    // If nothing is animating, pulsing, or dirty — put the loop to sleep so
    // we burn zero CPU/GPU until something happens. DOM event listeners
    // (mouse/keyboard/touch) and explicit requestRedraw() calls wake us up.
    //
    // Hold the loop awake briefly after every interaction so rapid clicks
    // don't each pay one RAF cycle to bring it back up — that read as input
    // lag (especially on selection glow).
    const idleMs = performance.now() - this.lastActivityTime;
    if (!pulseActive && !wasDirty && !this.animating && idleMs > TurnGameScene.SLEEP_DELAY_MS) {
      this.game.loop.sleep();
    }
  }
}
